/* eslint-disable no-useless-escape */
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import * as pdfjsLib from 'pdfjs-dist';
import JSZip from 'jszip';
import './App.css';
import { tcEldStatus, tcCamStatus, tcOverallStatus, tcAIContext } from './truckerCloud';
import { parseCABJson, sanitizeCabData, validateCabSchema } from './utils/cabJson';
import { exportMarketIntelligencePDF, buildMarketIntelligencePDFBlob } from './utils/exportMarketIntelligencePDF';
import { buildLeadSheetPDFBlob } from './utils/exportLeadSheetPDF';
import { CURRENT_TERMS_VERSION, CURRENT_PRIVACY_VERSION, TERMS_TEXT, PRIVACY_TEXT } from './config/legal';
import { buildSaveErrorMsg, isSaveRetryable } from './utils/saveErrorHelpers';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

// In dev the React app is on :3000, Express on :3001 — call the API server directly
// so we never depend on the CRA proxy being in sync with a restarted server.js.
const API_BASE = window.location.port === '3000' ? 'http://localhost:3001' : '';

// ─── Constants ───────────────────────────────────────────────────────────────

const TABS = ['Documents', 'Notes', 'Lead Sheet', 'Loss Runs', 'Equipment', 'Drivers', 'AI Analysis', 'Submission', 'Missing Items', 'Playbook', 'Markets'];

const NOTE_CATEGORIES = [
  { value: 'todo',         label: 'To Do',        color: '#b45309', bg: '#fffbeb', border: '#fcd34d' },
  { value: 'underwriting', label: 'Underwriting', color: '#1d4ed8', bg: '#eff6ff', border: '#93c5fd' },
  { value: 'relationship', label: 'Relationship', color: '#6d28d9', bg: '#f5f3ff', border: '#c4b5fd' },
  { value: 'renewal',      label: 'Renewal',      color: '#065f46', bg: '#ecfdf5', border: '#6ee7b7' },
  { value: 'documents',    label: 'Documents',    color: '#0d2e5e', bg: '#eef2ff', border: '#a5b4fc' },
  { value: 'other',        label: 'Other',        color: '#475569', bg: '#f8fafc', border: '#cbd5e1' },
];

const PIPELINE_STATUSES = {
  prospect:    { label: 'Prospect',    color: '#6366F1', bg: '#EEF2FF', border: '#A5B4FC' },
  in_progress: { label: 'In Progress', color: '#F59E0B', bg: '#FFFBEB', border: '#FCD34D' },
  bound:       { label: 'Bound',       color: '#10B981', bg: '#ECFDF5', border: '#6EE7B7' },
  lost:        { label: 'Lost',        color: '#EF4444', bg: '#FEF2F2', border: '#FCA5A5' },
  renewal:     { label: 'Renewal',     color: '#8B5CF6', bg: '#F5F3FF', border: '#C4B5FD' },
};

const LOST_REASONS = ['Price / Premium', 'Coverage Gap', 'Client went direct', 'Lost to competitor', 'Account non-renewed', 'Risk appetite', 'Other'];

const DOC_TYPES = [
  { id: 'appLossSummary', label: 'Application & Loss Summary',    icon: '📋' },
  { id: 'cab',            label: 'CAB Report',                    icon: '📋' },
  { id: 'lossRuns',       label: 'Loss Runs (3-5 yrs)',            icon: '📊' },
  { id: 'coi',            label: 'COI',                           icon: '🛡️' },
  { id: 'ifta',           label: 'IFTA Reports',                  icon: '⛽' },
  { id: 'equipmentList',  label: 'Equipment List',                icon: '🚛' },
  { id: 'driverList',     label: 'Driver List',                   icon: '👤' },
  { id: 'mvrs',           label: 'MVRs',                          icon: '🚗' },
  { id: 'cdl',            label: 'CDL Images',                    icon: '🪪' },
];

const TRACTOR_COLS  = ['Year', 'Make', 'VIN', 'Value', 'Loss Payee'];
const TRAILER_COLS  = ['Year', 'Make', 'VIN', 'Value', 'Loss Payee'];
const DRIVER_COLS   = ['Name', 'DOB', 'DL Number', 'State', 'Yrs Exp', 'Hire Date'];

// Known power-unit (tractor) makes — any unit with one of these makes is always
// a tractor regardless of what the AI returned or which VIN prefix it carries.
const TRACTOR_MAKES_SET = new Set([
  'VOLVO', 'FREIGHTLINER', 'KENWORTH', 'PETERBILT', 'MACK',
  'INTERNATIONAL', 'NAVISTAR', 'WESTERN STAR', 'FORD', 'GMC', 'RAM',
]);

// WMI / VIN prefix patterns that identify class 7-8 power units.
// Only consulted when the make is absent or unrecognised.
const TRACTOR_VIN_PREFIXES = [
  '4V4', '4V1',           // Volvo Trucks North America
  '1FUJ', '1FUB', '3AKJ', // Freightliner / Mercedes-Benz
  '1XP',  '2NP',          // Peterbilt
  '1HTM', '1HSM', '1HT',  // International (Navistar)
  '1M1',  '2M1',          // Mack Trucks
  '5PVN',                 // Western Star
  '1GJG', '1GD',          // GMC medium/heavy-duty
];

/**
 * Post-process AI-parsed equipment to correct tractor/trailer misclassification.
 * Any unit in the trailers array that has a recognised truck make or VIN prefix
 * is moved to tractors. Units with no recognised make and no truck VIN prefix
 * remain as trailers.
 */
function reclassifyEquipment(parsedTractors, parsedTrailers) {
  const isKnownTruck = (r) => {
    const make = String(r.make || '').trim().toUpperCase().replace(/\s+/g, ' ');
    const vin  = String(r.vin  || '').trim().toUpperCase();
    if (TRACTOR_MAKES_SET.has(make)) return true;
    return TRACTOR_VIN_PREFIXES.some(p => vin.startsWith(p));
  };
  const correctedTractors = [
    ...(parsedTractors || []),
    ...(parsedTrailers || []).filter(r => isKnownTruck(r)),
  ];
  const correctedTrailers = (parsedTrailers || []).filter(r => !isKnownTruck(r));
  return { tractors: correctedTractors, trailers: correctedTrailers };
}

const colKey = (col) => col.toLowerCase().replace(/\s+/g, '_');

const blankRow = (cols) => Object.fromEntries(cols.map(c => [colKey(c), '']));

const POLICY_YEARS = ['2025-2026', '2024-2025', '2023-2024', '2022-2023', '2021-2022'];

// ── Loss run Layer-2 helpers (deterministic JS, no AI) ────────────────────────
const CARRIER_COVERAGE_MAP = [
  ['progressive',               ['autoLiability', 'cargo', 'physicalDamage', 'generalLiability']],
  ['great west casualty',       ['autoLiability', 'cargo', 'physicalDamage', 'generalLiability']],
  ['geico',                     ['autoLiability', 'cargo', 'physicalDamage', 'generalLiability']],
  ['government employees',      ['autoLiability', 'cargo', 'physicalDamage', 'generalLiability']],
  ['united financial casualty', ['autoLiability', 'cargo', 'physicalDamage', 'generalLiability']],
  ['canal insurance',           ['autoLiability', 'cargo', 'physicalDamage', 'generalLiability']],
];
const LR_COVERAGE_DEFAULT = ['autoLiability'];

// Returns mapped lines for known carriers, null for unknown carriers.
function getLRCoverageLines(carrierName) {
  const lower = (carrierName || '').toLowerCase();
  for (const [key, lines] of CARRIER_COVERAGE_MAP) {
    if (lower.includes(key)) return lines;
  }
  return null;
}

// Module-level carrier name normalization — applied at export time so old Redis data gets
// normalized names without requiring a re-parse.
function normalizeCarrierName(name) {
  const n = (name || '').trim();
  const lower = n.toLowerCase();
  if (lower.includes('texcaz'))             return 'Trisura Specialty Insurance Company';
  if (lower.includes('progressive'))        return 'Progressive Northern Insurance Company';
  if (lower.includes('universal casualty')) return 'Universal Casualty Risk Retention Group Inc';
  return n;
}

// CDA/Prime loss run format: "Total Incurred" = claim paid only (not paid+reserve).
// All other carriers use the standard definition (total incurred = reserve + paid).
const CARRIER_PAID_ONLY_MAP = ['prime insurance', 'prime holdings', 'commercial driver'];
function getCarrierIncurredField(carrierName) {
  const lower = (carrierName || '').toLowerCase();
  return CARRIER_PAID_ONLY_MAP.some(k => lower.includes(k)) ? 'paid' : 'total';
}

// Slot "YYYY-(YYYY+1)" = 12-month window [Jul 1 YYYY, Jun 30 YYYY+1]
function lrSlotWindow(slotLabel) {
  const y1 = parseInt(slotLabel.split('-')[0], 10);
  return {
    start: new Date(y1, 6, 1),
    end:   new Date(y1 + 1, 5, 30, 23, 59, 59),
  };
}

// Generate `count` year slots going backwards from today
function computeYearSlots(count = 5) {
  const today   = new Date();
  const jul1    = new Date(today.getFullYear(), 6, 1);
  const baseYear = today >= jul1 ? today.getFullYear() : today.getFullYear() - 1;
  return Array.from({ length: count }, (_, i) => `${baseYear - i}-${baseYear - i + 1}`);
}

// Pure JS: map extracted documents → 4×N grid (no AI involvement)
// yib — years in business; when > 0 and < yearSlots.length, only slots within
// that window are flagged as missing (older slots predate the company's existence).
function buildLossRunGrid(docs, yearSlots, yib = 0) {
  const today    = new Date();
  const LR_LINES = ['autoLiability', 'cargo', 'physicalDamage', 'generalLiability'];
  const LR_LABELS = { autoLiability: 'Auto Liability', cargo: 'Cargo', physicalDamage: 'Physical Damage', generalLiability: 'General Liability' };

  const parseD = (s) => {
    if (!s || /^active$/i.test(s.trim())) return null;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  };

  // grid[line][slot]
  const grid = {};
  for (const line of LR_LINES) {
    grid[line] = {};
    for (const slot of yearSlots) {
      grid[line][slot] = { present: false, active: false, carrier: '', policyNumber: '', claims: 0, incurred: 0 };
    }
  }

  for (const doc of (docs || [])) {
    const docStart = parseD(doc.effDate);
    if (!docStart) continue;
    const expD    = parseD(doc.expDate);
    const docEnd  = expD || today;
    const isActive = !expD || expD > today;
    // Known carriers: carrier map is authoritative (package policies credit all lines
    // even when only one line has claims). Unknown carriers: AI-extracted lines first,
    // then conservative default.
    const mappedLines = getLRCoverageLines(doc.carrierName);
    const lines = mappedLines !== null
      ? mappedLines
      : (Array.isArray(doc.coverageLines) && doc.coverageLines.length > 0)
        ? doc.coverageLines
        : LR_COVERAGE_DEFAULT;

    for (const slot of yearSlots) {
      const { start, end } = lrSlotWindow(slot);
      if (docStart <= end && docEnd >= start) {
        for (const line of lines) {
          const cell = grid[line][slot];
          if (!cell.present || (isActive && !cell.active)) {
            cell.present      = true;
            cell.active       = isActive || cell.active;
            cell.carrier      = doc.carrierName  || cell.carrier;
            cell.policyNumber = doc.policyNumber || cell.policyNumber;
          }
          cell.claims   += (doc.claimCount   || 0);
          cell.incurred += (doc.totalIncurred || 0);
        }
      }
    }
  }

  // Only flag slots within the insured's years-in-business window as missing.
  const requiredSlots = (yib > 0 && yib < yearSlots.length)
    ? yearSlots.slice(0, Math.ceil(yib))
    : yearSlots;
  const missingParts = [];
  for (const line of LR_LINES) {
    const missing = requiredSlots.filter(s => !grid[line][s].present);
    if (missing.length > 0) missingParts.push(`${LR_LABELS[line]}: ${missing.join(', ')}`);
  }

  const result = {
    yearSlots,
    notes: missingParts.length ? 'Missing coverage:\n' + missingParts.join('\n') : '',
  };
  for (const line of LR_LINES) {
    result[line] = yearSlots.map(slot => ({ year: slot, ...grid[line][slot] }));
  }
  return result;
}

// ── Loss Summary Workbook Data Builder ───────────────────────────────────────
// Pure function: no DOM, no ExcelJS, no React state — testable standalone.
// Returns structured data consumed by exportLossExcel.
// Supports N policy-term rows per section (not limited to 5).
function buildLossSummaryWorkbookData(lossSummary, cabData) {
  const pn = (v) => { const n = parseFloat(String(v || '').replace(/[$,]/g, '')); return isNaN(n) ? 0 : n; };
  const SEC_KEYS = ['autoLiability', 'cargo', 'physicalDamage', 'generalLiability'];

  // Collect all unique policy-year labels from all sections and summary rows.
  const yearLabelSet = new Set();
  for (const sk of SEC_KEYS) {
    for (const row of (lossSummary?.[sk] || [])) {
      if (row.policyYear) yearLabelSet.add(row.policyYear);
    }
  }
  for (const row of (lossSummary?.summary || [])) {
    if (row.policyYear) yearLabelSet.add(row.policyYear);
  }
  // Exclude same-year short-term labels (e.g. "2025-2025") from summary/historical.
  // These still appear in section rows but don't get a summary row of their own.
  const yearSlots = Array.from(yearLabelSet)
    .filter(y => { const [y1, y2] = y.split('-').map(Number); return !isNaN(y1) && !isNaN(y2) && y1 !== y2; })
    .sort((a, b) => {
      const [a1, a2 = 0] = a.split('-').map(Number);
      const [b1, b2 = 0] = b.split('-').map(Number);
      return b1 !== a1 ? b1 - a1 : b2 - a2;
    });

  const fleetMap = computeFleetSummary(cabData?.fleet);
  const summaryRows = lossSummary?.summary || [];

  // Summary rows: financial totals aggregated from all section rows for each year label.
  // Only confirmed rows (loss run uploaded) count toward totals; missing rows do not.
  const summary = yearSlots.map(year => {
    let lossReserve = 0, totalIncurred = 0, totalClaims = 0;
    let hasConfirmed = false;
    for (const sk of SEC_KEYS) {
      for (const row of (lossSummary?.[sk] || []).filter(r => r.policyYear === year)) {
        if (row.lossRunStatus !== 'missing_loss_run' && row.lossRunStatus !== 'coverage_assumed') {
          hasConfirmed  = true;
          lossReserve   += pn(row.lossReserve);
          totalIncurred += pn(row.totalIncurred);
          totalClaims   += pn(row.numClaims);
        }
      }
    }
    const sr = summaryRows.find(r => r.policyYear === year) || {};
    const fv = fleetMap[year] || {};
    return {
      year,
      lossReserve:   hasConfirmed ? lossReserve   : null,
      totalIncurred: hasConfirmed ? totalIncurred  : null,
      totalClaims:   hasConfirmed ? totalClaims    : null,
      units:        pn(sr.units || fv.units || ''),
      totalMileage: pn(sr.totalMileage || fv.totalMileage || ''),
    };
  });

  // Per-section rows: all policy-term rows sorted descending, including recovery.
  const sections = {};
  for (const sk of SEC_KEYS) {
    const rows = [...(lossSummary?.[sk] || [])].sort((a, b) => {
      const [a1, a2 = 0] = (a.policyYear || '0-0').split('-').map(Number);
      const [b1, b2 = 0] = (b.policyYear || '0-0').split('-').map(Number);
      return b1 !== a1 ? b1 - a1 : b2 - a2;
    });
    sections[sk] = rows.map(row => {
      const isMissing = row.lossRunStatus === 'missing_loss_run' || row.lossRunStatus === 'coverage_assumed';
      return {
        insurer:             normalizeCarrierName(row.insurer || '') || null,
        policyYear:          row.policyYear     || null,
        policyNumber:        row.policyNumber   || null,
        effectiveDate:       row.effectiveDate  || null,
        expirationDate:      row.expirationDate || null,
        lossRunStatus:       row.lossRunStatus  || 'confirmed',
        numClaims:           isMissing ? null : pn(row.numClaims),
        lossReserve:         isMissing ? null : pn(row.lossReserve),
        totalIncurred:       isMissing ? null : pn(row.totalIncurred),
        recovery:            isMissing ? null : pn(row.recovery || 0),
        appReportedOverride: row.appReportedOverride || null,
        needsReview:         row.needsReview || false,
      };
    });
  }

  // Historical side table: one entry per year label aligned with yearSlots.
  const historical = yearSlots.map(year => {
    const sr = summaryRows.find(r => r.policyYear === year) || {};
    const fv = fleetMap[year] || {};
    const parts = year.split('-');
    const y1 = (parts[0] || '').slice(-2);
    const y2 = (parts[1] || '').slice(-2);
    return {
      year,
      yearShort:    (y1 && y2) ? `${y1}-${y2}` : year,
      units:        pn(sr.units || fv.units || ''),
      totalMileage: pn(sr.totalMileage || fv.totalMileage || ''),
    };
  });

  return { yearSlots, summary, sections, historical };
}

const NA_OPTIONS = [
  'N/A - No Claims Filed',
  'N/A - No Standalone Policy',
  'N/A - Loss Run Not Available',
];

const blankLossRow = (year = '') => ({
  insurer: '', policyYear: year, policyNumber: '', numClaims: '', lossReserve: '', totalIncurred: '', recovery: '', claimDetail: [],
});

const blankSummaryRow = (year) => ({
  policyYear: year, lossReserve: '', totalIncurred: '', totalClaims: '', units: '', totalMileage: '',
});

const initLossSummary = () => {
  const years = computeYearSlots(5);
  return {
    summary:         years.map(y => blankSummaryRow(y)),
    autoLiability:   years.map(y => blankLossRow(y)),
    cargo:           years.map(y => blankLossRow(y)),
    physicalDamage:  years.map(y => blankLossRow(y)),
    generalLiability:years.map(y => blankLossRow(y)),
  };
};

const LEAD_SHEET_REQUIRED_FIELDS = [
  // Company / identity (filled by CAB/APP)
  'ownerName','phone','fein','state','garagingAddress','currentInsurer',
  // Policy / timeline
  'currentXDate','yearsInBusiness','radiusOfOperation',
  // Underwriting questions (manual or APP)
  'fleetChangeLastYear','planningAddTrucks','beingNonRenewed',
  'ownerOpsOrCompany','driversOutOfState','eldsInstalled','dashcams',
];

const initLeadSheet = () => ({
  ownerName: '', ownerDob: '', phone: '',
  fein: '',
  state: '', garagingAddress: '', currentInsurer: '', currentXDate: '',
  yearsInBusiness: '',
  radiusOfOperation: '', fleetChangeLastYear: '', planningAddTrucks: '',
  addTrucksCount: '', beingNonRenewed: '', nonRenewalReason: '',
  ownerOpsOrCompany: '', driversOutOfState: '', driversOutOfStateExplain: '',
  eldsInstalled: '', eldBrand: '', dashcams: '', dashcamBrand: '',
  basicAlerts: '', correctiveActionPlan: '', targetPricing: '',
  flatBed:    { oversize: false, overweight: false, steel: false, steelCoils: false, oilfieldEquipment: false, logs: false },
  dryVan:     { amazonDelivery: false, householdGoods: false, doublesTriples: false, lastMileDelivery: false, electronics: false, liquor: false, usMail: false, cigarettes: false, designerClothing: false },
  reefer:     { seafood: false, eggs: false, dairy: false, shellfish: false, hangingMeat: false, frozenMeat: false, flowerNursery: false },
  autoHauler: { newVehicles: false, usedVehicles: false, dealerToManufacturer: false, dealerToDealer: false, exoticLuxury: false, classicAntique: false, avgCostPerVehicle: '' },
  loadAgreements: '',
  additionalNotes: '',
});

const COMMODITY_GROUPS = [
  { key: 'flatBed', label: 'Flat Bed', items: [
    { key: 'oversize', label: 'Oversize' }, { key: 'overweight', label: 'Overweight' },
    { key: 'steel', label: 'Steel' }, { key: 'steelCoils', label: 'Steel Coils' },
    { key: 'oilfieldEquipment', label: 'Oilfield Equipment' }, { key: 'logs', label: 'Logs' },
  ]},
  { key: 'dryVan', label: 'Dry Van / Box Truck', items: [
    { key: 'amazonDelivery', label: 'Amazon Delivery' }, { key: 'householdGoods', label: 'Household Goods' },
    { key: 'doublesTriples', label: 'Doubles/Triples' }, { key: 'lastMileDelivery', label: 'Last Mile Delivery' },
    { key: 'electronics', label: 'Electronics' }, { key: 'liquor', label: 'Liquor' },
    { key: 'usMail', label: 'US Mail' }, { key: 'cigarettes', label: 'Cigarettes' },
    { key: 'designerClothing', label: 'Designer Clothing' },
  ]},
  { key: 'reefer', label: 'Reefer Truck', items: [
    { key: 'seafood', label: 'Seafood' }, { key: 'eggs', label: 'Eggs' },
    { key: 'dairy', label: 'Dairy' }, { key: 'shellfish', label: 'Shellfish' },
    { key: 'hangingMeat', label: 'Hanging Meat' }, { key: 'frozenMeat', label: 'Frozen Meat' },
    { key: 'flowerNursery', label: 'Flower/Nursery' },
  ]},
  { key: 'autoHauler', label: 'Auto Hauler', items: [
    { key: 'newVehicles', label: 'New Vehicles' }, { key: 'usedVehicles', label: 'Used Vehicles' },
    { key: 'dealerToManufacturer', label: 'Dealer to Manufacturer' }, { key: 'dealerToDealer', label: 'Dealer to Dealer' },
    { key: 'exoticLuxury', label: 'Exotic/Luxury' }, { key: 'classicAntique', label: 'Classic/Antique' },
  ]},
];

// Maps CAB fleet history snapshots to policy years.
// Returns { "2025-2026": { units: "11", totalMileage: "..." }, ... }
function computeFleetSummary(fleet) {
  if (!fleet) return {};
  const result = {};

  const parseYear = (dateStr) => {
    if (!dateStr) return null;
    const parts = String(dateStr).match(/(\d+)[/-](\d+)[/-](\d+)/);
    if (!parts) return null;
    let yr = parseInt(parts[3]);
    if (yr < 100) yr += 2000;
    return yr;
  };

  const currentUnits   = parseFloat(fleet.totalPowerUnits) || 0;
  const currentMileage = parseFloat(String(fleet.annualMileage || '').replace(/,/g, '')) || 0;
  const baseMpu        = (currentUnits > 0 && currentMileage > 0) ? currentMileage / currentUnits : 0;

  const pool = (fleet.history || [])
    .slice()
    .sort((a, b) => (parseYear(b.date) || 0) - (parseYear(a.date) || 0));

  POLICY_YEARS.forEach((year, i) => {
    const [, y2Str] = year.split('-');
    const y2 = parseInt(y2Str);

    let snap = null;
    const idx = pool.findIndex(s => parseYear(s.date) === y2);
    if (idx !== -1) snap = pool.splice(idx, 1)[0];

    if (i === 0) {
      // Current year: prefer direct fleet fields
      result[year] = {
        units:        currentUnits   ? String(currentUnits)   : (snap && snap.powerUnits ? String(snap.powerUnits) : ''),
        totalMileage: currentMileage ? String(currentMileage) : (snap && snap.mileage    ? String(snap.mileage)    : ''),
      };
    } else if (snap) {
      const units   = snap.powerUnits || 0;
      const mileage = snap.mileage    || (baseMpu > 0 && units > 0 ? Math.round(baseMpu * units) : 0);
      result[year] = {
        units:        units   ? String(units)   : '',
        totalMileage: mileage ? String(mileage) : '',
      };
    }
  });

  return result;
}

// ─── Loss run normalization helpers ─────────────────────────────────────────

/**
 * Converts effective/expiration dates to a "YYYY-YYYY" policy year label.
 * e.g., buildPolicyYearLabel("04/01/2024", "03/31/2025") → "2024-2025"
 */
function buildPolicyYearLabel(effectiveDate, expirationDate) {
  try {
    const eff = new Date(effectiveDate);
    const exp = new Date(expirationDate);
    if (isNaN(eff.getTime()) || isNaN(exp.getTime())) return '';
    let effYear = eff.getFullYear();
    let expYear = exp.getFullYear();
    if (effYear < 100) effYear += 2000;
    if (expYear < 100) expYear += 2000;
    if (effYear < 1990 || effYear > 2100 || expYear < 1990 || expYear > 2100) return '';
    return `${effYear}-${expYear}`;
  } catch { return ''; }
}

/**
 * Splits a multi-year policy into annual slots.
 * e.g., getPolicyYearSlots("02/28/2022","02/28/2025") →
 *   [{label:"2022-2023",start:Date,end:Date},{label:"2023-2024",...},{label:"2024-2025",...}]
 */
function getPolicyYearSlots(effectiveDateStr, expirationDateStr) {
  try {
    const eff = new Date(effectiveDateStr);
    const exp = new Date(expirationDateStr);
    if (isNaN(eff.getTime()) || isNaN(exp.getTime())) return [];
    let effYear = eff.getFullYear();
    let expYear = exp.getFullYear();
    if (effYear < 100) effYear += 2000;
    if (expYear < 100) expYear += 2000;
    if (effYear < 1990 || effYear > 2100 || expYear < 1990 || expYear > 2100) return [];
    if (effYear >= expYear) {
      // Same-year policy (e.g. short-term): label stays as "YYYY-YYYY", not annualized.
      return [{ label: `${effYear}-${expYear}`, start: eff, end: exp }];
    }
    const slots = [];
    for (let yr = effYear; yr < expYear; yr++) {
      const slotStart = new Date(eff); slotStart.setFullYear(yr);
      const slotEnd   = new Date(eff); slotEnd.setFullYear(yr + 1);
      slots.push({ label: `${yr}-${yr + 1}`, start: slotStart, end: slotEnd });
    }
    return slots;
  } catch { return []; }
}

/**
 * Given a date-of-loss string, returns the label of the slot it falls into.
 * Falls back to the last slot if no exact match.
 */
function getClaimSlot(dateOfLossStr, slots) {
  if (!slots || !slots.length) return '';
  try {
    const dol = new Date(dateOfLossStr);
    if (isNaN(dol.getTime())) return slots[slots.length - 1].label;
    for (const slot of slots) {
      if (dol >= slot.start && dol < slot.end) return slot.label;
    }
    return slots[slots.length - 1].label;
  } catch { return slots[slots.length - 1].label; }
}

/**
 * Produces a normalized, source-priority-resolved view of all loss run data.
 * Exposure source priority: AI-extracted lossSummary → computeFleetSummary(CAB).
 * Carrier/policy source priority: AI extraction → lossRunVerification grid.
 * "N/A - No Policy on File" entries are treated as absent; lrv data fills them.
 */
function normalizeLossData(lossSummary, lossRunVerification, cabData) {
  const parseNum = (v) => {
    const n = parseFloat(String(v || '').replace(/[$,]/g, ''));
    return isNaN(n) ? 0 : n;
  };
  const fleetMap = computeFleetSummary(cabData?.fleet);
  const lrv      = lossRunVerification || {};
  const SECTIONS = ['autoLiability', 'cargo', 'physicalDamage', 'generalLiability'];

  // ── Exposure summary: AI extraction first, CAB fleet as last resort ────────
  const summaryByYear = {};
  for (const row of (lossSummary?.summary || [])) {
    if (!row.policyYear) continue;
    const yr = row.policyYear;
    const fv = fleetMap[yr] || {};
    summaryByYear[yr] = {
      policyYear:    yr,
      units:         row.units        || fv.units        || '',
      totalMileage:  row.totalMileage || fv.totalMileage || '',
      lossReserve:   row.lossReserve   || '',
      totalIncurred: row.totalIncurred || '',
      totalClaims:   row.totalClaims   || '',
    };
  }

  // ── Section data: resolve carrier names, clear N/A-only rows ──────────────
  const sectionData = {};
  for (const section of SECTIONS) {
    sectionData[section] = (lossSummary?.[section] || []).map((row, i) => {
      const lrvEntry = (lrv[section] || []).find(e => e.year === row.policyYear);
      const isNA     = typeof row.insurer === 'string' && row.insurer.startsWith('N/A');

      // Carrier: non-N/A AI extraction takes priority; fall back to lrv if N/A or missing
      const carrierName = isNA
        ? (lrvEntry?.present ? (lrvEntry.carrier || '') : '')
        : (row.insurer || (lrvEntry?.present ? (lrvEntry.carrier || '') : ''));

      const policyNumber  = row.policyNumber  || (lrvEntry?.policyNumber || '');
      const totalClaims   = parseNum(row.numClaims)     || (lrvEntry?.claims   || 0);
      const totalReserve  = parseNum(row.lossReserve);
      const totalIncurred = parseNum(row.totalIncurred) || (lrvEntry?.incurred  || 0);

      return {
        policyYearLabel: row.policyYear || POLICY_YEARS[i],
        carrierName,
        policyNumber,
        totalClaims,
        totalReserve,
        totalIncurred,
        claimDetail: row.claimDetail || [],
      };
    });
  }

  // ── Validation warnings ───────────────────────────────────────────────────
  const validationWarnings = [];
  for (const [yr, s] of Object.entries(summaryByYear)) {
    if (!s.units        || s.units === '0')       validationWarnings.push(`${yr}: units missing or zero`);
    if (!s.totalMileage || s.totalMileage === '0') validationWarnings.push(`${yr}: mileage missing or zero`);
  }
  // Duplicate policy numbers across sections
  const allPolicyNums = [];
  for (const rows of Object.values(sectionData))
    for (const p of rows) if (p.policyNumber) allPolicyNums.push(p.policyNumber);
  const dupeNums = allPolicyNums.filter((p, i) => allPolicyNums.indexOf(p) !== i);
  [...new Set(dupeNums)].forEach(p => validationWarnings.push(`Duplicate policy number: ${p}`));
  // Incurred > 0 but no claims recorded
  for (const [section, rows] of Object.entries(sectionData))
    for (const p of rows)
      if (p.totalIncurred > 0 && p.totalClaims === 0)
        validationWarnings.push(`${section} ${p.policyYearLabel}: incurred > $0 but 0 claims recorded`);
  // AL year missing from summary
  const summaryYears = new Set(Object.keys(summaryByYear));
  for (const p of (sectionData.autoLiability || []))
    if (p.carrierName && !summaryYears.has(p.policyYearLabel))
      validationWarnings.push(`Year ${p.policyYearLabel} in AL section but not in summary table`);

  const result = { sectionData, summaryByYear, validationWarnings };
  return result;
}

// ── Submission Readiness Agent ────────────────────────────────────────────────
// Pure deterministic function — no AI, no async.
// Accepts a snapshot of account state and optionally the current Market Intelligence results.
// Returns a structured readiness object used by the Lead Sheet checklist,
// the Readiness panel, the pipeline card badge, and the save snapshot.
// Do NOT call AI here. Do NOT touch eligibility.js or scoring.js logic.
function computeReadiness(
  {
    companyName = '',
    tractors = [],
    trailers = [],
    drivers = [],
    documents = {},
    lossRunVerification = null,
    appData = null,
    coiData = null,
    iftaData = null,
    leadSheet = {},
    results = {},
  },
  marketResults = []
) {
  // ── Equipment ────────────────────────────────────────────────────────────────
  const tractorRows = tractors.filter(t => t.year || t.make || t.vin);
  const trailerRows = trailers.filter(t => t.year || t.make || t.vin);
  const tractorsMissingVal = tractorRows.filter(t => !t.value);
  const trailersMissingVal = trailerRows.filter(t => !t.value);
  const equipHasData = tractorRows.length > 0 || trailerRows.length > 0;
  const equipComplete = equipHasData &&
    tractorsMissingVal.length === 0 && trailersMissingVal.length === 0;
  const equipDetail = !equipHasData
    ? 'No equipment entered yet'
    : [
        tractorsMissingVal.length ? `${tractorsMissingVal.length} tractor${tractorsMissingVal.length > 1 ? 's' : ''} missing stated value` : null,
        trailersMissingVal.length ? `${trailersMissingVal.length} trailer${trailersMissingVal.length > 1 ? 's' : ''} missing stated value` : null,
      ].filter(Boolean).join(' · ') ||
        `${tractorRows.length} tractor${tractorRows.length !== 1 ? 's' : ''} · ${trailerRows.length} trailer${trailerRows.length !== 1 ? 's' : ''} — all values present`;

  // ── Drivers ──────────────────────────────────────────────────────────────────
  const DRIVER_REQUIRED_FIELDS = [
    { key: 'dl_number', label: 'DL #' },
    { key: 'state',     label: 'State' },
    { key: 'yrs_exp',   label: 'Experience' },
    { key: 'hire_date', label: 'Hire Date' },
  ];
  const driverRows = drivers.filter(d => d.name);
  const driversIncomplete = driverRows.filter(d =>
    DRIVER_REQUIRED_FIELDS.some(f => !d[f.key])
  );
  const driversComplete = driverRows.length > 0 && driversIncomplete.length === 0;
  const driverDetail = driverRows.length === 0
    ? 'No drivers entered yet'
    : driversIncomplete.length > 0
      ? driversIncomplete.slice(0, 3).map(d => {
          const missing = DRIVER_REQUIRED_FIELDS.filter(f => !d[f.key]).map(f => f.label);
          return `${d.name}: missing ${missing.join(', ')}`;
        }).join(' · ') + (driversIncomplete.length > 3 ? ` +${driversIncomplete.length - 3} more` : '')
      : `${driverRows.length} driver${driverRows.length !== 1 ? 's' : ''} — all fields complete`;

  // ── COI ──────────────────────────────────────────────────────────────────────
  const coiFiles = documents['coi'] || [];
  const hasCoi = coiFiles.length > 0;
  const coiDetail = hasCoi
    ? (coiData ? `Parsed · expires ${coiData.expirationDate || 'unknown'}` : `${coiFiles.length} file${coiFiles.length > 1 ? 's' : ''} uploaded`)
    : 'Not uploaded';

  // ── IFTA ─────────────────────────────────────────────────────────────────────
  const iftaFiles = documents['ifta'] || [];
  const hasIfta = iftaFiles.length > 0;
  const iftaDetail = hasIfta
    ? (iftaData ? `${iftaData.length} quarter${iftaData.length !== 1 ? 's' : ''} parsed` : `${iftaFiles.length} file${iftaFiles.length > 1 ? 's' : ''} uploaded`)
    : 'Not uploaded';

  // ── Loss Runs ────────────────────────────────────────────────────────────────
  const LRV_LINES_CR = ['autoLiability', 'cargo', 'physicalDamage', 'generalLiability'];
  const LRV_SHORT_CR = { autoLiability: 'AL', cargo: 'Cargo', physicalDamage: 'PD', generalLiability: 'GL' };
  const lrFiles = documents['lossRuns'] || [];
  let lrComplete = false;
  let lrPartial = false;
  let lrDetail = '';
  if (!lossRunVerification) {
    lrPartial = lrFiles.length > 0;
    lrDetail = lrFiles.length > 0
      ? 'Files uploaded — not yet verified · Click to verify'
      : 'Not uploaded';
  } else {
    const lrvSlots = lossRunVerification.yearSlots || POLICY_YEARS;
    const missingByLine = LRV_LINES_CR.map(k => {
      const presentYears = (lossRunVerification[k] || []).filter(e => e.present).map(e => e.year);
      const missingYears = lrvSlots.filter(y => !presentYears.includes(y));
      return missingYears.length > 0 ? `${LRV_SHORT_CR[k]}: ${missingYears.length} yr missing` : null;
    }).filter(Boolean);
    lrComplete = missingByLine.length === 0;
    lrPartial = !lrComplete;
    lrDetail = lrComplete ? 'All 4 lines × 5 years verified' : missingByLine.join(' · ');
  }
  const lrFilesPresent = lrFiles.length > 0 || !!lossRunVerification;

  // ── Application ──────────────────────────────────────────────────────────────
  const appFiles = documents['appLossSummary'] || [];
  const appChecked = !!appData;
  const appPartial = appFiles.length > 0 && !appData;
  const appDetail = appData
    ? 'Parsed — fields populated to Lead Sheet'
    : appFiles.length > 0
      ? 'Uploaded — click Parse Application to extract'
      : 'Not uploaded (optional)';

  // ── Narrative ────────────────────────────────────────────────────────────────
  const hasNarrative = !!(results && results.narrative && results.narrative.length > 50);

  // ── New venture form requirement ─────────────────────────────────────────────
  const yearsInBizCR = parseInt(leadSheet.yearsInBusiness) || 99;
  const needsNewVentureForm = yearsInBizCR < 2;

  // ── Company identity ─────────────────────────────────────────────────────────
  const hasCompanyName = !!(companyName && companyName.trim().length > 0);

  // ── Market Intelligence results ──────────────────────────────────────────────
  const readyMarkets       = marketResults.filter(r => r.result === 'GREEN');
  const conditionalMarkets = marketResults.filter(r => r.result === 'YELLOW');
  const blockedMarkets     = marketResults.filter(r => r.result === 'RED');
  const hasViableMarkets   = readyMarkets.length > 0 || conditionalMarkets.length > 0;
  const marketRan          = marketResults.length > 0;
  const allMarketsBlocked  = marketRan && !hasViableMarkets;

  // ── Items list (mirrors existing checklist + market note) ────────────────────
  const items = [
    {
      id: 'app', label: 'Application & Loss Summary',
      checked: appChecked, partial: appPartial, detail: appDetail,
      optional: true, note: 'Upload first — auto-fills all other sections', tab: 'Documents',
    },
    {
      id: 'equipment', label: 'Equipment List (Tractors/Trailers)',
      checked: equipComplete, partial: equipHasData && !equipComplete,
      detail: equipDetail, tab: 'Equipment',
    },
    {
      id: 'drivers', label: "Driver's List",
      checked: driversComplete, partial: driverRows.length > 0 && !driversComplete,
      detail: driverDetail, tab: 'Drivers',
    },
    {
      id: 'coi', label: 'Certificate of Insurance (COI)',
      checked: hasCoi, partial: false, detail: coiDetail, tab: 'Documents',
    },
    {
      id: 'ifta', label: 'IFTA Reports (Last 4 Quarters)',
      checked: hasIfta, partial: false, detail: iftaDetail, tab: 'Documents',
    },
    {
      id: 'lossRuns', label: 'Loss Runs (3–5 Years)',
      checked: lrComplete, partial: lrPartial, detail: lrDetail, tab: 'Loss Runs',
    },
    ...(needsNewVentureForm ? [{
      id: 'newVenture', label: 'New Venture Form',
      checked: false, partial: false,
      detail: 'Required for carriers < 2 years in business', highlight: true, tab: 'Submission',
    }] : []),
  ];

  // ── Hard blockers (trigger NOT_READY) ────────────────────────────────────────
  const blockers = [];
  if (!hasCompanyName)        blockers.push('Company name is required');
  if (!equipHasData)          blockers.push('No equipment data — enter tractors/trailers');
  if (driverRows.length === 0) blockers.push('No drivers on file — add at least one driver');
  if (allMarketsBlocked)      blockers.push('No viable markets — all carriers returned ineligible');

  // ── Risks (YELLOW — not hard stops, but material gaps) ───────────────────────
  const risks = [];
  if (equipHasData && !equipComplete) {
    const n = tractorsMissingVal.length + trailersMissingVal.length;
    risks.push(`${n} vehicle${n > 1 ? 's' : ''} missing stated value`);
  }
  if (driverRows.length > 0 && !driversComplete)
    risks.push(`${driversIncomplete.length} driver${driversIncomplete.length > 1 ? 's' : ''} have incomplete fields`);
  if (!hasCoi)           risks.push('COI not uploaded');
  if (!hasIfta)          risks.push('IFTA reports not uploaded');
  if (!lrFilesPresent)   risks.push('Loss runs not uploaded');
  if (lrFilesPresent && !lrComplete) risks.push('Loss runs not fully verified across all lines/years');
  if (needsNewVentureForm) risks.push('New venture form required (< 2 years in business)');

  // ── Fixes = union of blockers + risks for display ────────────────────────────
  const fixes = [...blockers, ...risks];

  // ── Weighted score (0–100) ────────────────────────────────────────────────────
  // Optional bonus items (app parsed, narrative) don't count against possible.
  // Markets bucket only counted when Market Intelligence has been run.
  let earned = 0;
  let possible = 0;

  possible += 5;  if (hasCompanyName) earned += 5;          // company name
  possible += 20; if (equipHasData)   earned += 10;          // equipment present
                  if (equipComplete)  earned += 10;          // equipment complete
  possible += 20; if (driverRows.length > 0) earned += 10;  // drivers present
                  if (driversComplete)        earned += 10;  // drivers complete
  possible += 10; if (hasCoi)        earned += 10;           // COI
  possible += 10; if (hasIfta)       earned += 10;           // IFTA
  possible += 15; if (lrFilesPresent) earned += 8;           // loss runs present
                  if (lrComplete)     earned += 7;           // loss runs verified
  if (appChecked)    earned += 4;  // bonus
  if (hasNarrative)  earned += 3;  // bonus
  if (marketRan) {
    possible += 10;
    if (hasViableMarkets) earned += 10;                      // viable markets
  }

  const score = possible > 0 ? Math.min(100, Math.round((earned / possible) * 100)) : 0;

  // ── Status ────────────────────────────────────────────────────────────────────
  const status = blockers.length > 0
    ? 'NOT_READY'
    : risks.length > 0
      ? 'CONDITIONALLY_READY'
      : 'READY';

  // ── Required item counts ──────────────────────────────────────────────────────
  const requiredItems = items.filter(it => !it.optional);
  const completeCount = requiredItems.filter(it => it.checked).length;
  const totalCount    = requiredItems.length;

  // ── Summary sentence ─────────────────────────────────────────────────────────
  const summary = status === 'NOT_READY'
    ? `${blockers.length} hard blocker${blockers.length > 1 ? 's' : ''} must be resolved before this account can be marketed.`
    : status === 'CONDITIONALLY_READY'
      ? `Account can be marketed with ${risks.length} item${risks.length > 1 ? 's' : ''} outstanding — carriers may request these before quoting.`
      : `Submission is complete and ready to market.${marketRan ? ` ${readyMarkets.length} market${readyMarkets.length !== 1 ? 's' : ''} ready to receive.` : ''}`;

  // ── Next actions (top 5) ──────────────────────────────────────────────────────
  const nextActions = [];
  if (!hasCompanyName)    nextActions.push('Enter company name');
  if (!equipHasData)      nextActions.push('Add equipment (tractors/trailers)');
  if (driverRows.length === 0) nextActions.push('Add at least one driver');
  if (!hasCoi)            nextActions.push('Upload Certificate of Insurance');
  if (!hasIfta)           nextActions.push('Upload IFTA reports (last 4 quarters)');
  if (!lrFilesPresent)    nextActions.push('Upload loss runs');
  if (lrFilesPresent && !lossRunVerification) nextActions.push('Verify loss runs in the Loss Runs tab');
  if (equipHasData && !equipComplete) {
    const n = tractorsMissingVal.length + trailersMissingVal.length;
    nextActions.push(`Add stated value for ${n} vehicle${n > 1 ? 's' : ''}`);
  }
  if (driverRows.length > 0 && !driversComplete) nextActions.push('Complete missing driver fields');
  if (!hasNarrative)      nextActions.push('Generate submission narrative');
  if (!marketRan)         nextActions.push('Run Market Intelligence to evaluate carrier fit');

  return {
    status,
    score,
    completeCount,
    totalCount,
    items,
    blockers,
    risks,
    fixes,
    readyMarkets,
    conditionalMarkets,
    blockedMarkets,
    summary,
    nextActions: nextActions.slice(0, 5),
  };
}

// ─── Markets Constants & Helpers ─────────────────────────────────────────────

const MARKET_STATUSES = [
  { key: 'submitted',    label: 'Submitted — Waiting on Response', short: 'Submitted',    color: '#b45309', bg: '#fef3c7', border: '#fbbf24' },
  { key: 'underwriting', label: 'In Underwriting / Under Review',  short: 'Underwriting', color: '#1e40af', bg: '#dbeafe', border: '#93c5fd' },
  { key: 'quoted',       label: 'Quote Received',                  short: 'Quoted',       color: '#047857', bg: '#d1fae5', border: '#34d399' },
  { key: 'bound',        label: 'Bound ✓',                        short: 'Bound',        color: '#064e3b', bg: '#a7f3d0', border: '#10b981' },
  { key: 'declined',     label: 'Declined',                        short: 'Declined',     color: '#991b1b', bg: '#fee2e2', border: '#f87171' },
  { key: 'bor_blocked',  label: 'Blocked — BOR Needed',           short: 'BOR Needed',   color: '#7c2d12', bg: '#fff7ed', border: '#fb923c' },
  { key: 'bor_submitted',label: 'BOR Submitted — Awaiting Release',short: 'BOR Submitted',color: '#4338ca', bg: '#eef2ff', border: '#a5b4fc' },
];
const MARKET_STATUS_MAP = Object.fromEntries(MARKET_STATUSES.map(s => [s.key, s]));

const BOR_STATUSES = [
  { key: 'pending',      label: 'Pending — Awaiting carrier response', badge: 'BOR Pending',      badgeCls: 'bor-badge-pending'  },
  { key: 'acknowledged', label: 'Acknowledged — Carrier received BOR', badge: 'BOR Acknowledged', badgeCls: 'bor-badge-ack'      },
  { key: 'released',     label: 'Released — Previous broker released', badge: 'BOR Released',     badgeCls: 'bor-badge-released' },
  { key: 'bound',        label: 'Bound under BOR',                     badge: 'Bound under BOR',  badgeCls: 'bor-badge-bound'    },
  { key: 'declined',     label: 'Declined by carrier',                 badge: 'BOR Declined',     badgeCls: 'bor-badge-declined' },
];
const BOR_STATUS_MAP = Object.fromEntries(BOR_STATUSES.map(s => [s.key, s]));



const daysSince = (dateStr) => {
  if (!dateStr) return null;
  const s = String(dateStr);
  const d = new Date(s.length === 10 ? `${s}T12:00:00` : s);
  if (isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};

const parseMarketPremium = (v) => {
  const n = parseFloat(String(v || '').replace(/[$,]/g, ''));
  return isNaN(n) ? null : n;
};

const fmtPremium = (v) => {
  const n = parseMarketPremium(v);
  return n !== null ? `$${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—';
};

// ─── AI Prompt Constants ──────────────────────────────────────────────────────

const NARRATIVE_SYSTEM = `You are a senior commercial trucking insurance producer with 20 years of experience placing difficult trucking risks at E&S and specialty markets including Markel, James River, Benchmark, Vantage, Trisura, and State National. You write submission narratives that get quotes on accounts other agents cannot place. Your narratives are honest, specific, and strategically framed to highlight what underwriters actually care about — loss trajectory, safety culture, operational stability, shipper quality, and management engagement. You never hide problems but you always provide context. You write like a trusted advisor presenting a client, not a salesperson pitching a product.`;

const NARRATIVE_INSTRUCTION = `You are an elite commercial trucking insurance submission writer.
Your job is to turn raw account data into a clean, persuasive, underwriter-ready submission narrative that is:
- highly accurate
- professional
- concise but complete
- easy to scan
- confidence-building
- written in a way that improves underwriting appetite

The output should make the underwriter feel:
1. this producer understands the account,
2. the risk has been reviewed carefully,
3. the submission is organized and credible,
4. key concerns have already been addressed.

IMPORTANT RULES:
- Do NOT sound like marketing fluff.
- Do NOT exaggerate or invent positive facts.
- Do NOT hide negatives.
- Do NOT use generic filler like "great company" or "excellent risk" unless specifically supported by facts.
- If there are weaknesses, frame them honestly with context and corrective actions.
- Write like a sharp trucking insurance producer who understands underwriting.
- Prioritize clarity, structure, and credibility over sounding fancy.
- If data is missing, do not fabricate it. Instead, omit it or note that it is unknown if necessary.
- Keep sentences tight and useful.
- Make the narrative pleasant to read and logically structured.
- Do NOT mention MVRs unless mvr files are present in the documents.
- NEVER combine tractors and trailers into a single unit count. Always state them separately as "X power units (tractors) and Y trailers." Never write "Z-unit fleet" or "Z units" as a combined number — underwriters assume all units are power units, which overstates the risk.
- NEVER mention team driving, team drivers, or teams under any circumstances. Omit this information entirely — it signals higher fatigue exposure and risk to underwriters.
- NEVER infer team driving from driver-to-unit ratios. A ratio of more than 1 driver per tractor does NOT indicate team driving — it is normal for fleets to have more drivers than tractors (spare drivers, rotating schedules, etc.). Never mention team driving under any circumstances.

WHEN WRITING THE NARRATIVE, PRIORITIZE:
- nature of operation
- years in business
- fleet size and equipment profile
- operating radius / lanes / territory
- commodity hauled
- driver profile and hiring standards
- loss history and trend
- safety controls
- technology usage (ELD, cameras, telematics, GPS, etc.)
- maintenance practices
- why the risk makes sense for the target market
- explanation of any adverse items with context

OUTPUT FORMAT - return exactly this structure:

SUBMISSION NARRATIVE

Insured Overview:
[1 short paragraph summarizing the company, operation, experience, and overall risk profile.]

Operations:
- [clear bullet]
- [clear bullet]
- [clear bullet]

Equipment:
- [clear bullet]
- [clear bullet]

Drivers and Hiring:
- [clear bullet]
- [clear bullet]
- [clear bullet]

Safety and Risk Controls:
- [clear bullet]
- [clear bullet]
- [clear bullet]

Loss History:
[1 short paragraph summarizing loss activity, frequency/severity, trend, and any meaningful context.]

Underwriting Notes:
- [clear bullet]
- [clear bullet]
- [clear bullet]

MARKET FIT SUMMARY
[1 short paragraph explaining why this account may fit the target market, while staying honest about any concerns.]

STYLE REQUIREMENTS:
- Write in a confident commercial insurance tone.
- Make it sound human, not robotic.
- Avoid repetitive phrasing.
- Avoid overlong paragraphs.
- Keep bullets crisp.
- Make the narrative feel like it came from a top producer sending a clean file to a serious underwriter.

SPECIAL HANDLING RULES:
- If losses exist, summarize them clearly and neutrally. Mention whether they appear isolated, frequency-driven, severe, old, or improving if supported by facts.
- If SAFER or compliance issues exist, address them directly and explain context if available.
- If the account is new venture, acknowledge that clearly and strengthen the narrative with prior experience, controls, and operational discipline.
- If the account has strong telematics/camera/safety oversight, make that visible.
- If information conflicts across documents, prefer the most credible source and briefly reflect uncertainty only when necessary.
- If a weakness is offset by a control, pair them together.

NOW WRITE THE SUBMISSION NARRATIVE using the account data provided above.`;

const NARRATIVE_REVISION_SYSTEM = `You are an elite commercial trucking insurance submission editor.
Your job is NOT to rewrite blindly.
Your job is to IMPROVE the narrative so it increases underwriting confidence, reduces perceived risk, maintains full credibility, avoids sounding negative or uncertain, and avoids fluff or exaggeration.

CORE OBJECTIVE:
Balance the narrative. Remove unnecessary negative interpretation, keep all real concerns, frame those concerns with context, and strengthen the positives where justified. The goal is to move this from "good file" to "worth quoting now."

NON-NEGOTIABLE RULES:
- DO NOT fabricate information
- DO NOT exaggerate positives
- DO NOT hide real underwriting concerns
- DO NOT introduce speculative negatives such as instability, retention issues, or operational uncertainty unless explicitly proven in the data
- DO NOT use unsubstantiated words like excellent, great, or strong
- DO NOT use hedging language like "relatively clean" — use "clean"
- DO NOT speculate on driver-to-unit ratios in any way
- DO NOT use the word "estimated" for mileage — use "approximately" instead
- NEVER mention team driving under any circumstances
- NEVER mention MVRs unless confirmed uploaded
- NEVER combine power units and trailers into one number — always separate
- NEVER restate a conclusion already proven in the narrative

REQUIRED IMPROVEMENTS:

1. REMOVE SELF-INFLICTED NEGATIVES — apply these exact replacements:
- "relatively clean operation" → "clean operation" — no hedging
- "operational instability" → REMOVE entirely
- "retention challenges" → reframe as hiring for expansion
- "fleet size adjustments" → reframe as utilization variation
- "suggests owner-operator or team driver model" → REMOVE entirely
- "suggests recent downsizing" → REMOVE entirely
- "suggests focus on operational reliability and fuel efficiency" → REMOVE; replace with: "Equipment profile is weighted toward newer model years"
- "limits full loss development visibility" → "complete loss history pending for [year] policy period"
- "limits full historical perspective" → same as above

2. EXACT LANGUAGE UPGRADES — use these precise replacements:

LOSS HISTORY — replace entire Loss History section with this structure:
"Auto liability history reflects exceptionally low activity with [X claim(s)] totaling $[amount] in the [year] policy year (closed, no reserves). No cargo, physical damage, or general liability claims reported. Loss frequency remains minimal relative to multi-state operations and annual mileage, supporting a controlled loss environment."

VEHICLE MAINTENANCE BASIC — replace any passive listing with:
"Vehicle Maintenance BASIC at [X]% reflects elevated inspection-related items, while all driver-related BASIC categories remain clean with no unsafe driving or substance violations."

FLEET SIZE DISCREPANCY — if present, replace with:
"Fleet size discrepancy between CAB report ([X] units) and operational data appears tied to reporting timing differences; current operating figures reflect active fleet."

MILEAGE — replace "estimated at X million miles" with:
"approximately X million miles across the fleet"

3. SAFETY SECTION — must clearly separate:
- Driver risk = clean, no BASIC alerts in driver-related categories
- Maintenance risk = inspection-related items only, not accident-driven
Frame it so underwriters see these as two separate stories.

4. LOSS HISTORY — this is the #1 weapon in the submission:
- Emphasize exceptionally low frequency relative to multi-state exposure
- Reinforce zero claims in cargo, PD, and GL
- Make it sound impressive, not just acceptable

5. UNDERWRITING NOTES — professional tone only:
- Keep all real concerns
- Remove any language that sounds like internal critique
- Every concern must read like a professional observation, not a warning

6. MARKET FIT SUMMARY — replace entire section with a decisive conclusion:
Use this as the benchmark — adapt with actual account facts:
"This account aligns with markets comfortable underwriting small to mid-size dry van carriers demonstrating clean driver performance and low loss frequency. While vehicle maintenance metrics require improvement, exposure appears driven by inspection-related items rather than accident activity. The combination of minimal claims history, clean BASIC profile, and experienced driver base supports underwriting consideration for carriers focused on controlled growth and disciplined operations."
This must:
- define which markets this fits
- explain why it is viable despite concerns
- reinforce controlled risk
- sound like a decisive underwriting conclusion, not a passive disclaimer

GRAMMAR AND STYLE:
- "has built a operation" → "has built an operation"
- Remove all instances of "modern equipment" — replace with "predominantly late-model equipment" or remove entirely
- Do not restate conclusions already proven earlier in the narrative
- No long paragraphs — keep bullets crisp and useful
- Confident tone — no hedging, no uncertainty language

OUTPUT FORMAT:
1. Return the FULL revised narrative with the EXACT same section structure: Insured Overview, Operations, Equipment, Drivers and Hiring, Safety and Risk Controls, Loss History, Underwriting Notes, Market Fit Summary
2. After the narrative add a REVISIONS MADE section with 5 to 8 bullet points summarizing the most important improvements made

FINAL QUALITY CHECK — verify every item before returning:
- Is "relatively clean" or any hedging language removed? If NO — fix it
- Are power units and trailers stated separately everywhere? If NO — fix it
- Is loss history framed as a major strength, not just acceptable? If NO — revise
- Is driver risk clearly separated from maintenance risk? If NO — revise
- Is Market Fit Summary decisive and specific, not generic? If NO — revise
- Are all real concerns still present but professionally framed? If NO — revise
- Does it sound like a top producer wrote it? If NO — revise
- Would an underwriter feel more confident quoting this? If NO — revise
- Are there any speculative negatives that need to be removed? If YES — remove them
- Is any conclusion restated unnecessarily? If YES — remove the restatement
If ANY answer requires action — make the fix before returning.`;

const RISK_SYSTEM = `You are a commercial lines underwriter at a specialty trucking market. You write internal underwriting memos that are direct, data-driven, and actionable. You read CAB reports, loss runs, and FMCSA data to form clear opinions. You make specific recommendations — recommend to quote, refer to management, or decline — and you back them with data.`;

const RISK_INSTRUCTION = `Review this trucking submission and write an underwriter's internal assessment memo. Use real data from the documents provided above.

Structure as follows:

UNDERWRITING MEMO — [CARRIER NAME]
Date: [today's date]
Underwriter: AI Risk Assessment
Class: Commercial Trucking

RISK SUMMARY
One paragraph. What is this account? What is the single most important thing to know about it?

FLEET & OPERATIONS ANALYSIS
Fleet composition, age, value. Equipment condition signals from loss runs or inspection data. Operational scope and radius. Cargo type and associated exposure.
IMPORTANT: Always state power units (tractors) and trailers separately — never combine them into a single unit count. Use "X power units and Y trailers" format.

DRIVER QUALITY ASSESSMENT
Total drivers, average experience, hire date patterns. Any concerning MVR data. CDL compliance. Driver turnover signals.

FMCSA / SAFETY SCORE ANALYSIS
Review each BASIC category from the CAB report if provided. Flag any alerts. Identify the specific violations driving scores. Note inspection history and OOS rates.

LOSS HISTORY ANALYSIS
Year-by-year breakdown. Loss ratio by year if calculable. Frequency vs severity assessment. Any subrogation opportunities noted. Trend direction: improving, stable, or deteriorating.

COVERAGE EXPOSURE NOTES
Primary AL limit adequacy. Cargo sublimit vs commodity value. Named perils vs all-risk. Gap exposures. Any coverage concerns for the operations described.

KEY RISK FACTORS
Numbered list. Specific. No generic statements. Each factor must reference actual data from the submission.

RECOMMENDATION
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
[ ] RECOMMEND TO QUOTE
[ ] REFER TO MANAGEMENT
[ ] DECLINE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
State your recommendation and provide 3-5 specific reasons drawn from the data. If recommending to quote, state any conditions or endorsements required. If referring, state what additional information would change the decision. If declining, identify the specific factors that make this account non-qualifiable at this time.`;

const MISSING_SYSTEM = `You are a commercial trucking insurance submission specialist. Your job is to identify exactly what is missing or incomplete from a trucking insurance submission and draft a precise, professional email requesting only what is actually needed.`;

const MISSING_INSTRUCTION = `Review this trucking submission against the master checklist below. Use the uploaded documents and carrier data provided above.

MASTER CHECKLIST — check each item specifically:

1. LOSS RUNS — must cover last 5 years for ALL four lines:
   - Auto Liability
   - Motor Truck Cargo
   - Physical Damage
   - General Liability
   Must be carrier-issued, not agent-prepared summaries.
   Flag which specific lines are missing and which years are not covered.

2. IFTA REPORTS — must cover the last 4 quarters specifically.
   Flag which quarters are missing if not all 4 are present.

3. EQUIPMENT LIST — must include all four fields for every unit:
   - Year, Make, VIN number, Stated Value
   Stated Value is the most commonly missing field.
   Flag EACH individual unit that is missing any field — identify them by year/make or VIN.

4. DRIVER LIST — must include all five fields for every driver:
   - First and Last Name, Date of Birth, Driver's License State AND Number, Years of CDL experience, Year of hire
   Flag EACH individual driver that is missing any field — identify them by name.

5. COMMODITIES — a clear description of what freight they haul and approximate percentage breakdown if multiple types.
   Flag if no commodity description has been provided.

6. CERTIFICATE OF INSURANCE — current COI from active carrier.
   Flag if missing or if the one provided appears expired.

HELPFUL (but not required — list separately at the end if missing, do NOT include in the required items section):
- MVRs — Motor Vehicle Records for each driver. Helpful for carriers to evaluate driver risk but not required to begin marketing the account.

OUTPUT FORMAT:

MISSING ITEMS REPORT

For each checklist item above, state either:
  ✓ COMPLETE — [brief note on what was provided]
  ✗ MISSING / INCOMPLETE — [specific detail on what is missing]

Be granular. If 3 of 4 loss run lines are present, name the missing one. If 2 drivers are missing DOB, name those drivers. If 4 trailers are missing stated value, list them.

---

CLIENT EMAIL DRAFT

Write the client email using this exact structure. Only request items that are actually missing — do not list items that are already complete.

Subject: [Carrier Name] — Outstanding Items Needed for Insurance Submission

Hi [Contact Name],

Thank you for working with us on your commercial trucking insurance. We have reviewed what you have provided and are putting your submission together now. To make sure we get you the most competitive quotes possible we still need a few items from you:

[LIST ONLY THE ITEMS THAT ARE ACTUALLY MISSING — be specific, reference their actual company name and actual missing items by name]

Please send these over at your earliest convenience. The sooner we have everything the faster we can get your quotes back to you.

Feel free to call or reply to this email with any questions.

Best regards,
Carter Proffitt

Keep the email short, professional, and specific. Do not use generic language. Do not request anything that has already been provided.`;

// ─── File Reading Helpers (for document parsing) ──────────────────────────────

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = e => resolve(e.target.result.split(',')[1]);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const fileToText = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = e => resolve(e.target.result);
  reader.onerror = reject;
  reader.readAsText(file);
});

const fileToArrayBuffer = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = e => resolve(e.target.result);
  reader.onerror = reject;
  reader.readAsArrayBuffer(file);
});

// Extract plain text from a PDF using pdf.js
const extractPDFText = async (file) => {
  const buf = await fileToArrayBuffer(file);
  const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
  let text = '';
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const pageText = content.items.map(item => item.str).join(' ');
    text += `[Page ${i}]\n${pageText}\n\n`;
  }
  return text;
};

// Truncate text to maxChars, appending a note if truncated
const truncateContent = (text, maxChars, label = 'Content') => {
  if (text.length <= maxChars) return text;
  return text.slice(0, maxChars) +
    `\n\n[${label} truncated — original ${Math.round(text.length / 1000)}KB, sending first ${Math.round(maxChars / 1000)}KB only]`;
};

// Hard guard: if estimated tokens > 150k, truncate text blocks and warn
const guardApiContent = (contentBlocks, onWarning) => {
  const MAX_CHARS = 600000; // ~150k tokens at 4 chars/token
  const totalChars = contentBlocks.reduce((s, b) => s + (b.text ? b.text.length : 0), 0);
  if (totalChars <= MAX_CHARS) return contentBlocks;
  console.warn(`[guardApiContent] ~${Math.round(totalChars / 4)} estimated tokens — truncating to ~150k`);
  if (onWarning) onWarning('⚠️ Content was very large and was truncated to fit token limits.');
  let remaining = MAX_CHARS;
  return contentBlocks.map(block => {
    if (block.type !== 'text' || remaining <= 0) return block;
    if (block.text.length <= remaining) { remaining -= block.text.length; return block; }
    const out = { ...block, text: block.text.slice(0, remaining) + '\n[Truncated due to token limit]' };
    remaining = 0;
    return out;
  });
};

const normalizeName = (n) => String(n || '').trim().toLowerCase().replace(/\s+/g, ' ');

// Merge newly-parsed drivers into an existing drivers array without overwriting
// non-empty fields. Match priority: dl_number+state → dl_number → name+dob.
// Unmatched drivers are appended. Returns the merged array.
const mergeParsedDrivers = (existingDrivers, parsedDrivers) => {
  const merged = existingDrivers.map(d => ({ ...d }));
  for (const parsed of parsedDrivers) {
    const pDL    = String(parsed.dl_number || '').trim().toUpperCase();
    const pState = String(parsed.state     || '').trim().toUpperCase();
    const pName  = normalizeName(parsed.name);
    const pDOB   = String(parsed.dob || '').trim();
    let matchIdx = -1;
    if (pDL && pState) {
      matchIdx = merged.findIndex(e => {
        const eDL = String(e.dl_number || '').trim().toUpperCase();
        const eSt = String(e.state     || '').trim().toUpperCase();
        return eDL && eDL === pDL && eSt && eSt === pState;
      });
    }
    if (matchIdx === -1 && pDL) {
      matchIdx = merged.findIndex(e => {
        const eDL = String(e.dl_number || '').trim().toUpperCase();
        return eDL && eDL === pDL;
      });
    }
    if (matchIdx === -1 && pName && pDOB) {
      matchIdx = merged.findIndex(e =>
        normalizeName(e.name) === pName && String(e.dob || '').trim() === pDOB
      );
    }
    if (matchIdx !== -1) {
      const existing = merged[matchIdx];
      const updated = { ...existing };
      for (const [key, val] of Object.entries(parsed)) {
        if (val !== '' && val != null && !existing[key]) updated[key] = val;
      }
      merged[matchIdx] = updated;
    } else {
      merged.push({ ...parsed });
    }
  }
  return merged;
};

// Resolve a file reference to a real File/Blob.
// Supports two schemas:
//   NEW  { id, name }         — private Blob, fetched via authenticated /api/files/read
//   LEGACY { name, url }      — public Blob URL, fetched directly (migration window)
// Returns null if the file cannot be fetched.
// carrierId is required for new-schema entries; callers in the component scope provide it.
const resolveToFile = async (f, carrierId) => {
  if (f instanceof File || f instanceof Blob) return f;

  // NEW SCHEMA: has id but no public url — fetch via authenticated server proxy
  if (f?.id && !f?.url) {
    if (!carrierId) {
      console.warn(`[resolveToFile] new-schema entry "${f.name}" has no carrierId — cannot resolve`);
      return null;
    }
    console.log(`[resolveToFile] new-schema fetching "${f.name}" via /api/files/read (fileId=${f.id})`);
    try {
      const token = localStorage.getItem('tsp_auth') || '';
      const resp = await fetch(
        `${API_BASE}/api/files/read?carrierId=${encodeURIComponent(carrierId)}&fileId=${encodeURIComponent(f.id)}`,
        { headers: token ? { 'Authorization': `Bearer ${token}` } : {} }
      );
      console.log(`[resolveToFile] "${f.name}" — status: ${resp.status}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      if (blob.size === 0) throw new Error('Empty response (0 bytes) — file may have been deleted');
      const type = (blob.type && blob.type !== 'application/octet-stream')
        ? blob.type : (f.contentType || 'application/octet-stream');
      return new File([blob], f.name || 'file', { type });
    } catch (err) {
      console.warn(`[resolveToFile] new-schema "${f?.name}" failed:`, err.message);
    }
    return null;
  }

  // LEGACY SCHEMA: has public url — fetch directly (migration window)
  if (f?.url) {
    console.log(`[resolveToFile] legacy fetching "${f.name}" from URL:`, f.url);
    try {
      const resp = await fetch(f.url);
      console.log(`[resolveToFile] "${f.name}" — status: ${resp.status}, content-type: ${resp.headers.get('content-type')}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      console.log(`[resolveToFile] "${f.name}" — blob size: ${blob.size} bytes, type: "${blob.type}"`);
      if (blob.size === 0) throw new Error('Empty blob (0 bytes) — file may have been deleted from storage');
      const type = (blob.type && blob.type !== 'application/octet-stream')
        ? blob.type
        : (f.name || '').endsWith('.pdf') ? 'application/pdf'
        : (f.name || '').match(/\.xlsx?$/i) ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/octet-stream';
      return new File([blob], f.name || 'file', { type });
    } catch (err) {
      console.warn(`[resolveToFile] "${f?.name}" failed:`, err.message);
    }
  } else {
    console.log(`[resolveToFile] "${f?.name}" — no URL and no id`);
  }
  return null;
};

const readFileForAI = async (file, carrierId) => {
  // Cloud reference — resolve to real File first
  if (!(file instanceof File) && !(file instanceof Blob)) {
    if (file?.id || file?.url) {
      const resolved = await resolveToFile(file, carrierId);
      if (resolved) return readFileForAI(resolved, carrierId);
      return [{ type: 'text', text: `[Could not load "${file.name}" from cloud storage. Try re-uploading.]` }];
    }
    // Name-only placeholder — no content available
    return [{ type: 'text', text: `[Document: "${file.name}" — uploaded in a previous session. Re-upload to include content in AI analysis.]` }];
  }
  const ext = file.name.split('.').pop().toLowerCase();
  if (['xlsx', 'xls', 'csv'].includes(ext)) {
    const buf = await fileToArrayBuffer(file);
    const wb = XLSX.read(buf, { type: 'array' });
    let text = '';
    wb.SheetNames.forEach(name => {
      text += `Sheet: ${name}\n`;
      text += XLSX.utils.sheet_to_csv(wb.Sheets[name]) + '\n\n';
    });
    return [{ type: 'text', text: `File: ${file.name}\n\n${truncateContent(text, 50000, 'Spreadsheet data')}` }];
  }
  if (ext === 'pdf') {
    try {
      const fullText = await extractPDFText(file);
      return [{ type: 'text', text: `File: ${file.name}\n\n${truncateContent(fullText, 50000, 'PDF content')}` }];
    } catch (err) {
      console.warn('[readFileForAI] PDF text extraction failed, using base64 fallback:', err.message);
      const base64 = await fileToBase64(file);
      return [{ type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: base64 } }];
    }
  }
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
    const base64 = await fileToBase64(file);
    const mediaType = (ext === 'jpg' || ext === 'jpeg') ? 'image/jpeg' : ext === 'png' ? 'image/png' : ext === 'gif' ? 'image/gif' : 'image/webp';
    return [{ type: 'image', source: { type: 'base64', media_type: mediaType, data: base64 } }];
  }
  const rawText = await fileToText(file);
  return [{ type: 'text', text: `File: ${file.name}\n\n${truncateContent(rawText, 50000, file.name)}` }];
};

// ─── Progress Ring ────────────────────────────────────────────────────────────

function ProgressRing({ pct, size = 56, stroke = 6 }) {
  const r  = (size - stroke) / 2;
  const cx = size / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - pct / 100);
  const color = pct >= 80 ? '#4ADE80' : pct >= 50 ? '#FACC15' : '#60A5FA';
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={cx} cy={cx} r={r} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth={stroke} />
      <circle cx={cx} cy={cx} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.5s ease, stroke 0.5s ease' }} />
      <text x="50%" y="54%" dominantBaseline="middle" textAnchor="middle"
        fill="#fff" fontSize="13" fontWeight="700" fontFamily="DM Sans, sans-serif"
        style={{ transform: 'rotate(90deg)', transformOrigin: '50% 50%' }}>
        {pct}%
      </text>
    </svg>
  );
}

// ─── Editable Table ───────────────────────────────────────────────────────────

function EditableTable({ cols, rows, onRowChange, onAddRow, onDelRow }) {
  return (
    <>
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              {cols.map(c => <th key={c}>{c}</th>)}
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={cols.length + 1} style={{ padding: '20px', textAlign: 'center', color: '#94A3B8', fontSize: 13 }}>No entries yet. Click "+ Add Row" below.</td></tr>
            )}
            {rows.map((row, ri) => (
              <tr key={ri}>
                {cols.map(col => (
                  <td key={col}>
                    <input
                      className="cell-input"
                      value={row[colKey(col)] || ''}
                      onChange={e => onRowChange(ri, colKey(col), e.target.value)}
                      placeholder={col}
                    />
                  </td>
                ))}
                <td>
                  <button className="btn-del-row" onClick={() => onDelRow(ri)} title="Remove row">✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button className="btn-add-row" onClick={onAddRow}>＋ Add Row</button>
    </>
  );
}

// ─── CAB Report Components ───────────────────────────────────────────────────

const BASIC_KEYS = ['unsafeDriving', 'hoursOfService', 'driverFitness', 'controlledSubstances', 'vehicleMaintenance', 'hazmat', 'crashIndicator'];
const BASIC_LABELS = {
  unsafeDriving:       'Unsafe Driving',
  hoursOfService:      'HOS Compliance',
  driverFitness:       'Driver Fitness',
  controlledSubstances:'Controlled Sub.',
  vehicleMaintenance:  'Vehicle Maint.',
  hazmat:              'Hazmat',
  crashIndicator:      'Crash Indicator',
};

// Isolates CAB render errors so the rest of the workspace stays functional.
// key prop should change on account switch (carrierId) so the boundary resets.
class CABErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error) {
    console.error('[CABErrorBoundary] CAB render failed:', {
      message: error.message,
      // No cabData content — only the error message
    });
  }
  render() {
    if (this.state.error) {
      return (
        <div className="cab-error-state">
          <span className="cab-error-icon">⚠️</span>
          <span className="cab-error-msg">
            CAB display error — your data is intact.
            Click <strong>Read CAB Report</strong> to re-parse, or{' '}
            <button className="cab-error-retry" onClick={() => this.setState({ error: null })}>
              try again
            </button>.
          </span>
        </div>
      );
    }
    return this.props.children;
  }
}

export function CABSummaryCard({ cabData }) {
  if (!cabData) return null;
  const c = cabData.company || {};
  const s = cabData.scores || {};
  const oos = cabData.oos || {};
  const acc = cabData.accidents || {};
  const fl = cabData.fleet || {};
  const ins = cabData.insurance || {};
  const iss = s.iss ? parseInt(s.iss, 10) : null;
  const issColor = iss === null ? '#64748B' : iss >= 80 ? '#EF4444' : iss >= 60 ? '#F97316' : iss >= 40 ? '#EAB308' : '#22C55E';

  return (
    <div className="cab-summary-card">
      <div className="cab-summary-header">
        <div className="cab-summary-title">
          <span className="cab-summary-icon">📋</span>
          <div>
            <div className="cab-company-name">{c.name || 'CAB Report Loaded'}</div>
            <div className="cab-company-meta">DOT: {c.dot || '—'} {c.mc ? `· MC: ${c.mc}` : ''} · {c.status || ''}</div>
          </div>
        </div>
        {iss !== null && (
          <div className="cab-iss-badge" style={{ borderColor: issColor, color: issColor }}>
            <div className="cab-iss-score">{iss}</div>
            <div className="cab-iss-label">ISS Score</div>
          </div>
        )}
      </div>

      <div className="cab-basics-grid">
        {BASIC_KEYS.map(key => {
          const d = cabData.basics?.[key] || {};
          const rawScore = parseInt(d.score, 10);
          const score = isNaN(rawScore) ? null : rawScore;
          const showAlert = d.alert && score !== null;
          const barColor = showAlert ? '#EF4444' : score !== null && score >= 65 ? '#F97316' : score !== null ? '#22C55E' : '#94A3B8';
          return (
            <div key={key} className={`cab-basic-cell${showAlert ? ' cab-alert' : ''}`}>
              <div className="cab-basic-label">{BASIC_LABELS[key]}</div>
              <div className="cab-basic-bar-wrap">
                <div className="cab-basic-bar" style={{ width: score !== null ? `${Math.min(score, 100)}%` : '0%', background: barColor }} />
              </div>
              <div className="cab-basic-score" style={{ color: barColor }}>
                {score !== null ? `${score}%` : 'N/A'}
                {showAlert && <span className="cab-alert-tag">ALERT</span>}
              </div>
            </div>
          );
        })}
      </div>

      <div className="cab-stats-row">
        <div className="cab-stat">
          <span className="cab-stat-label">Power Units</span>
          <span className="cab-stat-val">{fl.totalPowerUnits || '—'}</span>
        </div>
        <div className="cab-stat">
          <span className="cab-stat-label">Drivers</span>
          <span className="cab-stat-val">{fl.drivers || '—'}</span>
        </div>
        <div className="cab-stat">
          <span className="cab-stat-label">OOS Overall</span>
          <span className="cab-stat-val">{oos.overall?.pct ? (String(oos.overall.pct).endsWith('%') ? oos.overall.pct : `${oos.overall.pct}%`) : '—'}</span>
        </div>
        <div className="cab-stat">
          <span className="cab-stat-label">Accidents</span>
          <span className="cab-stat-val">{acc.total ?? '—'}</span>
        </div>
        <div className="cab-stat">
          <span className="cab-stat-label">DOT Rating</span>
          <span className="cab-stat-val">{s.dotRating || 'Unrated'}</span>
        </div>
        <div className="cab-stat">
          <span className="cab-stat-label">Insurer</span>
          <span className="cab-stat-val cab-stat-insurer">{ins.carrier || '—'}</span>
        </div>
      </div>

      {cabData.alerts?.length > 0 && (
        <div className="cab-alerts-list">
          {cabData.alerts.map((a, i) => <div key={i} className="cab-alert-item">⚠️ {a}</div>)}
        </div>
      )}
    </div>
  );
}

// ─── Loss Run Verification Card ──────────────────────────────────────────────

const LR_LINES = [
  { key: 'autoLiability',    label: 'Auto Liability' },
  { key: 'cargo',            label: 'Cargo' },
  { key: 'physicalDamage',   label: 'Physical Damage' },
  { key: 'generalLiability', label: 'General Liability' },
];

const CONFIDENCE_ICONS = { high: '🟢', medium: '🟡', low: '🔴' };
const CONFIDENCE_LABELS = { high: 'High — found directly in carrier data', medium: 'Medium — inferred from related data', low: 'Low — estimated or missing, needs review' };

function AppAutoFillModal({ fields: initialFields, onAccept, onClose, onConfirmField }) {
  const [fields, setFields] = React.useState(initialFields);

  const toggleAccepted = (i) =>
    setFields(prev => prev.map((f, fi) => fi === i ? { ...f, accepted: !f.accepted } : f));

  const updateEdited = (i, val) =>
    setFields(prev => prev.map((f, fi) => fi === i ? { ...f, editedValue: val } : f));

  const acceptAll = () => setFields(prev => prev.map(f => ({ ...f, accepted: true })));

  const acceptedFields = fields.filter(f => f.accepted);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="autofill-modal" onClick={e => e.stopPropagation()}>
        <div className="autofill-modal-header">
          <div className="autofill-modal-title">🤖 Auto-Fill Preview</div>
          <div className="autofill-modal-subtitle">{fields.length} fields · {acceptedFields.length} accepted · Review and accept before applying</div>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="autofill-modal-legend">
          {Object.entries(CONFIDENCE_ICONS).map(([k, icon]) => (
            <span key={k} className="autofill-legend-item">{icon} {k.charAt(0).toUpperCase() + k.slice(1)} confidence</span>
          ))}
        </div>

        <div className="autofill-modal-body">
          <table className="autofill-table">
            <thead>
              <tr>
                <th style={{width:32}}>✓</th>
                <th>Field</th>
                <th>Value</th>
                <th style={{width:40}}>Conf.</th>
                <th>Source</th>
                <th style={{width:72}}>Confirm</th>
              </tr>
            </thead>
            <tbody>
              {fields.map((f, i) => (
                <tr key={i} className={f.accepted ? 'autofill-row-accepted' : ''}>
                  <td>
                    <input type="checkbox" checked={f.accepted} onChange={() => toggleAccepted(i)} />
                  </td>
                  <td className="autofill-field-name">{f.fieldName}</td>
                  <td>
                    <input
                      className="autofill-value-input"
                      value={f.editedValue}
                      onChange={e => updateEdited(i, e.target.value)}
                    />
                  </td>
                  <td className="autofill-conf" title={CONFIDENCE_LABELS[f.confidence] || ''}>
                    {CONFIDENCE_ICONS[f.confidence] || '⚪'}
                  </td>
                  <td className="autofill-source">{f.source}</td>
                  <td>
                    {!f.confirmed ? (
                      <button className="autofill-confirm-btn" onClick={() => {
                        onConfirmField(f.fieldName, f.filledValue, f.source, f.editedValue);
                        setFields(prev => prev.map((ff, fi) => fi === i ? { ...ff, confirmed: true } : ff));
                      }}>
                        ✓ Correct
                      </button>
                    ) : (
                      <span className="autofill-confirmed-badge">Saved</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="autofill-modal-footer">
          <button className="autofill-accept-all-btn" onClick={acceptAll}>Select All</button>
          <button
            className="autofill-apply-btn"
            disabled={!acceptedFields.length}
            onClick={() => onAccept(fields.filter(f => f.accepted))}
          >
            Apply {acceptedFields.length} Selected Field{acceptedFields.length !== 1 ? 's' : ''}
          </button>
          <button className="autofill-cancel-btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function LrCorrectionEditor({ grid, onGridChange, onSave, onCancel }) {
  const years = grid.yearSlots || POLICY_YEARS;
  const updateCell = (section, yearIdx, field, value) => {
    onGridChange(prev => {
      const next = { ...prev };
      next[section] = (prev[section] || []).map((cell, i) =>
        i === yearIdx ? { ...cell, [field]: value } : cell
      );
      return next;
    });
  };
  return (
    <div className="lr-correction-editor">
      <div className="lr-correction-title">✗ Fix Errors — correct the cells below then save</div>
      <div className="table-wrap">
        <table className="data-table lr-correction-table">
          <thead>
            <tr>
              <th>Line</th>
              {years.map(y => <th key={y}>{y}</th>)}
            </tr>
          </thead>
          <tbody>
            {LR_LINES.map(line => (
              <tr key={line.key}>
                <td className="lr-corr-label">{line.label}</td>
                {years.map((y, yi) => {
                  const cell = (grid[line.key] || [])[yi] || {};
                  return (
                    <td key={y} className="lr-corr-cell">
                      <label className="lr-corr-check">
                        <input type="checkbox" checked={!!cell.present}
                          onChange={e => updateCell(line.key, yi, 'present', e.target.checked)} />
                        <span>Present</span>
                      </label>
                      <input className="lr-corr-input" placeholder="Carrier" value={cell.carrier || ''}
                        onChange={e => updateCell(line.key, yi, 'carrier', e.target.value)} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="lr-correction-actions">
        <button className="lr-fb-btn lr-fb-correct" onClick={onSave}>💾 Save Correction</button>
        <button className="lr-fb-btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function LossRunVerificationCard({ data, yib = 0 }) {
  const [copied, setCopied] = React.useState(false);
  if (!data) return null;

  const years = data.yearSlots || POLICY_YEARS;
  // Limit required years to the insured's years-in-business; older slots predate
  // the company's existence and should not be flagged as missing.
  const requiredYears = (yib > 0 && yib < years.length)
    ? years.slice(0, Math.ceil(yib))
    : years;
  const lineStatus = LR_LINES.map(line => {
    const entries = data[line.key] || [];
    const presentYears = entries.filter(e => e.present || e.active).map(e => e.year);
    const activeYears  = entries.filter(e => e.active).map(e => e.year);
    const missingYears = requiredYears.filter(y => !presentYears.includes(y));
    return { ...line, entries, presentYears, activeYears, missingYears, complete: missingYears.length === 0 };
  });

  const incompleteLines = lineStatus.filter(l => !l.complete);
  const allComplete = incompleteLines.length === 0;

  const requestText = incompleteLines.length === 0 ? '' : [
    'We are still missing the following loss run coverage:',
    '',
    ...incompleteLines.map(l =>
      `• ${l.label}: need ${l.missingYears.join(', ')}`
    ),
    '',
    'Please provide carrier-issued loss runs (not agent summaries) covering the last 5 policy years for each line.',
  ].join('\n');

  return (
    <div className="lrv-card">
      <div className="lrv-header">
        <div className="lrv-title">
          <span className="lrv-icon">📊</span>
          <div>
            <div className="lrv-title-text">Loss Run Coverage Verification</div>
            <div className="lrv-subtitle">5-year grid — 4 coverage lines</div>
          </div>
        </div>
        <div className={`lrv-status-badge${allComplete ? ' lrv-complete' : ' lrv-incomplete'}`}>
          {allComplete ? '✓ Complete' : `${incompleteLines.length} line${incompleteLines.length > 1 ? 's' : ''} incomplete`}
        </div>
      </div>

      <div className="lrv-grid">
        <div className="lrv-grid-header">
          <div className="lrv-grid-label-col" />
          {years.map(y => (
            <div key={y} className="lrv-grid-year">{y}</div>
          ))}
        </div>
        {lineStatus.map(line => (
          <div key={line.key} className="lrv-grid-row">
            <div className="lrv-grid-label">{line.label}</div>
            {years.map(y => {
              const entry = line.entries.find(e => e.year === y);
              const isActive  = entry?.active;
              const isPresent = entry?.present || isActive;
              const cls = isActive ? ' lrv-cell-active' : isPresent ? ' lrv-cell-present' : ' lrv-cell-missing';
              const icon = isActive ? '●' : isPresent ? '✓' : '✗';
              return (
                <div key={y} className={`lrv-grid-cell${cls}`} title={isActive ? 'Active policy' : ''}>
                  {icon}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div className="lrv-summary">
        {lineStatus.map(line => (
          <div key={line.key} className={`lrv-summary-row${line.complete ? ' lrv-summary-complete' : ' lrv-summary-incomplete'}`}>
            <span className="lrv-summary-icon">{line.complete ? '✓' : '✗'}</span>
            <span className="lrv-summary-label">{line.label}</span>
            {line.complete
              ? <span className="lrv-summary-note">
                  All 5 years present{line.activeYears.length > 0 ? ` (${line.activeYears.join(', ')} active)` : ''}
                </span>
              : <span className="lrv-summary-note">Missing: {line.missingYears.join(', ')}</span>
            }
          </div>
        ))}
      </div>

      {data.notes && (
        <div className="lrv-notes">{data.notes}</div>
      )}

      {requestText && (
        <div className="lrv-request">
          <div className="lrv-request-header">
            <div className="lrv-request-label">Client Request Text</div>
            <button
              className="lrv-copy-btn"
              onClick={() => {
                navigator.clipboard.writeText(requestText).then(() => {
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                });
              }}
            >
              {copied ? '✓ Copied!' : 'Copy'}
            </button>
          </div>
          <pre className="lrv-request-text">{requestText}</pre>
        </div>
      )}
    </div>
  );
}

export function CABBasicsPanel({ cabData }) {
  if (!cabData?.basics) return null;
  return (
    <div className="cab-basics-panel card">
      <div className="card-title"><span className="section-icon">📊</span>FMCSA BASIC Scores</div>
      <div className="cab-basics-panel-grid">
        {BASIC_KEYS.map(key => {
          const d = cabData.basics[key] || {};
          const rawScore = parseInt(d.score, 10);
          const score = isNaN(rawScore) ? null : rawScore;
          const showAlert = d.alert && score !== null;
          const color = showAlert ? '#EF4444' : score !== null && score >= 65 ? '#F97316' : score !== null ? '#22C55E' : '#94A3B8';
          return (
            <div key={key} className={`cab-panel-basic${showAlert ? ' cab-panel-alert' : ''}`}>
              <div className="cab-panel-basic-label">{BASIC_LABELS[key]}</div>
              <div className={`cab-panel-bar-wrap${score === null ? ' cab-panel-bar-na' : ''}`}>
                {score !== null && <div className="cab-panel-bar" style={{ width: `${Math.min(score, 100)}%`, background: color }} />}
              </div>
              <span className="cab-panel-pct" style={{ color }}>{score !== null ? `${score}%` : 'N/A'}</span>
              {showAlert ? <span className="cab-panel-alert-tag">⚠️ ALERT</span> : <span />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Markdown renderer ───────────────────────────────────────────────────────

function renderMarkdown(text) {
  if (!text) return { __html: '' };
  const esc  = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const inline = (s) => esc(s)
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>');

  const lines = text.split('\n');
  let html = '';
  let inUl = false;

  const closeUl = () => { if (inUl) { html += '</ul>'; inUl = false; } };

  for (const line of lines) {
    const h3 = line.match(/^### (.+)$/);
    const h2 = line.match(/^## (.+)$/);
    const h1 = line.match(/^# (.+)$/);
    const li = line.match(/^[-*] (.+)$/) || line.match(/^\d+\. (.+)$/);
    const hr = line.match(/^[-*_]{3,}$/);
    const bold = line.match(/^\*\*(.+)\*\*:?\s*$/); // standalone bold line (section heading without ##)

    if (h1)   { closeUl(); html += `<h2 class="md-h2">${inline(h1[1])}</h2>`; }
    else if (h2)   { closeUl(); html += `<h2 class="md-h2">${inline(h2[1])}</h2>`; }
    else if (h3)   { closeUl(); html += `<h3 class="md-h3">${inline(h3[1])}</h3>`; }
    else if (hr)   { closeUl(); html += '<hr class="md-hr">'; }
    else if (bold) { closeUl(); html += `<h3 class="md-h3">${inline(bold[1])}</h3>`; }
    else if (li)   { if (!inUl) { html += '<ul class="md-ul">'; inUl = true; } html += `<li>${inline(li[1])}</li>`; }
    else if (line.trim() === '') { closeUl(); html += '<div class="md-gap"></div>'; }
    else           { closeUl(); html += `<p class="md-p">${inline(line)}</p>`; }
  }
  closeUl();
  return { __html: html };
}

// ─── Loss Section Table (module-level so React doesn't remount on every render) ──

function LossSectionTable({ sectionKey, title, rows, onUpdate }) {
  const parseNum = (v) => { const n = parseFloat(String(v || '').replace(/[$,]/g, '')); return isNaN(n) ? 0 : n; };
  const fmtNum   = (n) => n === 0 ? '—' : n.toLocaleString();
  const totals = {
    numClaims:    rows.reduce((a, r) => a + parseNum(r.numClaims), 0),
    lossReserve:  rows.reduce((a, r) => a + parseNum(r.lossReserve), 0),
    totalIncurred:rows.reduce((a, r) => a + parseNum(r.totalIncurred), 0),
  };
  return (
    <div className="loss-section">
      <div className="loss-section-title">{title}</div>
      <div className="table-wrap">
        <table className="data-table loss-table">
          <thead>
            <tr>
              <th>Insurance Company</th>
              <th>Policy Year</th>
              <th>Policy #</th>
              <th>No. Claims</th>
              <th>Loss Reserve</th>
              <th>Total Incurred</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <React.Fragment key={i}>
              <tr>
                <td>
                  <div className="loss-insurer-cell">
                    <input className="cell-input" value={row.insurer} onChange={e => onUpdate(sectionKey, i, 'insurer', e.target.value)} placeholder="Insurance Company" />
                    <select className="loss-na-select" value="" onChange={e => { if (e.target.value) onUpdate(sectionKey, i, 'insurer', e.target.value); }}>
                      <option value="">N/A →</option>
                      {NA_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                    {row.fromApp && <span className="ls-from-app" style={{fontSize:'10px',marginLeft:'4px'}}>FROM APP</span>}
                  </div>
                </td>
                <td><input className="cell-input" value={row.policyYear}   onChange={e => onUpdate(sectionKey, i, 'policyYear',   e.target.value)} /></td>
                <td><input className="cell-input" value={row.policyNumber} onChange={e => onUpdate(sectionKey, i, 'policyNumber', e.target.value)} placeholder="Policy #" /></td>
                <td><input className="cell-input" value={row.numClaims}    onChange={e => onUpdate(sectionKey, i, 'numClaims',    e.target.value)} placeholder="0" /></td>
                <td><input className="cell-input" value={row.lossReserve}  onChange={e => onUpdate(sectionKey, i, 'lossReserve',  e.target.value)} placeholder="$0" /></td>
                <td><input className="cell-input" value={row.totalIncurred} onChange={e => onUpdate(sectionKey, i, 'totalIncurred', e.target.value)} placeholder="$0" /></td>
              </tr>
              {row.needsReview && row.appReportedOverride && (
                <tr>
                  <td colSpan={6} style={{background:'#fffbeb',borderTop:'none',padding:'2px 8px 6px',fontSize:'11px',color:'#92400e'}}>
                    <strong>NEEDS REVIEW</strong> — APP reported: claims={row.appReportedOverride.numClaims ?? '—'}, reserve=${row.appReportedOverride.lossReserve ?? '—'}, incurred=${row.appReportedOverride.totalIncurred ?? '—'}. Loss run document did not confirm this coverage section. Enter verified figures above.
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr className="loss-total-row">
              <td colSpan={3}><strong>TOTAL</strong></td>
              <td><strong>{fmtNum(totals.numClaims)}</strong></td>
              <td><strong>${fmtNum(totals.lossReserve)}</strong></td>
              <td><strong>${fmtNum(totals.totalIncurred)}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// ─── Market Components ────────────────────────────────────────────────────────

function AddMarketForm({ onAdd, onClose }) {
  const [form, setForm] = React.useState({
    name: '', uwName: '', uwEmail: '', uwPhone: '',
    dateSubmitted: new Date().toISOString().slice(0, 10),
    status: 'submitted',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const handleSubmit = () => {
    if (!form.name.trim()) return;
    onAdd({
      id: `mkt_${Date.now()}_${Math.random().toString(36).slice(2, 5)}`,
      ...form,
      quotedPremium: '',
      quotedAt: null,
      notes: '',
      lastContactedAt: null,
    });
    onClose();
  };
  return (
    <div className="mkt-overlay" onClick={onClose}>
      <div className="mkt-form" onClick={e => e.stopPropagation()}>
        <div className="mkt-form-header">
          <div className="mkt-form-title">Add Market</div>
          <button className="mkt-form-close" onClick={onClose}>✕</button>
        </div>
        <div className="mkt-form-grid">
          <label className="mkt-form-full">
            Market Name *
            <input className="cell-input" value={form.name} onChange={e => set('name', e.target.value)}
              placeholder="e.g. Markel, James River, Benchmark..." autoFocus />
          </label>
          <label>
            Underwriter Name
            <input className="cell-input" value={form.uwName} onChange={e => set('uwName', e.target.value)} />
          </label>
          <label>
            Underwriter Email
            <input className="cell-input" type="email" value={form.uwEmail} onChange={e => set('uwEmail', e.target.value)} />
          </label>
          <label>
            Underwriter Phone
            <input className="cell-input" type="tel" value={form.uwPhone} onChange={e => set('uwPhone', e.target.value)} />
          </label>
          <label>
            Date Submitted
            <input className="cell-input" type="date" value={form.dateSubmitted} onChange={e => set('dateSubmitted', e.target.value)} />
          </label>
          <label className="mkt-form-full">
            Initial Status
            <select className="cell-input" value={form.status} onChange={e => set('status', e.target.value)}>
              {MARKET_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
        </div>
        <div className="mkt-form-actions">
          <button className="mkt-cancel-btn" onClick={onClose}>Cancel</button>
          <button className="mkt-submit-btn" onClick={handleSubmit} disabled={!form.name.trim()}>Add Market</button>
        </div>
      </div>
    </div>
  );
}

function EditMarketModal({ mkt, onSave, onClose }) {
  const [form, setForm] = React.useState({
    name:          mkt.name          || '',
    uwName:        mkt.uwName        || '',
    uwEmail:       mkt.uwEmail       || '',
    uwPhone:       mkt.uwPhone       || '',
    dateSubmitted: mkt.dateSubmitted || new Date().toISOString().slice(0, 10),
    status:        mkt.status        || 'submitted',
    notes:         mkt.notes         || '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const handleSave = () => {
    if (!form.name.trim()) return;
    onSave(form);
    onClose();
  };
  return (
    <div className="mkt-overlay" onClick={onClose}>
      <div className="mkt-form" onClick={e => e.stopPropagation()}>
        <div className="mkt-form-header">
          <div className="mkt-form-title">Edit Market</div>
          <button className="mkt-form-close" onClick={onClose}>✕</button>
        </div>
        <div className="mkt-form-grid">
          <label className="mkt-form-full">
            Market Name *
            <input className="cell-input" value={form.name} onChange={e => set('name', e.target.value)} autoFocus />
          </label>
          <label>
            Underwriter Name
            <input className="cell-input" value={form.uwName} onChange={e => set('uwName', e.target.value)} />
          </label>
          <label>
            Underwriter Email
            <input className="cell-input" type="email" value={form.uwEmail} onChange={e => set('uwEmail', e.target.value)} />
          </label>
          <label>
            Underwriter Phone
            <input className="cell-input" type="tel" value={form.uwPhone} onChange={e => set('uwPhone', e.target.value)} />
          </label>
          <label>
            Date Submitted
            <input className="cell-input" type="date" value={form.dateSubmitted} onChange={e => set('dateSubmitted', e.target.value)} />
          </label>
          <label className="mkt-form-full">
            Status
            <select className="cell-input" value={form.status} onChange={e => set('status', e.target.value)}>
              {MARKET_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
          <label className="mkt-form-full">
            Notes
            <textarea className="cell-input" value={form.notes} onChange={e => set('notes', e.target.value)} rows={3} style={{ resize: 'vertical' }} />
          </label>
        </div>
        <div className="mkt-form-actions">
          <button className="mkt-cancel-btn" onClick={onClose}>Cancel</button>
          <button className="mkt-submit-btn" onClick={handleSave} disabled={!form.name.trim()}>Save Changes</button>
        </div>
      </div>
    </div>
  );
}

function MarketCard({ mkt, onUpdate, onDelete, onEdit, onStatusChange, onMarkContacted, isFollowUp, daysSinceContact, borFollowUp, borDays, isBestQuote }) {
  const info = MARKET_STATUS_MAP[mkt.status] || MARKET_STATUS_MAP.submitted;
  const submittedDays = daysSince(mkt.dateSubmitted);
  const showPremium = mkt.status === 'quoted' || mkt.status === 'bound';
  const bor = mkt.bor || {};
  const borInfo = bor.status ? BOR_STATUS_MAP[bor.status] : null;

  return (
    <div className={`mkt-card${isFollowUp ? ' mkt-card-alert' : ''}`}>
      <div className="mkt-card-accent" style={{ background: info.border }} />
      <div className="mkt-card-inner">

        {/* Header */}
        <div className="mkt-card-top">
          <div className="mkt-card-name-col">
            {isBestQuote && <span className="mkt-best-badge">🏆 Best Quote</span>}
            <div className="mkt-card-name">
              {mkt.name}
              {bor.sent && borInfo && (
                <span className={`bor-badge ${borInfo.badgeCls}`}>{borInfo.badge}</span>
              )}
            </div>
          </div>
          <div className="mkt-card-top-right">
            <span className="mkt-status-pill" style={{ background: info.bg, color: info.color, border: `1px solid ${info.border}` }}>
              {info.label}
            </span>
            <button className="mkt-edit-btn" onClick={onEdit} title="Edit market" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, padding: '2px 5px', color: '#64748b', lineHeight: 1 }}>✏️</button>
            <button className="mkt-delete-btn" onClick={onDelete} title="Delete market">🗑</button>
          </div>
        </div>

        {/* Follow-up alert */}
        {isFollowUp && !borFollowUp && (
          <div className="mkt-alert-banner">
            <span>⚠️ Follow Up Needed — {daysSinceContact} day{daysSinceContact !== 1 ? 's' : ''} without contact</span>
            <button className="mkt-contacted-btn" onClick={onMarkContacted}>✓ Mark as Contacted</button>
          </div>
        )}
        {borFollowUp && (
          <div className="mkt-alert-banner mkt-alert-bor">
            <span>⚠️ BOR Pending {borDays} day{borDays !== 1 ? 's' : ''} — no carrier response yet</span>
          </div>
        )}

        {/* Contact details */}
        <div className="mkt-card-details">
          {mkt.uwName    && <div className="mkt-detail"><span className="mkt-detail-lbl">Underwriter</span><span>{mkt.uwName}</span></div>}
          {mkt.uwEmail   && <div className="mkt-detail"><span className="mkt-detail-lbl">Email</span><a href={`mailto:${mkt.uwEmail}`} className="mkt-link">{mkt.uwEmail}</a></div>}
          {mkt.uwPhone   && <div className="mkt-detail"><span className="mkt-detail-lbl">Phone</span><a href={`tel:${mkt.uwPhone}`} className="mkt-link">{mkt.uwPhone}</a></div>}
          {mkt.dateSubmitted && (
            <div className="mkt-detail">
              <span className="mkt-detail-lbl">Submitted</span>
              <span>{new Date(`${mkt.dateSubmitted}T12:00:00`).toLocaleDateString()} · {submittedDays ?? 0} day{submittedDays !== 1 ? 's' : ''} ago</span>
            </div>
          )}
        </div>

        {/* Quoted premium (visible when quote received or bound) */}
        {showPremium && (
          <div className="mkt-premium-wrap">
            <span className="mkt-detail-lbl">Quoted Premium</span>
            <input
              className="mkt-premium-input cell-input"
              value={mkt.quotedPremium}
              onChange={e => onUpdate('quotedPremium', e.target.value)}
              placeholder="$0"
            />
          </div>
        )}

        {/* Notes */}
        <textarea
          className="mkt-notes cell-input"
          value={mkt.notes}
          onChange={e => onUpdate('notes', e.target.value)}
          placeholder="Notes..."
          rows={2}
        />

        {/* Status buttons */}
        <div className="mkt-status-btns">
          {MARKET_STATUSES.map(s => (
            <button
              key={s.key}
              className={`mkt-status-btn${mkt.status === s.key ? ' active' : ''}`}
              style={mkt.status === s.key ? { background: s.bg, color: s.color, borderColor: s.border, borderWidth: 2 } : {}}
              onClick={() => onStatusChange(s.key)}
            >
              {s.short}
            </button>
          ))}
        </div>

        {/* BOR Section */}
        <div className="bor-section">
          <label className="bor-checkbox-label">
            <input
              type="checkbox"
              checked={!!bor.sent}
              onChange={e => {
                const sent = e.target.checked;
                const today = new Date().toISOString().slice(0, 10);
                onUpdate('bor', { ...bor, sent, dateSent: sent ? (bor.dateSent || today) : bor.dateSent, status: sent ? (bor.status || 'pending') : bor.status });
              }}
            />
            <span className="bor-checkbox-text">BOR letter sent to this market</span>
          </label>
          {bor.sent && (
            <div className="bor-details">
              <div className="bor-row">
                <label className="bor-lbl">Date BOR Sent</label>
                <input
                  type="date"
                  className="bor-input"
                  value={bor.dateSent || ''}
                  onChange={e => onUpdate('bor', { ...bor, dateSent: e.target.value })}
                />
              </div>
              <div className="bor-row">
                <label className="bor-lbl">BOR Status</label>
                <select
                  className="bor-input"
                  value={bor.status || 'pending'}
                  onChange={e => onUpdate('bor', { ...bor, status: e.target.value })}
                >
                  {BOR_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
              <div className="bor-row">
                <label className="bor-lbl">BOR Notes</label>
                <textarea
                  className="bor-input bor-notes"
                  rows={2}
                  value={bor.notes || ''}
                  onChange={e => onUpdate('bor', { ...bor, notes: e.target.value })}
                  placeholder="Any notes about the BOR process…"
                />
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

// ─── Login Screen ─────────────────────────────────────────────────────────────

function LoginScreen({ onLogin }) {
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError]       = React.useState('');
  const [loading, setLoading]   = React.useState(false);

  // Invite / account-creation state
  const [inviteToken, setInviteToken]         = React.useState('');
  const [inviteInfo, setInviteInfo]           = React.useState(null);  // { organizationName, role, expiresAt, valid }
  const [inviteInfoLoading, setInviteInfoLoading] = React.useState(false);
  const [signupName, setSignupName]           = React.useState('');
  const [signupUser, setSignupUser]           = React.useState('');
  const [signupPw, setSignupPw]               = React.useState('');
  const [signupPw2, setSignupPw2]             = React.useState('');
  const [signupErr, setSignupErr]             = React.useState('');
  const [signupLoading, setSignupLoading]     = React.useState(false);
  const [signupTerms, setSignupTerms]         = React.useState(false);
  const [signupPrivacy, setSignupPrivacy]     = React.useState(false);
  const [showLegalDoc, setShowLegalDoc]       = React.useState(null); // 'terms' | 'privacy'

  React.useEffect(() => {
    const t = new URLSearchParams(window.location.search).get('invite');
    if (!t) return;
    setInviteToken(t);
    setInviteInfoLoading(true);
    fetch(`${API_BASE}/api/invite/status?token=${encodeURIComponent(t)}`)
      .then(r => r.json())
      .then(d => setInviteInfo(d))
      .catch(() => setInviteInfo({ valid: false, error: 'Could not verify invite.' }))
      .finally(() => setInviteInfoLoading(false));
  }, []);

  const inputStyle = (hasError) => ({
    width: '100%', padding: '12px 14px', fontSize: 15, borderRadius: 8,
    border: hasError ? '1.5px solid #ef4444' : '1.5px solid #e2e8f0',
    outline: 'none', marginBottom: 12, boxSizing: 'border-box',
    fontFamily: 'inherit', color: '#1a2332',
  });

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password }),
      });
      const data = await res.json();
      if (res.ok && data.success && data.token) {
        localStorage.setItem('tsp_auth', data.token);
        localStorage.setItem('tsp_user', JSON.stringify({
          username: data.username, role: data.role, trialEndsAt: data.trialEndsAt || null,
        }));
        onLogin(data.token, data.username, data.role, data.trialEndsAt || null, !!data.legalAccepted);
      } else {
        setError(data.error || 'Incorrect username or password.');
      }
    } catch (e) {
      setError('Connection error. Please try again.');
    }
    setLoading(false);
  };

  const submitSignup = async (e) => {
    e.preventDefault();
    setSignupErr('');
    if (!signupTerms || !signupPrivacy) { setSignupErr('You must accept both agreements to continue.'); return; }
    if (signupPw !== signupPw2) { setSignupErr('Passwords do not match.'); return; }
    setSignupLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/invite/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token:          inviteToken,
          username:       signupUser.trim(),
          password:       signupPw,
          name:           signupName.trim(),
          termsVersion:   CURRENT_TERMS_VERSION,
          privacyVersion: CURRENT_PRIVACY_VERSION,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success && data.token) {
        localStorage.setItem('tsp_auth', data.token);
        localStorage.setItem('tsp_user', JSON.stringify({ username: data.username, role: data.role, trialEndsAt: null }));
        window.history.replaceState({}, '', window.location.pathname);
        onLogin(data.token, data.username, data.role, null, true);
      } else {
        setSignupErr(data.error || 'Could not create account. The link may have expired.');
      }
    } catch {
      setSignupErr('Connection error. Please try again.');
    }
    setSignupLoading(false);
  };

  const cardStyle = {
    background: '#fff', borderRadius: 16, padding: '48px 40px', width: '100%', maxWidth: 380,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center',
  };
  const wrapStyle = {
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(135deg, #0f2148 0%, #1a3a6b 100%)',
    fontFamily: "'Inter','Segoe UI',system-ui,sans-serif",
  };

  if (inviteToken) {
    return (
      <>
      <div style={wrapStyle}>
        <div style={cardStyle}>
          <img src="/processly-logo.svg" alt="Processly" style={{ width: 140, height: 'auto', display: 'block', margin: '0 auto 14px' }} />
          <div style={{ fontSize: 22, fontWeight: 700, color: '#0f2148', marginBottom: 4 }}>
            Trucking Submission Pro
          </div>
          {inviteInfoLoading && (
            <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20 }}>Verifying invite…</div>
          )}
          {!inviteInfoLoading && inviteInfo && !inviteInfo.valid && (
            <div style={{ marginBottom: 20, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 13, color: '#dc2626' }}>
              {inviteInfo.error || 'This invite link is invalid or has already been used.'}
            </div>
          )}
          {!inviteInfoLoading && inviteInfo && inviteInfo.valid && (
            <div style={{ marginBottom: 20, padding: '10px 14px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, textAlign: 'left' }}>
              <div style={{ fontSize: 12, color: '#0369a1', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>You&apos;re invited to join</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0f2148' }}>{inviteInfo.organizationName}</div>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>Role: {inviteInfo.role}</div>
            </div>
          )}
          {!inviteInfoLoading && !inviteInfo && (
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>Create your account</div>
          )}
          <form onSubmit={submitSignup}>
            <input
              type="text"
              placeholder="Full Name"
              value={signupName}
              onChange={e => setSignupName(e.target.value)}
              autoFocus
              style={inputStyle(false)}
            />
            <input
              type="text"
              placeholder="Username (letters, numbers, _ -)"
              value={signupUser}
              onChange={e => setSignupUser(e.target.value)}
              autoComplete="username"
              style={inputStyle(!!signupErr && !signupUser)}
            />
            <input
              type="password"
              placeholder="Password (min 8 characters)"
              value={signupPw}
              onChange={e => setSignupPw(e.target.value)}
              autoComplete="new-password"
              style={inputStyle(false)}
            />
            <input
              type="password"
              placeholder="Confirm password"
              value={signupPw2}
              onChange={e => setSignupPw2(e.target.value)}
              autoComplete="new-password"
              style={inputStyle(signupPw2.length > 0 && signupPw !== signupPw2)}
            />
            {/* Legal acceptance checkboxes */}
            <div style={{ textAlign: 'left', marginBottom: 14 }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10, cursor: 'pointer', fontSize: 13, color: '#334155', lineHeight: 1.4 }}>
                <input type="checkbox" checked={signupTerms} onChange={e => setSignupTerms(e.target.checked)}
                  style={{ marginTop: 2, flexShrink: 0, width: 16, height: 16, cursor: 'pointer' }} />
                <span>
                  I have read and agree to the{' '}
                  <button type="button" onClick={() => setShowLegalDoc('terms')}
                    style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', textDecoration: 'underline', fontSize: 13, padding: 0, fontFamily: 'inherit' }}>
                    Trucking Submission Pro User Agreement
                  </button>
                </span>
              </label>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 13, color: '#334155', lineHeight: 1.4 }}>
                <input type="checkbox" checked={signupPrivacy} onChange={e => setSignupPrivacy(e.target.checked)}
                  style={{ marginTop: 2, flexShrink: 0, width: 16, height: 16, cursor: 'pointer' }} />
                <span>
                  I acknowledge that I have read the{' '}
                  <button type="button" onClick={() => setShowLegalDoc('privacy')}
                    style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', textDecoration: 'underline', fontSize: 13, padding: 0, fontFamily: 'inherit' }}>
                    Trucking Submission Pro Privacy Policy
                  </button>
                </span>
              </label>
            </div>
            {signupErr && (
              <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{signupErr}</div>
            )}
            <button
              type="submit"
              disabled={signupLoading || !signupUser || !signupPw || !signupPw2 || !signupTerms || !signupPrivacy}
              style={{
                width: '100%', padding: '13px', fontSize: 15, fontWeight: 600,
                background: 'linear-gradient(135deg, #0f2148, #1a3a6b)',
                color: '#fff', border: 'none', borderRadius: 8, cursor: signupLoading ? 'wait' : 'pointer',
                opacity: (signupLoading || !signupUser || !signupPw || !signupPw2 || !signupTerms || !signupPrivacy) ? 0.6 : 1,
                transition: 'opacity 0.2s', fontFamily: 'inherit',
              }}
            >
              {signupLoading ? 'Creating account…' : 'Create Account'}
            </button>
          </form>
          <div style={{ fontSize: 11, color: '#cbd5e1', marginTop: 24 }}>
            Invite link expires in 7 days
          </div>
        </div>
      </div>
      {/* Legal document modal */}
      {showLegalDoc && (
        <div onClick={() => setShowLegalDoc(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: '32px', width: '100%', maxWidth: 720, maxHeight: '80vh', overflowY: 'auto', position: 'relative' }}>
            <button onClick={() => setShowLegalDoc(null)} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 13, color: '#64748b' }}>Close</button>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f2148', marginBottom: 24, marginTop: 0 }}>
              {showLegalDoc === 'terms' ? 'User Agreement — Version ' + CURRENT_TERMS_VERSION : 'Privacy Policy — Version ' + CURRENT_PRIVACY_VERSION}
            </h2>
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 13, color: '#334155', lineHeight: 1.6 }}>
              {showLegalDoc === 'terms' ? TERMS_TEXT : PRIVACY_TEXT}
            </pre>
          </div>
        </div>
      )}
      </>
    );
  }

  return (
    <div style={wrapStyle}>
      <div style={cardStyle}>
        <img src="/processly-logo.svg" alt="Processly" style={{ width: 140, height: 'auto', display: 'block', margin: '0 auto 14px' }} />
        <div style={{ fontSize: 22, fontWeight: 700, color: '#0f2148', marginBottom: 4 }}>
          Trucking Submission Pro
        </div>
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 32 }}>
          Commercial Trucking Insurance
        </div>
        <form onSubmit={submit}>
          <input
            type="text"
            placeholder="Name"
            value={username}
            onChange={e => setUsername(e.target.value)}
            autoFocus
            autoComplete="username"
            style={inputStyle(!!error)}
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoComplete="current-password"
            style={inputStyle(!!error)}
          />
          {error && (
            <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{error}</div>
          )}
          <button
            type="submit"
            disabled={loading || !username || !password}
            style={{
              width: '100%', padding: '13px', fontSize: 15, fontWeight: 600,
              background: 'linear-gradient(135deg, #0f2148, #1a3a6b)',
              color: '#fff', border: 'none', borderRadius: 8, cursor: loading ? 'wait' : 'pointer',
              opacity: (!username || !password || loading) ? 0.6 : 1, transition: 'opacity 0.2s',
              fontFamily: 'inherit',
            }}
          >
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
        <div style={{ fontSize: 11, color: '#cbd5e1', marginTop: 24 }}>
          🔒 Secure access — authorized users only
        </div>
      </div>
    </div>
  );
}

// ─── Legal Acceptance Screen ─────────────────────────────────────────────────

function LegalAcceptanceScreen({ username, onAccepted, onLogout }) {
  const [termsChecked,   setTermsChecked]   = React.useState(false);
  const [privacyChecked, setPrivacyChecked] = React.useState(false);
  const [loading,        setLoading]        = React.useState(false);
  const [error,          setError]          = React.useState('');
  const [showDoc,        setShowDoc]        = React.useState(null); // 'terms' | 'privacy'

  const canSubmit = termsChecked && privacyChecked && !loading;

  const submit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('tsp_auth') || '';
      const res = await fetch(`${API_BASE}/api/legal/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ termsVersion: CURRENT_TERMS_VERSION, privacyVersion: CURRENT_PRIVACY_VERSION }),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        onAccepted();
      } else {
        setError(data.error || 'Could not record acceptance. Please try again.');
      }
    } catch {
      setError('Connection error. Please try again.');
    }
    setLoading(false);
  };

  const checkStyle = { display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', fontSize: 14, color: '#1e293b', lineHeight: 1.5, marginBottom: 16, textAlign: 'left' };
  const cbStyle = { marginTop: 3, flexShrink: 0, width: 18, height: 18, cursor: 'pointer', accentColor: '#0f2148' };
  const linkStyle = { background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', textDecoration: 'underline', fontSize: 14, padding: 0, fontFamily: 'inherit' };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #0f2148 0%, #1a3a6b 100%)', fontFamily: "'Inter','Segoe UI',system-ui,sans-serif", padding: '24px 16px' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '48px 44px', width: '100%', maxWidth: 560, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', color: '#94a3b8', marginBottom: 8 }}>Trucking Submission Pro</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#0f2148', marginBottom: 8 }}>Before continuing</div>
          <div style={{ fontSize: 14, color: '#64748b', lineHeight: 1.5 }}>
            Please review and accept the agreements governing your use of Trucking Submission Pro.
          </div>
        </div>

        {/* Agreement cards */}
        {[
          { key: 'terms',   label: 'User Agreement',   version: CURRENT_TERMS_VERSION },
          { key: 'privacy', label: 'Privacy Policy',   version: CURRENT_PRIVACY_VERSION },
        ].map(doc => (
          <div key={doc.key} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '16px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 15, color: '#0f2148' }}>{doc.label}</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Version {doc.version}</div>
            </div>
            <button onClick={() => setShowDoc(doc.key)} style={{ ...linkStyle, fontSize: 13, fontWeight: 600, textDecoration: 'none', background: '#f1f5f9', padding: '6px 14px', borderRadius: 6, border: '1px solid #e2e8f0', color: '#334155' }}>
              View
            </button>
          </div>
        ))}

        {/* Checkboxes */}
        <div style={{ marginTop: 24, marginBottom: 8 }}>
          <label style={checkStyle}>
            <input type="checkbox" checked={termsChecked} onChange={e => setTermsChecked(e.target.checked)} style={cbStyle} />
            <span>I have read and agree to the <button type="button" onClick={() => setShowDoc('terms')} style={linkStyle}>Trucking Submission Pro User Agreement</button>.</span>
          </label>
          <label style={checkStyle}>
            <input type="checkbox" checked={privacyChecked} onChange={e => setPrivacyChecked(e.target.checked)} style={cbStyle} />
            <span>I acknowledge that I have read the <button type="button" onClick={() => setShowDoc('privacy')} style={linkStyle}>Trucking Submission Pro Privacy Policy</button>.</span>
          </label>
        </div>

        {error && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{error}</div>}

        <button
          onClick={submit}
          disabled={!canSubmit}
          style={{ width: '100%', padding: '14px', fontSize: 15, fontWeight: 700, background: canSubmit ? 'linear-gradient(135deg, #0f2148, #1a3a6b)' : '#cbd5e1', color: '#fff', border: 'none', borderRadius: 8, cursor: canSubmit ? 'pointer' : 'not-allowed', transition: 'background 0.2s', fontFamily: 'inherit', marginTop: 8 }}
        >
          {loading ? 'Saving…' : 'Accept & Continue'}
        </button>

        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <button onClick={onLogout} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>
            Sign out
          </button>
        </div>
      </div>

      {/* Legal document modal */}
      {showDoc && (
        <div onClick={() => setShowDoc(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: '36px 40px', width: '100%', maxWidth: 760, maxHeight: '85vh', overflowY: 'auto', position: 'relative' }}>
            <button onClick={() => setShowDoc(null)} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 14px', cursor: 'pointer', fontSize: 13, color: '#64748b' }}>Close</button>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f2148', marginTop: 0, marginBottom: 28 }}>
              {showDoc === 'terms' ? `User Agreement — Version ${CURRENT_TERMS_VERSION}` : `Privacy Policy — Version ${CURRENT_PRIVACY_VERSION}`}
            </h2>
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 13, color: '#334155', lineHeight: 1.7 }}>
              {showDoc === 'terms' ? TERMS_TEXT : PRIVACY_TEXT}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Conflict Modal ───────────────────────────────────────────────────────────

function ConflictModal({ conflict, onResolve }) {
  if (!conflict) return null;
  const { field, cabValue, currentValue, currentLabel } = conflict;
  return (
    <div className="conflict-overlay">
      <div className="conflict-modal">
        <div className="conflict-title">⚠️ Data Conflict Detected</div>
        <div className="conflict-subtitle">
          The value in your <strong>{field}</strong> field differs from the CAB report.
          Which value should be used for AI analysis?
        </div>
        <div className="conflict-values">
          <div className="conflict-value-block">
            <div className="conflict-value-label">CAB Report</div>
            <div className="conflict-value">{cabValue}</div>
          </div>
          <div className="conflict-vs">vs</div>
          <div className="conflict-value-block">
            <div className="conflict-value-label">{currentLabel}</div>
            <div className="conflict-value">{currentValue}</div>
          </div>
        </div>
        <div className="conflict-actions">
          <button className="conflict-btn conflict-btn-cab" onClick={() => onResolve('cab')}>
            Use CAB Report <span className="conflict-btn-val">({cabValue})</span>
          </button>
          <button className="conflict-btn conflict-btn-current" onClick={() => onResolve('current')}>
            Use {currentLabel} <span className="conflict-btn-val">({currentValue})</span>
          </button>
          <button className="conflict-btn conflict-btn-later" onClick={() => onResolve('later')}>
            Ask Me Later
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── TruckerCloud Badge ───────────────────────────────────────────────────────

function TCBadge({ status }) {
  if (status === 'yes')
    return (
      <span className="tc-badge tc-badge-yes">
        ✓ TruckerCloud Compatible
        <a className="tc-badge-link" href="https://truckercloud.com/eld" target="_blank" rel="noopener noreferrer">Learn more</a>
      </span>
    );
  if (status === 'no')
    return (
      <span className="tc-badge tc-badge-no">
        ⚠ Not TruckerCloud Compatible — check with carrier
        <a className="tc-badge-link" href="https://truckercloud.com/eld" target="_blank" rel="noopener noreferrer">View list</a>
      </span>
    );
  return <span className="tc-badge tc-badge-unknown">? TruckerCloud compatibility unknown</span>;
}


// ─── Market Directory Edit Modal ──────────────────────────────────────────────
// Extracted as a proper component so that typing in form fields does NOT
// re-render the parent (and reset the modal's scroll position).

function MktEditSection({ title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#64748b', borderBottom: '1px solid #f1f5f9', paddingBottom: 5, marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );
}
function MktEditRow({ label, children }) {
  return (
    <div style={{ marginBottom: 8 }}>
      {label && <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 3 }}>{label}</div>}
      {children}
    </div>
  );
}

function MarketDirectoryEditModal({ mkt, onSave, onDelete, onClose, apiFetch }) {
  const [form, setForm] = React.useState({ ...mkt });
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState('');

  const fld = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const em = form;

  const saveMarket = async () => {
    setSaving(true);
    setMsg('');
    try {
      const res = await apiFetch(`${API_BASE}/api/admin/markets`, {
        method: 'POST',
        body: JSON.stringify({
          ...em,
          activeStates: typeof em.activeStates === 'string'
            ? em.activeStates.split(/[\s,]+/).map(s => s.trim().toUpperCase()).filter(Boolean)
            : (em.activeStates || []),
          ineligibleStates: typeof em.ineligibleStates === 'string'
            ? em.ineligibleStates.split(/[\s,]+/).map(s => s.trim().toUpperCase()).filter(Boolean)
            : (em.ineligibleStates || []),
          targetStates: typeof em.targetStates === 'string'
            ? em.targetStates.split(/[\s,]+/).map(s => s.trim().toUpperCase()).filter(Boolean)
            : (em.targetStates || []),
          nonTargetStates: typeof em.nonTargetStates === 'string'
            ? em.nonTargetStates.split(/[\s,]+/).map(s => s.trim().toUpperCase()).filter(Boolean)
            : (em.nonTargetStates || []),
          preferredCommodities: typeof em.preferredCommodities === 'string'
            ? em.preferredCommodities.split(',').map(s => s.trim()).filter(Boolean)
            : (em.preferredCommodities || []),
          ineligibleCommodities: typeof em.ineligibleCommodities === 'string'
            ? em.ineligibleCommodities.split(',').map(s => s.trim()).filter(Boolean)
            : (em.ineligibleCommodities || []),
          tags: typeof em.tags === 'string'
            ? em.tags.split(',').map(s => s.trim()).filter(Boolean)
            : (em.tags || []),
          telematicsCompatibleProviders: typeof em.telematicsCompatibleProviders === 'string'
            ? em.telematicsCompatibleProviders.split(',').map(s => s.trim()).filter(Boolean)
            : (em.telematicsCompatibleProviders || []),
          criticalAlerts: typeof em.criticalAlerts === 'string'
            ? em.criticalAlerts.split('\n').map(s => s.trim()).filter(Boolean).map(s => ({ title: s, description: '' }))
            : (em.criticalAlerts || []),
          fmcsaAlertMax:   em.fmcsaAlertMax === '' || em.fmcsaAlertMax == null ? null : Number(em.fmcsaAlertMax),
          radiusMax:       em.radiusMax === '' || em.radiusMax == null ? null : Number(em.radiusMax),
          vehicleAgeMax:   em.vehicleAgeMax === '' || em.vehicleAgeMax == null ? null : Number(em.vehicleAgeMax),
          b1DriversAccepted: em.b1DriversAccepted === 'true' ? true : em.b1DriversAccepted === 'false' ? false : null,
          naftaAccepted:     em.naftaAccepted    === 'true' ? true : em.naftaAccepted    === 'false' ? false : null,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      onSave(data.market);
      setMsg('Saved');
      setTimeout(() => { onClose(); setMsg(''); }, 900);
    } catch (err) {
      setMsg('Save failed — ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteMarket = async () => {
    if (!window.confirm(`Delete "${em.name}"? This cannot be undone.`)) return;
    setSaving(true);
    try {
      const res = await apiFetch(`${API_BASE}/api/admin/markets`, {
        method: 'DELETE',
        body: JSON.stringify({ id: em.id }),
      });
      if (!res.ok) throw new Error(await res.text());
      onDelete(em.id);
      onClose();
    } catch (err) {
      setMsg('Delete failed — ' + err.message);
      setSaving(false);
    }
  };

  const activeStatesStr      = Array.isArray(em.activeStates)      ? em.activeStates.join(', ')      : (em.activeStates      || '');
  const ineligibleStatesStr  = Array.isArray(em.ineligibleStates)  ? em.ineligibleStates.join(', ')  : (em.ineligibleStates  || '');
  const criticalAlertsStr    = Array.isArray(em.criticalAlerts)
    ? em.criticalAlerts.map(a => (typeof a === 'object' ? a.title || '' : a)).join('\n')
    : (em.criticalAlerts || '');
  const inpStyle = { width: '100%', fontSize: 13, border: '1px solid #e2e8f0', borderRadius: 6, padding: '5px 8px', color: '#1e293b', background: '#fafafa', boxSizing: 'border-box', outline: 'none' };
  const selStyle = { ...inpStyle };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '32px 16px', overflowY: 'auto' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 12, maxWidth: 760, width: '100%', padding: '28px 32px', position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>×</button>
        {/* Title */}
        <div style={{ marginBottom: 20, paddingRight: 32 }}>
          <input value={em.name || ''} onChange={e => fld('name', e.target.value)}
            style={{ fontWeight: 800, fontSize: 22, color: '#0f172a', border: 'none', borderBottom: '2px solid #e2e8f0', width: '100%', outline: 'none', background: 'transparent', padding: '2px 0', marginBottom: 6 }}
            placeholder="Market Name" />
          <input value={em.mgaOrWholesaleBroker || ''} onChange={e => fld('mgaOrWholesaleBroker', e.target.value)}
            style={{ fontSize: 13, color: '#64748b', border: 'none', borderBottom: '1px solid #f1f5f9', width: '100%', outline: 'none', background: 'transparent', padding: '2px 0' }}
            placeholder="MGA / Wholesale Broker (optional)" />
        </div>
        {/* Overview */}
        <MktEditSection title="Overview">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', marginBottom: 8 }}>
            <MktEditRow label="Market Type">
              <select style={selStyle} value={em.marketType || 'carrier'} onChange={e => fld('marketType', e.target.value)}>
                <option value="carrier">Carrier — run full eligibility engine</option>
                <option value="mga">MGA / Wholesale Brokerage — access/routing only</option>
                <option value="program">Program — carrier-level under parent MGA</option>
              </select>
            </MktEditRow>
            <MktEditRow label="Parent Market ID (program only)">
              <input style={inpStyle} value={em.parentMarketId || ''} onChange={e => fld('parentMarketId', e.target.value || null)} placeholder="e.g. blue-ridge-specialty" />
            </MktEditRow>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
            <MktEditRow label="Program Type">
              <select style={selStyle} value={em.programType || ''} onChange={e => fld('programType', e.target.value)}>
                <option value="">Select…</option>
                <option value="Admitted">Admitted</option>
                <option value="E&S">E&S Non-Admitted</option>
                <option value="Specialty">Risk Retention Group</option>
                <option value="London">London Market</option>
                <option value="Program">MGA Program</option>
              </select>
            </MktEditRow>
            <MktEditRow label="Program Strength">
              <select style={selStyle} value={em.programStrength || 'standard'} onChange={e => fld('programStrength', e.target.value)}>
                <option value="preferred">Preferred — go-to for standard accounts</option>
                <option value="competitive">Competitive — price-competitive option</option>
                <option value="standard">Standard</option>
                <option value="lastResort">Last Resort — exhausted all other options</option>
              </select>
            </MktEditRow>
            <MktEditRow label="AM Best Rating">
              <input style={inpStyle} value={em.amBestRating || ''} onChange={e => fld('amBestRating', e.target.value)} placeholder="e.g. A, A+, B+" />
            </MktEditRow>
            <MktEditRow label="Submission Restriction">
              <select style={selStyle} value={em.submissionRestriction || ''} onChange={e => fld('submissionRestriction', e.target.value)}>
                <option value="">Open — no restriction</option>
                <option value="controlled-business-renewals-only">Controlled business / renewals only</option>
                <option value="referral-only">Referral only</option>
              </select>
            </MktEditRow>
            <MktEditRow label="Min Units">
              <input style={inpStyle} type="number" min="0" value={em.minUnits ?? ''} onChange={e => fld('minUnits', parseInt(e.target.value) || 0)} />
            </MktEditRow>
            <MktEditRow label="Max Units (0 = no max)">
              <input style={inpStyle} type="number" min="0" value={em.maxUnits ?? ''} onChange={e => fld('maxUnits', parseInt(e.target.value) || 0)} />
            </MktEditRow>
            <MktEditRow label="Min Years in Business">
              <input style={inpStyle} type="number" min="0" value={em.minYearsInBusiness ?? ''} onChange={e => fld('minYearsInBusiness', parseInt(e.target.value) || 0)} />
            </MktEditRow>
            <MktEditRow label=" ">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 14 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151' }}>
                  <input type="checkbox" checked={!!em.newVentureEligible} onChange={e => fld('newVentureEligible', e.target.checked)} />
                  New Venture Eligible
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151' }}>
                  <input type="checkbox" checked={!!em.onPause} onChange={e => fld('onPause', e.target.checked)} />
                  Market On Pause
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151' }}>
                  <input type="checkbox" checked={!!em.distressedOnly} onChange={e => fld('distressedOnly', e.target.checked)} />
                  Distressed Accounts Only
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151' }}>
                  <input type="checkbox" checked={!!em.lastResort} onChange={e => fld('lastResort', e.target.checked)} />
                  Last Resort Program
                </label>
              </div>
            </MktEditRow>
          </div>
          <MktEditRow label="Tags (comma separated)">
            <input style={inpStyle} value={Array.isArray(em.tags) ? em.tags.join(', ') : (em.tags || '')} onChange={e => fld('tags', e.target.value)} placeholder="e.g. non-fleet, preferred-market, nafta" />
          </MktEditRow>
        </MktEditSection>
        {/* Appetite — Geography */}
        <MktEditSection title="Appetite — Geography">
          <MktEditRow label="Active States (blank = nationwide)">
            <input style={inpStyle} value={activeStatesStr} onChange={e => fld('activeStates', e.target.value)} placeholder="e.g. IA, MN, WI" />
          </MktEditRow>
          <MktEditRow label="Ineligible States">
            <input style={inpStyle} value={ineligibleStatesStr} onChange={e => fld('ineligibleStates', e.target.value)} placeholder="e.g. NY, CA" />
          </MktEditRow>
          <MktEditRow label="Target States (scoring signal — in-territory boost)">
            <input style={inpStyle} value={Array.isArray(em.targetStates) ? em.targetStates.join(', ') : (em.targetStates || '')} onChange={e => fld('targetStates', e.target.value)} placeholder="e.g. IA, MN, WI" />
          </MktEditRow>
          <MktEditRow label="Non-Target States (writes here but not preferred)">
            <input style={inpStyle} value={Array.isArray(em.nonTargetStates) ? em.nonTargetStates.join(', ') : (em.nonTargetStates || '')} onChange={e => fld('nonTargetStates', e.target.value)} placeholder="e.g. CA, NY" />
          </MktEditRow>
          <MktEditRow label="Regional Focus">
            <select style={selStyle} value={em.regionalFocus || ''} onChange={e => fld('regionalFocus', e.target.value || null)}>
              <option value="">No regional focus (national)</option>
              <option value="midwest">Midwest</option>
              <option value="northeast">Northeast</option>
              <option value="california">California</option>
              <option value="texas">Texas</option>
            </select>
          </MktEditRow>
          <div style={{ display: 'flex', gap: 20, marginTop: 6, marginBottom: 4 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151' }}>
              <input type="checkbox" checked={!!em.domesticOnly} onChange={e => fld('domesticOnly', e.target.checked)} />
              Domestic Only (no cross-border)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151' }}>
              <input type="checkbox" checked={!!em.crossBorderOnly} onChange={e => fld('crossBorderOnly', e.target.checked)} />
              Cross-Border / Mexico Only
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151' }}>
              <input type="checkbox" checked={!!em.apdMtcOnly} onChange={e => fld('apdMtcOnly', e.target.checked)} />
              APD/MTC Only (no Auto Liability)
            </label>
          </div>
        </MktEditSection>
        {/* Appetite — Commodities */}
        <MktEditSection title="Appetite — Commodities">
          <MktEditRow label="Preferred Commodities">
            <input style={inpStyle} value={Array.isArray(em.preferredCommodities) ? em.preferredCommodities.join(', ') : (em.preferredCommodities || '')} onChange={e => fld('preferredCommodities', e.target.value)} placeholder="e.g. Dry van, Reefer, Flatbed" />
          </MktEditRow>
          <MktEditRow label="Ineligible Commodities">
            <input style={inpStyle} value={Array.isArray(em.ineligibleCommodities) ? em.ineligibleCommodities.join(', ') : (em.ineligibleCommodities || '')} onChange={e => fld('ineligibleCommodities', e.target.value)} placeholder="e.g. Hazmat, Logging, Auto-Haulers" />
          </MktEditRow>
        </MktEditSection>
        {/* Sweet Spot */}
        <MktEditSection title="Sweet Spot (scoring bonus — account fits perfectly here)">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px 16px' }}>
            <MktEditRow label="Sweet Spot Min Units">
              <input style={inpStyle} type="number" min="0" value={em.sweetSpotMinUnits ?? ''} onChange={e => fld('sweetSpotMinUnits', parseInt(e.target.value) || 0)} placeholder="0 = none" />
            </MktEditRow>
            <MktEditRow label="Sweet Spot Max Units">
              <input style={inpStyle} type="number" min="0" value={em.sweetSpotMaxUnits ?? ''} onChange={e => fld('sweetSpotMaxUnits', parseInt(e.target.value) || 0)} placeholder="0 = none" />
            </MktEditRow>
            <MktEditRow label="Sweet Spot Min YIB">
              <input style={inpStyle} type="number" min="0" value={em.sweetSpotMinYIB ?? ''} onChange={e => fld('sweetSpotMinYIB', parseInt(e.target.value) || 0)} placeholder="0 = none" />
            </MktEditRow>
          </div>
        </MktEditSection>
        {/* Driver Requirements */}
        <MktEditSection title="Driver Requirements">
          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>Min Age and Min Experience are <strong>hard filters</strong> — accounts with any driver below these thresholds are automatically ineligible. Max Age is a scoring signal only.</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '6px 16px' }}>
            <MktEditRow label="Min Driver Age (hard filter)">
              <input style={inpStyle} type="number" min="0" value={em.driverMinAge ?? ''} onChange={e => fld('driverMinAge', parseInt(e.target.value) || 0)} placeholder="0 = none" />
            </MktEditRow>
            <MktEditRow label="Max Driver Age (scoring)">
              <input style={inpStyle} type="number" min="0" value={em.driverMaxAge ?? ''} onChange={e => fld('driverMaxAge', parseInt(e.target.value) || 0)} placeholder="0 = none" />
            </MktEditRow>
            <MktEditRow label="Min CDL Experience (hard filter)">
              <input style={inpStyle} type="number" min="0" value={em.driverMinExperienceYears ?? ''} onChange={e => fld('driverMinExperienceYears', parseInt(e.target.value) || 0)} placeholder="0 = none" />
            </MktEditRow>
            <MktEditRow label="Max Vehicle Age (yrs old)">
              <input style={inpStyle} type="number" min="0" value={em.vehicleAgeMax ?? ''} onChange={e => fld('vehicleAgeMax', e.target.value === '' ? null : parseInt(e.target.value))} placeholder="blank = no limit" />
            </MktEditRow>
          </div>
        </MktEditSection>
        {/* Eligibility Rules */}
        <MktEditSection title="Eligibility Rules">
          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>Structured fields only — no rule logic reads Key Notes or Critical Alerts text.</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
            <MktEditRow label="B1 / International Drivers">
              <select style={selStyle} value={em.b1DriversAccepted == null ? 'null' : String(em.b1DriversAccepted)} onChange={e => fld('b1DriversAccepted', e.target.value)}>
                <option value="null">Not evaluated</option>
                <option value="true">Accepted</option>
                <option value="false">Hard decline</option>
              </select>
            </MktEditRow>
            <MktEditRow label="NAFTA / Cross-Border Operations">
              <select style={selStyle} value={em.naftaAccepted == null ? 'null' : String(em.naftaAccepted)} onChange={e => fld('naftaAccepted', e.target.value)}>
                <option value="null">Not evaluated</option>
                <option value="true">Accepted</option>
                <option value="false">Hard decline</option>
              </select>
            </MktEditRow>
            <MktEditRow label="FMCSA Alert Max (blank = not evaluated)">
              <input style={inpStyle} type="number" min="0" value={em.fmcsaAlertMax ?? ''} onChange={e => fld('fmcsaAlertMax', e.target.value === '' ? null : parseInt(e.target.value))} placeholder="e.g. 0, 1, 2, 3" />
            </MktEditRow>
            <MktEditRow label="Loss History Tolerance">
              <select style={selStyle} value={em.lossHistoryTolerance || ''} onChange={e => fld('lossHistoryTolerance', e.target.value || null)}>
                <option value="">Not evaluated</option>
                <option value="clean">Clean only</option>
                <option value="moderate">Moderate (1–2 losses)</option>
                <option value="adverse">Adverse OK</option>
                <option value="any">Any loss history</option>
              </select>
            </MktEditRow>
            <MktEditRow label="Max Operating Radius (miles, blank = none)">
              <input style={inpStyle} type="number" min="0" value={em.radiusMax ?? ''} onChange={e => fld('radiusMax', e.target.value === '' ? null : parseInt(e.target.value))} placeholder="e.g. 300, 500" />
            </MktEditRow>
            <MktEditRow label="DOT Safety Rating Required">
              <select style={selStyle} value={em.dotRatingRequired || ''} onChange={e => fld('dotRatingRequired', e.target.value || null)}>
                <option value="">Not evaluated</option>
                <option value="satisfactory_or_none">Satisfactory or None only</option>
                <option value="any">Any rating accepted</option>
              </select>
            </MktEditRow>
          </div>
        </MktEditSection>
        {/* Telematics */}
        <MktEditSection title="Telematics">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
            <MktEditRow label="Requirement">
              <select style={selStyle} value={em.telematicsRequirement || 'not_required'} onChange={e => fld('telematicsRequirement', e.target.value)}>
                <option value="not_required">Not Required</option>
                <option value="optional_discount">Optional — discount available</option>
                <option value="required_pre_quote">Required before quote</option>
                <option value="required_pre_bind">Required before bind</option>
              </select>
            </MktEditRow>
            <MktEditRow label="Compatible Providers">
              <input style={inpStyle} value={Array.isArray(em.telematicsCompatibleProviders) ? em.telematicsCompatibleProviders.join(', ') : (em.telematicsCompatibleProviders || '')} onChange={e => fld('telematicsCompatibleProviders', e.target.value)} placeholder="e.g. Samsara, KeepTruckin" />
            </MktEditRow>
          </div>
        </MktEditSection>
        {/* Submission & Contacts */}
        <MktEditSection title="Submission & Key Contacts">
          <MktEditRow label="Submission Email">
            <input style={inpStyle} value={em.submissionEmail || ''} onChange={e => fld('submissionEmail', e.target.value)} placeholder="submissions@example.com" />
          </MktEditRow>
          <MktEditRow label="Submission Instructions">
            <textarea style={{ ...inpStyle, resize: 'vertical' }} rows={2} value={em.submissionInstructions || ''} onChange={e => fld('submissionInstructions', e.target.value)} />
          </MktEditRow>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
            <MktEditRow label="Contact Name">
              <input style={inpStyle} value={em.keyContactName || ''} onChange={e => fld('keyContactName', e.target.value)} />
            </MktEditRow>
            <MktEditRow label="Contact Phone">
              <input style={inpStyle} value={em.keyContactPhone || ''} onChange={e => fld('keyContactPhone', e.target.value)} />
            </MktEditRow>
            <MktEditRow label="Contact Email">
              <input style={inpStyle} value={em.keyContactEmail || ''} onChange={e => fld('keyContactEmail', e.target.value)} />
            </MktEditRow>
            <MktEditRow label="Billing Type">
              <select style={selStyle} value={em.billingType || ''} onChange={e => fld('billingType', e.target.value)}>
                <option value="">Not specified</option>
                <option value="direct">Direct bill</option>
                <option value="agency">Agency bill</option>
              </select>
            </MktEditRow>
          </div>
        </MktEditSection>
        {/* Critical Alerts */}
        <MktEditSection title="Critical Alerts">
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>One per line — shown as warning boxes on the market detail card.</div>
          <textarea style={{ ...inpStyle, resize: 'vertical', fontFamily: 'inherit' }} rows={3} value={criticalAlertsStr} onChange={e => fld('criticalAlerts', e.target.value)} placeholder="e.g. NOT for trucking for hire accounts" />
        </MktEditSection>
        {/* Key Notes */}
        <MktEditSection title="Key Notes">
          <textarea style={{ ...inpStyle, resize: 'vertical', fontFamily: 'inherit' }} rows={5} value={em.keyNotes || ''} onChange={e => fld('keyNotes', e.target.value)} placeholder="Full notes, appetite narrative, tips for submitting…" />
        </MktEditSection>
        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24, paddingTop: 16, borderTop: '1px solid #f1f5f9' }}>
          <button onClick={deleteMarket} disabled={saving}
            style={{ fontSize: 12, padding: '6px 14px', borderRadius: 6, border: '1px solid #fecaca', background: '#fef2f2', color: '#991b1b', cursor: 'pointer', fontWeight: 500 }}>
            Delete Market
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {msg && (
              <span style={{ fontSize: 12, color: msg === 'Saved' ? '#15803d' : '#dc2626', fontWeight: 500 }}>
                {msg}
              </span>
            )}
            <button onClick={onClose} style={{ fontSize: 13, padding: '6px 14px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#475569', cursor: 'pointer' }}>
              Cancel
            </button>
            <button
              onClick={saveMarket}
              disabled={saving}
              style={{ fontSize: 13, padding: '6px 16px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600 }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Account Notes Box ────────────────────────────────────────────────────────

function AccountNotesBox({ carrierId, notes, onNotesChange, apiFetch }) {
  const [formText,     setFormText]     = React.useState('');
  const [formCategory, setFormCategory] = React.useState('other');
  const [formIsTodo,   setFormIsTodo]   = React.useState(false);
  const [editingId,    setEditingId]    = React.useState(null);
  const [editText,     setEditText]     = React.useState('');
  const [editCategory, setEditCategory] = React.useState('other');
  const [editIsTodo,   setEditIsTodo]   = React.useState(false);
  const [saving,       setSaving]       = React.useState(false);
  const [filter,       setFilter]       = React.useState('all');

  const callApi = async (action, payload) => {
    try {
      const res = await apiFetch(`${API_BASE}/api/carriers/notes`, {
        method: 'POST',
        body: JSON.stringify({ carrierId, action, ...payload }),
      });
      if (!res.ok) throw new Error('Server error');
      const data = await res.json();
      return data.notes;
    } catch (e) {
      console.error('[notes]', e);
      return null;
    }
  };

  const handleAdd = async () => {
    if (!formText.trim()) return;
    setSaving(true);
    const serverNotes = await callApi('add', {
      note: { text: formText, category: formCategory, isTodo: formIsTodo },
    });
    if (serverNotes) {
      onNotesChange(serverNotes);
    } else {
      const newNote = {
        id:        `note_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        text:      formText.trim(),
        category:  formCategory,
        isTodo:    formIsTodo,
        completed: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      onNotesChange([newNote, ...notes]);
    }
    setFormText(''); setFormIsTodo(false); setFormCategory('other');
    setSaving(false);
  };

  const handleDelete = async (noteId) => {
    const serverNotes = await callApi('delete', { noteId });
    onNotesChange(serverNotes ?? notes.filter(n => n.id !== noteId));
  };

  const handleToggle = async (noteId) => {
    const serverNotes = await callApi('toggle', { noteId });
    onNotesChange(serverNotes ?? notes.map(n => n.id === noteId ? { ...n, completed: !n.completed } : n));
  };

  const startEdit = (note) => {
    setEditingId(note.id);
    setEditText(note.text);
    setEditCategory(note.category || 'other');
    setEditIsTodo(note.isTodo || false);
  };

  const handleEditSave = async () => {
    if (!editText.trim()) return;
    const serverNotes = await callApi('edit', {
      noteId:  editingId,
      updates: { text: editText.trim(), category: editCategory, isTodo: editIsTodo },
    });
    if (serverNotes) {
      onNotesChange(serverNotes);
    } else {
      const now = new Date().toISOString();
      onNotesChange(notes.map(n =>
        n.id === editingId
          ? { ...n, text: editText.trim(), category: editCategory, isTodo: editIsTodo, updatedAt: now }
          : n
      ));
    }
    setEditingId(null);
  };

  const catMeta   = (val) => NOTE_CATEGORIES.find(c => c.value === val) || NOTE_CATEGORIES[5];
  const openCount = notes.filter(n => n.isTodo && !n.completed).length;
  const filtered  = notes.filter(n => {
    if (filter === 'open') return !n.completed;
    if (filter === 'done') return n.completed;
    return true;
  });

  return (
    <div className="card">
      <div className="card-title">
        <span className="section-icon">📝</span>
        Account Notes
        {openCount > 0 && (
          <span className="notes-open-badge">{openCount} open to-do{openCount !== 1 ? 's' : ''}</span>
        )}
      </div>

      {carrierId ? (
        <div className="notes-add-form">
          <textarea
            className="notes-textarea cell-input"
            placeholder="Add a note… (⌘+Enter to save)"
            value={formText}
            onChange={e => setFormText(e.target.value)}
            rows={2}
            onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAdd(); }}
          />
          <div className="notes-form-row">
            <select
              className="notes-cat-select cell-input"
              value={formCategory}
              onChange={e => {
                setFormCategory(e.target.value);
                if (e.target.value === 'todo') setFormIsTodo(true);
              }}
            >
              {NOTE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <label className="notes-todo-label">
              <input
                type="checkbox"
                checked={formIsTodo}
                onChange={e => { setFormIsTodo(e.target.checked); if (e.target.checked) setFormCategory('todo'); }}
              />
              To-do
            </label>
            <button
              className="notes-add-btn"
              onClick={handleAdd}
              disabled={saving || !formText.trim()}
            >
              {saving ? <span className="spinner dark" style={{ width: 10, height: 10, borderWidth: 1.5 }} /> : '＋'} Add Note
            </button>
          </div>
        </div>
      ) : (
        <p className="notes-unsaved-msg">Save the account first to add notes.</p>
      )}

      {notes.length > 0 && (
        <div className="notes-filter-bar">
          {[
            ['all',  `All (${notes.length})`],
            ['open', `Open (${notes.filter(n => !n.completed).length})`],
            ['done', `Done (${notes.filter(n => n.completed).length})`],
          ].map(([key, label]) => (
            <button
              key={key}
              className={`notes-filter-btn${filter === key ? ' active' : ''}`}
              onClick={() => setFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <div className="notes-list">
        {filtered.length === 0 ? (
          <div className="empty-state" style={{ padding: '24px 0', fontSize: 13 }}>
            {notes.length === 0 ? 'No notes yet — add one above.' : 'No notes match this filter.'}
          </div>
        ) : filtered.map(note => {
          const cat       = catMeta(note.category);
          const isEditing = editingId === note.id;
          return (
            <div
              key={note.id}
              className={`notes-item${note.completed ? ' notes-item-done' : ''}${note.isTodo && !note.completed ? ' notes-item-todo' : ''}`}
            >
              <div className="notes-item-header">
                {note.isTodo && (
                  <input
                    type="checkbox"
                    className="notes-checkbox"
                    checked={note.completed}
                    onChange={() => handleToggle(note.id)}
                    title={note.completed ? 'Mark incomplete' : 'Mark complete'}
                  />
                )}
                <span
                  className="notes-cat-badge"
                  style={{ background: cat.bg, color: cat.color, borderColor: cat.border }}
                >
                  {cat.label}
                </span>
                <div style={{ flex: 1 }} />
                <button className="notes-action-btn" onClick={() => startEdit(note)} title="Edit note">✏️</button>
                <button className="notes-action-btn notes-action-del" onClick={() => handleDelete(note.id)} title="Delete note">✕</button>
              </div>

              {isEditing ? (
                <div className="notes-edit-form">
                  <textarea
                    className="notes-textarea cell-input"
                    value={editText}
                    onChange={e => setEditText(e.target.value)}
                    rows={3}
                    autoFocus
                  />
                  <div className="notes-form-row">
                    <select
                      className="notes-cat-select cell-input"
                      value={editCategory}
                      onChange={e => setEditCategory(e.target.value)}
                    >
                      {NOTE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                    <label className="notes-todo-label">
                      <input type="checkbox" checked={editIsTodo} onChange={e => setEditIsTodo(e.target.checked)} />
                      To-do
                    </label>
                    <button className="notes-add-btn" onClick={handleEditSave} disabled={!editText.trim()}>Save</button>
                    <button className="notes-cancel-btn" onClick={() => setEditingId(null)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <p className={`notes-text${note.completed ? ' notes-text-done' : ''}`}>{note.text}</p>
                  <div className="notes-footer">
                    Added {new Date(note.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    {note.updatedAt && note.updatedAt !== note.createdAt && (
                      <span className="notes-edited"> · edited {new Date(note.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────

export default function App() {
  const [authed, setAuthed]             = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [authChecked, setAuthChecked]   = useState(false);
  const [currentUser, setCurrentUser]   = useState('');   // username string
  const [currentRole, setCurrentRole]   = useState('');   // 'admin' | 'platform_owner' | 'agency_admin' | 'marketer' | 'producer' | 'market_editor'
  const [trialEndsAt, setTrialEndsAt]   = useState(null); // ISO string or null
  const [activeTab, setActiveTab]   = useState('Documents');
  const [documents, setDocuments]   = useState({});
  const [equipSub, setEquipSub]     = useState('Tractors');
  const [tractors, setTractors]     = useState([blankRow(TRACTOR_COLS)]);
  const [trailers, setTrailers]     = useState([blankRow(TRAILER_COLS)]);
  const [drivers, setDrivers]       = useState([]);
  const [loading, setLoading]       = useState({});
  const [results, setResults]       = useState({
    risk: '', narrative: '', missing: '', missingEmail: '', playbook: ''
  });
  const [copied, setCopied]         = useState(false);
  const [parseMsg, setParseMsg]     = useState('');
  const [cdlMsg, setCdlMsg]         = useState('');
  const [cdlLoading, setCdlLoading] = useState(false);
  const [companyName, setCompanyName]           = useState('');
  const [dotNumber, setDotNumber]               = useState('');
  const [carrierId, setCarrierId]               = useState(null);
  const [savedMsg, setSavedMsg]                 = useState('');
  const [saveRevision, setSaveRevision]         = useState(0);   // server-confirmed revision; sent as expectedRevision on next save
  const [saveState, setSaveState]               = useState('idle'); // 'idle'|'saving'|'retrying'|'saved'|'unsynced'|'error'|'conflict'
  const [showCarrierList, setShowCarrierList]   = useState(false);
  const [savedCarriers, setSavedCarriers]       = useState([]);
  const [lossSummary, setLossSummary]           = useState(initLossSummary());
  const [cabData, setCabData]                   = useState(null);
  const [cabLoading, setCabLoading]             = useState(false);
  const [cabMsg, setCabMsg]                     = useState('');
  const [lossRunsData, setLossRunsData]         = useState(null);
  const [lossRunsLoading, setLossRunsLoading]   = useState(false);
  const [lossRunsMsg, setLossRunsMsg]           = useState('');
  const [lossRunVerification, setLossRunVerification]   = useState(null);
  const [lossRunVerifyLoading, setLossRunVerifyLoading] = useState(false);
  const [lossRunVerifyMsg, setLossRunVerifyMsg]         = useState('');
  const [trainingCount, setTrainingCount]               = useState(null);
  const [lrFeedbackMode, setLrFeedbackMode]             = useState(''); // '' | 'correcting'
  const [lrCorrectionGrid, setLrCorrectionGrid]         = useState(null);
  const [lrFeedbackMsg, setLrFeedbackMsg]               = useState('');
  // Computed validation warnings — updates whenever loss data changes
  const lossWarnings = useMemo(
    () => normalizeLossData(lossSummary, lossRunVerification, cabData).validationWarnings,
    [lossSummary, lossRunVerification, cabData]  // eslint-disable-line react-hooks/exhaustive-deps
  );
  const [appData, setAppData]                   = useState(null);
  const [appLoading, setAppLoading]             = useState(false);
  const [appMsg, setAppMsg]                     = useState('');
  const [appTemplateCount, setAppTemplateCount] = useState(null);
  const [autoFillModal, setAutoFillModal]       = useState(null); // null | { fields: [...], loading: bool }
  const [autoFillLoading, setAutoFillLoading]   = useState(false);
  const [autoFillMsg, setAutoFillMsg]           = useState('');
  const [coiData, setCoiData]                   = useState(null);
  const [coiLoading, setCoiLoading]             = useState(false);
  const [coiMsg, setCoiMsg]                     = useState('');
  const [iftaData, setIftaData]                 = useState(null);
  const [iftaLoading, setIftaLoading]           = useState(false);
  const [iftaMsg, setIftaMsg]                   = useState('');
  const [markets, setMarkets]                   = useState([]);
  const [showAddMarket, setShowAddMarket]       = useState(false);
  const [editingMkt,    setEditingMkt]          = useState(null);   // market entry being edited
  const [marketFilter, setMarketFilter]         = useState('all');
  // ── Market Intelligence (AI scoring) state ──────────────────────────────────
  const [tliResults,        setTliResults]       = useState([]);   // array of evaluated market results
  const [tliFilter,         setTliFilter]        = useState('all'); // 'all' | 'GREEN' | 'YELLOW' | 'RED'
  const [tliExpanded,       setTliExpanded]      = useState({});   // { marketId: bool }
  const [tliRunning,        setTliRunning]       = useState(false);
  const [tliLastKey,        setTliLastKey]       = useState('');   // account key at time of last run
  const [tliOutdated,       setTliOutdated]      = useState(false); // true when account changed since last run
  const [tliAiError,        setTliAiError]       = useState('');
  const [tliAnalyzedAt,     setTliAnalyzedAt]    = useState(null); // ISO timestamp of last analysis
  const [tliAccountProfile, setTliAccountProfile] = useState(null); // normalized account used for last run
  const [tliGroupedMarkets, setTliGroupedMarkets] = useState([]);   // grouped results: carrier + programs + accessRoutes
  // ── Market Finder / Directory state ─────────────────────────────────────────
  const [mktMainView,           setMktMainView]           = useState('finder');  // 'finder' | 'directory' | 'tracker'
  const [allMktMarkets,         setAllMktMarkets]         = useState([]);
  const [mktDetailMarket,       setMktDetailMarket]       = useState(null);
  const [mktDetailDocUploading, setMktDetailDocUploading] = useState(false);
  const [mktDetailDocError,     setMktDetailDocError]     = useState('');
  const [mktDirSearch,          setMktDirSearch]          = useState('');
  const [mktDirFilters,         setMktDirFilters]         = useState({ programType: '', state: '' });
  const [mktDirEditMarket,      setMktDirEditMarket]      = useState(null);  // market being edited in modal
  const [mktRecommendLoading,   setMktRecommendLoading]   = useState(false);
  const [mktRecommendError,     setMktRecommendError]     = useState('');
  const [mktRecommendResults,   setMktRecommendResults]   = useState(null);
  const [aiScores,              setAiScores]              = useState({});  // { [marketId]: { loading, result, error } }
  const [mktFinderProfile,      setMktFinderProfile]      = useState({
    powerUnits: '', yearsInBusiness: '', isNewVenture: false,
    domicileState: '', operatingStates: '',
    commodities: '', driverAges: '', driverExperienceYears: '',
    fmcsaAlertCount: '0', hasTelematics: 'unsure', telematicsProvider: '',
    hasB1OrInternationalDrivers: false, isNaftaFocused: false,
  });
  const [mktFinderAutoFilled,   setMktFinderAutoFilled]   = useState(false);
  // Ref stores the values as they were last auto-filled so we can detect manual edits
  const mktFinderLastAutoFillRef = useRef({});
  const [commodities, setCommodities]           = useState('');
  const [leadSheet,   setLeadSheet]             = useState(initLeadSheet());
  const [portals, setPortals]                   = useState([]);
  const [portalModal, setPortalModal]           = useState(null); // { url, id }
  const [portalLinkCopied, setPortalLinkCopied] = useState(false);
  const [submissionBanner, setSubmissionBanner] = useState(null); // { carrierName, submittedAt, portalId }
  const [portalGenerating, setPortalGenerating] = useState(false);
  const [overrides, setOverrides]               = useState({ units: null, drivers: null, mileage: null });
  const [conflictQueue, setConflictQueue]       = useState([]);
  const [carrierShared, setCarrierShared]       = useState(false);
  const [carrierOwner, setCarrierOwner]         = useState('');
  const [carrierSharedWith, setCarrierSharedWith] = useState([]);
  const [showShareDropdown, setShowShareDropdown] = useState(false);
  const [shareSearch, setShareSearch]           = useState('');
  const [availableUsers, setAvailableUsers]     = useState([]);
  const [shareMsg, setShareMsg]                 = useState('');
  const [carrierStatus, setCarrierStatus]       = useState('prospect');
  const [pipelineData, setPipelineData]         = useState({});
  const [pipelineTab, setPipelineTab]           = useState('active');
  const [savedSearch, setSavedSearch]           = useState('');
  const [savedViewMode, setSavedViewMode]       = useState('mine');
  const [allCarriers, setAllCarriers]           = useState([]);
  const [allCarriersLoading, setAllCarriersLoading] = useState(false);
  const [allCarriersError, setAllCarriersError] = useState('');
  const [allProducerFilter, setAllProducerFilter] = useState('');
  const [userDisplayNames, setUserDisplayNames] = useState({});
  const [showLostModal, setShowLostModal]       = useState(false);
  const [showBoundModal, setShowBoundModal]     = useState(false);
  const [lostForm, setLostForm]                 = useState({ reason: '', market: '', premium: '', notes: '' });
  const [boundForm, setBoundForm]               = useState({ market: '', effectiveDate: '', expDate: '', premium: '' });
  const [accountNotes, setAccountNotes]         = useState([]);
  const [narrativePhase, setNarrativePhase]     = useState(''); // '' | 'generating' | 'refining'
  const saveCarrierRef    = useRef(null);  // always points to latest saveCarrier — used by uploadFileToCloud auto-save
  const carrierDataOwnerRef = useRef(''); // username whose Redis key holds the currently loaded carrier (for admin routing)
  const saveInFlightRef   = useRef(false); // true while a save request is in-flight
  const savePendingRef    = useRef(null);  // { silent: bool } if a save was queued while in-flight
  const accountVersionRef = useRef(0);     // increments on account switch/load; in-flight saves discard their result if stale
  // saveRevisionRef mirrors saveRevision state synchronously. Queued saves
  // (fired via setTimeout from the mutex finally block) must use the latest
  // confirmed revision even before React has re-rendered with the new state.
  const saveRevisionRef   = useRef(0);
  // Auto-save: recentlyLoadedRef suppresses the debounced save that would
  // otherwise fire when applyData populates all state at once on account open.
  const recentlyLoadedRef = useRef(false);
  const autoSaveTimerRef  = useRef(null);
  // Stable carrier ID pre-allocated in newCarrier() so every autosave request
  // for a new account converges on the same Redis key. Reset to null when an
  // existing account is loaded (pendingCarrierIdRef is only for new accounts).
  const pendingCarrierIdRef = useRef(null);

  // ── Admin user management state ──
  const [adminUsers, setAdminUsers]             = useState(null); // null = not loaded
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  const [adminUserForm, setAdminUserForm]       = useState({ username: '', name: '', email: '', password: '', role: 'producer' });
  const [adminUserMsg, setAdminUserMsg]         = useState('');
  const [inviteRole, setInviteRole]             = useState('producer');
  const [inviteLink, setInviteLink]             = useState('');
  const [inviteLoading, setInviteLoading]       = useState(false);
  const [inviteCopied, setInviteCopied]         = useState(false);

  // ── Agency admin invite management state ──
  const [agencyInvites, setAgencyInvites]               = useState(null);
  const [agencyInvitesLoading, setAgencyInvitesLoading] = useState(false);
  const [agencyInviteRole, setAgencyInviteRole]         = useState('producer');
  const [agencyInviteLink, setAgencyInviteLink]         = useState('');
  const [agencyInviteCreating, setAgencyInviteCreating] = useState(false);
  const [agencyInviteCopied, setAgencyInviteCopied]     = useState(false);
  const [agencyInviteMsg, setAgencyInviteMsg]           = useState('');
  const [agencyInviteRevoking, setAgencyInviteRevoking] = useState(null);

  // ── Platform console state (platform_owner only) ──
  const [platformView, setPlatformView]             = useState('orgs'); // 'orgs'|'org-detail'|'create-org'|'audit'
  const [platformOrgs, setPlatformOrgs]             = useState(null);
  const [platformOrgsLoading, setPlatformOrgsLoading] = useState(false);
  const [platformOrgsError, setPlatformOrgsError]   = useState('');
  const [platformSelectedOrg, setPlatformSelectedOrg] = useState(null);
  const [platformOrgDetail, setPlatformOrgDetail]   = useState(null); // { org, users, carriers }
  const [platformOrgDetailLoading, setPlatformOrgDetailLoading] = useState(false);
  const [platformOrgDetailTab, setPlatformOrgDetailTab] = useState('overview');
  const [platformCarrier, setPlatformCarrier]       = useState(null);
  const [platformCarrierLoading, setPlatformCarrierLoading] = useState(false);
  const [platformCreateOrgForm, setPlatformCreateOrgForm] = useState({ name: '', slug: '', seatLimit: 25, status: 'active', subscriptionStatus: 'manual' });
  const [platformCreateOrgMsg, setPlatformCreateOrgMsg] = useState('');
  const [platformAuditLog, setPlatformAuditLog]     = useState(null);
  const [platformAuditLoading, setPlatformAuditLoading] = useState(false);
  const [platformStatusMsg, setPlatformStatusMsg]   = useState('');
  const [platformEditingSettings, setPlatformEditingSettings] = useState(false);
  const [platformSettingsForm, setPlatformSettingsForm]       = useState({});
  const [platformSettingsSaving, setPlatformSettingsSaving]   = useState(false);
  const [platformSettingsMsg, setPlatformSettingsMsg]         = useState('');
  const [platformOrgInvites, setPlatformOrgInvites]           = useState(null);
  const [platformOrgInvitesLoading, setPlatformOrgInvitesLoading] = useState(false);
  const [platformInviteRevoking, setPlatformInviteRevoking]   = useState(null);
  const [platformInviteMsg, setPlatformInviteMsg]             = useState('');
  const [platformCreateInviteOpen, setPlatformCreateInviteOpen]           = useState(false);
  const [platformCreateInviteRole, setPlatformCreateInviteRole]           = useState('producer');
  const [platformCreateInviteLoading, setPlatformCreateInviteLoading]     = useState(false);
  const [platformCreatedInviteUrl, setPlatformCreatedInviteUrl]           = useState('');
  const [platformCreateInviteUrlCopied, setPlatformCreateInviteUrlCopied] = useState(false);

  // ── Platform Console user management state (platform_owner) ──
  const [platformUserActionLoading, setPlatformUserActionLoading] = useState(null); // userId
  const [platformUserMsg, setPlatformUserMsg]                     = useState('');
  const [platformUserEditingId, setPlatformUserEditingId]         = useState(null); // userId being role-edited
  const [platformUserRoleNew, setPlatformUserRoleNew]             = useState({});   // { [userId]: newRole }

  // ── Agency Team Management state (agency_admin) ──
  const [teamTab, setTeamTab]                     = useState('active'); // 'active'|'disabled'|'invites'|'activity'
  const [teamUsers, setTeamUsers]                 = useState(null);
  const [teamUsersLoading, setTeamUsersLoading]   = useState(false);
  const [teamSeatUsage, setTeamSeatUsage]         = useState(null);
  const [teamMsg, setTeamMsg]                     = useState('');
  const [teamActionLoading, setTeamActionLoading] = useState(null); // userId
  const [teamRoleEditingId, setTeamRoleEditingId] = useState(null); // userId being role-edited
  const [teamRoleNew, setTeamRoleNew]             = useState({});   // { [userId]: newRole }
  const [teamAuditLog, setTeamAuditLog]           = useState(null);
  const [teamAuditLoading, setTeamAuditLoading]   = useState(false);
  const [offboardUser, setOffboardUser]           = useState(null); // user being offboarded (disable flow)
  const [offboardToUserId, setOffboardToUserId]   = useState('');
  const [offboardLoading, setOffboardLoading]     = useState(false);

  const fileRefs = useRef({});
  DOC_TYPES.forEach(d => { if (!fileRefs.current[d.id]) fileRefs.current[d.id] = React.createRef(); });

  // ── Lead Sheet helpers ──
  const updateLS       = (field, value)                => setLeadSheet(prev => ({ ...prev, [field]: value }));
  const updateLSNested = (section, field, value)       => setLeadSheet(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));

  // ── Conflict detection ──
  const checkConflicts = useCallback(() => {
    if (!cabData?.fleet) return;
    const newConflicts = [];

    const tractorCount = tractors.filter(r => r.year || r.make || r.vin).length;
    const cabUnits = parseInt(cabData.fleet.totalPowerUnits) || 0;
    if (cabUnits > 0 && tractorCount > 0 && cabUnits !== tractorCount && !overrides.units) {
      newConflicts.push({
        key: 'units', field: 'Power Units',
        cabValue: String(cabUnits),
        currentValue: String(tractorCount),
        currentLabel: 'Equipment List',
      });
    }

    const driverCount = drivers.filter(r => r.name || r.dob).length;
    const cabDrivers = parseInt(cabData.fleet.drivers) || 0;
    if (cabDrivers > 0 && driverCount > 0 && cabDrivers !== driverCount && !overrides.drivers) {
      newConflicts.push({
        key: 'drivers', field: 'Number of Drivers',
        cabValue: String(cabDrivers),
        currentValue: String(driverCount),
        currentLabel: 'Driver List',
      });
    }

    if (iftaData?.length > 0 && cabData.fleet.annualMileage) {
      const iftaTotal = iftaData.reduce((sum, q) => {
        const m = parseFloat(String(q.totalMiles || '').replace(/,/g, '')) || 0;
        return sum + m;
      }, 0);
      const cabMileage = parseFloat(String(cabData.fleet.annualMileage).replace(/,/g, '')) || 0;
      if (iftaTotal > 0 && cabMileage > 0 && Math.abs(iftaTotal - cabMileage) / cabMileage > 0.1 && !overrides.mileage) {
        newConflicts.push({
          key: 'mileage', field: 'Annual Mileage',
          cabValue: Math.round(cabMileage).toLocaleString(),
          currentValue: Math.round(iftaTotal).toLocaleString(),
          currentLabel: 'IFTA Reports',
        });
      }
    }

    if (newConflicts.length > 0) {
      setConflictQueue(prev => {
        const existing = new Set(prev.map(c => c.key));
        return [...prev, ...newConflicts.filter(c => !existing.has(c.key))];
      });
    }
  }, [cabData, tractors, drivers, iftaData, overrides]);

  const resolveConflict = (choice) => {
    const conflict = conflictQueue[0];
    if (!conflict) return;
    if (choice !== 'later') {
      const value  = choice === 'cab' ? conflict.cabValue : conflict.currentValue;
      const source = choice === 'cab' ? 'cab' : conflict.currentLabel.toLowerCase().replace(/\s+/g, '_');
      setOverrides(prev => ({ ...prev, [conflict.key]: { value, source, confirmed: true } }));
    }
    setConflictQueue(prev => prev.slice(1));
  };

  const autoFillLeadSheet = () => {
    const updates = {};

    // ── PRIORITY 4: CAB Report (baseline — lowest auto-fill priority) ──────────
    if (cabData) {
      const c   = cabData.company   || {};
      const f   = cabData.fleet     || {};
      const ins = cabData.insurance || {};

      const firstContact = Array.isArray(c.contacts) && c.contacts.length > 0
        ? c.contacts[0] : (c.contact || '');
      if (firstContact) updates.ownerName = firstContact;
      if (c.phone)   updates.phone           = c.phone;
      if (c.address) updates.garagingAddress = c.address;

      const insurer = ins.carrier || ins.company || '';
      if (insurer) updates.currentInsurer = insurer;
      if (ins.cancellationDate) updates.currentXDate = ins.cancellationDate;

      const parsePct = (v) => parseFloat(String(v).replace(/%/g, '').trim()) || 0;
      const radDist = (f.radiusDistribution || []).filter(r => r.range && parsePct(r.pct) > 0);
      if (radDist.length > 0) {
        const sorted = [...radDist].sort((a, b) => parsePct(b.pct) - parsePct(a.pct));
        updates.radiusOfOperation = sorted.map(r => `${Math.round(parsePct(r.pct))}% ${r.range}`).join(' | ');
      } else {
        const opType = f.carrierOperation || f.operationClass || '';
        updates.radiusOfOperation = opType || 'Interstate';
      }

      if (c.firstAssigned) {
        const assigned = new Date(c.firstAssigned);
        const yrs = new Date().getFullYear() - assigned.getFullYear();
        if (!isNaN(yrs) && yrs >= 0) updates.yearsInBusiness = String(yrs);
      }

      const alertCount = Object.values(cabData.basics || {}).filter(b => b?.alert).length;
      updates.basicAlerts = String(alertCount);

      const stateFromField = cabData.state || c.state || '';
      const stateFromAddr  = (c.address || '').match(/,\s*([A-Z]{2}),?\s*\d{5}/)?.[1] || '';
      const resolvedState  = stateFromField || stateFromAddr;
      console.log('[autoFill] cabData.state:', cabData.state, '| c.state:', c.state, '| c.address:', c.address, '| stateFromAddr:', stateFromAddr, '| resolved:', resolvedState);
      console.log('[autoFill] c.contacts:', JSON.stringify(c.contacts), '| c.phone:', c.phone, '| c.firstAssigned:', c.firstAssigned);
      if (resolvedState) updates.state = resolvedState;

      const oo = parseInt(f.ownerOperators) || 0;
      const cd = parseInt(f.companyDrivers)  || 0;
      if (oo > 0 && cd === 0) updates.ownerOpsOrCompany = 'owner-ops';
      else if (cd > 0 && oo === 0) updates.ownerOpsOrCompany = 'company';
      else if (oo > 0 && cd > 0)  updates.ownerOpsOrCompany = 'both';
    }

    // ── PRIORITY 3: COI — overrides CAB for policy dates ──────────────────────
    if (coiData) {
      const xdate = coiData.expirationDate || coiData.effectiveTo || coiData.expiration || '';
      if (xdate) updates.currentXDate = xdate;
    }

    // ── PRIORITY 1: Application & Loss Summary — overrides everything ──────────
    // APP data always wins. CAB/COI values above are overwritten here if APP has the field.
    if (appData) {
      const c = appData.company    || {};
      const o = appData.operations || {};
      const u = appData.underwriting || {};
      if (c.fein)         updates.fein            = c.fein;
      if (c.ownerName)    updates.ownerName        = c.ownerName;
      if (c.ownerDob)     updates.ownerDob         = c.ownerDob;
      if (c.phone)        updates.phone            = c.phone;
      if (c.address)      updates.garagingAddress  = c.address;
      if (o.yearsInBusiness > 0) updates.yearsInBusiness = String(o.yearsInBusiness);
      if (o.radiusOfOperation)   updates.radiusOfOperation = o.radiusOfOperation;
      if (o.commodities)         setCommodities(o.commodities);
      if (o.eldBrand)    { updates.eldsInstalled = 'yes'; updates.eldBrand = o.eldBrand; }
      if (o.dashcamBrand){ updates.dashcams = 'yes'; updates.dashcamBrand = o.dashcamBrand; }
      const oo = parseInt(o.ownerOps)       || 0;
      const cd = parseInt(o.companyDrivers) || 0;
      if (oo > 0 && cd === 0) updates.ownerOpsOrCompany = 'owner-ops';
      else if (cd > 0 && oo === 0) updates.ownerOpsOrCompany = 'company';
      else if (oo > 0 && cd > 0)  updates.ownerOpsOrCompany = 'both';
      if (u.beingNonRenewed != null)   updates.beingNonRenewed   = u.beingNonRenewed   ? 'yes' : 'no';
      if (u.nonRenewalReason)          updates.nonRenewalReason  = u.nonRenewalReason;
      if (u.planningAddTrucks != null) updates.planningAddTrucks = u.planningAddTrucks ? 'yes' : 'no';
      if (u.addTrucksCount > 0)        updates.addTrucksCount    = String(u.addTrucksCount);
      if (u.driversOutOfState != null) updates.driversOutOfState = u.driversOutOfState ? 'yes' : 'no';
      if (u.fleetChangeLastYear != null) updates.fleetChangeLastYear = u.fleetChangeLastYear ? 'yes' : 'no';
      // Commodity checkboxes — merge APP flags into existing checkbox state
      const cb = appData.commodityCheckboxes || {};
      const hasCbData = ['flatBed', 'dryVan', 'reefer', 'autoHauler'].some(k => cb[k] && Object.values(cb[k]).some(Boolean));
      if (hasCbData) {
        for (const group of ['flatBed', 'dryVan', 'reefer']) {
          if (cb[group]) updates[group] = { ...leadSheet[group], ...cb[group] };
        }
        if (cb.autoHauler) updates.autoHauler = { ...leadSheet.autoHauler, ...cb.autoHauler };
      }
      console.log('[autoFill] APP overrides applied:', Object.keys(updates).filter(k => c[k] || o[k] || u[k]));
    }

    setLeadSheet(prev => ({ ...prev, ...updates }));
  };

  // Auto-fill Lead Sheet whenever cabData or coiData changes after a successful read
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (cabData || coiData || appData) autoFillLeadSheet(); }, [cabData, coiData, appData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load training counts once on mount (passive — no spinner needed)
  useEffect(() => {
    fetchLrTrainingExamples();
    fetchAppTemplates();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load all markets from knowledge base API on mount
  useEffect(() => {
    apiFetch(`${API_BASE}/api/markets`)
      .then(r => r.json())
      .then(data => { if (data.markets) setAllMktMarkets(data.markets); })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-run AI Market Intelligence when the user opens the Markets/Intelligence tab
  // and no results exist yet for the loaded account.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab !== 'Markets') return;
    if (mktMainView !== 'intelligence') return;
    if (tliRunning) return;
    if (tliResults.length > 0) return; // already have results (possibly cached)
    const state = (leadSheet?.state || '').trim();
    if (!state) return; // no account loaded — wait
    runTliAnalysis(false);
  }, [activeTab, mktMainView, tliResults.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fill Market Finder profile from loaded lead sheet / account data
  // Fires when Markets tab opens OR when Market Finder sub-tab is switched to.
  // Only fills fields that are blank or still match the last auto-filled value.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab !== 'Markets') return;
    if (mktMainView !== 'finder') return;
    // Build candidate values from lead sheet + tractors + drivers
    const ls = leadSheet || {};
    const units = (tractors || []).filter(t => t && (t.vin || t.year || t.make)).length
      || parseInt(ls.powerUnits || ls.units || '0') || 0;
    const yib = ls.yearsInBusiness || ls.yib || '';
    const state = ls.state || ls.domicileState || ls.garagingState || '';
    const opStates = Array.isArray(ls.operatingStates || ls.states)
      ? (ls.operatingStates || ls.states).join(' ')
      : (ls.operatingStates || ls.states || '');
    const comms = ls.commodities || ls.commodity || commodities || '';
    const teleSource = ls.telematics ?? ls.eldsInstalled ?? ls.hasTelematics;
    const hasTele = teleSource == null || teleSource === ''
      ? 'unsure'
      : /yes|true|1/i.test(String(teleSource)) ? 'yes' : 'no';
    const hasB1 = !!(ls.b1Drivers || ls.internationalDrivers || ls.hasB1OrInternationalDrivers);
    const hasNafta = !!(ls.nafta || ls.naftaOperations || ls.isNaftaFocused);
    const driverAges = (drivers || []).map(d => d.age || d.dateOfBirth || '').filter(Boolean).join(', ');
    const driverExpNums = (drivers || []).map(d => parseFloat(d.yearsExperience || d.experience || d.cdlYears) || 0).filter(n => n > 0);
    const driverExp     = driverExpNums.length ? String(Math.min(...driverExpNums)) : '';
    const alerts = ls.fmcsaAlerts || ls.cabAlerts || ls.fmcsaAlertCount || '0';

    // Nothing useful to fill? Skip.
    if (!units && !state && !yib && !comms) return;

    const candidate = {
      powerUnits:                 units > 0 ? String(units) : '',
      yearsInBusiness:            String(yib),
      domicileState:              String(state).toUpperCase(),
      operatingStates:            opStates,
      commodities:                comms,
      hasTelematics:              hasTele,
      hasB1OrInternationalDrivers: hasB1,
      isNaftaFocused:             hasNafta,
      driverAges,
      driverExperienceYears:      driverExp,
      fmcsaAlertCount:            String(alerts || '0'),
    };

    const last = mktFinderLastAutoFillRef.current;
    setMktFinderProfile(prev => {
      const next = { ...prev };
      let changed = false;
      Object.entries(candidate).forEach(([k, v]) => {
        const prevVal = prev[k];
        const lastVal = last[k];
        // Fill if blank, false/0, or still at the last auto-filled value
        const isEmpty = prevVal === '' || prevVal === false || prevVal === '0' || prevVal === 0;
        const unchanged = prevVal === lastVal;
        if (isEmpty || unchanged) {
          if (next[k] !== v) { next[k] = v; changed = true; }
        }
      });
      return changed ? next : prev;
    });
    mktFinderLastAutoFillRef.current = candidate;
    setMktFinderAutoFilled(true);
  }, [activeTab, mktMainView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Detect account changes after a TLI run and show "outdated" note
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!tliLastKey) return; // no run yet, nothing to compare
    const currentKey = `${leadSheet?.state}|${leadSheet?.yearsInBusiness}|${leadSheet?.eldsInstalled}|${leadSheet?.eldBrand}|${(tractors||[]).length}|${commodities}`;
    if (currentKey !== tliLastKey) setTliOutdated(true);
  }, [leadSheet?.state, leadSheet?.yearsInBusiness, leadSheet?.eldsInstalled, leadSheet?.eldBrand, tractors?.length, commodities]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist AI results to Redis immediately when any output changes.
  // Must run in a useEffect (not inline after callAI) because setResults schedules a
  // re-render — the closure inside generateX still has the OLD results value.
  // By the time this effect fires, React has committed the new results state, so
  // saveCarrier reads the correct text.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const hasContent = results.risk || results.narrative || results.missing || results.playbook;
    if (hasContent && carrierId) saveCarrier(true);
  }, [results]);

  // ── Debounced auto-save ──────────────────────────────────────────────────────
  // Fires 1.5 s after any account field changes. recentlyLoadedRef suppresses
  // the spurious trigger that happens when applyData populates all state at once.
  // AI results and file uploads have their own immediate saves above; this effect
  // is the catch-all for every other field edit (lead sheet, markets, notes, etc).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // For new accounts (carrierId not yet set) only schedule an autosave once
    // the user has entered at least one meaningful field, preventing a blank
    // form from creating an empty Redis record.
    if (!carrierId) {
      const hasContent = !!(companyName.trim() || dotNumber.trim() ||
        drivers.some(d => d.name?.trim()) ||
        tractors.some(t => t.year?.trim() || t.make?.trim() || t.vin?.trim()) ||
        Object.values(documents).some(files => files.length > 0));
      if (!hasContent) return;
    }
    if (recentlyLoadedRef.current) return;
    clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => saveCarrierRef.current?.(true), 1500);
    return () => clearTimeout(autoSaveTimerRef.current);
  }, [
    carrierId,
    companyName, dotNumber,
    tractors, trailers, drivers,
    documents,
    lossSummary, cabData, lossRunsData, lossRunVerification,
    appData, coiData, iftaData,
    markets, commodities,
    leadSheet, overrides,
    carrierStatus, accountNotes,
  ]);

  const copyLeadSheetToClipboard = () => {
    const ls = leadSheet;
    const yn = v => v === 'yes' ? 'Yes' : v === 'no' ? 'No' : '—';
    const bool = v => v ? 'Yes' : 'No';
    const checkedItems = (group) =>
      COMMODITY_GROUPS.find(g => g.key === group)?.items
        .filter(item => ls[group]?.[item.key])
        .map(item => item.label).join(', ') || 'None';

    const lines = [
      `LEAD SHEET — ${companyName || 'Unknown Carrier'}`,
      `DOT #: ${dotNumber || '—'}`,
      `Generated: ${new Date().toLocaleDateString()}`,
      '',
      '=== COMPANY INFORMATION ===',
      `Company Name:            ${companyName || '—'}`,
      `DOT Number:              ${dotNumber || '—'}`,
      `FEIN:                    ${ls.fein || '—'}`,
      `Years in Business:       ${ls.yearsInBusiness || '—'}`,
      `State:                   ${ls.state || '—'}`,
      `Current Insurer:         ${ls.currentInsurer || '—'}`,
      `Owner's Name:            ${ls.ownerName || '—'}`,
      `Owner's DOB:             ${ls.ownerDob || '—'}`,
      `Garaging Address:        ${ls.garagingAddress || '—'}`,
      `Phone:                   ${ls.phone || '—'}`,
      `Current X-Date:          ${ls.currentXDate || '—'}`,
      '',
      '=== OPERATIONS ===',
      `Commodities Hauled:      ${commodities || '—'}`,
      `Radius of Operation:     ${ls.radiusOfOperation || '—'}`,
      `20%+ Fleet Change:       ${yn(ls.fleetChangeLastYear)}`,
      `Adding Trucks:           ${yn(ls.planningAddTrucks)}${ls.planningAddTrucks === 'yes' ? ` — ${ls.addTrucksCount || '?'} trucks` : ''}`,
      `Non-renewed/Canceled:    ${yn(ls.beingNonRenewed)}${ls.beingNonRenewed === 'yes' ? ` — ${ls.nonRenewalReason || '?'}` : ''}`,
      `Owner-Ops vs Company:    ${ls.ownerOpsOrCompany || '—'}`,
      `Drivers Out of State:    ${yn(ls.driversOutOfState)}${ls.driversOutOfState === 'yes' ? ` — ${ls.driversOutOfStateExplain || '?'}` : ''}`,
      '',
      '=== FLEET ===',
      `Number of Trucks:        ${tractors.filter(t => t.year || t.make).length || '—'}`,
      `Number of Trailers:      ${trailers.filter(t => t.year || t.make).length || '—'}`,
      `Number of Drivers:       ${drivers.filter(d => d.name).length || '—'}`,
      `ELDs Installed:          ${yn(ls.eldsInstalled)}${ls.eldsInstalled === 'yes' ? ` — ${ls.eldBrand || '?'}` : ''}`,
      `Dashcams:                ${yn(ls.dashcams)}${ls.dashcams === 'yes' ? ` — ${ls.dashcamBrand || '?'}` : ''}`,
      '',
      '=== SAFETY ===',
      `BASIC Alerts:            ${ls.basicAlerts || '—'}`,
      `Corrective Action Plan:  ${ls.correctiveActionPlan || '—'}`,
      `Target/Expiring Pricing: ${ls.targetPricing || '—'}`,
      '',
      '=== COMMODITIES ===',
      `Flat Bed:             ${checkedItems('flatBed')}`,
      `Dry Van / Box Truck:  ${checkedItems('dryVan')}`,
      `Reefer:               ${checkedItems('reefer')}`,
      `Auto Hauler:          ${checkedItems('autoHauler')}${ls.autoHauler?.avgCostPerVehicle ? ` — Avg $${ls.autoHauler.avgCostPerVehicle}/vehicle` : ''}`,
      '',
      '=== LOAD AGREEMENTS ===',
      ls.loadAgreements || '—',
      '',
      ...(ls.additionalNotes ? ['=== ADDITIONAL NOTES ===', ls.additionalNotes] : []),
    ];
    navigator.clipboard.writeText(lines.join('\n'));
  };

  // ── Auth: verify stored token on mount ──

  // Helper: fetch with Authorization header automatically attached
  const apiFetch = (url, opts = {}) => {
    const token   = localStorage.getItem('tsp_auth') || '';
    const headers = { 'Content-Type': 'application/json', ...(opts.headers || {}) };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return fetch(url, { ...opts, headers }).then(r => {
      if (r.status === 401) window.dispatchEvent(new Event('tsp:unauthorized'));
      return r;
    });
  };

  useEffect(() => {
    const token = localStorage.getItem('tsp_auth');
    if (!token) { setAuthChecked(true); return; }
    // Restore user info from localStorage immediately (no round-trip needed)
    try {
      const stored = JSON.parse(localStorage.getItem('tsp_user') || '{}');
      if (stored.username) {
        setCurrentUser(stored.username);
        setCurrentRole(stored.role || '');
        if (stored.trialEndsAt) setTrialEndsAt(stored.trialEndsAt);
      }
    } catch {}
    fetch(`${API_BASE}/api/auth`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then(r => r.json())
      .then(d => {
        if (d.valid) {
          setAuthed(true);
          setLegalAccepted(!!d.legalAccepted);
          if (d.username) { setCurrentUser(d.username); setCurrentRole(d.role || ''); }
          if (d.trialEndsAt) setTrialEndsAt(d.trialEndsAt);
        }
      })
      .catch(() => {})
      .finally(() => setAuthChecked(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Global 401 handler — fires when any apiFetch gets a 401 back
  useEffect(() => {
    const handler = () => {
      localStorage.removeItem('tsp_auth');
      localStorage.removeItem('tsp_user');
      setAuthed(false);
      setLegalAccepted(false);
      setCurrentUser('');
      setCurrentRole('');
      setTrialEndsAt(null);
    };
    window.addEventListener('tsp:unauthorized', handler);
    return () => window.removeEventListener('tsp:unauthorized', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = () => {
    localStorage.removeItem('tsp_auth');
    localStorage.removeItem('tsp_user');
    localStorage.removeItem('tsp_index');
    setAuthed(false);
    setLegalAccepted(false);
    setCurrentUser('');
    setCurrentRole('');
    setTrialEndsAt(null);
    setSavedCarriers([]);
  };

  // ── Helper: apply a loaded carrier data object to state ──
  const applyCarrierData = (data, id) => {
    setCompanyName(data.companyName || '');
    setDotNumber(data.dotNumber || '');
    setTractors(data.tractors?.length ? data.tractors : [blankRow(TRACTOR_COLS)]);
    setTrailers(data.trailers?.length ? data.trailers : [blankRow(TRAILER_COLS)]);
    setDrivers(data.drivers?.length ? data.drivers : []);
    const loadedDocs = {};
    for (const [docId, names] of Object.entries(data.documents || {})) {
      if (names.length) loadedDocs[docId] = names.map(f =>
        typeof f === 'string' ? { name: f, url: null } : f?.id ? { id: f.id, name: f.name || '' } : { name: f.name || '', url: f.url || null }
      );
    }
    setDocuments(loadedDocs);
    setResults({ risk: '', narrative: '', missing: '', missingEmail: '', playbook: '', ...(data.results || {}) });
    if (data.lossSummary) setLossSummary(data.lossSummary);
    if (data.cabData) {
      // Shallow-clone before sanitizing so we do not mutate the loaded object.
      // sanitizeCabData normalizes array fields that the AI or old records may
      // have stored as wrong types (e.g. alerts as a string). This cannot cause
      // an accidental Redis overwrite — recentlyLoadedRef suppresses autosave.
      const cabCopy = { ...data.cabData };
      validateCabSchema(cabCopy, 'Redis load (applyCarrierData)');
      setCabData(sanitizeCabData(cabCopy));
    }
    if (data.lossRunsData) setLossRunsData(data.lossRunsData);
    if (data.appData) setAppData(data.appData);
    if (data.coiData) setCoiData(data.coiData);
    if (data.iftaData) setIftaData(data.iftaData);
    setMarkets(data.markets || []);
    if (data.commodities) setCommodities(data.commodities);
    if (data.leadSheet) setLeadSheet({ ...initLeadSheet(), ...data.leadSheet });
    if (data.overrides) setOverrides(data.overrides);
    setCarrierStatus(data.status || 'prospect');
    setPipelineData(data.pipelineData || {});
    setAccountNotes(Array.isArray(data.accountNotes) ? data.accountNotes : []);
    setCarrierShared(!!(data.isShared));
    setCarrierOwner(data.owner || '');
    setCarrierSharedWith(data.sharedWith || []);
    setCarrierId(id);
    pendingCarrierIdRef.current = null; // not a new account
    // Revision must be initialized before any save fires for this account
    accountVersionRef.current += 1;
    const rev = typeof data.revision === 'number' ? data.revision : 0;
    saveRevisionRef.current = rev; // synchronous — queued saves read this before React re-renders
    setSaveRevision(rev);
    setSaveState('idle');
    setSavedMsg('');
    // Reconciliation safety: if a previous session failed to cloud-save this carrier,
    // surface the unsynced warning so the user is not silently in a local-only state.
    try {
      const unsyncedMarker = localStorage.getItem(`tsp_unsynced_${id}`);
      if (unsyncedMarker) {
        setSaveState('unsynced');
        setSavedMsg('⚠ Unsynced changes from a previous session were found — not yet saved to cloud');
      }
    } catch {}
    // Suppress the debounced auto-save that would otherwise fire because
    // applyData just changed every state variable at once.
    recentlyLoadedRef.current = true;
    clearTimeout(autoSaveTimerRef.current);
    setTimeout(() => { recentlyLoadedRef.current = false; }, 2000);
  };

  // ── Fetch carrier list for a given username (or fall back to localStorage) ──
  const fetchCarriersForUser = (uname) => {
    const loadFromLocalStorage = () => {
      try {
        const index = JSON.parse(localStorage.getItem('tsp_index') || '[]');
        setSavedCarriers(index);
        if (index.length === 1) {
          const data = JSON.parse(localStorage.getItem(`tsp_${index[0].id}`) || 'null');
          if (data) applyCarrierData(data, index[0].id);
        }
      } catch (e) {
        console.error('localStorage fallback failed:', e);
      }
    };
    if (!uname) { loadFromLocalStorage(); return; }
    console.log('[fetchCarriers] listing for user:', uname);
    apiFetch(`${API_BASE}/api/carriers/list`)
      .then(r => r.json())
      .then(index => {
        if (!Array.isArray(index)) { loadFromLocalStorage(); return; }
        setSavedCarriers(index);
        if (index.length === 1) {
          apiFetch(`${API_BASE}/api/carriers/${index[0].id}`)
            .then(r => r.json())
            .then(data => { if (data && !data.error) applyCarrierData(data, index[0].id); })
            .catch(() => {});
        }
      })
      .catch(() => loadFromLocalStorage());
  }; // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-load on mount: fetch index from Redis, fall back to localStorage ──
  useEffect(() => {
    // One-time data restore: ensure UNITED SILK RD INC record has companyName/dotNumber
    const silkKey = 'tsp_carrier_1776373349001';
    const silkRaw = localStorage.getItem(silkKey);
    if (silkRaw) {
      const silkData = JSON.parse(silkRaw);
      if (!silkData.companyName) {
        silkData.companyName = 'UNITED SILK RD INC';
        silkData.dotNumber = '4196660';
        localStorage.setItem(silkKey, JSON.stringify(silkData));
      }
    }

    // currentUser state is empty at mount (set async by auth effect) —
    // read from localStorage directly so we get the real username.
    let listUser = currentUser;
    if (!listUser) {
      try { listUser = JSON.parse(localStorage.getItem('tsp_user') || '{}').username || ''; } catch {}
    }
    fetchCarriersForUser(listUser);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-populate mileage/units from CAB data into all Loss Summary rows ──
  useEffect(() => {
    if (!cabData?.fleet) return;
    const fleetMap = computeFleetSummary(cabData.fleet);
    if (!Object.keys(fleetMap).length) return;
    setLossSummary(prev => ({
      ...prev,
      summary: (Array.isArray(prev.summary) ? prev.summary : initLossSummary().summary).map(row => {
        const vals = fleetMap[row.policyYear];
        if (!vals) return row;
        return {
          ...row,
          ...(vals.units        ? { units:        vals.units        } : {}),
          ...(vals.totalMileage ? { totalMileage: vals.totalMileage } : {}),
        };
      }),
    }));
  }, [cabData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Trigger conflict detection when equipment/drivers/IFTA/CAB change ──
  useEffect(() => {
    if (cabData) checkConflicts();
  }, [tractors.length, drivers.length, iftaData, cabData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Portal polling — check for new submissions every 30s ──
  // Use a ref for carrierId so fetchPortals stays stable (no deps) and the
  // interval is never recreated when carrierId changes — prevents 30+ API calls.
  const carrierIdRef = useRef(carrierId);
  useEffect(() => { carrierIdRef.current = carrierId; }, [carrierId]);

  const fetchPortals = useCallback(async () => {
    try {
      const res  = await apiFetch(`${API_BASE}/api/portals`);
      const list = await res.json();
      if (!Array.isArray(list)) return;
      setPortals(list);
      // Show banner for new unacknowledged submissions
      const newSub = list.find(p =>
        p.status === 'submitted' &&
        !p.notificationSeen &&
        p.carrierId === carrierIdRef.current
      );
      if (newSub) {
        const latest = newSub.submissions?.[newSub.submissions.length - 1];
        setSubmissionBanner({
          carrierName: newSub.carrierName,
          submittedAt: latest?.submittedAt || newSub.lastActivity,
          portalId: newSub.id,
        });
      }
    } catch { /* server may not be running in dev */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchPortals();
    const interval = setInterval(fetchPortals, 30000);
    return () => clearInterval(interval);
  }, [fetchPortals]); // fetchPortals is now stable — effect runs once on mount

  // ── Derived ──
  const uploadedCount = Object.values(documents).filter(files => files.length > 0).length;
  const completePct   = Math.round((uploadedCount / DOC_TYPES.length) * 100);
  const headerReadiness = computeReadiness(
    { companyName, tractors, trailers, drivers, documents, lossRunVerification, appData, coiData, iftaData, leadSheet, results },
    tliResults
  );

  // ── Client Portal ──
  const generatePortal = async () => {
    setPortalGenerating(true);
    try {
      const id = carrierId || `carrier_${Date.now()}`;
      if (!carrierId) setCarrierId(id);
      console.log('Portal drivers:', JSON.stringify(drivers.filter(d => d.name).map(d => ({
        name: d.name, dl: d.dl_number, state: d.state, exp: d.yrs_exp, hireDate: d.hire_date,
      }))));
      const res = await apiFetch(`${API_BASE}/api/portals/create`, {
        method: 'POST',
        body: JSON.stringify({
          carrierId: id,
          carrierName: companyName || 'Your Company',
          initialData: {
            tractors: tractors.filter(t => t.year || t.make || t.vin).map(t => ({
              year: t.year || '', make: t.make || '', vin: t.vin || '', value: t.value || '',
            })),
            trailers: trailers.filter(t => t.year || t.make || t.vin).map(t => ({
              year: t.year || '', make: t.make || '', vin: t.vin || '', value: t.value || '',
            })),
            // Map to portal field names: dl_number→dl, yrs_exp→exp, hire_date→hireDate
            drivers: drivers.filter(d => d.name).map(d => ({
              name: d.name || '', dob: d.dob || '',
              dl: d.dl_number || '', state: d.state || '',
              exp: d.yrs_exp || '', hireDate: d.hire_date || '',
            })),
            commodities: commodities || '',
            // Which doc categories already have files on file
            docsOnFile: Object.fromEntries(
              Object.entries(documents).map(([k, v]) => [k, k === 'ifta' ? v.length : v.length > 0])
            ),
            lossRunVerification: lossRunVerification || null,
            commodityCheckboxes: {
              flatBed:    leadSheet.flatBed,
              dryVan:     leadSheet.dryVan,
              reefer:     leadSheet.reefer,
              autoHauler: leadSheet.autoHauler,
            },
            loadAgreements: leadSheet.loadAgreements || '',
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create portal');
      const url = `${window.location.origin}/portal/${data.id}`;
      setPortalModal({ url, id: data.id });
      fetchPortals();
    } catch (err) {
      alert('Could not create portal: ' + err.message);
    } finally {
      setPortalGenerating(false);
    }
  };

  const closePortal = async (portalId) => {
    await apiFetch(`${API_BASE}/api/portals/${portalId}/close`, { method: 'PUT' });
    fetchPortals();
  };

  const copyPortalLink = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      setPortalLinkCopied(true);
      setTimeout(() => setPortalLinkCopied(false), 2000);
    });
  };

  const dismissBanner = async () => {
    if (submissionBanner?.portalId) {
      await apiFetch(`${API_BASE}/api/portals/${submissionBanner.portalId}/notification-seen`, { method: 'PUT' });
    }
    setSubmissionBanner(null);
    fetchPortals();
  };

  const applyPortalSubmission = async (portalId) => {
    const portal = portals.find(p => p.id === portalId);
    if (!portal) return;
    const sub = portal.submissions?.[portal.submissions.length - 1];
    if (!sub) return;

    // Apply equipment & drivers
    if (sub.tractors?.length) setTractors(sub.tractors.map(t => ({
      year: t.year || '', make: t.make || '', vin: t.vin || '', value: t.value || '', loss_payee: t.loss_payee || '',
    })));
    if (sub.trailers?.length) setTrailers(sub.trailers.map(t => ({
      year: t.year || '', make: t.make || '', vin: t.vin || '', value: t.value || '',
    })));
    if (sub.drivers?.length) setDrivers(sub.drivers.map(d => ({
      name: d.name || '', dob: d.dob || '', dl_number: d.dl || '', state: d.state || '',
      yrs_exp: d.exp || '', hire_date: d.hireDate || '',
    })));
    if (sub.commodities) setCommodities(sub.commodities);
    setLeadSheet(prev => {
      const updates = {};
      if (sub.fein)            updates.fein            = sub.fein;
      if (sub.eldsInstalled)   updates.eldsInstalled   = sub.eldsInstalled;
      if (sub.eldBrand)        updates.eldBrand        = sub.eldBrand;
      if (sub.dashcams)        updates.dashcams        = sub.dashcams;
      if (sub.dashcamBrand)    updates.dashcamBrand    = sub.dashcamBrand;
      if (sub.loadAgreements)      updates.loadAgreements      = sub.loadAgreements;
      if (sub.ownerDob)            updates.ownerDob            = sub.ownerDob;
      if (sub.radiusOfOperation)   updates.radiusOfOperation   = sub.radiusOfOperation;
      if (sub.commodityCheckboxes) {
        updates.flatBed    = { ...prev.flatBed,    ...(sub.commodityCheckboxes.flatBed    || {}) };
        updates.dryVan     = { ...prev.dryVan,     ...(sub.commodityCheckboxes.dryVan     || {}) };
        updates.reefer     = { ...prev.reefer,     ...(sub.commodityCheckboxes.reefer     || {}) };
        updates.autoHauler = { ...prev.autoHauler, ...(sub.commodityCheckboxes.autoHauler || {}) };
      }
      return { ...prev, ...updates };
    });

    // Fetch uploaded files from server and add to documents
    const docUpdates = {};
    for (const [cat, filenames] of Object.entries(sub.documents || {})) {
      if (!filenames || !filenames.length) continue;
      const blobs = await Promise.all(
        filenames.map(async (fname) => {
          try {
            const r = await fetch(`${API_BASE}/api/portals/${portalId}/files/${cat}/${fname}`);
            if (!r.ok) return null;
            const blob = await r.blob();
            return new File([blob], fname, { type: blob.type });
          } catch { return null; }
        })
      );
      const valid = blobs.filter(Boolean);
      if (valid.length) docUpdates[cat] = valid;
    }
    if (Object.keys(docUpdates).length) {
      setDocuments(prev => {
        const updated = { ...prev };
        for (const [cat, files] of Object.entries(docUpdates)) {
          updated[cat] = [...(prev[cat] || []), ...files];
        }
        return updated;
      });
    }

    // Mark notification seen
    await apiFetch(`${API_BASE}/api/portals/${portalId}/notification-seen`, { method: 'PUT' });
    setSubmissionBanner(null);
    fetchPortals();
    alert('Portal submission applied! Equipment, drivers, and documents have been updated.');
  };

  // ── Doc upload ──
  const [dragOver, setDragOver]           = useState(null);
  const [uploadProgress, setUploadProgress] = useState({}); // { [docId]: 'uploading'|'error'|null }

  const handleDocClick = (id) => fileRefs.current[id].current.click();

  // Upload a single file to Vercel Blob. On success updates the matching entry
  // in documents state from a File object to { name, url }. No-ops on localhost.
  const uploadFileToCloud = useCallback(async (file, docId) => {
    if (window.location.hostname === 'localhost') return;
    const cid = carrierId;
    if (!cid) return; // no carrier ID yet — will upload on next explicit save
    setUploadProgress(prev => ({ ...prev, [docId]: 'uploading' }));
    try {
      const token = localStorage.getItem('tsp_auth') || '';
      const res = await fetch(
        `${API_BASE}/api/files/upload?carrierId=${encodeURIComponent(cid)}&category=${docId}&filename=${encodeURIComponent(file.name)}`,
        { method: 'POST', body: file, headers: token ? { 'Authorization': `Bearer ${token}` } : {} }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data.ok && !data.success) throw new Error(data.error || 'Upload failed');
      // Swap the File object for a metadata reference.
      // New schema (private): { id, name } — no public URL.
      // Legacy schema (public): { name, url } — retained for migration window (should not occur for new uploads).
      const entry = data.fileId
        ? { id: data.fileId, name: data.name || file.name }
        : { name: file.name, url: data.url };
      setDocuments(prev => ({
        ...prev,
        [docId]: (prev[docId] || []).map(f => f === file ? entry : f),
      }));
      setUploadProgress(prev => ({ ...prev, [docId]: null }));
      // Persist the URL to Redis immediately so shared-carrier users (e.g.
      // Carter parsing files uploaded by Josh) get a resolvable URL even if
      // the uploader never explicitly clicks Save afterward.
      // We use saveCarrierRef (a ref to the latest saveCarrier) to avoid
      // the stale-closure problem inside useCallback.
      setTimeout(() => saveCarrierRef.current?.(true), 600);
    } catch (err) {
      console.warn('[uploadFileToCloud]', err.message);
      setUploadProgress(prev => ({ ...prev, [docId]: 'error' }));
      setTimeout(() => setUploadProgress(prev => ({ ...prev, [docId]: null })), 5000);
    }
  }, [dotNumber, carrierId]);

  const handleFileChange = (id, e) => {
    const newFiles = Array.from(e.target.files);
    if (!newFiles.length) return;
    setDocuments(prev => ({ ...prev, [id]: [...(prev[id] || []), ...newFiles] }));
    e.target.value = '';
    newFiles.forEach(f => uploadFileToCloud(f, id));
  };

  const handleDrop = (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(null);
    const newFiles = Array.from(e.dataTransfer.files);
    if (!newFiles.length) return;
    setDocuments(prev => ({ ...prev, [id]: [...(prev[id] || []), ...newFiles] }));
    newFiles.forEach(f => uploadFileToCloud(f, id));
  };

  const dragHandlers = (id) => ({
    onDragOver:  (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(id); },
    onDragEnter: (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(id); },
    onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(null); },
    onDrop:      (e) => handleDrop(id, e),
  });

  const handleRemoveFile = (id, idx) => {
    const entry = documents[id]?.[idx];
    if (window.location.hostname !== 'localhost') {
      const cid = carrierId;
      if (entry?.id && cid) {
        // New schema — fileId-based delete
        apiFetch(`${API_BASE}/api/files/delete`, {
          method: 'POST',
          body: JSON.stringify({ carrierId: cid, fileId: entry.id }),
        }).catch(err => console.warn('[deleteBlob fileId]', err.message));
      } else if (entry?.url) {
        // Legacy schema — URL-based delete
        apiFetch(`${API_BASE}/api/files/delete`, {
          method: 'POST',
          body: JSON.stringify({ url: entry.url }),
        }).catch(err => console.warn('[deleteBlob url]', err.message));
      }
    }
    setDocuments(prev => ({ ...prev, [id]: (prev[id] || []).filter((_, i) => i !== idx) }));
  };

  // ── Export all documents as ZIP ──
  const [exportingDocs, setExportingDocs] = useState(false);

  const exportAllDocuments = useCallback(async () => {
    const allFiles = DOC_TYPES.flatMap(d =>
      (documents[d.id] || []).map(f => ({ file: f, category: d.label }))
    );
    const cloudFiles = allFiles.filter(({ file }) => file?.url || file?.id);
    const localFiles = allFiles.filter(({ file }) => file instanceof File);

    const hasLeadSheet = !!(companyName || dotNumber);
    const hasMI = tliResults.length > 0;
    if (cloudFiles.length === 0 && localFiles.length === 0 && !hasLeadSheet && !hasMI) {
      alert('No documents uploaded yet.');
      return;
    }

    setExportingDocs(true);
    try {
      const zip = new JSZip();

      const _exportCid = carrierId;
      for (const { file, category } of cloudFiles) {
        try {
          let resp;
          if (file?.id && _exportCid) {
            // New schema — fetch via authenticated read endpoint
            const token = localStorage.getItem('tsp_auth') || '';
            resp = await fetch(
              `${API_BASE}/api/files/read?carrierId=${encodeURIComponent(_exportCid)}&fileId=${encodeURIComponent(file.id)}`,
              { headers: token ? { 'Authorization': `Bearer ${token}` } : {} }
            );
          } else {
            // Legacy schema — direct public URL
            resp = await fetch(file.url);
          }
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          const arrayBuf = await resp.arrayBuffer();
          zip.file(`${category}/${file.name}`, arrayBuf);
        } catch (err) {
          console.warn(`[exportAll] skipped "${file.name}":`, err.message);
        }
      }

      for (const { file, category } of localFiles) {
        const arrayBuf = await file.arrayBuffer();
        zip.file(`${category}/${file.name}`, arrayBuf);
      }

      if (hasLeadSheet) {
        try {
          const lsBlob = buildLeadSheetPDFBlob({ companyName, dotNumber, leadSheet, tractors, trailers, drivers, commodities });
          zip.file('Lead Sheet/Lead Sheet.pdf', lsBlob);
        } catch (err) {
          console.warn('[exportAll] Lead Sheet PDF skipped:', err.message);
        }
      }

      if (hasMI) {
        try {
          const miBlob = buildMarketIntelligencePDFBlob({ tliResults, tliGroupedMarkets, tliAccountProfile, tliAnalyzedAt, companyName });
          zip.file('Market Intelligence/Market Intelligence.pdf', miBlob);
        } catch (err) {
          console.warn('[exportAll] Market Intelligence PDF skipped:', err.message);
        }
      }

      const hasEquipmentOrDrivers = tractors.some(t => t.year || t.make || t.vin) || drivers.some(d => d.name || d.dob);
      if (hasEquipmentOrDrivers) {
        try {
          const xlBlob = await buildUnitDriverBlob();
          const safeXlName = (companyName || 'Carrier').replace(/[/\\?%*:|"<>]/g, '_');
          zip.file(`Unit & Driver List/${safeXlName}_Unit_Driver_List.xlsx`, xlBlob);
        } catch (err) {
          console.warn('[exportAll] Excel skipped:', err.message);
        }
      }

      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${companyName || 'Submission'} - Documents.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Export failed: ' + err.message);
    } finally {
      setExportingDocs(false);
    }
  }, [documents, companyName, dotNumber, carrierId, leadSheet, tractors, trailers, drivers, commodities, tliResults, tliGroupedMarkets, tliAccountProfile, tliAnalyzedAt]);

  // ── Table helpers ──
  const makeHandlers = (setter, cols) => ({
    onChange: (ri, key, val) => setter(prev => prev.map((r, i) => i === ri ? { ...r, [key]: val } : r)),
    onAdd:    ()             => setter(prev => [...prev, blankRow(cols)]),
    onDel:    (ri)           => setter(prev => prev.filter((_, i) => i !== ri)),
  });

  const tH = makeHandlers(setTractors, TRACTOR_COLS);
  const trH = makeHandlers(setTrailers, TRAILER_COLS);
  const dH  = makeHandlers(setDrivers,  DRIVER_COLS);

  const updateLossRow = (section, rowIdx, field, value) => {
    setLossSummary(prev => ({
      ...prev,
      [section]: prev[section].map((row, i) => i === rowIdx ? { ...row, [field]: value } : row),
    }));
  };

  // ── Build context blocks for AI (reads actual file contents) ──
  const buildContextBlocks = useCallback(async () => {
    // Text summary of manually-entered data + doc checklist
    const docList = DOC_TYPES.map(d => {
      const files = documents[d.id] || [];
      return files.length > 0
        ? `${d.label}: ✓ ${files.map(f => f.name).join(', ')}`
        : `${d.label}: ✗ Missing`;
    }).join('\n');
    const tractorStr = tractors.map((r, i) => `  ${i+1}. ${r.year} ${r.make} VIN:${r.vin} Value:${r.value} Payee:${r.loss_payee}`).join('\n');
    const trailerStr = trailers.map((r, i) => `  ${i+1}. ${r.year} ${r.make} VIN:${r.vin} Value:${r.value} Payee:${r.loss_payee}`).join('\n');
    const driverStr  = drivers.map((r, i) => `  ${i+1}. ${r.name} DOB:${r.dob} DL:${r.dl_number} ${r.state} Exp:${r.yrs_exp}yrs Hired:${r.hire_date}`).join('\n');
    // Lead sheet summary for AI context
    const ls = leadSheet;
    const lsCommodities = COMMODITY_GROUPS.map(g => {
      const checked = g.items.filter(i => ls[g.key]?.[i.key]).map(i => i.label);
      return checked.length ? `${g.label}: ${checked.join(', ')}` : null;
    }).filter(Boolean).join('\n');
    const lsContext = [
      ls.ownerName       ? `Owner: ${ls.ownerName}` : '',
      ls.fein            ? `FEIN: ${ls.fein}` : '',
      ls.currentXDate    ? `X-Date: ${ls.currentXDate}` : '',
      ls.currentInsurer  ? `Current Insurer: ${ls.currentInsurer}` : '',
      ls.yearsInBusiness ? `Years in Business: ${ls.yearsInBusiness}` : '',
      ls.radiusOfOperation ? `Radius: ${ls.radiusOfOperation}` : '',
      ls.ownerOpsOrCompany ? `Truck Ownership: ${ls.ownerOpsOrCompany}` : '',
      ls.eldsInstalled   ? `ELDs: ${ls.eldsInstalled}${ls.eldBrand ? ` (${ls.eldBrand})` : ''}` : '',
      ls.dashcams        ? `Dashcams: ${ls.dashcams}${ls.dashcamBrand ? ` (${ls.dashcamBrand})` : ''}` : '',
      ls.basicAlerts     ? `BASIC Alerts: ${ls.basicAlerts}` : '',
      ls.beingNonRenewed === 'yes' ? `⚠️ Being non-renewed/canceled: ${ls.nonRenewalReason || 'reason not provided'}` : '',
      ls.targetPricing   ? `Target Pricing: ${ls.targetPricing}` : '',
      ls.loadAgreements  ? `Load Agreements: ${ls.loadAgreements}` : '',
      lsCommodities      ? `Commodity Checkboxes:\n${lsCommodities}` : '',
      ls.correctiveActionPlan ? `Corrective Action Plan: ${ls.correctiveActionPlan}` : '',
    ].filter(Boolean).join('\n');

    const tcContext = tcAIContext(ls.eldBrand, ls.dashcamBrand);

    const borContext = markets
      .filter(m => m.bor?.sent)
      .map(m => {
        const borSt = BOR_STATUS_MAP[m.bor.status]?.label || m.bor.status;
        return `BOR letter sent to ${m.name}${m.bor.dateSent ? ` on ${m.bor.dateSent}` : ''}. Current BOR status: ${borSt}.${m.bor.notes ? ` Notes: ${m.bor.notes}` : ''}`;
      })
      .join('\n');

    const appContext = appData ? (() => {
      const c = appData.company    || {};
      const o = appData.operations || {};
      const u = appData.underwriting || {};
      return [
        'GROUND TRUTH — APPLICATION DATA FROM WHOLESALE PARTNER:',
        'The following data comes from a professionally prepared application and loss summary.',
        'Treat it as ground truth. Where it conflicts with CAB report data, use the application',
        'figures and note the discrepancy in your narrative.',
        '',
        c.fein            ? `FEIN: ${c.fein}` : '',
        c.ownerName       ? `Owner: ${c.ownerName}` : '',
        c.phone           ? `Phone: ${c.phone}` : '',
        o.yearsInBusiness ? `Years in Business: ${o.yearsInBusiness}` : '',
        o.powerUnits      ? `Total Fleet Units on Application (WARNING: this number may combine tractors + trailers — see DATA PRIORITY NOTE at end for accurate tractor/trailer breakdown): ${o.powerUnits}` : '',
        o.drivers         ? `Drivers (from app): ${o.drivers}` : '',
        o.radiusOfOperation ? `Radius: ${o.radiusOfOperation}` : '',
        o.commodities     ? `Commodities: ${o.commodities}` : '',
        o.eldBrand        ? `ELD: ${o.eldBrand}` : '',
        o.dashcamBrand    ? `Dashcam: ${o.dashcamBrand}` : '',
        u.beingNonRenewed ? `⚠️ Being non-renewed: ${u.nonRenewalReason || 'yes'}` : '',
        u.planningAddTrucks ? `Planning to add trucks: ${u.addTrucksCount || 'yes'}` : '',
      ].filter(Boolean).join('\n');
    })() : '';

    const blocks = [{
      type: 'text',
      text: `COMMERCIAL TRUCKING INSURANCE SUBMISSION\n\nCarrier: ${companyName || '(Name not provided)'}\nDOT Number: ${dotNumber || '(Not provided)'}${leadSheet.fein ? `\nFEIN: ${leadSheet.fein}` : ''}\n${commodities ? `\nCOMMODITIES HAULED:\n${commodities}\n` : ''}${appContext ? `\nAPPLICATION DATA (wholesale partner):\n${appContext}\n` : ''}${lsContext ? `\nLEAD SHEET DATA:\n${lsContext}\n` : ''}${tcContext ? `\nTRUCKERCLOUD TELEMATICS:\n${tcContext}\n` : ''}${borContext ? `\nBOR STATUS:\n${borContext}\n` : ''}\nDOCUMENTS CHECKLIST:\n${docList}\n\nTRACTORS (${tractors.length}):\n${tractorStr}\n\nTRAILERS (${trailers.length}):\n${trailerStr}\n\nDRIVERS (${drivers.length}):\n${driverStr}`,
    }];

    // Read uploaded document contents in priority order.
    // CAB is handled via structured cabData below — never send raw PDF.
    // Loss runs, COI, IFTA use structured JSON when parsed; otherwise raw files.
    // MVRs, equipment list, driver list always send raw files.
    const priorityOrder = ['lossRuns', 'coi', 'ifta', 'mvrs', 'equipmentList', 'driverList'];
    for (const docId of priorityOrder) {
      const files = documents[docId] || [];
      if (!files.length) continue;
      const docType = DOC_TYPES.find(d => d.id === docId);
      blocks.push({ type: 'text', text: `\n${'='.repeat(60)}\nDOCUMENT: ${docType.label}\n${'='.repeat(60)}` });

      if (docId === 'lossRuns' && lossRunsData) {
        blocks.push({ type: 'text', text: `STRUCTURED LOSS RUNS DATA:\n${JSON.stringify(lossRunsData, null, 2)}` });
        if (lossRunVerification) {
          const lrv = lossRunVerification;
          const lrvLines = ['autoLiability','cargo','physicalDamage','generalLiability'];
          const lrvLabels = { autoLiability: 'Auto Liability', cargo: 'Motor Truck Cargo', physicalDamage: 'Physical Damage', generalLiability: 'General Liability' };
          const lrvSummary = lrvLines.map(k => {
            const entries = lrv[k] || [];
            const missing = (lrv.yearSlots || POLICY_YEARS).filter(y => !entries.find(e => e.year === y && e.present));
            return missing.length === 0
              ? `  ${lrvLabels[k]}: ✓ All 5 years present`
              : `  ${lrvLabels[k]}: ✗ Missing ${missing.join(', ')}`;
          }).join('\n');
          blocks.push({ type: 'text', text: `\nLOSS RUN VERIFICATION GRID:\n${lrvSummary}` });
        }
        continue;
      }
      if (docId === 'coi' && coiData) {
        blocks.push({ type: 'text', text: `STRUCTURED COI DATA:\n${JSON.stringify(coiData, null, 2)}` });
        continue;
      }
      if (docId === 'ifta' && iftaData) {
        blocks.push({ type: 'text', text: `STRUCTURED IFTA DATA:\n${JSON.stringify(iftaData, null, 2)}` });
        continue;
      }

      for (const file of files) {
        try {
          const fileBlocks = await readFileForAI(file, carrierId);
          for (const block of fileBlocks) {
            if (block.type === 'text' && block.text.length > 50000) {
              blocks.push({ ...block, text: block.text.slice(0, 50000) + '\n[...truncated at 50,000 characters]' });
            } else {
              blocks.push(block);
            }
          }
        } catch (err) {
          blocks.push({ type: 'text', text: `[Could not read ${file.name}: ${err.message}]` });
        }
      }
    }
    // Inject structured CAB data as a readable text block so Claude sees exact numbers
    if (cabData) {
      const fmt = (v, sfx = '') => (v !== '' && v !== null && v !== undefined) ? `${v}${sfx}` : '—';
      const bl = (key, label) => {
        const d = cabData.basics?.[key] || {};
        return `  ${label}: ${d.score ? `${d.score}%` : '—'} ${d.alert ? '⚠️ ALERT' : '✓ Clean'}`;
      };
      blocks.push({ type: 'text', text:
        `\nSTRUCTURED CAB DATA:\n` +
        `Carrier: ${fmt(cabData.company?.name)} | DOT: ${fmt(cabData.company?.dot)} | MC: ${fmt(cabData.company?.mc)}\n` +
        `Status: ${fmt(cabData.company?.status)} | Authority: ${fmt(cabData.company?.authorityType)}\n` +
        `ISS Score: ${fmt(cabData.scores?.iss)} | ${fmt(cabData.scores?.issRecommendation)} | DOT Rating: ${cabData.scores?.dotRating || 'Unrated'}\n\n` +
        `FMCSA BASIC SCORES:\n` +
        [bl('unsafeDriving','Unsafe Driving'), bl('hoursOfService','Hours of Service'),
         bl('driverFitness','Driver Fitness'), bl('controlledSubstances','Controlled Substances'),
         bl('vehicleMaintenance','Vehicle Maintenance'), bl('hazmat','Hazmat'),
         bl('crashIndicator','Crash Indicator')].join('\n') + '\n\n' +
        `OOS RATES:\n` +
        `  Overall: ${fmt(cabData.oos?.overall?.pct,'%')} (Nat'l Avg: ${fmt(cabData.oos?.overall?.nationalAvg,'%')})\n` +
        `  Vehicle: ${fmt(cabData.oos?.vehicle?.pct,'%')} (Nat'l Avg: ${fmt(cabData.oos?.vehicle?.nationalAvg,'%')})\n` +
        `  Driver:  ${fmt(cabData.oos?.driver?.pct,'%')} (Nat'l Avg: ${fmt(cabData.oos?.driver?.nationalAvg,'%')})\n\n` +
        `ACCIDENTS: Fatal: ${fmt(cabData.accidents?.fatal)}, Injury: ${fmt(cabData.accidents?.injury)}, Tow: ${fmt(cabData.accidents?.tow)}, Total: ${fmt(cabData.accidents?.total)}\n` +
        `FLEET (SAFER/CAB registered figures — uploaded document data takes priority, see DATA PRIORITY NOTE below): ${fmt(cabData.fleet?.totalPowerUnits)} power units | ${fmt(cabData.fleet?.drivers)} drivers | Annual Miles: ${fmt(cabData.fleet?.annualMileage)}\n` +
        `INSURANCE: ${fmt(cabData.insurance?.carrier)} | BIPD: ${fmt(cabData.insurance?.bipd)} | Cargo: ${fmt(cabData.insurance?.cargo)} | Exp: ${fmt(cabData.insurance?.cancellationDate)}` +
        (cabData.alerts?.length ? `\n\nACTIVE ALERTS:\n${cabData.alerts.map(a => `  • ${a}`).join('\n')}` : '')
      });
    }
    // Inject loss summary structured data
    const hasLossData = ['summary','autoLiability','cargo','physicalDamage','generalLiability']
      .some(k => lossSummary[k].some(r => Object.values(r).some(v => v && v !== r.policyYear)));
    if (hasLossData) {
      const ls = lossSummary;
      const fmtRow = (r) => Object.entries(r).map(([k,v]) => `${k}: ${v||'—'}`).join(' | ');
      blocks.push({ type: 'text', text:
        `\n${'='.repeat(60)}\nLOSS SUMMARY DATA\n${'='.repeat(60)}\n\n` +
        `SUMMARY TOTALS:\n${ls.summary.map(r => fmtRow(r)).join('\n')}\n\n` +
        `AUTO LIABILITY:\n${ls.autoLiability.map(r => fmtRow(r)).join('\n')}\n\n` +
        `MOTOR TRUCK CARGO:\n${ls.cargo.map(r => fmtRow(r)).join('\n')}\n\n` +
        `PHYSICAL DAMAGE:\n${ls.physicalDamage.map(r => fmtRow(r)).join('\n')}\n\n` +
        `GENERAL LIABILITY:\n${ls.generalLiability.map(r => fmtRow(r)).join('\n')}`
      });
    }
    // Inject override context when any conflict has been resolved
    const confirmedOverrides = Object.entries(overrides).filter(([, v]) => v?.confirmed);
    if (confirmedOverrides.length > 0) {
      const overrideLines = confirmedOverrides.map(([key, v]) => {
        const label = key === 'units' ? 'Power Units' : key === 'drivers' ? 'Number of Drivers' : 'Annual Mileage';
        const sourceLabel = v.source === 'cab' ? 'CAB Report' : v.source.replace(/_/g, ' ');
        return `  ${label}: Using ${v.value} (source: ${sourceLabel}) — confirmed override, discrepancy acknowledged`;
      }).join('\n');
      blocks.push({ type: 'text', text: `\nDATA OVERRIDE NOTES:\nThe following fields have confirmed overrides where the Equipment/Driver list or IFTA data differs from the CAB report. Use the override values for all calculations:\n${overrideLines}` });
    }

    // ── Data priority note — always last so it overrides any CAB fleet numbers ──
    // Uploaded Equipment/Driver tab data takes absolute priority over SAFER/CAB figures.
    // Tractors and trailers are ALWAYS stated separately — never combined.
    const tractorCount = tractors.filter(r => Object.values(r).some(v => v)).length || tractors.length;
    const trailerCount = trailers.filter(r => Object.values(r).some(v => v)).length || trailers.length;
    const tractorSource = tractorCount > 0 ? 'from uploaded equipment list' : (cabData?.fleet?.totalPowerUnits ? 'CAB/SAFER estimate — no uploaded list' : null);
    const trailerSource = trailerCount > 0 ? 'from uploaded equipment list' : null;
    const driverCount   = drivers.length > 0 ? drivers.length : (cabData?.fleet?.drivers || null);
    const driverSource  = drivers.length > 0 ? 'from uploaded driver list' : (cabData?.fleet?.drivers ? 'CAB/SAFER estimate' : null);
    const combinedTotal = (tractorCount || 0) + (trailerCount || 0);
    blocks.push({ type: 'text', text:
      `\n${'='.repeat(60)}\nDATA PRIORITY NOTE — MANDATORY OVERRIDE (highest priority — supersedes all other data above)\n${'='.repeat(60)}\n\n` +
      `TRACTORS (power units): ${tractorCount || '—'}${tractorSource ? ` — ${tractorSource}` : ''}\n` +
      `TRAILERS: ${trailerCount || '—'}${trailerSource ? ` — ${trailerSource}` : ' — not in uploaded data'}\n` +
      `DRIVERS: ${driverCount || '—'}${driverSource ? ` — ${driverSource}` : ''}\n\n` +
      (tractorCount && trailerCount ? (
        `FLEET MATH WARNING:\n` +
        `  ${tractorCount} tractors + ${trailerCount} trailers = ${combinedTotal} total equipment pieces\n` +
        `  The number ${combinedTotal} is the combined equipment total. ` +
        `  NEVER use ${combinedTotal} as a unit count, power unit count, or fleet size.\n` +
        `  IMPORTANT: This fleet has ${tractorCount} power units (tractors) and ${trailerCount} trailers.\n` +
        `  Do NOT say "${combinedTotal} units" or "${combinedTotal} power units" — this is WRONG.\n` +
        `  Correct phrasing: "${tractorCount} power units (tractors) and ${trailerCount} trailers"\n\n`
      ) : '') +
      `RULES:\n` +
      `- State tractors and trailers separately in every instance. Never combine them.\n` +
      `- Format: "X power units (tractors) and Y trailers" — always.\n` +
      `- Uploaded equipment list counts take absolute priority over application form numbers, CAB/SAFER fleet numbers, and any other source.\n` +
      `- Application form "power units" or "units" fields often combine tractors + trailers — do NOT use that combined number.`
    });

    return blocks;
  }, [companyName, dotNumber, carrierId, documents, tractors, trailers, drivers, cabData, lossRunsData, lossRunVerification, appData, coiData, iftaData, lossSummary, commodities, leadSheet, overrides]);

  // ── AI call — takes pre-built context blocks + a plain-text instruction ──
  const callAI = async (type, system, contextBlocks, instruction, maxTokens = 4096) => {
    setLoading(p => ({ ...p, [type]: true }));
    try {
      const rawContent = [...contextBlocks, { type: 'text', text: instruction }];
      const userContent = guardApiContent(rawContent, (w) => setResults(p => ({ ...p, [type]: w })));
      const res = await apiFetch(`${API_BASE}/api/claude`, {
        method: 'POST',
        body: JSON.stringify({ system, userContent, maxTokens }),
      });
      const data = await res.json();
      if (data.error) throw new Error(typeof data.error === 'string' ? data.error : JSON.stringify(data.error));
      const text = data.content?.[0]?.text || '';
      setResults(p => ({ ...p, [type]: text }));
      return text;
    } catch (err) {
      const msg = '⚠️ Error: ' + err.message;
      setResults(p => ({ ...p, [type]: msg }));
      return msg;
    } finally {
      setLoading(p => ({ ...p, [type]: false }));
    }
  };

  // ── Retry wrapper for overloaded_error ──────────────────────────────────────
  const callClaudeWithRetry = async (payload, maxRetries = 3, onRetry) => {
    const WAIT_MS = [5000, 10000, 20000];
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      const res = await apiFetch(`${API_BASE}/api/claude`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      const errStr = typeof data.error === 'string' ? data.error : JSON.stringify(data.error || '');
      const isOverloaded = errStr.includes('overloaded_error') || errStr.includes('overloaded');
      if (!isOverloaded) {
        if (data.error) throw new Error(errStr);
        return data.content?.[0]?.text || '';
      }
      if (attempt < maxRetries) {
        if (onRetry) onRetry(attempt + 1);
        await new Promise(r => setTimeout(r, WAIT_MS[attempt]));
      }
    }
    throw new Error('The AI service is currently busy. Please wait 30 seconds and try again.');
  };

  const generateRisk = async () => {
    const blocks = await buildContextBlocks();
    const hasMvrs = (documents['mvrs'] || []).length > 0;
    const mvrNote = hasMvrs ? '' : '\n\nCRITICAL: Do NOT mention MVRs, motor vehicle records, or driver records at any point in this analysis. No MVR files have been uploaded — omit all references entirely. Do not say they are missing, do not say they are needed, do not mention them at all.';
    const todayStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const instruction = RISK_INSTRUCTION.replace("[today's date]", todayStr) + mvrNote;
    return callAI('risk', RISK_SYSTEM, blocks, instruction, 1500);
  };

  const generateNarrative = async () => {
    console.log('[narrative] fleet data:', {
      tractors: tractors.length,
      trailers: trailers.length,
      combined_should_not_appear: tractors.length + trailers.length
    });
    const blocks = await buildContextBlocks();
    const hasMvrs = (documents['mvrs'] || []).length > 0;
    const mvrNote = hasMvrs ? '' : '\n\nCRITICAL: Do NOT mention MVRs, motor vehicle records, or driver records at any point in the narrative. No MVR files have been uploaded — omit all references entirely. Do not say they are missing, do not say they are needed, do not mention them at all.';

    // ── Pass 1: Generate initial draft (not shown to user) ──────────────────
    setNarrativePhase('generating');
    setLoading(p => ({ ...p, narrative: true }));
    let pass1Text = '';
    try {
      const rawContent = [...blocks, { type: 'text', text: NARRATIVE_INSTRUCTION + mvrNote }];
      const userContent = guardApiContent(rawContent, () => {});
      pass1Text = await callClaudeWithRetry(
        { system: NARRATIVE_SYSTEM, userContent, maxTokens: 2000 },
        3,
        (attempt) => setNarrativePhase(`retry-p1-${attempt}`)
      );
    } catch (err) {
      setResults(p => ({ ...p, narrative: '⚠️ ' + err.message }));
      setLoading(p => ({ ...p, narrative: false }));
      setNarrativePhase('');
      return;
    }

    // 2-second pause between passes to reduce back-to-back overload risk
    await new Promise(r => setTimeout(r, 2000));

    // ── Pass 2: Revise and strengthen (this result is shown to user) ─────────
    setNarrativePhase('refining');
    const revisionUserMsg = `Here is the narrative to improve:\n\n${pass1Text}${mvrNote}`;
    try {
      const pass2Text = await callClaudeWithRetry(
        { system: NARRATIVE_REVISION_SYSTEM, userContent: [{ type: 'text', text: revisionUserMsg }], maxTokens: 2500 },
        3,
        (attempt) => setNarrativePhase(`retry-p2-${attempt}`)
      );
      setResults(p => ({ ...p, narrative: pass2Text }));
      setNarrativePhase('');
      setLoading(p => ({ ...p, narrative: false }));
      return pass2Text;
    } catch (err) {
      // Pass 2 failed — fall back to Pass 1 result with a notice
      const fallback = pass1Text + '\n\n---\n*Note: AI refinement unavailable — showing base narrative. Try regenerating in a moment.*';
      setResults(p => ({ ...p, narrative: fallback }));
      setNarrativePhase('');
      setLoading(p => ({ ...p, narrative: false }));
      return fallback;
    }
  };

  // Build loss run verification note — shared by generateMissing and runAll
  const buildLrNote = () => {
    if (!lossRunVerification) return '';
    const LR_KEYS   = ['autoLiability', 'cargo', 'physicalDamage', 'generalLiability'];
    const LR_LABELS = { autoLiability: 'Auto Liability', cargo: 'Cargo', physicalDamage: 'Physical Damage', generalLiability: 'General Liability' };
    const missingParts = [];
    for (const key of LR_KEYS) {
      const entries = lossRunVerification[key] || [];
      const missing = entries.filter(e => !e.present && !e.active).map(e => e.year);
      if (missing.length > 0) missingParts.push(`${LR_LABELS[key]}: missing years ${missing.join(', ')}`);
    }
    const note = missingParts.length === 0
      ? '\n\nLOSS RUN VERIFICATION STATUS: COMPLETE — All 5 years verified for all 4 coverage lines. Do NOT include loss runs in the missing items list.\n\n'
      : `\n\nLOSS RUN VERIFICATION STATUS: INCOMPLETE — The following specific years are still missing:\n${missingParts.map(p => '  • ' + p).join('\n')}\nOnly flag these specific gaps, not the entire loss run requirement.\n\n`;
    console.log('[buildLrNote] lossRunVerification:', JSON.stringify(lossRunVerification).slice(0, 200));
    console.log('[buildLrNote] lrNote:', note.trim().slice(0, 120));
    return note;
  };

  const buildDriverNote = () => {
    if (!drivers.length) return '';
    const driverLines = drivers
      .map((d, i) => `  ${i + 1}. ${d.name || 'Unnamed'} — DOB:${d.dob || '—'} DL#:${d.dl_number || '—'} State:${d.state || '—'} Exp:${d.yrs_exp || '—'} yrs Hired:${d.hire_date || '—'}`)
      .join('\n');
    return `\n\nDRIVER DATA STATUS: ${drivers.length} driver${drivers.length > 1 ? 's have' : ' has'} been entered directly in the submission system — treat this as the driver list. Evaluate whether each driver below has all five required fields (name, DOB, DL number, DL state, years experience, hire date). Do NOT say the driver list is missing:\n${driverLines}\n\n`;
  };

  const generateMissing = async () => {
    const blocks = await buildContextBlocks();
    const lrNote = buildLrNote();
    const driverNote = buildDriverNote();
    const hasMvrs = (documents['mvrs'] || []).length > 0;
    const mvrNote = hasMvrs ? '' : '\n\nCRITICAL: Do NOT mention MVRs, motor vehicle records, or driver records anywhere in the missing items list or email. No MVR files have been uploaded — omit all references entirely. Do not list them as missing, do not say they are needed, do not mention them at all.';
    console.log('[generateMissing] lrNote length:', lrNote.length, '| lossRunVerification set:', !!lossRunVerification, '| hasMvrs:', hasMvrs, '| drivers:', drivers.length);
    const instruction = lrNote + driverNote + MISSING_INSTRUCTION + mvrNote;
    const text = await callAI('missing', MISSING_SYSTEM, blocks, instruction, 1500);
    const emailMatch = text.match(/(?:CLIENT EMAIL DRAFT|EMAIL DRAFT|Dear \[?[A-Za-z]+\]?,)[^\n]*([\s\S]*)/i);
    if (emailMatch) {
      const emailStart = text.indexOf(emailMatch[0]);
      setResults(p => ({ ...p, missingEmail: text.slice(emailStart) }));
    }
  };

  const generatePlaybook = async () => {
    const blocks = await buildContextBlocks();
    return callAI('playbook',
      'You are a commercial trucking safety expert and insurance coach. Use the actual fleet and driver information from the documents to make specific recommendations.',
      blocks,
      'Create a customized safety coaching playbook for this trucking operation. Include: driver safety program, vehicle maintenance checklist, hours-of-service compliance, incident reporting procedures, safety training recommendations, and loss prevention tips. Make it practical and actionable.',
      2000
    );
  };

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const runAll = async () => {
    // Build context once, share across all four calls to avoid reading files 4x
    const blocks = await buildContextBlocks();

    const todayStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    await callAI('risk', RISK_SYSTEM, blocks, RISK_INSTRUCTION.replace("[today's date]", todayStr), 1500);
    await delay(5000);

    await generateNarrative();
    await delay(5000);

    const missingInstruction = buildLrNote() + buildDriverNote() + MISSING_INSTRUCTION;
    const text = await callAI('missing', MISSING_SYSTEM, blocks, missingInstruction, 1500);
    const emailMatch = text.match(/(?:CLIENT EMAIL DRAFT|EMAIL DRAFT|Dear \[?[A-Za-z]+\]?,)[^\n]*([\s\S]*)/i);
    if (emailMatch) setResults(p => ({ ...p, missingEmail: text.slice(text.indexOf(emailMatch[0])) }));
    await delay(5000);

    await callAI('playbook',
      'You are a commercial trucking safety expert and insurance coach. Use the actual fleet and driver information from the documents to make specific recommendations.',
      blocks,
      'Create a customized safety coaching playbook for this trucking operation. Include: driver safety program, vehicle maintenance checklist, hours-of-service compliance, incident reporting procedures, safety training recommendations, and loss prevention tips. Make it practical and actionable.',
      2000
    );

    setActiveTab('AI Analysis');
  };

  const anyLoading = Object.values(loading).some(Boolean);

  // ── Excel Export Helpers ──
  const _xlsxDownload = async (wb, filename) => {
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const _addEquipmentSheet = (wb) => {
    const ws = wb.addWorksheet('Equipment');
    const thin    = { style: 'thin' };
    const allThin = { top: thin, bottom: thin, left: thin, right: thin };
    const f11b    = { name: 'Calibri', size: 11, bold: true };
    const f11     = { name: 'Calibri', size: 11 };
    const ctr     = { horizontal: 'center', vertical: 'middle' };
    const mid     = { vertical: 'middle' };

    ws.columns = [
      { width: 8 }, { width: 20 }, { width: 22 }, { width: 14 }, { width: 24 },
    ];

    let r = 1;

    // Title
    ws.mergeCells(`A${r}:E${r}`);
    Object.assign(ws.getCell(r, 1), {
      value: `${companyName || 'Carrier'} — Equipment List`,
      font: { name: 'Calibri', size: 14, bold: true }, alignment: ctr,
    });
    ws.getRow(r).height = 24; r += 2;

    // Tractors section
    ws.mergeCells(`A${r}:E${r}`);
    Object.assign(ws.getCell(r, 1), { value: 'TRACTORS / POWER UNITS', font: f11b, alignment: ctr, border: { bottom: thin } });
    ws.getRow(r).height = 18; r++;

    ['Year', 'Make / Model', 'VIN', 'Value', 'Loss Payee'].forEach((h, c) => {
      Object.assign(ws.getCell(r, c + 1), { value: h, font: f11b, border: allThin, alignment: ctr });
    });
    ws.getRow(r).height = 16; r++;

    const tractorRows = tractors.filter(t => t.year || t.make || t.vin);
    (tractorRows.length ? tractorRows : [{}]).forEach(row => {
      [row.year||'', row.make||'', row.vin||'', row.value||'', row.loss_payee||''].forEach((v, c) => {
        Object.assign(ws.getCell(r, c + 1), { value: v, font: f11, border: allThin, alignment: c === 0 ? ctr : mid });
      });
      r++;
    });
    r++; // blank row between sections

    // Trailers section
    ws.mergeCells(`A${r}:D${r}`);
    Object.assign(ws.getCell(r, 1), { value: 'TRAILERS', font: f11b, alignment: ctr, border: { bottom: thin } });
    ws.getRow(r).height = 18; r++;

    ['Year', 'Make / Model', 'VIN', 'Value'].forEach((h, c) => {
      Object.assign(ws.getCell(r, c + 1), { value: h, font: f11b, border: allThin, alignment: ctr });
    });
    ws.getRow(r).height = 16; r++;

    const trailerRows = trailers.filter(t => t.year || t.make || t.vin);
    (trailerRows.length ? trailerRows : [{}]).forEach(row => {
      [row.year||'', row.make||'', row.vin||'', row.value||''].forEach((v, c) => {
        Object.assign(ws.getCell(r, c + 1), { value: v, font: f11, border: allThin, alignment: c === 0 ? ctr : mid });
      });
      r++;
    });
  };

  const _addDriversSheet = (wb) => {
    const ws = wb.addWorksheet('Drivers');
    const thin    = { style: 'thin' };
    const allThin = { top: thin, bottom: thin, left: thin, right: thin };
    const f11b    = { name: 'Calibri', size: 11, bold: true };
    const f11     = { name: 'Calibri', size: 11 };
    const ctr     = { horizontal: 'center', vertical: 'middle' };
    const mid     = { vertical: 'middle' };

    ws.columns = [
      { width: 24 }, { width: 14 }, { width: 18 }, { width: 8 }, { width: 10 }, { width: 14 },
    ];

    let r = 1;

    // Title
    ws.mergeCells(`A${r}:F${r}`);
    Object.assign(ws.getCell(r, 1), {
      value: `${companyName || 'Carrier'} — Driver List`,
      font: { name: 'Calibri', size: 14, bold: true }, alignment: ctr,
    });
    ws.getRow(r).height = 24; r += 2;

    // Column headers
    ['Name', 'DOB', 'DL Number', 'State', 'Yrs Exp', 'Hire Date'].forEach((h, c) => {
      Object.assign(ws.getCell(r, c + 1), { value: h, font: f11b, border: allThin, alignment: ctr });
    });
    ws.getRow(r).height = 16; r++;

    const driverRows = drivers.filter(d => d.name || d.dob);
    (driverRows.length ? driverRows : [{}]).forEach(row => {
      [row.name||'', row.dob||'', row.dl_number||'', row.state||'', row.yrs_exp||'', row.hire_date||''].forEach((v, c) => {
        Object.assign(ws.getCell(r, c + 1), { value: v, font: f11, border: allThin, alignment: mid });
      });
      r++;
    });
  };

  // ── Export to Excel — Combined (Equipment + Drivers) ──
  const exportExcel = async () => {
    const { default: ExcelJS } = await import('exceljs');
    const wb = new ExcelJS.Workbook();
    _addEquipmentSheet(wb);
    _addDriversSheet(wb);
    const safe = (companyName || 'Carrier').replace(/[/\\?%*:|"<>]/g, '_');
    await _xlsxDownload(wb, `${safe}_Unit_Driver_List.xlsx`);
  };

  const buildUnitDriverBlob = async () => {
    const { default: ExcelJS } = await import('exceljs');
    const wb = new ExcelJS.Workbook();
    _addEquipmentSheet(wb);
    _addDriversSheet(wb);
    const buffer = await wb.xlsx.writeBuffer();
    return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  };

  // ── Export Equipment Only ──
  const exportEquipmentExcel = async () => {
    const { default: ExcelJS } = await import('exceljs');
    const wb = new ExcelJS.Workbook();
    _addEquipmentSheet(wb);
    const safe = (companyName || 'Carrier').replace(/[/\\?%*:|"<>]/g, '_');
    await _xlsxDownload(wb, `${safe}_Equipment_List.xlsx`);
  };

  // ── Export Drivers Only ──
  const exportDriversExcel = async () => {
    const { default: ExcelJS } = await import('exceljs');
    const wb = new ExcelJS.Workbook();
    _addDriversSheet(wb);
    const safe = (companyName || 'Carrier').replace(/[/\\?%*:|"<>]/g, '_');
    await _xlsxDownload(wb, `${safe}_Driver_List.xlsx`);
  };

  // ── Export Loss Summary to Excel ──
  const exportLossExcel = async () => {
    const { default: ExcelJS } = await import('exceljs');
    const wb  = new ExcelJS.Workbook();
    const ws  = wb.addWorksheet('Sheet1');

    // Column widths (A–H)
    ws.columns = [
      { width: 32 }, { width: 16 }, { width: 16 }, { width: 14 },
      { width: 15 }, { width: 16 }, { width: 16 }, { width: 15 }, { width: 18 },
      { width: 10 }, { width: 16 }, { width: 16 }, { width: 16 }, { width: 16 },
    ];

    // ── Style constants ──
    const thin       = { style: 'thin' };
    const dbl        = { style: 'double' };
    const allThin    = { top: thin, bottom: thin, left: thin, right: thin };
    const topThinBotDbl = { top: thin, bottom: dbl };
    const botThinOnly   = { bottom: thin };
    const topThinOnly   = { top: thin };
    const center     = { horizontal: 'center', vertical: 'middle' };
    const f11b       = { name: 'Calibri', size: 11, bold: true };
    const f11        = { name: 'Calibri', size: 11 };
    const f20b       = { name: 'Calibri', size: 20, bold: true };
    const greenFill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC2D69B' } };
    const CUR        = '$#,##0';
    const INT        = '#,##0';
    const DEC2       = '0.00';

    const parseNum = (v) => {
      const n = parseFloat(String(v ?? '').replace(/[$,]/g, ''));
      return isNaN(n) ? null : n;
    };

    // Set cell helpers
    const setVal = (rowNum, colNum, value, opts = {}) => {
      const cell = ws.getCell(rowNum, colNum);
      if (value !== null && value !== undefined) cell.value = value;
      if (opts.font)      cell.font      = opts.font;
      if (opts.fill)      cell.fill      = opts.fill;
      if (opts.alignment) cell.alignment = opts.alignment;
      if (opts.border)    cell.border    = opts.border;
      if (opts.numFmt)    cell.numFmt    = opts.numFmt;
    };

    const setFml = (rowNum, colNum, formula, opts = {}) => {
      const { result, ...styleOpts } = opts;
      const val = result !== undefined ? { formula, result } : { formula };
      setVal(rowNum, colNum, val, styleOpts);
    };

    const setNum = (rowNum, colNum, rawVal, opts = {}) => {
      const n = parseNum(rawVal);
      setVal(rowNum, colNum, n !== null ? n : null, opts);
    };

    // ── Row 1: Title ──
    ws.mergeCells('A1:N1');
    ws.getRow(1).height = 28;
    setVal(1, 1, 'Loss Run Summary & Historical Information',
      { font: f20b, fill: greenFill, alignment: center });

    // ── Row 3: SUMMARY TOTALS ──
    ws.mergeCells('A3:I3');
    setVal(3, 1, 'SUMMARY TOTALS',
      { font: f11b, border: botThinOnly, alignment: center });

    // ── Row 4: Summary column headers ──
    ['Policy Year','Loss Reserve','Total Incurred','Total Claims','Units','Loss Frequency','Mileage Per Unit','Total Mileage','Revenue Per Truck']
      .forEach((h, i) => setVal(4, i + 1, h, { font: f11b, border: allThin, alignment: center }));

    // ── Build structured data (pure, testable) then write to cells ──────────────
    const wb_data = buildLossSummaryWorkbookData(lossSummary, cabData);

    // Export-time COI injection: add missing-loss-run stubs from current coiData so
    // Highlander rows appear after COI re-parse alone (no loss run re-parse required).
    {
      const SEC_KEYS_ALL = ['autoLiability', 'cargo', 'physicalDamage', 'generalLiability'];
      for (const policy of (coiData?.additionalPolicies || [])) {
        if (!policy.carrierName || !policy.policyNumber) continue;
        const sectionKey = policy.coverageLine;
        if (!SEC_KEYS_ALL.includes(sectionKey)) continue;
        const normName = normalizeCarrierName(policy.carrierName);
        const alreadyExists = (wb_data.sections[sectionKey] || [])
          .some(r => r.policyNumber === policy.policyNumber);
        if (alreadyExists) continue;
        const yearLabel = buildPolicyYearLabel(policy.effectiveDate, policy.expirationDate);
        if (!yearLabel) continue;
        if (!wb_data.sections[sectionKey]) wb_data.sections[sectionKey] = [];
        wb_data.sections[sectionKey].unshift({
          insurer:        normName,
          policyYear:     yearLabel,
          policyNumber:   policy.policyNumber,
          effectiveDate:  policy.effectiveDate  || null,
          expirationDate: policy.expirationDate || null,
          lossRunStatus:  'missing_loss_run',
          numClaims:      null,
          lossReserve:    null,
          totalIncurred:  null,
          recovery:       null,
        });
      }
      // Re-sort each section year-descending after injection
      for (const sk of SEC_KEYS_ALL) {
        if (wb_data.sections[sk]) {
          wb_data.sections[sk].sort((a, b) => {
            const [a1 = 0] = (a.policyYear || '0-0').split('-').map(Number);
            const [b1 = 0] = (b.policyYear || '0-0').split('-').map(Number);
            return b1 - a1;
          });
        }
      }

      // Cross-reference COI policy numbers to restore state prefixes that old parse may have stripped.
      // Strip leading 2-3 letter prefix for comparison; if COI has a prefix-bearing version, use it.
      const allCoiPolicies = [
        coiData?.policyNumber
          ? { policyNumber: coiData.policyNumber, carrierName: coiData.carrierName }
          : null,
        ...(coiData?.additionalPolicies || []).map(p => ({ policyNumber: p.policyNumber, carrierName: p.carrierName })),
      ].filter(p => p && p.policyNumber);
      for (const coiPol of allCoiPolicies) {
        const coiStripped = coiPol.policyNumber.replace(/^[A-Za-z]{2,3}/, '');
        if (!coiStripped || coiStripped === coiPol.policyNumber) continue; // no prefix in COI
        for (const sk of SEC_KEYS_ALL) {
          for (const row of (wb_data.sections[sk] || [])) {
            if (!row.policyNumber || row.policyNumber === coiPol.policyNumber) continue;
            const rowStripped = row.policyNumber.replace(/^[A-Za-z]{2,3}/, '');
            if (rowStripped === coiStripped) row.policyNumber = coiPol.policyNumber;
          }
        }
      }

      console.log('[exportLossExcel] COI injection: coiData.additionalPolicies=',
        JSON.stringify(coiData?.additionalPolicies || []));
    }

    const { yearSlots: summaryYearSlots, summary: wbSummary } = wb_data;
    console.log('[exportLossExcel] yearSlots:', summaryYearSlots, '| summary:', JSON.stringify(wbSummary));

    // Dynamic row layout based on actual number of policy-year rows
    const N = Math.max(wbSummary.length, 1);
    const sumDataStart  = 5;
    const sumDataEnd    = sumDataStart + N - 1;
    const sumTotalRow   = sumDataEnd + 2;      // one blank row gap before TOTAL
    const sumAvgRow     = sumTotalRow + 1;
    const firstSecRow   = sumAvgRow + 3;       // two blank rows after AVERAGE
    // Fleet history: headers at section title row; future-year placeholder at col-header row;
    // historical data aligns row-for-row with section data rows.
    const histHeaderRow = firstSecRow;         // "Year / Units / …" labels at section title row
    const histDataStart = firstSecRow + 2;     // first historical data row = first section data row

    wbSummary.forEach((row, i) => {
      const rowNum = sumDataStart + i;
      const base   = { font: f11, border: allThin, alignment: center };
      setVal(rowNum, 1, row.year || '', base);
      setNum(rowNum, 2, row.lossReserve,   { ...base, numFmt: CUR });
      setNum(rowNum, 3, row.totalIncurred, { ...base, numFmt: CUR });
      setNum(rowNum, 4, row.totalClaims,   { ...base, numFmt: INT });
      setNum(rowNum, 5, row.units        || null,                        { ...base, numFmt: INT });
      setFml(rowNum, 6, `IFERROR(D${rowNum}/E${rowNum},0)`,              { ...base, numFmt: DEC2 });
      setFml(rowNum, 7, `IFERROR(H${rowNum}/E${rowNum},0)`,              { ...base, numFmt: INT });
      setNum(rowNum, 8, row.totalMileage || null,                        { ...base, numFmt: INT });
      // Revenue Per Truck = Total Revenue (N) / Units (J) for this summary year's historical row
      setFml(rowNum, 9, `IFERROR(N${histDataStart+i}/J${histDataStart+i},0)`, { ...base, numFmt: CUR });
    });

    // ── TOTAL row ──
    // Compute JS results so cells display in apps that don't evaluate formulas (e.g. Numbers).
    // Only include confirmed rows (non-null values) — missing-loss-run rows are excluded.
    const confirmedSummary = wbSummary.filter(r => r.lossReserve !== null);
    const sumTotReserve  = confirmedSummary.reduce((s, r) => s + (r.lossReserve   || 0), 0);
    const sumTotIncurred = confirmedSummary.reduce((s, r) => s + (r.totalIncurred || 0), 0);
    const sumTotClaims   = confirmedSummary.reduce((s, r) => s + (r.totalClaims   || 0), 0);
    const sumTotUnits    = wbSummary.filter(r => r.units).reduce((s, r) => s + (r.units || 0), 0);
    const sumTotMileage  = wbSummary.filter(r => r.totalMileage).reduce((s, r) => s + (r.totalMileage || 0), 0);
    const avgCount       = confirmedSummary.length || 1;
    const totBase = { font: f11b, border: topThinBotDbl, alignment: center };
    setVal(sumTotalRow, 1, 'TOTAL', totBase);
    setFml(sumTotalRow, 2, `SUM(B${sumDataStart}:B${sumDataEnd})`, { ...totBase, numFmt: CUR, result: sumTotReserve });
    setFml(sumTotalRow, 3, `SUM(C${sumDataStart}:C${sumDataEnd})`, { ...totBase, numFmt: CUR, result: sumTotIncurred });
    setFml(sumTotalRow, 4, `SUM(D${sumDataStart}:D${sumDataEnd})`, { ...totBase, numFmt: INT, result: sumTotClaims });
    setFml(sumTotalRow, 5, `SUM(E${sumDataStart}:E${sumDataEnd})`, { ...totBase, numFmt: INT, result: sumTotUnits });
    setFml(sumTotalRow, 8, `SUM(H${sumDataStart}:H${sumDataEnd})`, { ...totBase, numFmt: INT, result: sumTotMileage });

    // ── AVERAGE PER YEAR row ──
    const avgBase = { font: f11b, alignment: center };
    setVal(sumAvgRow, 1, 'AVERAGE PER YEAR', { ...avgBase, border: topThinOnly });
    setFml(sumAvgRow, 2, `AVERAGE(B${sumDataStart}:B${sumDataEnd})`, { ...avgBase, numFmt: CUR, result: sumTotReserve  / avgCount });
    setFml(sumAvgRow, 3, `AVERAGE(C${sumDataStart}:C${sumDataEnd})`, { ...avgBase, numFmt: CUR, result: sumTotIncurred / avgCount });
    setFml(sumAvgRow, 4, `AVERAGE(D${sumDataStart}:D${sumDataEnd})`, { ...avgBase, numFmt: INT, result: Math.round(sumTotClaims / avgCount) });
    setFml(sumAvgRow, 5, `AVERAGE(E${sumDataStart}:E${sumDataEnd})`, { ...avgBase, numFmt: INT, result: Math.round(sumTotUnits  / avgCount) });
    setFml(sumAvgRow, 6, `IFERROR(D${sumAvgRow}/E${sumAvgRow},0)`,   { ...avgBase, numFmt: DEC2, result: sumTotUnits ? sumTotClaims / sumTotUnits : 0 });
    setFml(sumAvgRow, 8, `AVERAGE(H${sumDataStart}:H${sumDataEnd})`, { ...avgBase, numFmt: INT });

    // ── Fleet history table (columns I–N, alongside AL section) ──
    // Row histHeaderRow (= section title row): column headers
    // Row histHeaderRow+1 (= section col-header row): future-year placeholder
    // Rows histDataStart+ (= section data rows): 5 historical years, one per row
    const rsHdr = { font: f11b, border: allThin, alignment: center };
    ['Year', 'Units', 'Mileage PPU', 'Total Mileage', 'Revenue PPU', 'Total Revenue']
      .forEach((h, ci) => setVal(histHeaderRow, 9 + ci, h, rsHdr));

    // Future-year placeholder row (at the same row as AL section column headers)
    const futureYearRow = histHeaderRow + 1;
    if (wb_data.historical.length > 0) {
      const [fy1, fy2] = (wb_data.historical[0].year || '').split('-').map(Number);
      if (!isNaN(fy2)) {
        const fyShort = `${String(fy2).slice(-2)}-${String(fy2 + 1).slice(-2)}`;
        const rsBase  = { font: f11, border: allThin, alignment: center };
        setVal(futureYearRow, 9,  fyShort, rsBase);
        setFml(futureYearRow, 11, `IFERROR(L${futureYearRow}/J${futureYearRow},0)`, { ...rsBase, numFmt: INT });
        setFml(futureYearRow, 14, `IFERROR(J${futureYearRow}*M${futureYearRow},0)`, { ...rsBase, numFmt: CUR });
      }
    }

    // Historical data rows aligned with AL section data rows
    wb_data.historical.forEach((hist, i) => {
      const rsR    = histDataStart + i;
      const rsBase = { font: f11, border: allThin, alignment: center };
      setVal(rsR, 9,  hist.yearShort || '',     rsBase);
      setNum(rsR, 10, hist.units     || null,   { ...rsBase, numFmt: INT });
      setFml(rsR, 11, `IFERROR(L${rsR}/J${rsR},0)`, { ...rsBase, numFmt: INT });
      setNum(rsR, 12, hist.totalMileage || null, { ...rsBase, numFmt: INT });
      setVal(rsR, 13, null, rsBase);             // Revenue PPU — manual entry
      setFml(rsR, 14, `IFERROR(J${rsR}*M${rsR},0)`, { ...rsBase, numFmt: CUR });
    });

    // ── Loss section writer ────────────────────────────────────────────────────────
    // hasRecovery=true (AL only): 7 columns A–G with Recoveries header and SUM.
    // hasRecovery=false (cargo/PD/GL): 6 columns A–F, no recovery column.
    const writeLossSection = (sectionKey, headerRow, title, hasRecovery) => {
      const sectionRows = wb_data.sections[sectionKey] || [];
      const rowCount  = Math.max(sectionRows.length, 1); // at least 1 row for SUM formula
      const colHdrRow = headerRow + 1;
      const dataStart = headerRow + 2;
      const dataEnd   = dataStart + rowCount - 1;
      const totalRow  = dataEnd + 1;
      const lastCol   = hasRecovery ? 'G' : 'F';

      ws.mergeCells(`A${headerRow}:${lastCol}${headerRow}`);
      setVal(headerRow, 1, title, { font: f11b, border: botThinOnly, alignment: center });
      const colHeaders = ['Insurance Company','Policy Year','Policy Number','No. of Claims','Loss Reserve','Total Incurred'];
      if (hasRecovery) colHeaders.push('Recoveries');
      colHeaders.forEach((h, i) => setVal(colHdrRow, i + 1, h, { font: f11b, border: allThin, alignment: center }));

      for (let i = 0; i < rowCount; i++) {
        const row  = sectionRows[i] || null;
        const r    = dataStart + i;
        const base = { font: f11, border: allThin, alignment: center };
        setVal(r, 1, row?.insurer      || null, base);
        setVal(r, 2, row?.policyYear   || null, base);
        setVal(r, 3, row?.policyNumber || null,  base);
        setNum(r, 4, row?.numClaims    ?? null,  { ...base, numFmt: INT });
        setNum(r, 5, row?.lossReserve  ?? null,  { ...base, numFmt: CUR });
        setNum(r, 6, row?.totalIncurred ?? null, { ...base, numFmt: CUR });
        if (hasRecovery) {
          setNum(r, 7, row?.recovery ?? null, { ...base, numFmt: CUR });
        }
      }

      const confirmedSecRows = sectionRows.filter(r => r.lossRunStatus !== 'missing_loss_run' && r.lossRunStatus !== 'coverage_assumed');
      const secTotReserve  = confirmedSecRows.reduce((s, r) => s + (parseFloat(r.lossReserve)   || 0), 0);
      const secTotIncurred = confirmedSecRows.reduce((s, r) => s + (parseFloat(r.totalIncurred) || 0), 0);
      const secTotRecovery = confirmedSecRows.reduce((s, r) => s + (parseFloat(r.recovery)      || 0), 0);
      const totStyle = { font: f11b, border: topThinBotDbl, alignment: center };
      setVal(totalRow, 4, 'TOTAL', totStyle);
      setFml(totalRow, 5, `SUM(E${dataStart}:E${dataEnd})`, { ...totStyle, numFmt: CUR, result: secTotReserve });
      setFml(totalRow, 6, `SUM(F${dataStart}:F${dataEnd})`, { ...totStyle, numFmt: CUR, result: secTotIncurred });
      if (hasRecovery) {
        setFml(totalRow, 7, `SUM(G${dataStart}:G${dataEnd})`, { ...totStyle, numFmt: CUR, result: secTotRecovery });
      }

      return totalRow + 1; // first row after this section (used for next section start)
    };

    const sectionsToWrite = [
      ['autoLiability',    'AUTO LIABILITY LOSSES',      true],
      ['cargo',            'MOTOR TRUCK CARGO LOSSES',   false],
      ['physicalDamage',   'PHYSICAL DAMAGE LOSSES',     false],
      ['generalLiability', 'GENERAL LIABILITY LOSSES',   false],
    ];
    let nextSecRow = firstSecRow;
    for (const [sectionKey, title, hasRecovery] of sectionsToWrite) {
      const afterSection = writeLossSection(sectionKey, nextSecRow, title, hasRecovery);
      nextSecRow = afterSection + 2; // 2 blank rows between sections
    }

    // ── Download ──
    const buffer = await wb.xlsx.writeBuffer();
    const blob   = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url    = URL.createObjectURL(blob);
    const a      = document.createElement('a');
    a.href       = url;
    a.download   = `Loss Summary & Historicals - ${companyName || 'Carrier'}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ── Export Submission Tracker to Excel ──
  const exportTrackerExcel = async () => {
    const { default: ExcelJS } = await import('exceljs');
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Submission Tracker');

    const thin    = { style: 'thin' };
    const allThin = { top: thin, bottom: thin, left: thin, right: thin };
    const f14b    = { name: 'Calibri', size: 14, bold: true };
    const f10     = { name: 'Calibri', size: 10 };
    const f11b    = { name: 'Calibri', size: 11, bold: true };
    const f11     = { name: 'Calibri', size: 11 };
    const ctr     = { horizontal: 'center', vertical: 'middle' };
    const mid     = { vertical: 'middle' };

    const COLS = 16;
    ws.columns = [
      { width: 24 }, { width: 22 }, { width: 20 }, { width: 24 }, { width: 16 },
      { width: 16 }, { width: 10 }, { width: 16 }, { width: 14 }, { width: 12 },
      { width: 16 }, { width: 30 }, { width: 10 }, { width: 16 }, { width: 22 }, { width: 30 },
    ];

    const today = new Date().toISOString().slice(0, 10);
    let r = 1;

    // Title
    ws.mergeCells(`A${r}:P${r}`);
    Object.assign(ws.getCell(r, 1), {
      value: `${companyName || 'Account'} — Submission Tracker`,
      font: f14b, alignment: ctr,
    });
    ws.getRow(r).height = 24; r++;

    // Exported date
    ws.mergeCells(`A${r}:P${r}`);
    Object.assign(ws.getCell(r, 1), {
      value: `Exported: ${today}`,
      font: { ...f10, color: { argb: 'FF94A3B8' } }, alignment: ctr,
    });
    ws.getRow(r).height = 14; r++;

    r++; // blank row

    // Column headers
    const headers = [
      'Market Name', 'Status', 'Underwriter', 'UW Email', 'UW Phone',
      'Date Submitted', 'Days Submitted', 'Quoted Premium', 'Quote Date', 'Days to Quote',
      'Last Contacted', 'Notes', 'BOR Sent', 'BOR Date Sent', 'BOR Status', 'BOR Notes',
    ];
    headers.forEach((h, c) => {
      Object.assign(ws.getCell(r, c + 1), { value: h, font: f11b, border: allThin, alignment: ctr });
    });
    ws.getRow(r).height = 16; r++;

    // Data rows
    markets.forEach(mkt => {
      const bor = mkt.bor || {};
      const daysToQuote = (mkt.quotedAt && mkt.dateSubmitted)
        ? Math.max(0, Math.floor(
            (new Date(`${mkt.quotedAt}T12:00:00`) - new Date(`${mkt.dateSubmitted}T12:00:00`)) / 86400000
          ))
        : '';
      const row = [
        mkt.name                                        || '',
        MARKET_STATUS_MAP[mkt.status]?.label            || mkt.status || '',
        mkt.uwName                                      || '',
        mkt.uwEmail                                     || '',
        mkt.uwPhone                                     || '',
        mkt.dateSubmitted                               || '',
        daysSince(mkt.dateSubmitted)                    ?? '',
        mkt.quotedPremium                               || '',
        mkt.quotedAt                                    || '',
        daysToQuote,
        mkt.lastContactedAt                             || '',
        mkt.notes                                       || '',
        bor.sent ? 'Yes' : 'No',
        bor.dateSent                                    || '',
        BOR_STATUS_MAP[bor.status]?.label               || '',
        bor.notes                                       || '',
      ];
      row.forEach((v, c) => {
        Object.assign(ws.getCell(r, c + 1), { value: v, font: f11, border: allThin, alignment: mid });
      });
      r++;
    });

    const safe = (companyName || 'Account').replace(/[/\\?%*:|"<>]/g, '_');
    await _xlsxDownload(wb, `${safe}_Submission_Tracker_${today}.xlsx`);
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  // ── Document Parser ──
  const parseDocument = async (docId, combined = false) => {
    const files = documents[docId] || [];
    if (!files.length) return;
    setParseMsg('');
    setLoading(p => ({ ...p, parse: true }));

    const isDriverCategory = !combined && ['driverList', 'mvrs'].includes(docId);
    const isEquipmentCategory = !combined && docId === 'equipmentList';

    const driverPrompt = `These are CDL driver files, MVRs, or driver lists for a commercial trucking company. Extract every driver's information you can find across all documents. Return ONLY a JSON object:
{
  "drivers": [
    {
      "name": "First Last",
      "dob": "MM/DD/YYYY",
      "dl": "license number",
      "state": "two letter state",
      "exp": "years of experience as number",
      "hireDate": "MM/YYYY or MM/DD/YYYY"
    }
  ]
}
Look for: full names, dates of birth, license numbers, license states, CDL class, hire dates, years driving. These may be MVR reports, driver applications, or operator lists. Extract every person found. Return only the JSON. No explanation.`;

    const equipmentPrompt = `This is an equipment list or unit list for a commercial trucking company. Extract all vehicles. Return ONLY JSON:
{
  "tractors": [{"year": "", "make": "", "vin": "", "value": "", "lossPayee": ""}],
  "trailers": [{"year": "", "make": "", "vin": "", "value": "", "lossPayee": ""}]
}
For value: extract the stated value or ACV (e.g. "45000"). For lossPayee: extract the lienholder/loss payee name if listed. Return only the JSON. No explanation.`;

    const genericPrompt = `Extract all equipment and driver information from this document. Return JSON with this exact structure:
{
  "tractors": [{"year": "", "make": "", "vin": "", "value": "", "lossPayee": ""}],
  "trailers": [{"year": "", "make": "", "vin": "", "value": "", "lossPayee": ""}],
  "drivers": [{"name": "", "dob": "", "dl": "", "state": "", "exp": "", "hireDate": ""}]
}
If a field is not found leave it as empty string. Only return the JSON, nothing else.`;

    const prompt = isDriverCategory ? driverPrompt : isEquipmentCategory ? equipmentPrompt : genericPrompt;

    try {
      // For driver/mvr/combined categories process ALL uploaded files, otherwise just the first
      const rawFiles = (isDriverCategory || combined) ? (documents[docId] || []) : [files[0]];

      // Pre-resolve cloud { name, url } references to actual File objects.
      // Log each file's URL so we can diagnose fetch failures.
      setParseMsg('Loading files…');
      console.log(`[parseDocument] rawFiles for "${docId}":`, rawFiles.map(f =>
        f instanceof File ? `File("${f.name}", ${f.size}B)` : `{name:"${f?.name}", url:${f?.url ? f.url.slice(0, 60) + '…' : 'null'}}`
      ));

      // Deduplicate by URL/name so the same file uploaded to multiple doc slots
      // (e.g. same sheet in both equipmentList and driverList) isn't processed twice.
      const seen = new Set();
      const dedupedFiles = rawFiles.filter(f => {
        const key = f instanceof File ? (f.name + f.size) : (f?.url || f?.name || '');
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      const resolvedFiles = [];
      for (const f of dedupedFiles) {
        const resolved = await resolveToFile(f, carrierId);
        if (resolved) resolvedFiles.push(resolved);
      }
      if (!resolvedFiles.length) {
        const urls = dedupedFiles.map(f => f instanceof File ? '(local file)' : (f?.url || 'no URL')).join(', ');
        console.error('[parseDocument] All files failed to resolve. URLs attempted:', urls);
        throw new Error(
          'Could not load the file content. The file was likely uploaded in a previous session before cloud storage was active.\n\nFix: Delete the file from this document slot and re-upload it, then try parsing again.'
        );
      }

      const allBlocks = [];
      for (const file of resolvedFiles) {
        const rawBlocks = await readFileForAI(file);
        allBlocks.push(...rawBlocks);
      }
      const contentBlocks = guardApiContent(allBlocks, (w) => setParseMsg(w));
      const bodyStr = JSON.stringify({
        system: 'You are a data extraction specialist. Extract structured data from documents and return valid JSON only.',
        userContent: [...contentBlocks, { type: 'text', text: prompt }],
        maxTokens: 4096,
      });
      console.log('[parseDocument] docId:', docId, '| files:', resolvedFiles.length, '| body size:', Math.round(bodyStr.length / 1024), 'KB');
      const res = await apiFetch(`${API_BASE}/api/claude`, {
        method: 'POST',
        body: bodyStr,
      });
      let data;
      try { data = await res.json(); } catch { throw new Error(`HTTP ${res.status} — response was not JSON`); }
      if (!res.ok) {
        const msg = typeof data?.error === 'string' ? data.error : (data?.error?.message || JSON.stringify(data));
        throw new Error(`Server ${res.status}: ${msg}`);
      }
      const text = data.content?.[0]?.text || '';
      const jsonMatch = text.match(/[[{][\s\S]*[\]}]/);
      if (!jsonMatch) throw new Error('No JSON found in response: ' + text.slice(0, 200));
      const parsed = JSON.parse(jsonMatch[0]);
      const { tractors: rTractors, trailers: rTrailers } = reclassifyEquipment(parsed.tractors, parsed.trailers);
      const t  = rTractors.length || 0;
      const tr = rTrailers.length || 0;
      const d  = parsed.drivers?.length  || 0;
      if (t)  setTractors(rTractors.map(r => ({ year: r.year||'', make: r.make||'', vin: r.vin||'', value: r.value||'', loss_payee: r.lossPayee||r.lossPayer||'' })));
      if (tr) setTrailers(rTrailers.map(r => ({ year: r.year||'', make: r.make||'', vin: r.vin||'', value: r.value||'', loss_payee: r.lossPayee||r.lossPayer||'' })));
      if (d)  setDrivers(parsed.drivers.map(r => ({ name: r.name||'', dob: r.dob||'', dl_number: r.dl||'', state: r.state||'', yrs_exp: r.exp||'', hire_date: r.hireDate||'' })));

      if (isDriverCategory) {
        setParseMsg(`✓ Found ${d} driver${d !== 1 ? 's' : ''} — review and fill in any missing fields`);
      } else if (isEquipmentCategory) {
        setParseMsg(`✓ Found ${t} tractor${t !== 1 ? 's' : ''}, ${tr} trailer${tr !== 1 ? 's' : ''} — review values`);
      } else if (combined) {
        setParseMsg(`✓ Found: ${t} tractor${t!==1?'s':''}, ${tr} trailer${tr!==1?'s':''}, ${d} driver${d!==1?'s':''} — review Equipment and Drivers tabs`);
      } else {
        setParseMsg(`✓ Found: ${t} tractor${t!==1?'s':''}, ${tr} trailer${tr!==1?'s':''}, ${d} driver${d!==1?'s':''}`);
      }
    } catch (err) {
      console.error('[parseDocument] error:', err.message);
      setParseMsg(`⚠️ ${err.message}`);
    } finally {
      setLoading(p => ({ ...p, parse: false }));
    }
  };

  // ── CDL Image Parser ──
  const parseCDLImages = async () => {
    const files = documents['cdl'] || [];
    if (!files.length) return;
    setCdlMsg('');
    setCdlLoading(true);
    const CDL_IMAGE_MAX_BYTES = 5 * 1024 * 1024; // 5 MB per image
    const cdlPrompt = `You are reading one or more CDL (Commercial Driver's License) card images. Extract every driver's information visible on the card(s). Return ONLY a JSON object with no markdown:
{
  "drivers": [
    {
      "name": "First Last",
      "dob": "MM/DD/YYYY",
      "dl": "license number as printed",
      "state": "two letter state abbreviation",
      "cdl_class": "A, B, or C if visible",
      "cdl_expiration": "MM/DD/YYYY if visible",
      "endorsements": "comma-separated endorsement codes if visible",
      "exp": "",
      "hireDate": ""
    }
  ]
}
Only extract what is visible on the card. Do not guess or infer exp or hireDate — leave them empty string. Use MM/DD/YYYY for all dates. Use the 2-letter state abbreviation. Return strict JSON only. No explanation.`;
    try {
      const rawFiles = documents['cdl'] || [];
      const seen = new Set();
      const dedupedFiles = rawFiles.filter(f => {
        const key = f instanceof File ? (f.name + f.size) : (f?.url || f?.name || '');
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setCdlMsg('Loading CDL images…');
      const resolvedFiles = [];
      for (const f of dedupedFiles) {
        const resolved = await resolveToFile(f, carrierId);
        if (resolved) resolvedFiles.push(resolved);
      }
      if (!resolvedFiles.length) throw new Error('Could not load CDL image files. Try re-uploading.');
      for (const file of resolvedFiles) {
        if (file.size > CDL_IMAGE_MAX_BYTES) {
          throw new Error(`"${file.name}" is ${Math.round(file.size / 1024 / 1024 * 10) / 10} MB — please use an image under 5 MB.`);
        }
      }
      const allBlocks = [];
      for (const file of resolvedFiles) {
        const blocks = await readFileForAI(file);
        allBlocks.push(...blocks);
      }
      const bodyStr = JSON.stringify({
        system: 'You are a data extraction specialist. Extract structured data from CDL images and return valid JSON only.',
        userContent: [...allBlocks, { type: 'text', text: cdlPrompt }],
        maxTokens: 2048,
      });
      console.log('[parseCDLImages] files:', resolvedFiles.length, '| body size:', Math.round(bodyStr.length / 1024), 'KB');
      const res = await apiFetch(`${API_BASE}/api/claude`, { method: 'POST', body: bodyStr });
      let data;
      try { data = await res.json(); } catch { throw new Error(`HTTP ${res.status} — response was not JSON`); }
      if (!res.ok) {
        const msg = typeof data?.error === 'string' ? data.error : (data?.error?.message || JSON.stringify(data));
        throw new Error(`Server ${res.status}: ${msg}`);
      }
      const text = data.content?.[0]?.text || '';
      const jsonMatch = text.match(/[[{][\s\S]*[\]}]/);
      if (!jsonMatch) throw new Error('No JSON found in CDL response: ' + text.slice(0, 200));
      const parsed = JSON.parse(jsonMatch[0]);
      const incoming = (parsed.drivers || []).map(r => ({
        name:           r.name           || '',
        dob:            r.dob            || '',
        dl_number:      r.dl             || '',
        state:          r.state          || '',
        yrs_exp:        r.exp            || '',
        hire_date:      r.hireDate       || '',
        cdl_class:      r.cdl_class      || '',
        cdl_expiration: r.cdl_expiration || '',
        endorsements:   r.endorsements   || '',
      }));
      if (!incoming.length) {
        setCdlMsg('⚠️ No driver data found in CDL image(s). Check image quality and try again.');
        return;
      }
      setDrivers(prev => {
        const merged = mergeParsedDrivers(prev, incoming);
        const addedCount   = merged.length - prev.length;
        const updatedCount = incoming.length - addedCount;
        const parts = [];
        if (addedCount > 0)   parts.push(`${addedCount} driver${addedCount !== 1 ? 's' : ''} added`);
        if (updatedCount > 0) parts.push(`${updatedCount} driver${updatedCount !== 1 ? 's' : ''} updated`);
        setCdlMsg(`✓ CDL parsed: ${parts.join(', ') || 'no changes'}`);
        return merged;
      });
    } catch (err) {
      console.error('[parseCDLImages] error:', err.message);
      setCdlMsg(`⚠️ ${err.message}`);
    } finally {
      setCdlLoading(false);
    }
  };

  // ── CAB Report Reader ──
  const readCABReport = async () => {
    const files = documents['cab'] || [];
    if (!files.length) return;
    setCabMsg('');
    setCabLoading(true);
    const cabParseStart = Date.now();
    const logStage = (stage, extra = {}) => {
      console.log('[CAB parse]', stage, {
        accountId: carrierId || '(new)',
        stage,
        elapsedMs: Date.now() - cabParseStart,
        ...extra,
      });
    };
    logStage('started', { fileName: files[0]?.name, fileSize: files[0]?.size ?? '?' });
    try {
      const file = files[0];
      const ext = file.name.split('.').pop().toLowerCase();
      const CHUNK_SIZE = 60000;

      // Extract full text — PDF gets text-extracted, everything else via readFileForAI
      let fullText = '';
      if (ext === 'pdf' && (file instanceof File || file instanceof Blob)) {
        setCabMsg('Extracting text from PDF...');
        fullText = await extractPDFText(file);
      } else {
        const blocks = await readFileForAI(file, carrierId);
        fullText = blocks.map(b => b.text || '').join('\n');
      }
      logStage('pdf_extracted', { textLen: fullText.length });

      const extractionPrompt = `You are reading a CAB report from Central Analysis Bureau. Extract every piece of data you can find and return it as a single JSON object with this exact structure:

{
  "company": { "name": "", "dot": "", "mc": "", "address": "", "state": "", "phone": "", "email": "", "contacts": [], "firstAssigned": "", "status": "", "authorityType": "" },
  "scores": { "iss": "", "issRecommendation": "", "dotRating": "" },
  "basics": {
    "unsafeDriving": { "score": "", "alert": false },
    "hoursOfService": { "score": "", "alert": false },
    "driverFitness": { "score": "", "alert": false },
    "controlledSubstances": { "score": "", "alert": false },
    "vehicleMaintenance": { "score": "", "alert": false },
    "hazmat": { "score": "", "alert": false },
    "crashIndicator": { "score": "", "alert": false }
  },
  "oos": {
    "overall": { "total": "", "outOfService": "", "pct": "", "nationalAvg": "", "diff": "" },
    "vehicle": { "total": "", "outOfService": "", "pct": "", "nationalAvg": "", "diff": "" },
    "driver": { "total": "", "outOfService": "", "pct": "", "nationalAvg": "", "diff": "" }
  },
  "accidents": { "fatal": 0, "injury": 0, "tow": 0, "total": 0, "details": [] },
  "insurance": { "carrier": "", "policyNumber": "", "effectiveDate": "", "cancellationDate": "", "bipd": "", "cargo": "", "bond": "", "history": [{ "carrier": "", "effectiveDate": "", "cancellationDate": "", "bipd": "", "cargo": "" }] },
  "fleet": { "totalPowerUnits": 0, "ownedTractors": 0, "leasedTractors": 0, "leasedTrailers": 0, "drivers": 0, "cdlDrivers": 0, "ownerOperators": 0, "companyDrivers": 0, "annualMileage": 0, "operationClass": "", "carrierOperation": "", "hauledHazmat": false, "radiusDistribution": [], "history": [{ "date": "", "powerUnits": 0, "drivers": 0, "mileage": 0 }] },
  "cargo": [],
  "violations": { "vehicle": [], "driver": [], "hazmat": [] },
  "inspections": { "annual": [] },
  "alerts": []
}

IMPORTANT extraction notes:
- company.contacts: extract ALL contact/principal names as a string array (e.g. ["JOHN SMITH", "JANE DOE"])
- company.state: extract the 2-letter state abbreviation from the carrier's state of domicile (e.g. "PA", "TX", "CA") — look for it near the carrier name/address header or labeled as "State of Domicile"
- company.firstAssigned: extract the authority first assigned / effective date (format: MM/DD/YYYY)
- insurance.carrier: extract the FULL insurance company name (e.g. "TRISURA SPECIALTY INSURANCE COMPANY")
- insurance.cancellationDate: extract the policy expiration or cancellation date (format: MM/DD/YYYY)
- insurance.history: if the CAB report shows an insurance history table or list of prior carriers, extract ALL of them as an array — each with carrier, effectiveDate, cancellationDate, bipd, cargo. If no history table exists, return [].
- fleet.ownerOperators: number of owner-operator (leased on, IC) drivers — look for "Owner/Operators" or "O/O" count in the carrier profile; 0 if not found
- fleet.companyDrivers: number of company (W-2) drivers — look for "Company Drivers" count in the carrier profile; 0 if not found
- fleet.radiusDistribution: look for a "RADIUS BUCKET" or "Radius Distribution" table. It typically has columns for radius range and a PERCENT column.
  Common radius ranges you will see: "0-50", "51-200", "201+" (or "local", "51-200 miles", "over 200 miles").
  Extract as array of objects with string "range" and numeric "pct" (percentage as a number, not string — e.g. 57.14 not "57.14%").
  Example from a table showing 0-50=9.52%, 51-200=33.33%, 201+=57.14%:
  [{ "range": "0-50", "pct": 9.52 }, { "range": "51-200", "pct": 33.33 }, { "range": "201+", "pct": 57.14 }]
  If no radius table is found, return an empty array [].

Return only the JSON. No explanation. No markdown. Just the raw JSON object.`;

      const CAB_SYSTEM = 'You are a data extraction specialist. Extract all data from CAB reports and return structured JSON only.';

      const callCABPass = async (textChunk, passLabel) => {
        const userContent = [
          { type: 'text', text: `FILE: ${file.name}${passLabel}\n\n${textChunk}` },
          { type: 'text', text: extractionPrompt },
        ];
        const res = await apiFetch(`${API_BASE}/api/claude`, {
          method: 'POST',
          body: JSON.stringify({ system: CAB_SYSTEM, userContent, maxTokens: 8192 }),
        });
        let data;
        try { data = await res.json(); } catch { throw new Error(`HTTP ${res.status} — not JSON`); }
        if (!res.ok) throw new Error(`Server ${res.status}: ${typeof data?.error === 'string' ? data.error : (data?.error?.message || JSON.stringify(data))}`);
        const raw = data.content?.[0]?.text || '';
        return parseCABJson(raw, passLabel.includes('pages 11') ? 'CAB pass 2' : 'CAB pass 1');
      };

      const chunk1 = fullText.slice(0, CHUNK_SIZE);
      const chunk2 = fullText.length > CHUNK_SIZE ? fullText.slice(CHUNK_SIZE, CHUNK_SIZE * 2) : '';

      setCabMsg('Reading CAB report (pass 1)...');
      logStage('ai_pass1_started');
      let parsed = await callCABPass(chunk1, chunk2 ? ' — pages 1-10' : '');
      logStage('ai_pass1_completed');

      if (chunk2) {
        setCabMsg('Reading CAB report (pass 2)...');
        logStage('ai_pass2_started');
        const pass2 = await callCABPass(
          chunk2,
          ` — pages 11+ (continuation)\n\nPass 1 already extracted:\n${JSON.stringify(parsed, null, 2)}\n\nMerge pass 2 data into the existing object. Fill in any missing fields. Return the complete merged JSON.`
        );
        logStage('ai_pass2_completed');
        // Deep merge: pass2 fields win only if pass1 field was empty/zero
        const merge = (a, b) => {
          if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return b ?? a;
          const out = { ...a };
          for (const k of Object.keys(b)) {
            if (Array.isArray(b[k]) && b[k].length > 0 && (!a[k] || a[k].length === 0)) out[k] = b[k];
            else if (typeof b[k] === 'object' && !Array.isArray(b[k])) out[k] = merge(a[k], b[k]);
            else if (b[k] !== '' && b[k] !== 0 && b[k] !== false && (a[k] === '' || a[k] === 0)) out[k] = b[k];
          }
          return out;
        };
        parsed = merge(parsed, pass2);
      }

      // Log any type mismatches in the raw AI response before fixing them.
      validateCabSchema(parsed, 'AI response');
      // Normalize AI response so array fields are always arrays before hitting React state.
      // Prevents "alerts.map is not a function" TypeError that blanks the entire app.
      sanitizeCabData(parsed);
      logStage('response_validated');

      console.log('[readCABReport] Full fleet data:', parsed.fleet);
      console.log('[readCABReport] Radius data:', parsed.fleet?.radiusDistribution);
      console.log('[readCABReport] Full company data:', JSON.stringify(parsed.company));
      console.log('[readCABReport] Full insurance data:', JSON.stringify(parsed.insurance));
      setCabData(parsed);
      logStage('account_updated', { companyName: parsed.company?.name });
      if (parsed.company?.name) setCompanyName(parsed.company.name);
      if (parsed.company?.dot)  setDotNumber(parsed.company.dot);
      setCabMsg(`✓ CAB data extracted successfully${chunk2 ? ' (2-pass)' : ''}`);
    } catch (err) {
      console.error('[CAB parse] failed', {
        accountId: carrierId || '(new)',
        stage: 'error',
        errorType: err.constructor?.name || 'Error',
        message: err.message,
        elapsedMs: Date.now() - cabParseStart,
      });
      setCabMsg(`⚠️ ${err.message}`);
    } finally {
      setCabLoading(false);
    }
  };

  // ── Structured document parsers ──
  const parseStructuredDoc = async (docId, system, prompt, onSuccess, setLoading_, setMsg) => {
    const files = documents[docId] || [];
    if (!files.length) return;
    setMsg('');
    setLoading_(true);
    try {
      const contentBlocks = [];
      for (const file of files) {
        const blocks = await readFileForAI(file, carrierId);
        contentBlocks.push(...blocks);
      }
      const guardedBlocks = guardApiContent(contentBlocks, (w) => setMsg(w));
      const res = await apiFetch(`${API_BASE}/api/claude`, {
        method: 'POST',
        body: JSON.stringify({
          system,
          userContent: [...guardedBlocks, { type: 'text', text: prompt }],
          maxTokens: 8192,
        }),
      });
      let data;
      try { data = await res.json(); } catch { throw new Error(`HTTP ${res.status} — response was not JSON`); }
      if (!res.ok) throw new Error(`Server ${res.status}: ${typeof data?.error === 'string' ? data.error : (data?.error?.message || JSON.stringify(data))}`);
      const text = data.content?.[0]?.text || '';
      const jsonMatch = text.match(/[[{][\s\S]*[\]}]/);
      if (!jsonMatch) throw new Error('No JSON in response: ' + text.slice(0, 200));
      const parsed = JSON.parse(jsonMatch[0]);
      onSuccess(parsed);
    } catch (err) {
      console.error(`[parse ${docId}]`, err.message);
      setMsg(`⚠️ ${err.message}`);
    } finally {
      setLoading_(false);
    }
  };

  // ── Loss run training / feedback helpers ───────────────────────────────────

  const fetchLrTrainingExamples = async () => {
    try {
      const res  = await apiFetch(`${API_BASE}/api/training/loss-runs`);
      if (!res.ok) return [];
      const data = await res.json();
      setTrainingCount(data.count ?? 0);
      return data.examples || [];
    } catch {
      return [];
    }
  };

  const saveLrTrainingExample = async (grid, label = 'confirmed') => {
    const docs         = grid.extractedDocuments || [];
    const carrierNames = [...new Set(docs.map(d => d.carrierName).filter(Boolean))];
    const policyPeriods= docs
      .map(d => (d.effDate && d.expDate) ? `${d.effDate}–${d.expDate}` : '')
      .filter(Boolean);
    const example = {
      carrierNames,
      policyPeriods,
      correctGrid: {
        yearSlots:        grid.yearSlots,
        autoLiability:    grid.autoLiability,
        cargo:            grid.cargo,
        physicalDamage:   grid.physicalDamage,
        generalLiability: grid.generalLiability,
      },
      documentCount: (documents['lossRuns'] || []).length,
      savedAt:       new Date().toISOString(),
      savedBy:       currentUser || 'Carter',
      label,
    };
    try {
      const res  = await apiFetch(`${API_BASE}/api/training/loss-runs`, {
        method: 'POST',
        body:   JSON.stringify(example),
      });
      const d = await res.json();
      if (d.count != null) setTrainingCount(d.count);
      return !!d.success;
    } catch {
      return false;
    }
  };

  // Pick up to `n` most relevant examples by carrier name overlap (falls back to most recent)
  const pickRelevantExamples = (examples, carrierHints, n = 3) => {
    if (!examples.length) return [];
    const hints = (carrierHints || []).map(s => s.toLowerCase());
    const scored = examples.map(ex => {
      const names = (ex.carrierNames || []).map(s => s.toLowerCase());
      const score = hints.reduce((acc, h) => acc + (names.some(n => n.includes(h) || h.includes(n)) ? 1 : 0), 0);
      return { ex, score };
    });
    scored.sort((a, b) => b.score - a.score || 0); // stable: keeps recency order for ties
    return scored.slice(0, n).map(s => s.ex);
  };

  const verifyLossRuns = async () => {
    const files = documents['lossRuns'] || [];
    console.log('[verifyLossRuns] loss run files:', files.map(f => f.name));
    if (!files.length) return;
    setLossRunVerifyMsg('');
    setLrFeedbackMode('');
    setLrCorrectionGrid(null);
    setLrFeedbackMsg('');
    setLossRunVerifyLoading(true);
    try {
      // Fetch training examples before sending to AI (few-shot injection)
      const trainingExamples = await fetchLrTrainingExamples();
      const contentBlocks = [];
      for (const file of files) {
        const blocks = await readFileForAI(file, carrierId);
        contentBlocks.push(...blocks);
      }
      const guardedBlocks = guardApiContent(contentBlocks, (w) => setLossRunVerifyMsg(w));

      // ── LAYER 1: AI extraction (raw data only, no grid logic) ──────────────
      const extractPrompt = `You are a data extraction specialist. Extract structured data from each insurance loss run document.

For each document found, extract:
- carrierName: exact legal name from the document header
- policyNumber: exact policy number (strip any state-code prefix like "CA" or "PA" if it appears before the number)
- effDate: effective/inception date in MM/DD/YYYY format
- expDate: expiration date in MM/DD/YYYY format, or "active" if the policy has no expiration or is currently in force
- claimCount: total number of claims listed (integer)
- totalIncurred: total incurred/paid amount as a number
- coverageLines: array of coverage line types explicitly present in the document — use ONLY values from: "autoLiability", "cargo", "physicalDamage", "generalLiability". Look for section headers, coverage type columns, or explicit line labels. Do NOT infer lines that are not present.
- claims: array of individual claims

Return ONLY this JSON — no explanation, no markdown:
{
  "documents": [
    {
      "carrierName": "",
      "policyNumber": "",
      "effDate": "MM/DD/YYYY",
      "expDate": "MM/DD/YYYY",
      "claimCount": 0,
      "totalIncurred": 0,
      "coverageLines": ["autoLiability"],
      "claims": [
        {
          "date": "MM/DD/YYYY",
          "claimNumber": "",
          "status": "OPEN|CWP|CLSD",
          "paidAmount": 0,
          "driver": "",
          "vehicle": ""
        }
      ]
    }
  ]
}

IMPORTANT: Extract ONLY what you see in the documents.
Do NOT determine which years are covered.
Do NOT decide if any years are missing.
For coverageLines: only include lines that have an explicit section header, column, or label in the document. If coverage type is unclear, default to ["autoLiability"].
Just extract the raw policy dates, coverage lines, and claim data from each document.`;

      // ── Few-shot injection from confirmed training examples ─────────────────
      const fileNames = files.map(f => (typeof f === 'string' ? f : f.name || '')).filter(Boolean);
      const relevant  = pickRelevantExamples(trainingExamples, fileNames);
      const fewShot   = relevant.length > 0
        ? `\n\nHere are ${relevant.length} example(s) of correctly extracted loss run data from similar documents. Use these as guidance for extraction accuracy:\n${JSON.stringify(relevant.map(e => ({ carrierNames: e.carrierNames, policyPeriods: e.policyPeriods, correctGrid: e.correctGrid })), null, 2)}`
        : '';

      const res = await apiFetch(`${API_BASE}/api/claude`, {
        method: 'POST',
        body: JSON.stringify({
          system: 'You are a data extraction specialist. Extract insurance document data and return structured JSON only.',
          userContent: [...guardedBlocks, { type: 'text', text: extractPrompt + fewShot }],
          maxTokens: 2048,
        }),
      });
      let d;
      try { d = await res.json(); } catch { throw new Error(`HTTP ${res.status} — not JSON`); }
      if (!res.ok) throw new Error(`Server ${res.status}: ${typeof d?.error === 'string' ? d.error : (d?.error?.message || JSON.stringify(d))}`);
      const raw = d.content?.[0]?.text || '';
      const extracted = parseCABJson(raw, 'verifyLossRuns');
      console.log('[verifyLossRuns] Layer 1 — extracted documents:', JSON.stringify(extracted.documents));

      // ── LAYER 2: pure JS grid computation ──────────────────────────────────
      const yearSlots = computeYearSlots(5);
      const yib = parseFloat(leadSheet?.yearsInBusiness) || 0;
      const grid = buildLossRunGrid(extracted.documents || [], yearSlots, yib);
      grid.extractedDocuments = extracted.documents || [];
      console.log('[verifyLossRuns] Layer 2 — computed grid:', JSON.stringify(grid));

      setLossRunVerification(grid);

      // parseLossRunsIntoTab is the authoritative source for lossSummary insurer/policyNumber.
      // The grid's carrier field is first-doc-wins (AI return order) and credits package
      // carriers across all 4 sections — syncing it back here would overwrite the correct
      // section-accurate attribution and populate Cargo/PD/GL with carrier names from
      // policies that have no claims in those lines.

      const lines = ['autoLiability', 'cargo', 'physicalDamage', 'generalLiability'];
      const completeCounts = lines.filter(k => (grid[k] || []).every(e => e.present)).length;
      setLossRunVerifyMsg(`✓ Verified — ${completeCounts}/4 lines complete across ${yearSlots.length} years`);

      // ── Auto-compare vs APP loss summary ────────────────────────────────────
      // If application loss summary is present, check how closely the AI extraction
      // matches. 90%+ agreement → auto-save as confirmed training example.
      const appLs = appData?.lossSummary;
      if (appLs && lines.some(k => (appLs[k] || []).some(r => r.insurer))) {
        let total = 0, agree = 0;
        for (const k of lines) {
          for (const slot of yearSlots) {
            const appRow   = (appLs[k] || []).find(r => r.policyYear === slot);
            const gridCell = (grid[k]   || []).find(e => e.year      === slot);
            const appSays  = !!(appRow  && appRow.insurer);
            const gridSays = !!(gridCell && gridCell.present);
            total++;
            if (appSays === gridSays) agree++;
          }
        }
        const pct = total > 0 ? agree / total : 0;
        console.log(`[verifyLossRuns] APP vs Verify agreement: ${Math.round(pct * 100)}% (${agree}/${total})`);
        if (pct >= 0.9) {
          const saved = await saveLrTrainingExample(grid, 'auto-confirmed');
          if (saved) setLrFeedbackMsg('📊 Auto-saved as training example — APP and verification matched.');
        } else {
          const diffCount = total - agree;
          setLrFeedbackMsg(`⚠️ ${diffCount} cell${diffCount > 1 ? 's' : ''} differ between the uploaded application and this verification. Review below and click "Mark as Correct" or "Fix Errors".`);
        }
      }
    } catch (err) {
      console.error('[verifyLossRuns]', err.message);
      setLossRunVerifyMsg(`⚠️ ${err.message}`);
    } finally {
      setLossRunVerifyLoading(false);
    }
  };

  // ── Application template training helpers ──────────────────────────────────

  const fetchAppTemplates = async () => {
    try {
      const res  = await apiFetch(`${API_BASE}/api/training/app-templates`);
      if (!res.ok) return [];
      const data = await res.json();
      setAppTemplateCount(data.count ?? 0);
      return data.templates || [];
    } catch {
      return [];
    }
  };

  const saveAppTemplate = async (parsed) => {
    const tmpl = parsed.template || {};
    if (!tmpl.fields || !tmpl.fields.length) return;
    const template = {
      source:    tmpl.source || '',
      fields:    tmpl.fields,
      savedAt:   new Date().toISOString(),
      savedBy:   currentUser || 'Carter',
    };
    try {
      const res  = await apiFetch(`${API_BASE}/api/training/app-templates`, {
        method: 'POST',
        body:   JSON.stringify(template),
      });
      const d = await res.json();
      if (d.count != null) setAppTemplateCount(d.count);
    } catch (err) {
      console.warn('[saveAppTemplate]', err.message);
    }
  };

  const saveAppFieldMapping = async (fieldName, filledValue, source, confirmedValue) => {
    const mapping = {
      fieldName,
      filledValue,
      confirmedValue: confirmedValue ?? filledValue,
      source,
      confirmedAt: new Date().toISOString(),
      confirmedBy: currentUser || 'Carter',
    };
    try {
      await apiFetch(`${API_BASE}/api/training/app-field-mappings`, {
        method: 'POST',
        body:   JSON.stringify(mapping),
      });
    } catch (err) {
      console.warn('[saveAppFieldMapping]', err.message);
    }
  };

  const autoFillApplication = async () => {
    setAutoFillMsg('');
    setAutoFillLoading(true);
    try {
      // Fetch stored templates and confirmed field mappings in parallel
      const [templates, mappingsRes] = await Promise.all([
        fetchAppTemplates(),
        apiFetch(`${API_BASE}/api/training/app-field-mappings`).then(r => r.ok ? r.json() : { mappings: [] }).catch(() => ({ mappings: [] })),
      ]);
      const confirmedMappings = mappingsRes.mappings || [];

      if (!templates.length) {
        setAutoFillMsg('⚠️ No application templates learned yet. Upload and parse a completed application first.');
        setAutoFillLoading(false);
        return;
      }

      // Build carrier data snapshot for the AI
      const carrierSnapshot = {
        companyName, dotNumber,
        company:     appData?.company    || {},
        operations:  appData?.operations || {},
        underwriting:appData?.underwriting || {},
        leadSheet: {
          ownerName:       leadSheet.ownerName,
          fein:            leadSheet.fein,
          phone:           leadSheet.phone,
          garagingAddress: leadSheet.garagingAddress,
          currentInsurer:  leadSheet.currentInsurer,
          currentXDate:    leadSheet.currentXDate,
          yearsInBusiness: leadSheet.yearsInBusiness,
          radiusOfOperation: leadSheet.radiusOfOperation,
          eldsInstalled:   leadSheet.eldsInstalled,
          eldBrand:        leadSheet.eldBrand,
          dashcams:        leadSheet.dashcams,
          dashcamBrand:    leadSheet.dashcamBrand,
          beingNonRenewed: leadSheet.beingNonRenewed,
          nonRenewalReason:leadSheet.nonRenewalReason,
          ownerOpsOrCompany: leadSheet.ownerOpsOrCompany,
          driversOutOfState: leadSheet.driversOutOfState,
        },
        tractors: tractors.filter(t => t.year || t.make),
        trailers: trailers.filter(t => t.year || t.make),
        drivers:  drivers.filter(d => d.name),
        cabData:  cabData ? { company: cabData.company, fleet: cabData.fleet, basics: cabData.basics } : null,
        coiData,
      };

      // Use the most recent template as the target
      const targetTemplate = templates[0];

      const autoFillPrompt = `You are an insurance application auto-fill assistant for a commercial trucking submission.

APPLICATION TEMPLATE TO FILL:
Source: ${targetTemplate.source || 'unknown'}
Fields:
${JSON.stringify(targetTemplate.fields, null, 2)}

CARRIER DATA:
${JSON.stringify(carrierSnapshot, null, 2)}

${confirmedMappings.length > 0 ? `PREVIOUSLY CONFIRMED FIELD MAPPINGS (use these first — they are known-correct):
${JSON.stringify(confirmedMappings.slice(0, 30), null, 2)}` : ''}

For each field in the template, fill in the best value from the carrier data.
Assign a confidence level:
- "high" — value found directly and exactly in carrier data
- "medium" — value inferred or calculated from related data
- "low" — value estimated, not found, or requires manual input

Return ONLY this JSON — no explanation, no markdown:
{
  "fields": [
    {
      "fieldName": "...",
      "location": "...",
      "filledValue": "...",
      "confidence": "high|medium|low",
      "source": "brief note on where this value came from"
    }
  ]
}`;

      const res = await apiFetch(`${API_BASE}/api/claude`, {
        method: 'POST',
        body:   JSON.stringify({
          system:     'You are an insurance application auto-fill assistant. Return structured JSON only.',
          userContent:[{ type: 'text', text: autoFillPrompt }],
          maxTokens:  3000,
        }),
      });
      let d;
      try { d = await res.json(); } catch { throw new Error(`HTTP ${res.status} — not JSON`); }
      if (!res.ok) throw new Error(`Server ${res.status}: ${typeof d?.error === 'string' ? d.error : (d?.error?.message || JSON.stringify(d))}`);
      const raw = d.content?.[0]?.text || '';
      const m   = raw.match(/\{[\s\S]*\}/);
      if (!m) throw new Error('No JSON in response: ' + raw.slice(0, 200));
      const result = JSON.parse(m[0]);
      console.log('[autoFillApplication] result:', JSON.stringify(result).slice(0, 400));

      // Build modal state — add accepted/edited fields tracking
      const fields = (result.fields || []).map(f => ({
        ...f,
        editedValue: f.filledValue,
        accepted:    f.confidence === 'high', // pre-accept high-confidence fields
        confirmed:   false,
      }));
      setAutoFillModal({ fields });
    } catch (err) {
      console.error('[autoFillApplication]', err.message);
      setAutoFillMsg(`⚠️ ${err.message}`);
    } finally {
      setAutoFillLoading(false);
    }
  };

  // ── Application & Loss Summary parser ──────────────────────────────────────
  const parseApplication = async () => {
    const files = documents['appLossSummary'] || [];
    if (!files.length) return;
    setAppMsg('');
    setAppLoading(true);
    try {
      const contentBlocks = [];
      for (const file of files) {
        const resolved = await resolveToFile(file, carrierId);
        if (resolved) {
          const blocks = await readFileForAI(resolved);
          contentBlocks.push(...blocks);
        }
      }
      if (!contentBlocks.length) throw new Error('Could not read files — try re-uploading');
      const guarded = guardApiContent(contentBlocks, (w) => setAppMsg(w));

      const prompt = `You are a data extraction specialist. Extract all data from this wholesale insurance application and/or loss summary document.

Return ONLY this JSON — no explanation, no markdown:
{
  "company": {
    "name": "",
    "dot": "",
    "mc": "",
    "fein": "",
    "ownerName": "",
    "ownerDob": "",
    "phone": "",
    "email": "",
    "address": ""
  },
  "operations": {
    "yearsInBusiness": 0,
    "powerUnits": 0,
    "trailers": 0,
    "drivers": 0,
    "radiusOfOperation": "",
    "commodities": "",
    "eldBrand": "",
    "dashcamBrand": "",
    "ownerOps": 0,
    "companyDrivers": 0
  },
  "underwriting": {
    "beingNonRenewed": false,
    "nonRenewalReason": "",
    "planningAddTrucks": false,
    "addTrucksCount": 0,
    "driversOutOfState": false,
    "fleetChangeLastYear": false
  },
  "commodityCheckboxes": {
    "flatBed":    { "oversize": false, "overweight": false, "steel": false, "steelCoils": false, "oilfieldEquipment": false, "logs": false },
    "dryVan":     { "amazonDelivery": false, "householdGoods": false, "doublesTriples": false, "lastMileDelivery": false, "electronics": false, "liquor": false, "usMail": false, "cigarettes": false, "designerClothing": false },
    "reefer":     { "seafood": false, "eggs": false, "dairy": false, "shellfish": false, "hangingMeat": false, "frozenMeat": false, "flowerNursery": false },
    "autoHauler": { "newVehicles": false, "usedVehicles": false, "dealerToManufacturer": false, "dealerToDealer": false, "exoticLuxury": false, "classicAntique": false }
  },
  "lossSummary": {
    "autoLiability":    [{ "policyYear": "YYYY-YYYY", "insurer": "", "policyNumber": "", "effDate": "", "expDate": "", "numClaims": 0, "lossReserve": 0, "totalIncurred": 0, "openClaims": 0 }],
    "cargo":            [{ "policyYear": "YYYY-YYYY", "insurer": "", "policyNumber": "", "effDate": "", "expDate": "", "numClaims": 0, "lossReserve": 0, "totalIncurred": 0, "openClaims": 0 }],
    "physicalDamage":   [{ "policyYear": "YYYY-YYYY", "insurer": "", "policyNumber": "", "effDate": "", "expDate": "", "numClaims": 0, "lossReserve": 0, "totalIncurred": 0, "openClaims": 0 }],
    "generalLiability": [{ "policyYear": "YYYY-YYYY", "insurer": "", "policyNumber": "", "effDate": "", "expDate": "", "numClaims": 0, "lossReserve": 0, "totalIncurred": 0, "openClaims": 0 }]
  },
  "template": {
    "source": "",
    "fields": [
      { "fieldName": "", "location": "", "value": "" }
    ]
  }
}

EXTRACTION NOTES:
- policyYear format: "YYYY-YYYY" (e.g. "2024-2025") — use the year the policy started and the year it expires
- If the loss summary shows a single combined section (not split by line), duplicate the same data across all four coverage lines
- numClaims, lossReserve, totalIncurred, openClaims: extract numeric values only (no $ signs or commas)
- lossReserve: the outstanding reserve amount (may be labeled "reserve", "open reserve", "outstanding reserve")
- openClaims: number of currently open/pending claims (may be labeled "open", "pending", "outstanding claims"); use 0 if not stated
- If a field is not present in the document, leave it as the default (empty string or 0)
- Extract ALL years shown in the loss summary — include every row, not just the most recent
- ownerOps: number of owner-operator/leased-on drivers; companyDrivers: number of W-2 company drivers
- eldBrand / dashcamBrand: exact brand name (e.g. "Samsara", "Motive", "Lytx"); leave blank if not mentioned
- For underwriting yes/no questions: set true/false based on the applicant's answers
- commodityCheckboxes: read the application for what the insured hauls (look for words like "commodity", "freight", "cargo", "hauls", "transports", "dry van", "flatbed", "reefer", "auto hauler", "car hauler" etc.) and set the matching boolean flags to true. Flat Bed items: oversize, overweight, steel, steelCoils, oilfieldEquipment, logs. Dry Van items: amazonDelivery, householdGoods, doublesTriples, lastMileDelivery, electronics, liquor, usMail, cigarettes, designerClothing. Reefer items: seafood, eggs, dairy, shellfish, hangingMeat, frozenMeat, flowerNursery. Auto Hauler items: newVehicles, usedVehicles, dealerToManufacturer, dealerToDealer, exoticLuxury, classicAntique. Only set flags that are clearly mentioned — do not guess.
- template.source: the name of the insurance company or safety company that produced this application form (look for a logo, header, or footer), or "" if unknown
- template.fields: list every labeled field you see in the document — field name (label), rough location on the page (e.g. "page 1 top", "section 2", "loss summary table"), and the filled-in value. This captures the document structure for future auto-fill training. Include all fields, even those not in the structured sections above.`;

      const res = await apiFetch(`${API_BASE}/api/claude`, {
        method: 'POST',
        body: JSON.stringify({
          system: 'You are a data extraction specialist. Extract insurance application data and return structured JSON only.',
          userContent: [...guarded, { type: 'text', text: prompt }],
          maxTokens: 3000,
        }),
      });
      let d;
      try { d = await res.json(); } catch { throw new Error(`HTTP ${res.status} — not JSON`); }
      if (!res.ok) throw new Error(`Server ${res.status}: ${typeof d?.error === 'string' ? d.error : (d?.error?.message || JSON.stringify(d))}`);
      const raw = d.content?.[0]?.text || '';
      const m = raw.match(/\{[\s\S]*\}/);
      if (!m) throw new Error('No JSON in response: ' + raw.slice(0, 200));
      const parsed = JSON.parse(m[0]);
      console.log('[parseApplication] extracted:', JSON.stringify(parsed));
      setAppData(parsed);

      // ── Auto-populate Lead Sheet from application data ──
      const updates = {};
      const c = parsed.company    || {};
      const o = parsed.operations || {};
      const u = parsed.underwriting || {};

      if (c.name  && !companyName) setCompanyName(c.name);
      if (c.dot   && !dotNumber)   setDotNumber(c.dot);
      if (c.fein)        updates.fein           = c.fein;
      if (c.ownerName)   updates.ownerName      = c.ownerName;
      if (c.ownerDob)    updates.ownerDob       = c.ownerDob;
      if (c.phone)       updates.phone          = c.phone;
      if (c.address)     updates.garagingAddress = c.address;

      if (o.yearsInBusiness > 0) updates.yearsInBusiness = String(o.yearsInBusiness);
      if (o.radiusOfOperation)   updates.radiusOfOperation = o.radiusOfOperation;
      if (o.commodities)         setCommodities(o.commodities);

      // ── Commodity checkboxes from application ──
      const cb = parsed.commodityCheckboxes || {};
      const hasCbData = ['flatBed', 'dryVan', 'reefer', 'autoHauler'].some(k => cb[k] && Object.values(cb[k]).some(Boolean));
      if (hasCbData) {
        setLeadSheet(prev => {
          const next = { ...prev };
          for (const group of ['flatBed', 'dryVan', 'reefer']) {
            if (cb[group]) next[group] = { ...prev[group], ...cb[group] };
          }
          if (cb.autoHauler) next.autoHauler = { ...prev.autoHauler, ...cb.autoHauler };
          return next;
        });
      }

      if (o.eldBrand) {
        updates.eldsInstalled = 'yes';
        updates.eldBrand = o.eldBrand;
      }
      if (o.dashcamBrand) {
        updates.dashcams = 'yes';
        updates.dashcamBrand = o.dashcamBrand;
      }
      const oo = parseInt(o.ownerOps)       || 0;
      const cd = parseInt(o.companyDrivers) || 0;
      if (oo > 0 && cd === 0) updates.ownerOpsOrCompany = 'owner-ops';
      else if (cd > 0 && oo === 0) updates.ownerOpsOrCompany = 'company';
      else if (oo > 0 && cd > 0)  updates.ownerOpsOrCompany = 'both';

      if (u.beingNonRenewed != null)  updates.beingNonRenewed  = u.beingNonRenewed  ? 'yes' : 'no';
      if (u.nonRenewalReason)         updates.nonRenewalReason = u.nonRenewalReason;
      if (u.planningAddTrucks != null) updates.planningAddTrucks = u.planningAddTrucks ? 'yes' : 'no';
      if (u.addTrucksCount > 0)        updates.addTrucksCount  = String(u.addTrucksCount);
      if (u.driversOutOfState != null) updates.driversOutOfState = u.driversOutOfState ? 'yes' : 'no';
      if (u.fleetChangeLastYear != null) updates.fleetChangeLastYear = u.fleetChangeLastYear ? 'yes' : 'no';

      if (Object.keys(updates).length) {
        setLeadSheet(prev => ({ ...prev, ...updates }));
      }

      // ── Override Loss Summary tables with APP data (highest priority source) ──
      const ls = parsed.lossSummary || {};
      const LR_SECTIONS = ['autoLiability', 'cargo', 'physicalDamage', 'generalLiability'];
      const hasAppLossData = LR_SECTIONS.some(k => (ls[k] || []).some(r => r.insurer));

      if (hasAppLossData) {
        setLossSummary(prev => {
          const next = { ...prev };
          for (const section of LR_SECTIONS) {
            const appRows = ls[section] || [];
            if (!appRows.length) continue;
            const existingYears = new Set(prev[section].map(r => r.policyYear));
            // Update matched years — APP always wins
            next[section] = prev[section].map(row => {
              const match = appRows.find(r => r.policyYear === row.policyYear);
              if (!match) return row;
              // For coverage_assumed rows: preserve financial fields as-is (null) and
              // stash APP-reported values in appReportedOverride for producer review.
              // This prevents APP data from silently promoting an unverified section to
              // a confirmed zero-loss history.
              if (row.lossRunStatus === 'coverage_assumed') {
                const hasAppFinancials = match.numClaims != null || match.lossReserve != null || match.totalIncurred != null;
                return {
                  ...row,
                  fromApp:      true,
                  insurer:      match.insurer      || row.insurer,
                  policyNumber: match.policyNumber || row.policyNumber,
                  effDate:      match.effDate       || row.effDate       || '',
                  expDate:      match.expDate       || row.expDate       || '',
                  openClaims:   match.openClaims    != null ? String(match.openClaims) : (row.openClaims || ''),
                  ...(hasAppFinancials ? {
                    appReportedOverride: {
                      numClaims:     match.numClaims     != null ? String(match.numClaims)     : null,
                      lossReserve:   match.lossReserve   != null ? String(match.lossReserve)   : null,
                      totalIncurred: match.totalIncurred != null ? String(match.totalIncurred) : null,
                    },
                    needsReview: true,
                  } : {}),
                };
              }
              return {
                ...row,
                fromApp:       true,
                insurer:       match.insurer       || row.insurer,
                policyNumber:  match.policyNumber  || row.policyNumber,
                effDate:       match.effDate        || row.effDate       || '',
                expDate:       match.expDate        || row.expDate       || '',
                numClaims:     match.numClaims     != null ? String(match.numClaims)     : row.numClaims,
                lossReserve:   match.lossReserve   != null ? String(match.lossReserve)   : (row.lossReserve || ''),
                totalIncurred: match.totalIncurred != null ? String(match.totalIncurred) : row.totalIncurred,
                openClaims:    match.openClaims    != null ? String(match.openClaims)    : (row.openClaims  || ''),
              };
            });
            // Append APP rows for years not already in the table
            for (const appRow of appRows) {
              if (!existingYears.has(appRow.policyYear) && appRow.insurer) {
                next[section] = [...next[section], {
                  policyYear:    appRow.policyYear,
                  fromApp:       true,
                  insurer:       appRow.insurer       || '',
                  policyNumber:  appRow.policyNumber  || '',
                  effDate:       appRow.effDate        || '',
                  expDate:       appRow.expDate        || '',
                  numClaims:     appRow.numClaims     != null ? String(appRow.numClaims)     : '',
                  lossReserve:   appRow.lossReserve   != null ? String(appRow.lossReserve)   : '',
                  totalIncurred: appRow.totalIncurred != null ? String(appRow.totalIncurred) : '',
                  openClaims:    appRow.openClaims    != null ? String(appRow.openClaims)    : '',
                }];
              }
            }
          }
          return next;
        });

        // ── Build lossRunVerification grid directly from APP loss summary ──
        // No need to run Verify Loss Runs — mark years as present if APP has data for them.
        const yearSlots = computeYearSlots(5);
        const appGrid = { yearSlots, fromApp: true };
        for (const section of LR_SECTIONS) {
          const appRows = ls[section] || [];
          appGrid[section] = yearSlots.map(slot => {
            const row = appRows.find(r => r.policyYear === slot);
            const present = !!(row && row.insurer);
            return {
              year:         slot,
              present,
              active:       false,
              carrier:      row?.insurer       || '',
              policyNumber: row?.policyNumber  || '',
              claims:       typeof row?.numClaims      === 'number' ? row.numClaims      : 0,
              incurred:     typeof row?.totalIncurred  === 'number' ? row.totalIncurred  : 0,
            };
          });
        }
        setLossRunVerification(appGrid);

        // ── Expose APP loss data to AI context blocks (used in narrative/risk prompts) ──
        setLossRunsData({ source: 'appLossSummary', ...ls });
      }

      // ── Save document structure as a template for auto-fill training ──
      await saveAppTemplate(parsed);

      const fieldCount = Object.keys(updates).length;
      const tmplFields = (parsed.template?.fields || []).length;
      setAppMsg(`✓ Application parsed — ${fieldCount} fields populated${tmplFields ? ` · ${tmplFields} template fields learned` : ''}`);
    } catch (err) {
      console.error('[parseApplication]', err.message);
      setAppMsg(`⚠️ ${err.message}`);
    } finally {
      setAppLoading(false);
    }
  };

  const parseCOI = () => parseStructuredDoc(
    'coi',
    'You are a data extraction specialist. Extract certificate of insurance data and return structured JSON only.',
    `Extract the certificate of insurance data from this document. Return a single JSON object:
{
  "carrierName": "primary AL carrier full legal name",
  "policyNumber": "primary AL policy number",
  "effectiveDate": "MM/DD/YYYY",
  "expirationDate": "MM/DD/YYYY",
  "bipdLimit": "",
  "cargoLimit": "",
  "additionalPolicies": [
    {
      "carrierName": "carrier full legal name",
      "policyNumber": "policy number exactly as shown",
      "effectiveDate": "MM/DD/YYYY",
      "expirationDate": "MM/DD/YYYY",
      "coverageLine": "cargo | physicalDamage | generalLiability | autoLiability"
    }
  ]
}
The additionalPolicies array must include ALL non-primary policies listed (motor truck cargo, physical damage/APD, general liability, umbrella — any carrier or policy number distinct from the primary AL policy). Use: "autoLiability" for primary AL, "cargo" for motor truck cargo/MTC, "physicalDamage" for physical damage/APD, "generalLiability" for GL/umbrella. If no additional policies exist, use an empty array.
Return only the JSON object. No explanation. No markdown.`,
    (parsed) => {
      setCoiData(parsed);
      setCoiMsg(`✓ COI extracted — expires ${parsed.expirationDate || 'unknown'}`);
    },
    setCoiLoading,
    setCoiMsg
  );

  const parseIFTA = () => parseStructuredDoc(
    'ifta',
    'You are a data extraction specialist. Extract IFTA report data and return structured JSON only.',
    `Extract IFTA report data from these documents. Return a JSON array where each element is one quarter.
Each element must have EXACTLY these three fields and no others:
  "quarter"    — quarter label string (e.g. "Q1 2024")
  "totalMiles" — total miles across all jurisdictions as a string (e.g. "45,230")
  "states"     — array of two-letter jurisdiction abbreviation strings ONLY (e.g. ["TX", "OK", "KS"])
Do NOT include per-state mileage, fuel gallons, tax amounts, tax rates, credits, or any other breakdown.
Do NOT add fields beyond the three listed above.
Return only the JSON array. No markdown. No explanation.`,
    (parsed) => {
      const arr = Array.isArray(parsed) ? parsed : [parsed];
      setIftaData(arr);
      setIftaMsg(`✓ ${arr.length} quarter${arr.length !== 1 ? 's' : ''} extracted`);
    },
    setIftaLoading,
    setIftaMsg
  );

  // ── Parse loss runs directly into the Loss Summary tab ──
  const parseLossRunsIntoTab = async () => {
    console.log('PARSER VERSION: multi-year-split-v2');
    const rawEntries = documents['lossRuns'] || [];
    if (!rawEntries.length) {
      setLossRunsMsg('⚠️ No loss run files uploaded');
      return;
    }
    setLossRunsMsg('Resolving files…');
    setLossRunsLoading(true);
    // Resolve cloud { name, url } entries to real File objects
    const files = [];
    for (const f of rawEntries) {
      const resolved = await resolveToFile(f, carrierId);
      if (resolved) files.push(resolved);
    }
    if (!files.length) {
      setLossRunsMsg('⚠️ No readable files found — files may still be uploading or re-upload to parse');
      setLossRunsLoading(false);
      return;
    }
    setLossRunsMsg('');
    let parseSucceeded = false;
    try {
      // ── Branch 1: Excel file — try reading from known cell positions ──
      const excelFile = files.find(f => /\.(xlsx|xls)$/i.test(f.name));
      if (excelFile) {
        const buf = await fileToArrayBuffer(excelFile);
        const wb  = XLSX.read(buf, { type: 'array' });
        const ws  = wb.Sheets[wb.SheetNames[0]];
        if (ws) {
          const gc = (addr) => { const c = ws[addr]; return c ? String(c.v ?? '') : ''; };
          // Detect safety company format (AUTO LIABILITY at A13) vs our own template (A15)
          const isSafetyFormat  = /auto.*liability/i.test(gc('A13'));
          const isOurTemplate   = !isSafetyFormat && (
            /auto.*liability/i.test(gc('A15')) || /\d{4}-\d{4}/.test(gc('B17')) || gc('A5') !== ''
          );

          const buildSummary = (rows) => {
            const fleetMap = computeFleetSummary(cabData?.fleet);
            return rows.map(row => {
              const vals = fleetMap[row.policyYear];
              if (!vals) return row;
              return {
                ...row,
                ...(vals.units        && !row.units        ? { units:        vals.units        } : {}),
                ...(vals.totalMileage && !row.totalMileage ? { totalMileage: vals.totalMileage } : {}),
              };
            });
          };

          const makeReadSection = (numRows) => (startRow) =>
            Array.from({ length: numRows }, (_, i) => ({
              insurer:       gc(`A${startRow + i}`),
              policyYear:    gc(`B${startRow + i}`) || POLICY_YEARS[i],
              policyNumber:  gc(`C${startRow + i}`),
              numClaims:     gc(`D${startRow + i}`),
              lossReserve:   gc(`E${startRow + i}`),
              totalIncurred: gc(`F${startRow + i}`),
            }));

          if (isSafetyFormat) {
            // Safety company format: AUTO at A13, data rows 15-17; Cargo A21 data 23-25; PD A29 data 31-33; GL A37 data 39-41
            const readSection = makeReadSection(5);
            // Summary: rows 5-7 have data, row 8 blank, row 9 = TOTAL — read only real data rows
            const summary = Array.from({ length: 5 }, (_, i) => {
              const yr = gc(`A${5 + i}`);
              if (!yr || /total|average/i.test(yr)) return { policyYear: POLICY_YEARS[i], lossReserve: '', totalIncurred: '', totalClaims: '', units: '', totalMileage: '' };
              return {
                policyYear:    yr,
                lossReserve:   gc(`B${5 + i}`),
                totalIncurred: gc(`C${5 + i}`),
                totalClaims:   gc(`D${5 + i}`),
                units:         gc(`E${5 + i}`),
                totalMileage:  gc(`H${5 + i}`),
              };
            });
            setLossSummary({
              summary:         buildSummary(summary),
              autoLiability:   readSection(15),
              cargo:           readSection(23),
              physicalDamage:  readSection(31),
              generalLiability:readSection(39),
            });
            const total = [15, 23, 31, 39].reduce((s, r) =>
              s + Array.from({ length: 5 }, (_, i) => parseInt(gc(`D${r + i}`) || '0', 10)).reduce((a, b) => a + b, 0), 0);
            setLossRunsMsg(`✓ Excel template loaded — ${total} total claims across all lines`);
            return;
          }

          if (isOurTemplate) {
            const readSection = makeReadSection(5);
            const summary = Array.from({ length: 5 }, (_, i) => ({
              policyYear:    gc(`A${5 + i}`) || POLICY_YEARS[i],
              lossReserve:   gc(`B${5 + i}`),
              totalIncurred: gc(`C${5 + i}`),
              totalClaims:   gc(`D${5 + i}`),
              units:         gc(`E${5 + i}`),
              totalMileage:  gc(`H${5 + i}`),
            }));
            setLossSummary({ summary: buildSummary(summary), autoLiability: readSection(17), cargo: readSection(27), physicalDamage: readSection(37), generalLiability: readSection(47) });
            const total = [17, 27, 37, 47].reduce((s, r) =>
              s + Array.from({ length: 5 }, (_, i) => parseInt(gc(`D${r + i}`) || '0', 10)).reduce((a, b) => a + b, 0), 0);
            setLossRunsMsg(`✓ Excel template loaded — ${total} total claims across all lines`);
            return;
          }
        }
      }

      // ── Branch 2: PDFs — send to Claude as base64 documents ──
      // Each PDF is parsed by Claude reading the document natively (not text extraction).
      // See /api/loss-runs/parse.js for the extraction prompt.

      // Parse each PDF via Claude document API
      const pdfFiles = files.filter(f => /\.pdf$/i.test(f.name));
      if (!pdfFiles.length) {
        setLossRunsMsg('⚠️ No PDF or Excel files found — please upload loss run PDFs');
        return;
      }

      // ── Carrier name normalization — map known MGA names to paper carrier ──────
      const normalizeCarrier = (name) => {
        const n = (name || '').trim();
        const lower = n.toLowerCase();
        if (lower.includes('texcaz'))                    return 'Trisura Specialty Insurance Company';
        if (lower.includes('progressive'))               return 'Progressive Northern Insurance Company';
        if (lower.includes('universal casualty'))        return 'Universal Casualty Risk Retention Group Inc';
        return n;
      };

      // ── Coverage code → section mapping (done in JS, not by Claude) ──────────
      const codeToSection = (code) => {
        const c = (code || '').toUpperCase().trim();
        if (c.includes('BI/PD'))                                        return 'autoLiability';
        if (c === 'PD' || c === 'APD' || c === 'COMP' || c === 'COLL') return 'physicalDamage';
        if (c.includes('PHYSICAL DAMAGE') || c.includes('APD'))        return 'physicalDamage';
        if (c === 'MTC' || c === 'TI' || c === 'CA' || c === 'CARGO') return 'cargo';
        if (c.includes('MOTOR TRUCK CARGO') || c.includes('TRIP INTERRUPTION')) return 'cargo';
        if (c === 'GL')                       return 'generalLiability';
        if (c.includes('GENERAL LIABILITY')) return 'generalLiability';
        return 'autoLiability';
      };

      // ── Parse each PDF with retry on 529 overloaded ───────────────────────────
      // Returns { ok: true, ...parsed } on success or { ok: false, stage, message } on failure.
      // Never throws — callers check result.ok to decide whether to skip this file.
      const MAX_RETRIES = 3;
      const parsePDF = async (file, posLabel) => {
        const pdfBase64 = await fileToBase64(file);
        console.log(`[parsePDF] "${file.name}" pdfBase64.length=${pdfBase64.length}`);
        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
          if (attempt > 1) {
            setLossRunsMsg(`API busy, retrying ${posLabel} (attempt ${attempt} of ${MAX_RETRIES})...`);
            await new Promise(r => setTimeout(r, [15000, 30000, 60000][attempt - 2])); // 15s, 30s, 60s
          } else {
            setLossRunsMsg(`Parsing ${posLabel}...`);
          }
          let resp, result;
          try {
            resp = await apiFetch(`${API_BASE}/api/loss-runs/parse`, {
              method: 'POST',
              body: JSON.stringify({ pdfBase64, filename: file.name }),
            });
          } catch (fetchErr) {
            console.error(`[parsePDF] "${file.name}" fetch error:`, fetchErr.message);
            return { ok: false, fileName: file.name, stage: 'network', message: fetchErr.message };
          }
          try { result = await resp.json(); } catch {
            return { ok: false, fileName: file.name, stage: 'http', message: `HTTP ${resp.status} — response not JSON` };
          }
          if (resp.status === 529) {
            const hdrs529 = {};
            resp.headers.forEach((v, k) => { hdrs529[k] = v; });
            console.warn(
              `[parsePDF] 529 overloaded — "${file.name}" attempt ${attempt}`,
              '\nResponse headers:', JSON.stringify(hdrs529),
              '\nResponse body:', JSON.stringify(result),
            );
            if (attempt === MAX_RETRIES) {
              return { ok: false, fileName: file.name, stage: 'capacity', message: `API overloaded after ${MAX_RETRIES} attempts — try again in a minute` };
            }
            continue; // retry
          }
          if (!resp.ok) {
            const stage   = result?.stage   || 'api';
            const message = result?.message || result?.error || `HTTP ${resp.status}`;
            console.error(`[parsePDF] "${file.name}" failed [${stage}]:`, message, result?.details || '');
            return { ok: false, fileName: file.name, stage, message, details: result?.details };
          }
          return result; // { ok: true, fileName, policyTerms: [...] }
        }
        // Exhausted all retries without returning (shouldn't happen but guard anyway)
        return { ok: false, fileName: file.name, stage: 'retry_exhausted', message: `No response after ${MAX_RETRIES} attempts` };
      };

      const rawResults = [];
      const fileErrors = [];
      for (let idx = 0; idx < pdfFiles.length; idx++) {
        const file = pdfFiles[idx];
        const posLabel = `loss run ${idx + 1} of ${pdfFiles.length}`;
        const result = await parsePDF(file, posLabel);
        if (!result || !result.ok) {
          const stage   = result?.stage   || 'unknown';
          const message = result?.message || 'Unknown error';
          console.error(`[parseLossRuns] "${file.name}" failed [${stage}]: ${message}`);
          fileErrors.push({ fileName: file.name, stage, message });
          continue; // keep parsing the remaining files
        }
        // Support multi-policy response ({ policyTerms: [...] }) and legacy single-doc shape
        const terms = Array.isArray(result?.policyTerms)
          ? result.policyTerms
          : [result];
        for (const term of terms) {
          console.log(`[parseLossRuns] "${file.name}" → policy=${term.policyNumber} carrier="${term.carrierName}" ${term.effectiveDate}–${term.expirationDate} printDate=${term.printDate} items=${term.claims?.length}`);
          rawResults.push({ ...term, _filename: file.name });
        }
      }

      console.log('[parseLossRuns] all raw results:', JSON.stringify(rawResults.map(r => ({
        file: r._filename, policy: r.policyNumber, carrier: r.carrierName,
        eff: r.effectiveDate, exp: r.expirationDate, print: r.printDate, items: r.claims?.length,
      }))));

      // ── Deduplicate: same policyNumber → keep latest printDate ────────────────
      const byPolicy = {};
      for (const r of rawResults) {
        const key = (r.policyNumber || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase() || r._filename;
        const existing = byPolicy[key];
        if (!existing) {
          byPolicy[key] = r;
        } else {
          const parseD = (s) => { const d = new Date(s); return isNaN(d.getTime()) ? 0 : d.getTime(); };
          if (parseD(r.printDate) > parseD(existing.printDate)) byPolicy[key] = r;
        }
      }
      const dedupedDocs = Object.values(byPolicy);

      // ── Policy-term aggregation ───────────────────────────────────────────────
      // Aggregation key: normalizedCarrier || policyNumber || effectiveDate || expirationDate
      // Each unique policy gets its own row per section — no merging across carriers
      // that happen to overlap the same calendar year.
      const SECTION_RANK = { autoLiability: 0, physicalDamage: 1, cargo: 2, generalLiability: 3 };
      const SEC_KEYS = ['autoLiability', 'cargo', 'physicalDamage', 'generalLiability'];
      const yearUniqueClaims = {}; // yearLabel → Set<claimId|policyKey> for summary totals
      let slotWarningCount = 0;

      // Carrier incurred-field: CDA/Prime reports claim paid only; others use total incurred.
      const CARRIER_PAID_ONLY = ['prime insurance', 'prime holdings', 'commercial driver'];
      const carrierIncurredField = (name) => {
        const lower = (name || '').toLowerCase();
        return CARRIER_PAID_ONLY.some(k => lower.includes(k)) ? 'paid' : 'total';
      };

      // Step 1 — flatten every claim into rawClaims, attributed to its source policy.
      // Claims are NOT distributed across year slots — each stays in its policy bucket.
      const rawClaims = [];
      for (const doc of dedupedDocs) {
        const carrierName = normalizeCarrier(doc.carrierName);
        const policyKey   = `${carrierName}||${doc.policyNumber || ''}||${doc.effectiveDate}||${doc.expirationDate}`;
        const yearLabel   = buildPolicyYearLabel(doc.effectiveDate, doc.expirationDate);
        if (!yearLabel) { slotWarningCount++; continue; }
        const incField = carrierIncurredField(carrierName);
        for (const item of (doc.claims || [])) {
          const identity = item.claimNumber
            || `${item.dateOfLoss}_${item.status}_${String(item.paidAmount || 0)}_${String(item.reserveAmount || 0)}`;
          const paid = incField === 'paid'
            ? ((item.paidLoss || 0) + (item.paidExpense || 0))
            : (item.paidAmount || 0);
          rawClaims.push({
            carrier:            carrierName,
            sourceCarrierName:  doc.carrierName       || '',
            policyNumber:       doc.policyNumber      || '',
            policyKey,
            yearLabel,
            effectiveDate:      doc.effectiveDate     || '',
            expirationDate:     doc.expirationDate    || '',
            sourceDocumentName: doc._filename         || '',
            parserProfile:      incField === 'paid' ? 'paid_only' : 'total_incurred',
            identity,
            claimId:  `${identity}|${policyKey}`,
            entryId:  `${identity}|${(item.coverageCode || '').toUpperCase().trim()}|${policyKey}`,
            section:            codeToSection(item.coverageCode),
            paid,
            reserve:            item.reserveAmount  || 0,
            recovery:           item.recoveryAmount || 0,
            dateOfLoss:         item.dateOfLoss     || '',
            claimNumber:        item.claimNumber    || '',
            status:             item.status         || '',
            coverageCode:       item.coverageCode   || '',
          });
        }
      }

      // Step 2 — deduplicate financial entries.
      // Groups by entryId (claimNumber|coverageCode|policyKey). Within each group:
      //   - Entries with identical paid|reserve|recovery → true duplicate → keep one.
      //   - Entries with different financials → retained as separate entries (multi-claimant scenario).
      //   - When multiple distinct entries survive for the same claim+coverage, a reconciliation
      //     warning is attached: the extraction contract has no claimant ID to resolve the ambiguity.
      const entryMap = new Map();           // resolvedEntryId → augmented record
      const multiEntryWarnings = new Map(); // policyKey → [warningMessage, ...]
      {
        const coverageGroups = new Map();   // entryId → [records]
        for (const rec of rawClaims) {
          if (!coverageGroups.has(rec.entryId)) coverageGroups.set(rec.entryId, []);
          coverageGroups.get(rec.entryId).push(rec);
        }
        for (const [, group] of coverageGroups) {
          const seen = new Set();
          const distinct = group.filter(r => {
            const fp = `${r.paid}|${r.reserve}|${r.recovery}`;
            if (seen.has(fp)) return false;
            seen.add(fp);
            return true;
          });
          distinct.forEach(rec => {
            const resolvedEntryId = `${rec.entryId}|${rec.paid}|${rec.reserve}|${rec.recovery}`;
            entryMap.set(resolvedEntryId, { ...rec, resolvedEntryId });
          });
          if (distinct.length > 1) {
            if (!multiEntryWarnings.has(group[0].policyKey)) multiEntryWarnings.set(group[0].policyKey, []);
            multiEntryWarnings.get(group[0].policyKey).push(
              `claim ${group[0].claimNumber} has ${distinct.length} entries with coverage ` +
              `"${group[0].coverageCode}" and different amounts — possible multi-claimant record; verify totals manually`
            );
          }
        }
      }

      // Step 3 — aggregate into per-policy per-section buckets.
      const aggMap = { autoLiability: {}, cargo: {}, physicalDamage: {}, generalLiability: {} };
      for (const rec of entryMap.values()) {
        if (!yearUniqueClaims[rec.yearLabel]) yearUniqueClaims[rec.yearLabel] = new Set();
        yearUniqueClaims[rec.yearLabel].add(rec.claimId);
        if (!aggMap[rec.section][rec.policyKey]) {
          aggMap[rec.section][rec.policyKey] = {
            insurer:            rec.carrier,
            sourceCarrierName:  rec.sourceCarrierName,
            policyNumber:       rec.policyNumber,
            effectiveDate:      rec.effectiveDate,
            expirationDate:     rec.expirationDate,
            yearLabel:          rec.yearLabel,
            lineOfBusiness:     rec.section,
            sourceDocumentName: rec.sourceDocumentName,
            parserProfile:      rec.parserProfile,
            lossRunStatus:      'confirmed',
            claimGroups:        new Map(),
            distinctClaims:     new Set(),
            claimDetail:        [],
            validationWarnings: [...(multiEntryWarnings.get(rec.policyKey) || [])],
          };
        }
        const cell = aggMap[rec.section][rec.policyKey];
        cell.distinctClaims.add(rec.claimId);
        const eg = cell.claimGroups.get(rec.resolvedEntryId) || { paid: 0, reserve: 0, recovery: 0 };
        cell.claimGroups.set(rec.resolvedEntryId, {
          paid:     eg.paid     + rec.paid,
          reserve:  eg.reserve  + rec.reserve,
          recovery: eg.recovery + rec.recovery,
        });
        cell.claimDetail.push({
          date: rec.dateOfLoss, claimNumber: rec.claimNumber, status: rec.status,
          coverageTypes: [rec.coverageCode].filter(Boolean), totalIncurred: rec.paid,
        });
      }

      // Step 3b — store document-level policy totals on aggMap cells.
      // For Prime/CDA, use the PDF's printed total row instead of floating-point claim accumulation.
      for (const doc of dedupedDocs) {
        if (!doc.policyTotalPaid && !doc.policyTotalIncurred) continue;
        const carrierName = normalizeCarrier(doc.carrierName);
        const policyKey   = `${carrierName}||${doc.policyNumber || ''}||${doc.effectiveDate}||${doc.expirationDate}`;
        for (const sk of SEC_KEYS) {
          if (aggMap[sk][policyKey]) {
            if (doc.policyTotalPaid      > 0) aggMap[sk][policyKey].documentTotalPaid      = doc.policyTotalPaid;
            if (doc.policyTotalIncurred  > 0) aggMap[sk][policyKey].documentTotalIncurred  = doc.policyTotalIncurred;
          }
        }
      }

      // Step 4 — carrier-presence stubs: one stub per policy per applicable section.
      // Policies with no claims in a section get a zero-count stub row.
      for (const doc of dedupedDocs) {
        const carrierName = normalizeCarrier(doc.carrierName);
        const policyKey   = `${carrierName}||${doc.policyNumber || ''}||${doc.effectiveDate}||${doc.expirationDate}`;
        const yearLabel   = buildPolicyYearLabel(doc.effectiveDate, doc.expirationDate);
        if (!yearLabel) continue;
        if (!yearUniqueClaims[yearLabel]) yearUniqueClaims[yearLabel] = new Set();
        const mappedLines = getLRCoverageLines(doc.carrierName);
        const coverageSections = mappedLines
          || (doc.coverageLines?.length ? doc.coverageLines : LR_COVERAGE_DEFAULT);
        const docEvidencedSections = new Set(doc.coverageLines || []);
        const stubIncField = carrierIncurredField(carrierName);
        for (const section of coverageSections) {
          if (!aggMap[section][policyKey]) {
            const isMapAssumed = !docEvidencedSections.has(section);
            aggMap[section][policyKey] = {
              insurer:            carrierName,
              sourceCarrierName:  doc.carrierName    || '',
              policyNumber:       doc.policyNumber   || '',
              effectiveDate:      doc.effectiveDate  || '',
              expirationDate:     doc.expirationDate || '',
              yearLabel,
              lineOfBusiness:     section,
              sourceDocumentName: doc._filename      || '',
              parserProfile:      stubIncField === 'paid' ? 'paid_only' : 'total_incurred',
              lossRunStatus:      isMapAssumed ? 'coverage_assumed' : 'confirmed',
              claimGroups:        new Map(),
              claimDetail:        [],
              validationWarnings: [],
            };
          }
        }
      }

      // Step 5 — COI policy injection: create missing-loss-run stubs for coverage
      // that appears on the COI but has no uploaded loss run.
      if (coiData) {
        const coiPolicies = [];
        if (coiData.carrierName && coiData.policyNumber) {
          coiPolicies.push({
            carrierName:    coiData.carrierName,
            policyNumber:   coiData.policyNumber,
            effectiveDate:  coiData.effectiveDate  || '',
            expirationDate: coiData.expirationDate || '',
            coverageLine:   null, // use CARRIER_COVERAGE_MAP to derive sections
          });
        }
        for (const p of (coiData.additionalPolicies || [])) {
          if (p.carrierName && p.policyNumber) coiPolicies.push(p);
        }
        for (const policy of coiPolicies) {
          const cn  = normalizeCarrier(policy.carrierName);
          const pk  = `${cn}||${policy.policyNumber}||${policy.effectiveDate}||${policy.expirationDate}`;
          const yl  = buildPolicyYearLabel(policy.effectiveDate, policy.expirationDate);
          if (!yl) continue;
          const sects = policy.coverageLine
            ? [policy.coverageLine]
            : (getLRCoverageLines(policy.carrierName) || ['autoLiability']);
          for (const section of sects) {
            if (!aggMap[section][pk]) {
              aggMap[section][pk] = {
                insurer:            cn,
                sourceCarrierName:  policy.carrierName,
                policyNumber:       policy.policyNumber,
                effectiveDate:      policy.effectiveDate,
                expirationDate:     policy.expirationDate,
                yearLabel:          yl,
                lineOfBusiness:     section,
                sourceDocumentName: 'COI',
                parserProfile:      'total_incurred',
                lossRunStatus:      'missing_loss_run',
                claimGroups:        new Map(),
                claimDetail:        [],
                validationWarnings: ['Coverage found but no loss run uploaded.'],
              };
            }
          }
        }
      }

      // Collect all unique year labels from aggMap cells, validate, and sort descending.
      const allYearLabelSet = new Set();
      for (const s of SEC_KEYS) {
        for (const cell of Object.values(aggMap[s])) {
          const y1 = parseInt((cell.yearLabel || '').split('-')[0], 10);
          if (!isNaN(y1) && y1 >= 1990 && y1 <= 2100) allYearLabelSet.add(cell.yearLabel);
        }
      }
      const yearSlots = Array.from(allYearLabelSet).sort((a, b) => {
        const [a1, a2 = 0] = a.split('-').map(Number);
        const [b1, b2 = 0] = b.split('-').map(Number);
        return b1 !== a1 ? b1 - a1 : b2 - a2;
      });

      if (!yearSlots.length) {
        if (fileErrors.length > 0 && rawResults.length === 0) {
          const errLines = fileErrors.map(e => `• "${e.fileName}": [${e.stage}] ${e.message}`).join('\n');
          setLossRunsMsg(`⚠️ All ${fileErrors.length} file${fileErrors.length !== 1 ? 's' : ''} failed to parse:\n${errLines}`);
        } else {
          setLossRunsMsg('⚠️ Could not determine policy years from uploaded PDFs — verify effective/expiration dates');
        }
        return;
      }

      // Helper: total paid, reserve, recovery and claim count across all claimGroups in a cell.
      // For Prime/CDA (paid_only): use documentTotalPaid from the PDF's printed total row to avoid
      // floating-point accumulation errors (penny reconciliation). NOT applied to total_incurred
      // carriers — for those, the accumulated paidAmount (= totalIncurred from parse) is correct.
      const cellTotals = (cell) => {
        let paid = 0, reserve = 0, recovery = 0;
        for (const g of (cell?.claimGroups?.values() || [])) {
          paid += g.paid; reserve += g.reserve; recovery += (g.recovery || 0);
        }
        if (cell?.parserProfile === 'paid_only' && cell?.documentTotalPaid !== undefined) {
          paid = cell.documentTotalPaid;
        }
        return {
          paid:     Math.round(paid     * 100) / 100,
          reserve:  Math.round(reserve  * 100) / 100,
          recovery: Math.round(recovery * 100) / 100,
          count:    cell?.distinctClaims?.size ?? cell?.claimGroups?.size ?? 0,
        };
      };

      // Per-term validation warnings: flag structural issues before building output rows.
      for (const s of SEC_KEYS) {
        for (const cell of Object.values(aggMap[s])) {
          if (!cell.policyNumber)  cell.validationWarnings.push('policyNumber missing');
          if (!cell.effectiveDate) cell.validationWarnings.push('effectiveDate missing');
          const { paid, count } = cellTotals(cell);
          if (paid > 0 && count === 0) cell.validationWarnings.push('incurred > 0 but 0 claims');
        }
      }

      // Build per-section rows: one row per policy term, sorted year descending.
      const buildSection = (section) => {
        const cells = Object.values(aggMap[section]);
        cells.sort((a, b) => {
          const [a1, a2 = 0] = (a.yearLabel || '0-0').split('-').map(Number);
          const [b1, b2 = 0] = (b.yearLabel || '0-0').split('-').map(Number);
          return b1 !== a1 ? b1 - a1 : b2 - a2;
        });
        return cells.map(cell => {
          const { paid, reserve, recovery, count } = cellTotals(cell);
          const isMissing = cell.lossRunStatus === 'missing_loss_run' || cell.lossRunStatus === 'coverage_assumed';
          return {
            insurer:            cell.insurer,
            sourceCarrierName:  cell.sourceCarrierName  || '',
            policyYear:         cell.yearLabel,
            policyNumber:       cell.policyNumber,
            effectiveDate:      cell.effectiveDate       || '',
            expirationDate:     cell.expirationDate      || '',
            lineOfBusiness:     cell.lineOfBusiness      || section,
            lossRunStatus:      cell.lossRunStatus       || 'confirmed',
            numClaims:          isMissing ? null : String(count),
            lossReserve:        isMissing ? null : reserve.toFixed(2),
            totalIncurred:      isMissing ? null : paid.toFixed(2),
            recovery:           isMissing ? null : recovery.toFixed(2),
            claimDetail:        cell.claimDetail,
            sourceDocumentName: cell.sourceDocumentName  || '',
            parserProfile:      cell.parserProfile       || '',
            validationWarnings: cell.validationWarnings  || [],
          };
        });
      };

      const fleetMap = computeFleetSummary(cabData?.fleet);
      const summary = yearSlots.map(year => {
        const fv = fleetMap[year] || {};
        const totalClaims = yearUniqueClaims[year]?.size || 0;
        let totalIncurred = 0, lossReserve = 0;
        for (const s of SEC_KEYS) {
          for (const cell of Object.values(aggMap[s])) {
            if (cell.yearLabel === year) {
              const { paid, reserve } = cellTotals(cell);
              totalIncurred += paid;
              lossReserve   += reserve;
            }
          }
        }
        return {
          policyYear:    year,
          lossReserve:   totalClaims > 0 ? lossReserve.toFixed(2)   : '',
          totalIncurred: totalClaims > 0 ? totalIncurred.toFixed(2) : '',
          totalClaims:   totalClaims > 0 ? String(totalClaims)      : '',
          units:         fv.units        || '',
          totalMileage:  fv.totalMileage || '',
        };
      });

      const finalSummary = {
        summary,
        autoLiability:    buildSection('autoLiability'),
        cargo:            buildSection('cargo'),
        physicalDamage:   buildSection('physicalDamage'),
        generalLiability: buildSection('generalLiability'),
        _parsedAt:           new Date().toISOString(),
        _lossRunSchemaVersion: '4a',
      };
      console.log('[parseLossRuns] yearSlots:', yearSlots);
      console.log('[parseLossRuns] policy buckets per section:',
        JSON.stringify(SEC_KEYS.reduce((acc, s) => {
          acc[s] = Object.values(aggMap[s]).map(cell => {
            const { paid, reserve, count } = cellTotals(cell);
            return { year: cell.yearLabel, count, paid, reserve, insurer: cell.insurer };
          });
          return acc;
        }, {}))
      );
      console.log('[parseLossRuns] final summary rows:', JSON.stringify(finalSummary.summary));
      setLossSummary(finalSummary);

      // Derive lossRunVerification grid from authoritative parsed PolicyTerm data.
      // No secondary AI call — always in sync with parseLossRunsIntoTab output.
      {
        const gridDocs = dedupedDocs.map(doc => {
          const cn = normalizeCarrier(doc.carrierName);
          const pk = `${cn}||${doc.policyNumber || ''}||${doc.effectiveDate}||${doc.expirationDate}`;
          let totalClaims = 0, totalIncurred = 0;
          for (const s of SEC_KEYS) {
            const cell = aggMap[s][pk];
            if (cell) { const t = cellTotals(cell); totalClaims += t.count; totalIncurred += t.paid; }
          }
          return {
            carrierName:   cn,
            policyNumber:  doc.policyNumber  || '',
            effDate:       doc.effectiveDate,
            expDate:       doc.expirationDate || 'active',
            claimCount:    totalClaims,
            totalIncurred,
            coverageLines: doc.coverageLines || [],
          };
        });
        const gridYearSlots = computeYearSlots(5);
        const yib = parseFloat(leadSheet?.yearsInBusiness) || 0;
        const parsedGrid = buildLossRunGrid(gridDocs, gridYearSlots, yib);
        parsedGrid.extractedDocuments = gridDocs;
        setLossRunVerification(parsedGrid);
      }

      parseSucceeded = true;
      const countSec = (s) => Object.values(aggMap[s]).reduce((n, cell) => n + (cell.distinctClaims != null ? cell.distinctClaims.size : (cell.claimGroups?.size ?? 0)), 0);
      const warnSuffix = slotWarningCount > 0
        ? ` (${slotWarningCount} claim${slotWarningCount > 1 ? 's' : ''} outside year range — check dates)`
        : '';
      const parsedCount = pdfFiles.length - fileErrors.length;
      const failSuffix  = fileErrors.length > 0
        ? `\n⚠️ ${fileErrors.length} file${fileErrors.length !== 1 ? 's' : ''} failed — ${fileErrors.map(e => `"${e.fileName}": [${e.stage}] ${e.message}`).join('; ')}`
        : '';
      setLossRunsMsg(
        `✓ Parsed ${parsedCount} of ${pdfFiles.length} PDF${pdfFiles.length !== 1 ? 's' : ''} — ` +
        `${countSec('autoLiability')} AL, ${countSec('cargo')} Cargo, ` +
        `${countSec('physicalDamage')} PD, ${countSec('generalLiability')} GL claims across ${yearSlots.length} policy term${yearSlots.length !== 1 ? 's' : ''}${warnSuffix}${failSuffix}`
      );
    } catch (err) {
      console.error('[parseLossRunsIntoTab]', err.message);
      setLossRunsMsg(`⚠️ ${err.message}`);
    } finally {
      setLossRunsLoading(false);
    }
  };

  // ── Carrier persistence ──
  const saveCarrier = async (silent = false) => {
    // ── Concurrency guard ────────────────────────────────────────────────────
    // If a save is already in-flight, queue this request. When the in-flight
    // save finishes it will re-run saveCarrier via saveCarrierRef so that
    // the queued save always uses the fully up-to-date state from that render.
    if (saveInFlightRef.current) {
      // Preserve the least-silent setting: if a manual save was already queued
      // (silent=false), keep it non-silent so the user still gets feedback.
      const prev = savePendingRef.current;
      savePendingRef.current = { silent: prev ? (prev.silent && silent) : silent };
      if (!silent) { setSavedMsg('Saving…'); setSaveState('saving'); }
      return;
    }
    saveInFlightRef.current = true;
    savePendingRef.current  = null;

    // Capture account version NOW — used to discard this save's response if the
    // user switches accounts while the fetch is in-flight.
    const myVersion = accountVersionRef.current;

    // savedAt must reflect when this save was INITIATED, not when it completes.
    const savedAt = new Date().toISOString();
    // Use the stable pre-allocated ID for new accounts so concurrent autosave
    // calls all target the same Redis key. Fall back to a fresh timestamp+random
    // only when neither carrierId nor pendingCarrierIdRef is available.
    const id = carrierId || pendingCarrierIdRef.current || `carrier_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

    if (!silent) { setSavedMsg('Saving…'); setSaveState('saving'); }

    try {
      // ── Upload any File objects that still lack a Blob URL ──────────────────
      // This covers two cases:
      //  1. Files added before the carrier had an ID (upload was skipped)
      //  2. Files whose upload completed AFTER the last save (race condition)
      const freshUrlMap = {}; // { [docId]: Map<File, string> }
      if (window.location.hostname !== 'localhost') {
        const pendingUploads = [];
        for (const [docId, files] of Object.entries(documents)) {
          for (const file of files) {
            if (!(file instanceof File)) continue;
            const _uploadToken = localStorage.getItem('tsp_auth') || '';
            const p = fetch(
              `${API_BASE}/api/files/upload?carrierId=${encodeURIComponent(id)}&category=${encodeURIComponent(docId)}&filename=${encodeURIComponent(file.name)}`,
              { method: 'POST', body: file, headers: _uploadToken ? { 'Authorization': `Bearer ${_uploadToken}` } : {} }
            )
              .then(r => r.ok ? r.json() : null)
              .then(data => {
                if (data?.fileId || (data?.success && data.url)) {
                  if (!freshUrlMap[docId]) freshUrlMap[docId] = new Map();
                  // New schema: store { id, name }. Legacy: store url string for backward compat.
                  freshUrlMap[docId].set(file, data.fileId ? { fileId: data.fileId, name: data.name } : data.url);
                }
              })
              .catch(err => console.warn('[saveCarrier] pending upload failed:', file.name, err.message));
            pendingUploads.push(p);
          }
        }
        if (pendingUploads.length > 0) {
          await Promise.all(pendingUploads);
          // Reflect newly-uploaded URLs back into documents state so the UI
          // shows the cloud-linked state for future edits.
          if (Object.keys(freshUrlMap).length > 0) {
            setDocuments(prev => {
              const updated = { ...prev };
              for (const [docId, fileMap] of Object.entries(freshUrlMap)) {
                updated[docId] = (prev[docId] || []).map(f => {
                  const result = (f instanceof File) ? fileMap.get(f) : undefined;
                  if (!result) return f;
                  // New schema: result is { fileId, name }. Legacy: result is a URL string.
                  return typeof result === 'object'
                    ? { id: result.fileId, name: result.name || f.name }
                    : { name: f.name, url: result };
                });
              }
              return updated;
            });
          }
        }
      }

      const serializeDoc = (docId, f) => {
        if (f instanceof File) {
          const result = freshUrlMap[docId]?.get(f) || null;
          if (result && typeof result === 'object') return { id: result.fileId, name: result.name || f.name };
          return { name: f.name, url: result || null };
        }
        // Preserve all fields — new schema has id, legacy has url
        if (f?.id) return { id: f.id, name: f.name || '' };
        return { name: f.name || '', url: f.url || null };
      };

      const data = {
        id, companyName, dotNumber, tractors, trailers, drivers,
        documents: Object.fromEntries(
          Object.entries(documents).map(([k, files]) => [k, files.map(f => serializeDoc(k, f))])
        ),
        results,
        lossSummary,
        cabData: cabData || null,
        lossRunsData: lossRunsData || null,
        lossRunVerification: lossRunVerification || null,
        appData: appData || null,
        coiData: coiData || null,
        iftaData: iftaData || null,
        markets,
        commodities,
        leadSheet: leadSheet || initLeadSheet(),
        overrides,
        savedAt,
        username: currentUser || 'admin',
        status: carrierStatus || 'prospect',
        pipelineData: (() => {
          const snap = computeReadiness(
            { companyName, tractors, trailers, drivers, documents, lossRunVerification, appData, coiData, iftaData, leadSheet, results },
            tliResults
          );
          return { ...(pipelineData || {}), readiness: { status: snap.status, score: snap.score, updatedAt: new Date().toISOString() } };
        })(),
        accountNotes,
        // Use the ref, not React state — the ref is synchronous and is already
        // updated by the time a queued save fires from the mutex finally block,
        // even before React has re-rendered with the new saveRevision state.
        expectedRevision: saveRevisionRef.current,
        // Admin/platform_owner editing another producer's carrier: route save to the correct owner's key.
        ...((currentRole === 'admin' || currentRole === 'platform_owner') && carrierDataOwnerRef.current && carrierDataOwnerRef.current !== currentUser
          ? { ownerUsername: carrierDataOwnerRef.current }
          : {}),
      };
      console.log('[playbook persist] saving:', results.playbook?.slice(0, 50));
      // Always mirror to localStorage before the cloud attempt.
      // Store server-confirmed revision so offline→online transitions start correctly.
      try {
        localStorage.setItem(`tsp_${id}`, JSON.stringify(data));
        const localIndex = [
          { id, companyName, dotNumber, savedAt: data.savedAt, status: data.status, pipelineData: data.pipelineData },
          ...JSON.parse(localStorage.getItem('tsp_index') || '[]').filter(c => c.id !== id),
        ];
        localStorage.setItem('tsp_index', JSON.stringify(localIndex));
      } catch (e) { /* localStorage quota exceeded — ignore */ }

      // ── Cloud save with bounded retry for transient failures ─────────────
      // Only SECURITY_STATE_UNAVAILABLE is retried — all other errors are
      // non-retryable. 409 conflicts are handled separately and never retried.
      // Network errors abort immediately (can't determine retryability).
      const SAVE_MAX_RETRIES = 3;
      const SAVE_RETRY_DELAYS = [500, 1500]; // ms before attempt 2, attempt 3
      let lastErrCode   = null;
      let lastErrStatus = null;
      let cloudSaveOk   = false;

      for (let attempt = 1; attempt <= SAVE_MAX_RETRIES && !cloudSaveOk; attempt++) {
        if (attempt > 1) {
          if (accountVersionRef.current !== myVersion) break; // account switched — abort
          if (!silent) { setSavedMsg('⚠ Cloud save temporarily unavailable — retrying…'); setSaveState('retrying'); }
          await new Promise(r => setTimeout(r, SAVE_RETRY_DELAYS[attempt - 2]));
          if (accountVersionRef.current !== myVersion) break; // check again after wait
        }

        try {
          const res = await apiFetch(`${API_BASE}/api/carriers/save`, {
            method: 'POST',
            body: JSON.stringify(data),
          });

          // 409 conflict — non-retryable; handled separately
          // Another save has a higher revision. Data is in localStorage.
          // User must reload the account to resolve.
          if (res.status === 409) {
            const errBody = await res.json().catch(() => ({}));
            console.warn('[saveCarrier] Conflict — expected:', saveRevision, 'server current:', errBody.currentRevision);
            if (accountVersionRef.current === myVersion) {
              savePendingRef.current = null;
              setSavedMsg('⚠ Conflict: another save is newer. Reload this account to resolve.');
              setSaveState('conflict');
            }
            return; // exit saveCarrier — finally block still runs
          }

          if (!res.ok) {
            const errBody = await res.json().catch(() => ({}));
            lastErrCode   = errBody.code   || null;
            lastErrStatus = res.status;
            if (isSaveRetryable(lastErrCode) && attempt < SAVE_MAX_RETRIES) {
              console.warn(`[saveCarrier] ${lastErrCode} on attempt ${attempt}/${SAVE_MAX_RETRIES} — will retry`);
              continue;
            }
            break; // non-retryable or all retries exhausted
          }

          // ── Cloud save succeeded ────────────────────────────────────────────
          const respJson = await res.json().catch(() => ({}));
          const newRevision = typeof respJson.revision === 'number' ? respJson.revision : saveRevision + 1;

          // Clear per-carrier unsynced marker — cloud has the confirmed latest data
          try { localStorage.removeItem(`tsp_unsynced_${id}`); } catch {}

          // Update localStorage with the server-confirmed revision
          try {
            const stored = JSON.parse(localStorage.getItem(`tsp_${id}`) || '{}');
            localStorage.setItem(`tsp_${id}`, JSON.stringify({ ...stored, revision: newRevision }));
          } catch {}

          const newIndex = [
            { id, companyName, dotNumber, savedAt: data.savedAt, status: data.status, pipelineData: data.pipelineData },
            ...savedCarriers.filter(c => c.id !== id),
          ];
          setSavedCarriers(newIndex);

          if (accountVersionRef.current === myVersion) {
            // Sync ref BEFORE state so queued saves read the new revision immediately
            saveRevisionRef.current = newRevision;
            setSaveRevision(newRevision);
            // Set carrierId only when still on the same account version — prevents
            // a late-completing new-account save from hijacking a newly loaded account.
            if (!carrierId) setCarrierId(id);
            setSaveState('saved');
            if (!silent) { setSavedMsg('All changes saved ✓'); setTimeout(() => setSavedMsg(''), 2500); }
          }
          cloudSaveOk = true;

        } catch (netErr) {
          // fetch itself threw — network error, not a retryable server error
          console.error('[saveCarrier] Network error on attempt', attempt, ':', netErr.message);
          lastErrCode   = null;
          lastErrStatus = 0;
          break;
        }
      }

      if (!cloudSaveOk) {
        // Persist a per-carrier unsynced marker so future sessions can warn the user
        try {
          localStorage.setItem(`tsp_unsynced_${id}`, JSON.stringify({
            ts:       new Date().toISOString(),
            reason:   lastErrCode || 'NETWORK_ERROR',
            revision: saveRevisionRef.current,
          }));
        } catch {}

        const newIndex = [
          { id, companyName, dotNumber, savedAt: data.savedAt, status: data.status, pipelineData: data.pipelineData },
          ...savedCarriers.filter(c => c.id !== id),
        ];
        setSavedCarriers(newIndex);
        if (accountVersionRef.current === myVersion) {
          if (!carrierId) setCarrierId(id);
          setSaveState('unsynced');
          // Message is intentionally persistent — user must see the unsynced state
          if (!silent) setSavedMsg(buildSaveErrorMsg(lastErrCode, lastErrStatus));
        }
      }
    } finally {
      saveInFlightRef.current = false;
      // If a save was requested while this one was in-flight, re-run now with
      // the latest state (saveCarrierRef always points to the current render's
      // saveCarrier, which closes over the most recent React state).
      const pending = savePendingRef.current;
      if (pending) {
        savePendingRef.current = null;
        setTimeout(() => saveCarrierRef.current?.(pending.silent), 0);
      }
    }
  };

  const loadCarrier = async (id) => {
    const applyData = (data) => {
      setCompanyName(data.companyName || '');
      setDotNumber(data.dotNumber || '');
      setTractors(data.tractors?.length ? data.tractors : [blankRow(TRACTOR_COLS)]);
      setTrailers(data.trailers?.length ? data.trailers : [blankRow(TRAILER_COLS)]);
      setDrivers(data.drivers?.length ? data.drivers : []);
      const loadedDocs = {};
      for (const [docId, names] of Object.entries(data.documents || {})) {
        if (names.length) loadedDocs[docId] = names.map(f =>
        typeof f === 'string' ? { name: f, url: null } : f?.id ? { id: f.id, name: f.name || '' } : { name: f.name || '', url: f.url || null }
      );
      }
      setDocuments(loadedDocs);
      setResults({ risk: '', narrative: '', missing: '', missingEmail: '', playbook: '', ...(data.results || {}) });
      setLossSummary(data.lossSummary || initLossSummary());
      if (data.cabData) {
        const cabCopy = { ...data.cabData };
        validateCabSchema(cabCopy, 'Redis load (loadCarrier)');
        setCabData(sanitizeCabData(cabCopy));
      } else {
        setCabData(null);
      }
      setCabMsg('');
      setLossRunsData(data.lossRunsData || null); setLossRunsMsg('');
      setLossRunVerification(data.lossRunVerification || null); setLossRunVerifyMsg('');
      setAppData(data.appData || null); setAppMsg('');
      setCoiData(data.coiData || null); setCoiMsg('');
      setIftaData(data.iftaData || null); setIftaMsg('');
      setMarkets(data.markets || []);
      setCommodities(data.commodities || '');
      setLeadSheet(data.leadSheet ? { ...initLeadSheet(), ...data.leadSheet } : initLeadSheet());
      setOverrides(data.overrides || { units: null, drivers: null, mileage: null });
      setConflictQueue([]);
      // Reset Market Intelligence so it re-analyzes for the new account
      setTliResults([]); setTliLastKey(''); setTliOutdated(false); setTliAnalyzedAt(null); setTliAiError(''); setTliAccountProfile(null); setTliGroupedMarkets([]);
      setCarrierId(id);
      pendingCarrierIdRef.current = null; // clear new-account pending ID when loading existing carrier
      setShowCarrierList(false);
      setActiveTab('Documents');
      // Track actual Redis owner so admin saves route to the correct key
      carrierDataOwnerRef.current = allCarriers.find(c => c.id === id)?.username || currentUser;
      // Share state — derive from the index entry if available
      const indexEntry = savedCarriers.find(c => c.id === id);
      setCarrierShared(!!(data.isShared || indexEntry?.isShared));
      setCarrierOwner(data.owner || indexEntry?.owner || '');
      setCarrierSharedWith(data.sharedWith || indexEntry?.sharedWith || []);
      setShowShareDropdown(false);
      // Pipeline state
      setCarrierStatus(data.status || 'prospect');
      setPipelineData(data.pipelineData || {});
      setLostForm({ reason: '', market: '', premium: '', notes: '', ...(data.pipelineData || {}) });
      setBoundForm({ market: '', effectiveDate: '', expDate: '', premium: '', ...(data.pipelineData || {}) });
      setAccountNotes(Array.isArray(data.accountNotes) ? data.accountNotes : []);
      // Revision tracking — must happen after all other state to signal a clean load
      accountVersionRef.current += 1;
      const rev = typeof data.revision === 'number' ? data.revision : 0;
      saveRevisionRef.current = rev; // synchronous — queued saves read this before React re-renders
      setSaveRevision(rev);
      setSaveState('idle');
      setSavedMsg('');
      savePendingRef.current = null; // discard any pending save queued for the previous account
      // Reconciliation safety: surface any unsynced-local-data warning from a prior failed save
      try {
        const unsyncedMarker = localStorage.getItem(`tsp_unsynced_${id}`);
        if (unsyncedMarker) {
          setSaveState('unsynced');
          setSavedMsg('⚠ Unsynced changes from a previous session were found — not yet saved to cloud');
        }
      } catch {}
      // Suppress the debounced auto-save triggered by the bulk state update on load
      recentlyLoadedRef.current = true;
      clearTimeout(autoSaveTimerRef.current);
      setTimeout(() => { recentlyLoadedRef.current = false; }, 2000);
    };
    try {
      const res = await apiFetch(`${API_BASE}/api/carriers/${id}`);
      if (res.ok) { applyData(await res.json()); return; }
    } catch (e) { /* fall through to localStorage */ }
    try {
      const data = JSON.parse(localStorage.getItem(`tsp_${id}`) || 'null');
      if (data) applyData(data);
    } catch (e) { console.error('Load failed:', e); }
  };

  // Keep ref current every render so uploadFileToCloud can call latest saveCarrier
  saveCarrierRef.current = saveCarrier;

  const deleteCarrier = async (id) => {
    try {
      await apiFetch(`${API_BASE}/api/carriers/${id}`, { method: 'DELETE' });
    } catch (e) { /* ignore network error, still update local state */ }
    try {
      localStorage.removeItem(`tsp_${id}`);
      const newIndex = JSON.parse(localStorage.getItem('tsp_index') || '[]').filter(c => c.id !== id);
      localStorage.setItem('tsp_index', JSON.stringify(newIndex));
    } catch (e) {}
    setSavedCarriers(prev => prev.filter(c => c.id !== id));
    if (carrierId === id) setCarrierId(null);
  };

  const patchCarrierStatus = async (id, newStatus, extraPipelineData = {}) => {
    const updatedPipelineData = { ...pipelineData, ...extraPipelineData };
    setCarrierStatus(newStatus);
    setPipelineData(updatedPipelineData);
    setSavedCarriers(prev => prev.map(c =>
      c.id === id ? { ...c, status: newStatus, pipelineData: updatedPipelineData } : c
    ));
    // Update localStorage
    try {
      const existing = JSON.parse(localStorage.getItem(`tsp_${id}`) || '{}');
      localStorage.setItem(`tsp_${id}`, JSON.stringify({ ...existing, status: newStatus, pipelineData: updatedPipelineData }));
      const localIndex = JSON.parse(localStorage.getItem('tsp_index') || '[]');
      localStorage.setItem('tsp_index', JSON.stringify(
        localIndex.map(c => c.id === id ? { ...c, status: newStatus, pipelineData: updatedPipelineData } : c)
      ));
    } catch (e) {}
    // Persist to API
    try {
      await apiFetch(`${API_BASE}/api/carriers/patch`, {
        method: 'PATCH',
        body: JSON.stringify({ id, status: newStatus, pipelineData: updatedPipelineData }),
      });
    } catch (e) { /* offline — local state already updated */ }
  };

  const fetchAvailableUsers = async () => {
    if (availableUsers.length > 0) return;
    try {
      const res = await apiFetch(`${API_BASE}/api/users/list`);
      if (res.ok) {
        const list = await res.json();
        setAvailableUsers(list.filter(u => u.username !== currentUser));
      }
    } catch (e) { console.error('Failed to fetch users:', e); }
  };

  const fetchAllCarriers = async () => {
    setAllCarriersLoading(true);
    setAllCarriersError('');
    try {
      const [carriersRes, usersRes] = await Promise.all([
        apiFetch(`${API_BASE}/api/carriers/all`),
        apiFetch(`${API_BASE}/api/admin/users`),
      ]);
      if (!carriersRes.ok) throw new Error((await carriersRes.json()).error || 'Failed to load all accounts');
      const carriers = await carriersRes.json();
      setAllCarriers(carriers);
      if (usersRes.ok) {
        const users = await usersRes.json();
        const nameMap = {};
        users.forEach(u => { nameMap[u.username.toLowerCase()] = u.name || u.username; });
        setUserDisplayNames(nameMap);
      }
    } catch (e) {
      setAllCarriersError(e.message);
    } finally {
      setAllCarriersLoading(false);
    }
  };

  const handleShare = async (shareWith) => {
    if (!carrierId) return;
    setShowShareDropdown(false);
    try {
      const res = await apiFetch(`${API_BASE}/api/carriers/share`, {
        method: 'POST',
        body: JSON.stringify({ carrierId, shareWith }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Share failed');
      const { sharedWith } = await res.json();
      setCarrierShared(true);
      setCarrierOwner(currentUser);
      setCarrierSharedWith(sharedWith);
      setSavedCarriers(prev => prev.map(c => c.id === carrierId
        ? { ...c, isShared: true, owner: currentUser, sharedWith }
        : c
      ));
      setShareMsg(`Shared with ${shareWith} ✓`);
      setTimeout(() => setShareMsg(''), 3000);
    } catch (e) {
      setShareMsg(`Error: ${e.message}`);
      setTimeout(() => setShareMsg(''), 3000);
    }
  };

  const handleRemoveCollaborator = async (removeUser) => {
    if (!carrierId) return;
    try {
      const res = await apiFetch(`${API_BASE}/api/carriers/unshare`, {
        method: 'POST',
        body: JSON.stringify({ carrierId, removeUser }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to remove collaborator');
      const d = await res.json();
      if (d.sharedWith && d.sharedWith.length > 0) {
        // Still shared with remaining collaborators
        setCarrierSharedWith(d.sharedWith);
        setSavedCarriers(prev => prev.map(c => c.id === carrierId
          ? { ...c, sharedWith: d.sharedWith }
          : c
        ));
        setShareMsg(`Removed ${removeUser} ✓`);
      } else {
        // No more collaborators — fully unshared
        setCarrierShared(false);
        setCarrierOwner('');
        setCarrierSharedWith([]);
        setSavedCarriers(prev => prev.map(c => c.id === carrierId
          ? { ...c, isShared: false, owner: undefined, sharedWith: undefined }
          : c
        ));
        setShareMsg('Unshared ✓');
      }
      setTimeout(() => setShareMsg(''), 3000);
    } catch (e) {
      setShareMsg(`Error: ${e.message}`);
      setTimeout(() => setShareMsg(''), 3000);
    }
  };

  const handleUnshare = async () => {
    if (!carrierId) return;
    if (!window.confirm('Remove all collaborators and make this account private?')) return;
    try {
      const res = await apiFetch(`${API_BASE}/api/carriers/unshare`, {
        method: 'POST',
        body: JSON.stringify({ carrierId }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Unshare failed');
      setCarrierShared(false);
      setCarrierOwner('');
      setCarrierSharedWith([]);
      setSavedCarriers(prev => prev.map(c => c.id === carrierId
        ? { ...c, isShared: false, owner: undefined, sharedWith: undefined }
        : c
      ));
      setShareMsg('Unshared ✓');
      setTimeout(() => setShareMsg(''), 3000);
    } catch (e) {
      setShareMsg(`Error: ${e.message}`);
      setTimeout(() => setShareMsg(''), 3000);
    }
  };

  const newCarrier = () => {
    // Cancel any pending debounce before saving so we capture the right state.
    clearTimeout(autoSaveTimerRef.current);
    if (carrierId) {
      saveCarrierRef.current?.(true); // flush existing named account
    } else {
      // New unsaved account: flush if there's any content worth preserving.
      const hasContent = !!(companyName.trim() || dotNumber.trim() ||
        drivers.some(d => d.name?.trim()) ||
        tractors.some(t => t.year?.trim() || t.make?.trim() || t.vin?.trim()) ||
        Object.values(documents).some(files => files.length > 0));
      if (hasContent) saveCarrierRef.current?.(true);
    }
    // Pre-allocate a stable ID for the incoming new account so concurrent
    // autosave calls all write to the same Redis key.
    pendingCarrierIdRef.current = `carrier_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    setCompanyName(''); setDotNumber('');
    setTractors([blankRow(TRACTOR_COLS)]); setTrailers([blankRow(TRAILER_COLS)]); setDrivers([]);
    setDocuments({});
    setResults({ risk: '', narrative: '', missing: '', missingEmail: '', playbook: '' });
    setLossSummary(initLossSummary());
    setCabData(null); setCabMsg('');
    setLossRunsData(null); setLossRunsMsg('');
    setLossRunVerification(null); setLossRunVerifyMsg('');
    setAppData(null); setAppMsg('');
    setCoiData(null); setCoiMsg('');
    setIftaData(null); setIftaMsg('');
    setMarkets([]); setMarketFilter('all');
    setCommodities(''); setLeadSheet(initLeadSheet()); setSubmissionBanner(null);
    setOverrides({ units: null, drivers: null, mileage: null }); setConflictQueue([]);
    setCarrierId(null); setParseMsg(''); setShowCarrierList(false); setActiveTab('Documents');
    setCarrierShared(false); setCarrierOwner(''); setCarrierSharedWith([]);
    setShowShareDropdown(false); setShareMsg('');
    setCarrierStatus('prospect'); setPipelineData({});
    setLostForm({ reason: '', market: '', premium: '', notes: '' });
    setBoundForm({ market: '', effectiveDate: '', expDate: '', premium: '' });
    setAccountNotes([]);
    accountVersionRef.current += 1;
    saveRevisionRef.current = 0;
    setSaveRevision(0);
    setSaveState('idle');
    setSavedMsg('');
    savePendingRef.current = null;
  };

  // Save the current account (flush debounce) then load the requested one.
  // The accountVersionRef mechanism ensures the in-flight save cannot corrupt
  // the newly loaded account's state even if it completes late.
  const selectCarrier = (id) => {
    clearTimeout(autoSaveTimerRef.current);
    const hasContent = !!(companyName.trim() || dotNumber.trim() ||
      drivers.some(d => d.name?.trim()) ||
      tractors.some(t => t.year?.trim() || t.make?.trim() || t.vin?.trim()) ||
      Object.values(documents).some(files => files.length > 0));
    if (carrierId || (!carrierId && hasContent)) {
      saveCarrierRef.current?.(true); // fire-and-forget; version guard handles the rest
    }
    loadCarrier(id);
  };

  // ── Lead Sheet renderer ──
  const renderLeadSheet = () => {
    const ls = leadSheet;
    const filledCount = LEAD_SHEET_REQUIRED_FIELDS.filter(f => ls[f]).length;
    const pct = Math.round(filledCount / LEAD_SHEET_REQUIRED_FIELDS.length * 100);
    const cabTag = cabData ? <span className="ls-from-cab">from CAB</span> : null;
    const coiTag = coiData ? <span className="ls-from-coi">from COI</span> : null;
    const appTag = appData ? <span className="ls-from-app">from APP</span> : null;

    // Inline conflict badge: shown when appData and cabData disagree on a numeric field
    const conflictBadge = (appVal, cabVal) => {
      if (!appData || !cabData) return null;
      const a = String(appVal || '').trim();
      const c = String(cabVal || '').trim();
      if (!a || !c || a === c) return null;
      return (
        <span className="ls-conflict" title={`APP: ${a} · CAB: ${c}`}>
          ⚠ APP: {a} · CAB: {c}
        </span>
      );
    };

    const YNToggle = ({ field }) => (
      <div className="ls-yn">
        {['yes','no'].map(v => (
          <button key={v} className={`ls-yn-btn${ls[field] === v ? ' active' : ''}`}
            onClick={() => updateLS(field, v)}>{v === 'yes' ? 'Yes' : 'No'}</button>
        ))}
      </div>
    );

    const yearsInBiz = parseInt(ls.yearsInBusiness) || (cabData?.company?.firstAssigned
      ? new Date().getFullYear() - new Date(cabData.company.firstAssigned).getFullYear() : 99);

    return (
      <>
        {/* Action Bar */}
        <div className="card ls-action-bar">
          <div className="ls-action-left">
            <button className="ls-autofill-btn" onClick={autoFillLeadSheet}
              disabled={!cabData && !coiData && !appData} title={!cabData && !coiData && !appData ? 'Parse a document first' : ''}>
              ✨ Auto-Fill from {appData ? 'APP' : 'CAB'}{coiData ? ' / COI' : ''}
            </button>
            {pct > 0 && <span className="ls-pct-note">{filledCount}/{LEAD_SHEET_REQUIRED_FIELDS.length} required fields — {pct}% complete</span>}
          </div>
          <div className="ls-export-btns">
            <button className="ls-export-btn" onClick={copyLeadSheetToClipboard}>📋 Copy to Clipboard</button>
            <button className="ls-export-btn" onClick={() => window.print()}>🖨️ Print / Save as PDF</button>
          </div>
        </div>

        {/* Section 1 — Company Information */}
        <div className="card">
          <div className="card-title"><span className="section-icon">🏢</span>Company Information</div>
          <div className="ls-grid">
            <div className="ls-field">
              <label className="ls-label">Company Name</label>
              <input className="ls-input ls-input-readonly" value={companyName} readOnly />
            </div>
            <div className="ls-field">
              <label className="ls-label">DOT Number</label>
              <input className="ls-input ls-input-readonly" value={dotNumber} readOnly />
            </div>
            <div className="ls-field">
              <label className="ls-label">FEIN {appData?.company?.fein && appTag}</label>
              <input className="ls-input" value={ls.fein}
                onChange={e => updateLS('fein', e.target.value)} placeholder="XX-XXXXXXX" />
            </div>
            <div className="ls-field">
              <label className="ls-label">Years in Business {appData?.operations?.yearsInBusiness > 0 ? appTag : (cabData?.company?.firstAssigned && cabTag)} {conflictBadge(appData?.operations?.yearsInBusiness, cabData?.company?.firstAssigned ? String(new Date().getFullYear() - new Date(cabData.company.firstAssigned).getFullYear()) : '')}</label>
              <input className="ls-input" value={ls.yearsInBusiness}
                onChange={e => updateLS('yearsInBusiness', e.target.value)} placeholder="e.g. 7" />
            </div>
            <div className="ls-field">
              <label className="ls-label">State {cabData?.company?.address && cabTag}</label>
              <input className="ls-input" value={ls.state}
                onChange={e => updateLS('state', e.target.value)} placeholder="e.g. SC" />
            </div>
            <div className="ls-field">
              <label className="ls-label">Current Insurer {(cabData?.insurance?.carrier || cabData?.insurance?.company) && cabTag}</label>
              <input className="ls-input" value={ls.currentInsurer}
                onChange={e => updateLS('currentInsurer', e.target.value)} placeholder="Insurance company" />
            </div>
            <div className="ls-field">
              <label className="ls-label">Owner's Name {appData?.company?.ownerName ? appTag : ((Array.isArray(cabData?.company?.contacts) ? cabData.company.contacts.length > 0 : cabData?.company?.contact) && cabTag)}</label>
              <input className="ls-input" value={ls.ownerName}
                onChange={e => updateLS('ownerName', e.target.value)} placeholder="Principal contact" />
            </div>
            <div className="ls-field">
              <label className="ls-label">Owner's Date of Birth</label>
              <input className="ls-input" type="date" value={ls.ownerDob}
                onChange={e => updateLS('ownerDob', e.target.value)} />
            </div>
            <div className="ls-field ls-field-wide">
              <label className="ls-label">Garaging Address {appData?.company?.address ? appTag : (cabData?.company?.address && cabTag)}</label>
              <input className="ls-input" value={ls.garagingAddress}
                onChange={e => updateLS('garagingAddress', e.target.value)} placeholder="Full garaging address" />
            </div>
            <div className="ls-field">
              <label className="ls-label">Phone Number {appData?.company?.phone ? appTag : (cabData?.company?.phone && cabTag)}</label>
              <input className="ls-input" value={ls.phone}
                onChange={e => updateLS('phone', e.target.value)} placeholder="(555) 555-5555" />
            </div>
            <div className="ls-field">
              <label className="ls-label">Current X-Date / Policy Expiration {coiData ? coiTag : (cabData?.insurance?.cancellationDate && cabTag)}</label>
              <input className="ls-input" value={ls.currentXDate}
                onChange={e => updateLS('currentXDate', e.target.value)} placeholder="MM/DD/YYYY" />
            </div>
          </div>
        </div>

        {/* Section 2 — Operations */}
        <div className="card">
          <div className="card-title"><span className="section-icon">🚛</span>Operations</div>
          <div className="ls-grid">
            <div className="ls-field ls-field-wide">
              <label className="ls-label">Commodities Hauled</label>
              <textarea className="ls-textarea" rows={2} value={commodities}
                onChange={e => setCommodities(e.target.value)} placeholder="What do you primarily haul?" />
            </div>
            <div className="ls-field">
              <label className="ls-label">Radius of Operation {appData?.operations?.radiusOfOperation ? appTag : (cabData?.fleet?.radiusDistribution?.length > 0 && cabTag)}</label>
              <input className="ls-input" value={ls.radiusOfOperation}
                onChange={e => updateLS('radiusOfOperation', e.target.value)} placeholder="e.g. 500 miles" />
            </div>
            <div className="ls-field">
              <label className="ls-label">20%+ change in fleet in last year?</label>
              <YNToggle field="fleetChangeLastYear" />
            </div>
            <div className="ls-field">
              <label className="ls-label">Planning on adding trucks this year?</label>
              <div className="ls-yn-with-detail">
                <YNToggle field="planningAddTrucks" />
                {ls.planningAddTrucks === 'yes' && (
                  <input className="ls-input ls-inline-input" value={ls.addTrucksCount}
                    onChange={e => updateLS('addTrucksCount', e.target.value)} placeholder="How many?" />
                )}
              </div>
            </div>
            <div className="ls-field">
              <label className="ls-label">Being non-renewed or canceled?</label>
              <div className="ls-yn-with-detail">
                <YNToggle field="beingNonRenewed" />
                {ls.beingNonRenewed === 'yes' && (
                  <input className="ls-input ls-inline-input" value={ls.nonRenewalReason}
                    onChange={e => updateLS('nonRenewalReason', e.target.value)} placeholder="Why?" />
                )}
              </div>
            </div>
            <div className="ls-field">
              <label className="ls-label">Trucks Owner-Ops or Company Owned {appData?.operations?.ownerOps > 0 || appData?.operations?.companyDrivers > 0 ? appTag : (cabData?.fleet && cabTag)}</label>
              <div className="ls-yn">
                {['owner-ops','company','both'].map(v => (
                  <button key={v} className={`ls-yn-btn${ls.ownerOpsOrCompany === v ? ' active' : ''}`}
                    onClick={() => updateLS('ownerOpsOrCompany', v)}>
                    {v === 'owner-ops' ? 'Owner-Ops' : v === 'company' ? 'Company' : 'Both'}
                  </button>
                ))}
              </div>
            </div>
            <div className="ls-field">
              <label className="ls-label">Any drivers out of state?</label>
              <div className="ls-yn-with-detail">
                <YNToggle field="driversOutOfState" />
                {ls.driversOutOfState === 'yes' && (
                  <input className="ls-input ls-inline-input" value={ls.driversOutOfStateExplain}
                    onChange={e => updateLS('driversOutOfStateExplain', e.target.value)} placeholder="Explain" />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Section 3 — Fleet */}
        <div className="card">
          <div className="card-title"><span className="section-icon">🚚</span>Fleet</div>
          <div className="ls-grid">
            <div className="ls-field">
              <label className="ls-label">Number of Trucks {conflictBadge(appData?.operations?.powerUnits, cabData?.fleet?.totalPowerUnits)}</label>
              <input className="ls-input ls-input-readonly"
                value={tractors.filter(t => t.year || t.make || t.vin).length || ''} readOnly />
            </div>
            <div className="ls-field">
              <label className="ls-label">Number of Trailers {conflictBadge(appData?.operations?.trailers, cabData?.fleet?.leasedTrailers)}</label>
              <input className="ls-input ls-input-readonly"
                value={trailers.filter(t => t.year || t.make || t.vin).length || parseInt(cabData?.fleet?.leasedTrailers) || ''} readOnly />
            </div>
            <div className="ls-field">
              <label className="ls-label">Number of Drivers {conflictBadge(appData?.operations?.drivers, cabData?.fleet?.drivers)}</label>
              <input className="ls-input ls-input-readonly"
                value={drivers.filter(d => d.name).length || parseInt(cabData?.fleet?.drivers) || ''} readOnly />
            </div>
            <div className="ls-field" />
            <div className="ls-field">
              <label className="ls-label">Trucks equipped with ELDs?</label>
              <div className="ls-yn-with-detail">
                <YNToggle field="eldsInstalled" />
                {ls.eldsInstalled === 'yes' && (
                  <>
                    <input className="ls-input ls-inline-input" value={ls.eldBrand}
                      onChange={e => updateLS('eldBrand', e.target.value)} placeholder="Brand" />
                    <TCBadge status={tcEldStatus(ls.eldBrand)} />
                  </>
                )}
              </div>
            </div>
            <div className="ls-field">
              <label className="ls-label">Trucks equipped with dashcams?</label>
              <div className="ls-yn-with-detail">
                <YNToggle field="dashcams" />
                {ls.dashcams === 'yes' && (
                  <>
                    <input className="ls-input ls-inline-input" value={ls.dashcamBrand}
                      onChange={e => updateLS('dashcamBrand', e.target.value)} placeholder="Brand" />
                    <TCBadge status={tcCamStatus(ls.dashcamBrand)} />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* TruckerCloud Connection */}
        {(ls.eldsInstalled || ls.dashcams) && (() => {
          const eldSt = ls.eldsInstalled === 'yes' ? tcEldStatus(ls.eldBrand) : 'unknown';
          const camSt = ls.dashcams      === 'yes' ? tcCamStatus(ls.dashcamBrand) : 'unknown';
          const overall = tcOverallStatus(eldSt, camSt);
          const overallInfo = {
            both:    { cls: 'tc-overall-green',  icon: '✓', text: 'Ready for TruckerCloud connection' },
            partial: { cls: 'tc-overall-yellow', icon: '⚠', text: 'Partial — one provider compatible, one not listed' },
            none:    { cls: 'tc-overall-red',    icon: '✗', text: 'Not TruckerCloud ready — incompatible providers' },
            unknown: { cls: 'tc-overall-gray',   icon: '?', text: 'TruckerCloud status unknown — enter provider brands above' },
          }[overall];
          return (
            <div className="card tc-card">
              <div className="card-title"><span className="section-icon">🔗</span>TruckerCloud Connection</div>
              <div className="tc-rows">
                {ls.eldsInstalled === 'yes' && (
                  <div className="tc-row">
                    <span className="tc-row-label">ELD Compatible with TruckerCloud</span>
                    <TCBadge status={eldSt} />
                  </div>
                )}
                {ls.dashcams === 'yes' && (
                  <div className="tc-row">
                    <span className="tc-row-label">Dashcam Compatible with TruckerCloud</span>
                    <TCBadge status={camSt} />
                  </div>
                )}
                <div className={`tc-overall ${overallInfo.cls}`}>
                  <span className="tc-overall-icon">{overallInfo.icon}</span>
                  <span>{overallInfo.text}</span>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Section 4 — Safety */}
        <div className="card">
          <div className="card-title"><span className="section-icon">🛡️</span>Safety</div>
          <div className="ls-grid">
            <div className="ls-field">
              <label className="ls-label">Number of BASIC Alerts {cabData?.basics && cabTag}</label>
              <input className="ls-input" value={ls.basicAlerts}
                onChange={e => updateLS('basicAlerts', e.target.value)} placeholder="e.g. 2" />
            </div>
            <div className="ls-field">
              <label className="ls-label">Target / Expiring Pricing</label>
              <input className="ls-input" value={ls.targetPricing}
                onChange={e => updateLS('targetPricing', e.target.value)} placeholder="$0,000" />
            </div>
            <div className="ls-field ls-field-wide">
              <label className="ls-label">Corrective Action Plan</label>
              <textarea className="ls-textarea" rows={3} value={ls.correctiveActionPlan}
                onChange={e => updateLS('correctiveActionPlan', e.target.value)}
                placeholder="Describe any safety improvements or corrective actions taken…" />
            </div>
          </div>
        </div>

        {/* Section 5 — Commodity Checkboxes */}
        <div className="card">
          <div className="card-title"><span className="section-icon">📦</span>Commodity Checkboxes</div>
          <div className="ls-commodity-groups">
            {COMMODITY_GROUPS.map(group => (
              <div key={group.key} className="ls-commodity-group">
                <div className="ls-commodity-group-title">{group.label}</div>
                <div className="ls-commodity-items">
                  {group.items.map(item => (
                    <label key={item.key} className="ls-checkbox-item">
                      <input type="checkbox" checked={!!ls[group.key]?.[item.key]}
                        onChange={e => updateLSNested(group.key, item.key, e.target.checked)} />
                      <span>{item.label}</span>
                    </label>
                  ))}
                  {group.key === 'autoHauler' && (
                    <div className="ls-avg-cost">
                      <label className="ls-label">Avg cost per vehicle</label>
                      <input className="ls-input" value={ls.autoHauler?.avgCostPerVehicle || ''}
                        onChange={e => updateLSNested('autoHauler', 'avgCostPerVehicle', e.target.value)}
                        placeholder="$00,000" />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Section 6 — Load Agreements */}
        <div className="card">
          <div className="card-title"><span className="section-icon">📝</span>Load Agreements</div>
          <label className="ls-label">What companies do you have agreements/contracts with for your loads?</label>
          <textarea className="ls-textarea" rows={4} value={ls.loadAgreements}
            onChange={e => updateLS('loadAgreements', e.target.value)}
            placeholder="e.g. Amazon DSP, Walmart, Convoy, Uber Freight, direct shippers…" />
        </div>

        {/* Section 7 — Submission Readiness */}
        <div className="card">
          <div className="card-title">
            <span className="section-icon">☑</span>Submission Readiness
            <span className="ls-checklist-hint">Click any item to navigate</span>
          </div>
          {(() => {
            const rd = headerReadiness;
            const statusColor  = rd.status === 'READY' ? '#16a34a' : rd.status === 'CONDITIONALLY_READY' ? '#d97706' : '#dc2626';
            const statusBg     = rd.status === 'READY' ? '#f0fdf4' : rd.status === 'CONDITIONALLY_READY' ? '#fffbeb' : '#fef2f2';
            const statusBorder = rd.status === 'READY' ? '#bbf7d0' : rd.status === 'CONDITIONALLY_READY' ? '#fde68a' : '#fecaca';
            const statusLabel  = rd.status === 'READY' ? 'READY' : rd.status === 'CONDITIONALLY_READY' ? 'CONDITIONAL' : 'NOT READY';
            return (
              <>
                {/* Status header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: statusBg, border: `1px solid ${statusBorder}`, borderRadius: 8, marginBottom: 12 }}>
                  <span style={{ fontWeight: 800, fontSize: 12, color: statusColor, letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{statusLabel}</span>
                  <span style={{ fontWeight: 800, fontSize: 24, color: statusColor, lineHeight: 1, whiteSpace: 'nowrap' }}>{rd.score}%</span>
                  <span style={{ flex: 1, fontSize: 12, color: statusColor, lineHeight: 1.4 }}>{rd.summary}</span>
                  <span style={{ fontSize: 11, color: statusColor, whiteSpace: 'nowrap', opacity: 0.8 }}>{rd.completeCount}/{rd.totalCount} required</span>
                </div>
                {/* Blockers */}
                {rd.blockers.length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    {rd.blockers.map((b, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#dc2626', fontWeight: 600, marginBottom: 3 }}>
                        <span style={{ flexShrink: 0 }}>🚫</span>{b}
                      </div>
                    ))}
                  </div>
                )}
                {/* Checklist items */}
                <div className="ls-doc-checklist">
                  {rd.items.map((item, i) => {
                    const icon = item.checked ? '✓' : item.partial ? '⚠' : item.optional ? '○' : '✗';
                    const cls = item.checked ? 'ls-doc-checked' : item.partial ? 'ls-doc-partial' : item.highlight ? 'ls-doc-required' : item.optional ? 'ls-doc-optional' : 'ls-doc-missing';
                    return (
                      <div key={i} className={`ls-doc-item ${cls}`} onClick={() => setActiveTab(item.tab)} title={`Go to ${item.tab} tab`}>
                        <span className="ls-doc-icon">{icon}</span>
                        <div className="ls-doc-content">
                          <span className="ls-doc-label">{item.label}</span>
                          <span className="ls-doc-detail">{item.detail}</span>
                          {item.note && !item.checked && <span className="ls-doc-note">{item.note}</span>}
                        </div>
                        <span className="ls-doc-arrow">›</span>
                      </div>
                    );
                  })}
                </div>
                {/* Next actions */}
                {rd.nextActions.length > 0 && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>Next Actions</div>
                    {rd.nextActions.map((a, i) => (
                      <div key={i} style={{ fontSize: 12, color: '#475569', marginBottom: 3 }}>→ {a}</div>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
          <div className="ls-checklist-footer">
            {results.missing
              ? <button className="ls-missing-btn ls-missing-view" onClick={() => setActiveTab('Missing Items')}>View Full Missing Items Report →</button>
              : <button className="ls-missing-btn" disabled={anyLoading} onClick={() => { generateMissing(); setActiveTab('Missing Items'); }}>
                  {loading.missing ? <><span className="spinner parse-spinner" /> Running…</> : '🟡 Run Missing Items Report'}
                </button>
            }
          </div>
        </div>

        {/* Section 8 — Additional Notes */}
        <div className="card">
          <div className="card-title"><span className="section-icon">💬</span>Additional Notes</div>
          <textarea className="ls-textarea" rows={5} value={ls.additionalNotes}
            onChange={e => updateLS('additionalNotes', e.target.value)}
            placeholder="Any other relevant information about this account…" />
        </div>
      </>
    );
  };

  // ── Render helpers ──
  const renderDocs = () => {
    const activePortals = portals.filter(p => p.active && p.carrierId === carrierId);
    const portalUrl = (id) => `${window.location.origin}/portal/${id}`;
    const statusLabel = (p) =>
      p.status === 'submitted' ? 'Submitted' :
      p.submissions?.length > 0 ? 'Partially Submitted' : 'Waiting';
    const statusColor = (p) =>
      p.status === 'submitted' ? '#059669' :
      p.submissions?.length > 0 ? '#d97706' : '#6366f1';

  return (
    <>
    {/* Submission Banner */}
    {submissionBanner && (
      <div className="portal-banner">
        <div className="portal-banner-left">
          <span className="portal-banner-icon">🔔</span>
          <div>
            <div className="portal-banner-title">Client submitted new information on {new Date(submissionBanner.submittedAt).toLocaleString()}</div>
            <div className="portal-banner-sub">{submissionBanner.carrierName} submitted through the client portal — review and apply below</div>
          </div>
        </div>
        <div className="portal-banner-actions">
          <button className="portal-banner-apply" onClick={() => applyPortalSubmission(submissionBanner.portalId)}>
            Apply to Carrier
          </button>
          <button className="portal-banner-dismiss" onClick={dismissBanner}>Dismiss</button>
        </div>
      </div>
    )}

    <div className="card">
      <div className="card-title">
        <span className="section-icon">📁</span>Document Checklist
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          {activePortals.length > 0 && (
            <span className="portal-active-badge">{activePortals.length} Active Portal{activePortals.length > 1 ? 's' : ''}</span>
          )}
          <button className="portal-generate-btn" onClick={exportAllDocuments} disabled={exportingDocs}>
            {exportingDocs ? <span className="spinner parse-spinner" /> : '⬇️'} Export All
          </button>
          <button className="portal-generate-btn" onClick={generatePortal} disabled={portalGenerating}>
            {portalGenerating ? <span className="spinner parse-spinner" /> : '🔗'} Generate Client Portal
          </button>
        </div>
      </div>
      <div className="doc-grid">
        {DOC_TYPES.map(d => {
          const files = documents[d.id] || [];
          const hasFiles = files.length > 0;
          return (
            <div key={d.id} className={`doc-tile${hasFiles ? ' uploaded' : ''}${dragOver === d.id ? ' drag-over' : ''}`} {...dragHandlers(d.id)}>
              <div className="doc-tile-header" onClick={() => handleDocClick(d.id)}>
                <div className="doc-tile-icon">{d.icon}</div>
                <div className="doc-tile-label">{dragOver === d.id ? 'Drop files here' : d.label}</div>
                {dragOver === d.id
                  ? null
                  : hasFiles
                    ? <span className="doc-count-badge">{files.length} file{files.length > 1 ? 's' : ''}</span>
                    : <div className="doc-upload-hint">Click or drag to upload</div>
                }
              </div>
              {uploadProgress[d.id] === 'uploading' && (
                <div className="doc-upload-status uploading" onClick={e => e.stopPropagation()}>
                  <span className="spinner doc-upload-spinner" /> Uploading to cloud...
                </div>
              )}
              {uploadProgress[d.id] === 'error' && (
                <div className="doc-upload-status error" onClick={e => e.stopPropagation()}>
                  &#9888; Upload failed — stored locally only
                </div>
              )}
              {hasFiles && (
                <>
                  <ul className="doc-file-list">
                    {files.map((f, i) => (
                      <li key={i} className="doc-file-item">
                        <span className="doc-file-name" title={f instanceof File ? f.name : f.name}>
                          {f instanceof File ? f.name : f.name}
                          {f?.url && <span className="doc-cloud-icon" title="Saved to cloud">&#9729;</span>}
                        </span>
                        <button
                          className="doc-file-remove"
                          onClick={e => { e.stopPropagation(); handleRemoveFile(d.id, i); }}
                          title="Remove file"
                        >✕</button>
                      </li>
                    ))}
                    <li className="doc-file-add" onClick={() => handleDocClick(d.id)}>＋ Add more</li>
                  </ul>
                  {d.id === 'cab' && (
                    <div className="parse-action">
                      <button className="parse-btn" onClick={e => { e.stopPropagation(); readCABReport(); }} disabled={cabLoading}>
                        {cabLoading ? <span className="spinner parse-spinner" /> : '🔍'} Read CAB Report
                      </button>
                      {cabMsg && <div className={`parse-msg${cabMsg.startsWith('⚠️') ? ' error' : ''}`}>{cabMsg}</div>}
                    </div>
                  )}
                  {d.id === 'lossRuns' && (
                    <div className="parse-action">
                      <button
                        className="parse-btn parse-btn-verify"
                        onClick={e => { e.stopPropagation(); setActiveTab('Loss Runs'); }}
                      >
                        ✓ Verify Coverage →
                      </button>
                    </div>
                  )}
                  {d.id === 'appLossSummary' && (
                    <div className="parse-action">
                      <div className="parse-btn-row">
                        <button className="parse-btn" onClick={e => { e.stopPropagation(); parseApplication(); }} disabled={appLoading}>
                          {appLoading ? <span className="spinner parse-spinner" /> : '🔍'} Parse Application
                        </button>
                        <button className="parse-btn autofill-trigger-btn" onClick={e => { e.stopPropagation(); autoFillApplication(); }} disabled={autoFillLoading}>
                          {autoFillLoading ? <span className="spinner parse-spinner" /> : '🤖'} Auto-Fill Application
                        </button>
                      </div>
                      {appMsg && <div className={`parse-msg${appMsg.startsWith('⚠️') ? ' error' : ''}`}>{appMsg}</div>}
                      {autoFillMsg && <div className={`parse-msg${autoFillMsg.startsWith('⚠️') ? ' error' : ''}`}>{autoFillMsg}</div>}
                      {appTemplateCount !== null && (
                        <div className="app-template-count">
                          📚 {appTemplateCount} application template{appTemplateCount !== 1 ? 's' : ''} learned
                          {appTemplateCount === 0 && <span className="app-template-hint"> — upload completed applications to train the system</span>}
                        </div>
                      )}
                    </div>
                  )}
                  {d.id === 'coi' && (
                    <div className="parse-action">
                      <button className="parse-btn" onClick={e => { e.stopPropagation(); parseCOI(); }} disabled={coiLoading}>
                        {coiLoading ? <span className="spinner parse-spinner" /> : '🔍'} Parse COI
                      </button>
                      {coiMsg && <div className={`parse-msg${coiMsg.startsWith('⚠️') ? ' error' : ''}`}>{coiMsg}</div>}
                    </div>
                  )}
                  {d.id === 'ifta' && (
                    <div className="parse-action">
                      <button className="parse-btn" onClick={e => { e.stopPropagation(); parseIFTA(); }} disabled={iftaLoading}>
                        {iftaLoading ? <span className="spinner parse-spinner" /> : '🔍'} Parse IFTA
                      </button>
                      {iftaMsg && <div className={`parse-msg${iftaMsg.startsWith('⚠️') ? ' error' : ''}`}>{iftaMsg}</div>}
                    </div>
                  )}
                  {['equipmentList', 'driverList', 'mvrs'].includes(d.id) && (
                    <div className="parse-action">
                      <button
                        className="parse-btn"
                        onClick={e => { e.stopPropagation(); parseDocument(d.id); }}
                        disabled={loading.parse}
                      >
                        {loading.parse ? <span className="spinner parse-spinner" /> : '🔍'}
                        {['driverList', 'mvrs'].includes(d.id) ? ' Parse Drivers' : ' Parse Equipment'}
                      </button>
                      {d.id === 'equipmentList' && (
                        <button
                          className="parse-btn"
                          style={{ marginLeft: 6 }}
                          onClick={e => { e.stopPropagation(); parseDocument(d.id, true); }}
                          disabled={loading.parse}
                          title="Use when this document contains both equipment and driver information"
                        >
                          {loading.parse ? <span className="spinner parse-spinner" /> : '🔍'} Parse Equipment & Drivers
                        </button>
                      )}
                      {parseMsg && <div className={`parse-msg${parseMsg.startsWith('⚠️') ? ' error' : ''}`}>{parseMsg}</div>}
                    </div>
                  )}
                  {d.id === 'cdl' && (
                    <div className="parse-action">
                      <button className="parse-btn" onClick={e => { e.stopPropagation(); parseCDLImages(); }} disabled={cdlLoading}>
                        {cdlLoading ? <span className="spinner parse-spinner" /> : '🪪'} Parse CDL
                      </button>
                      {cdlMsg && <div className={`parse-msg${cdlMsg.startsWith('⚠️') ? ' error' : ''}`}>{cdlMsg}</div>}
                    </div>
                  )}
                </>
              )}
              <input ref={fileRefs.current[d.id]} type="file" multiple className="file-input-hidden"
                accept={d.id === 'cdl' ? 'image/jpeg,image/jpg,image/png' : undefined}
                onChange={e => handleFileChange(d.id, e)} />
            </div>
          );
        })}
      </div>
    </div>
    <CABErrorBoundary key={carrierId || 'new'}>
      {cabData && <CABSummaryCard cabData={cabData} />}
    </CABErrorBoundary>

    {/* Portal Modal */}
    {/* Auto-fill application preview modal */}
    {autoFillModal && (
      <AppAutoFillModal
        fields={autoFillModal.fields}
        onClose={() => { setAutoFillModal(null); setAutoFillMsg(''); }}
        onConfirmField={(fieldName, filledValue, source, editedValue) => {
          saveAppFieldMapping(fieldName, filledValue, source, editedValue);
        }}
        onAccept={(acceptedFields) => {
          // TODO (Phase 2): once enough templates are trained, use accepted fields to
          // fill a blank PDF application form via pdf-lib or a similar library.
          // For now, show a summary of accepted fields to the user.
          const summary = acceptedFields.map(f => `${f.fieldName}: ${f.editedValue}`).join('\n');
          setAutoFillMsg(`✓ ${acceptedFields.length} field${acceptedFields.length !== 1 ? 's' : ''} accepted. Copy to your application:\n${summary}`);
          setAutoFillModal(null);
        }}
      />
    )}

    {portalModal && (
      <div className="portal-modal-overlay" onClick={() => setPortalModal(null)}>
        <div className="portal-modal" onClick={e => e.stopPropagation()}>
          <div className="portal-modal-header">
            <div className="portal-modal-title">🔗 Client Portal Link Generated</div>
            <button className="portal-modal-close" onClick={() => setPortalModal(null)}>✕</button>
          </div>
          <div className="portal-modal-body">
            <p className="portal-modal-desc">
              Send this link to <strong>{companyName || 'your client'}</strong>. They can use it to submit documents, equipment, and driver information directly.
            </p>
            <div className="portal-link-box">
              <span className="portal-link-text">{portalModal.url}</span>
              <button className="portal-link-copy" onClick={() => copyPortalLink(portalModal.url)}>
                {portalLinkCopied ? '✓ Copied!' : 'Copy Link'}
              </button>
            </div>
            <p className="portal-modal-note">The portal stays active until you close it from the Portals section below.</p>
          </div>
        </div>
      </div>
    )}

    {/* Portals Management Section */}
    {portals.filter(p => p.carrierId === carrierId).length > 0 && (
      <div className="card portal-mgmt-card">
        <div className="card-title"><span className="section-icon">🔗</span>Client Portals</div>
        <div className="portal-list">
          {portals
            .filter(p => p.carrierId === carrierId)
            .map(p => {
              const sub = p.submissions?.[p.submissions.length - 1];
              return (
                <div key={p.id} className={`portal-row${p.active ? '' : ' portal-row-closed'}`}>
                  <div className="portal-row-info">
                    <div className="portal-row-name">
                      {p.carrierName}
                      {p.status === 'submitted' && !p.notificationSeen && (
                        <span className="portal-new-badge">NEW</span>
                      )}
                    </div>
                    <div className="portal-row-meta">
                      Created {new Date(p.createdAt).toLocaleDateString()} ·
                      Last activity {new Date(p.lastActivity).toLocaleDateString()}
                    </div>
                    {sub && (
                      <div className="portal-row-sub-info">
                        Submitted {new Date(sub.submittedAt).toLocaleString()} ·
                        {(sub.tractors?.length || 0)} tractors · {(sub.trailers?.length || 0)} trailers · {(sub.drivers?.length || 0)} drivers
                      </div>
                    )}
                  </div>
                  <div className="portal-row-status" style={{ color: statusColor(p) }}>
                    {p.active ? statusLabel(p) : 'Closed'}
                  </div>
                  <div className="portal-row-actions">
                    {p.active && (
                      <>
                        <button className="portal-action-btn" onClick={() => { setPortalLinkCopied(false); setPortalModal({ url: portalUrl(p.id), id: p.id }); }} title="Copy link">
                          Resend Link
                        </button>
                        <button className="portal-action-btn portal-action-close" onClick={() => { if (window.confirm('Close this portal? The link will stop working.')) closePortal(p.id); }} title="Close portal">
                          Close Portal
                        </button>
                      </>
                    )}
                    {sub && (
                      <button className="portal-action-btn portal-action-apply" onClick={() => applyPortalSubmission(p.id)}>
                        Apply Data
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    )}
    </>
  );
  }; // end renderDocs

  const renderLossRuns = () => {
    const lrFiles = documents['lossRuns'] || [];
    const hasFiles = lrFiles.length > 0;
    const parseNum = (v) => { const n = parseFloat(String(v || '').replace(/[$,]/g, '')); return isNaN(n) ? 0 : n; };
    const fmtNum = (n) => n === 0 ? '—' : n.toLocaleString();
    const sumField = (section, field) => lossSummary[section].reduce((a, r) => a + parseNum(r[field]), 0);
    const n = lossSummary.summary.filter(r =>
      parseNum(r.lossReserve) > 0 || parseNum(r.totalIncurred) > 0 ||
      parseNum(r.totalClaims) > 0 || parseNum(r.units) > 0
    ).length || 1;
    const sumTotals = { lossReserve: sumField('summary','lossReserve'), totalIncurred: sumField('summary','totalIncurred'), totalClaims: sumField('summary','totalClaims'), units: sumField('summary','units'), totalMileage: sumField('summary','totalMileage') };
    const sumAvgs = Object.fromEntries(Object.entries(sumTotals).map(([k,v]) => [k, n > 0 ? v / n : 0]));
    return (
      <>
        <div className="card">
          <div className="card-title"><span className="section-icon">📄</span>Loss Runs</div>

          {/* Upload zone */}
          <div className={`doc-tile lr-upload-tile${hasFiles ? ' uploaded' : ''}${dragOver === 'lossRuns' ? ' drag-over' : ''}`} {...dragHandlers('lossRuns')}>
            <div className="doc-tile-header" onClick={() => handleDocClick('lossRuns')}>
              <div className="doc-tile-icon">📊</div>
              <div className="doc-tile-label">{dragOver === 'lossRuns' ? 'Drop files here' : 'Upload loss run files — last 5 years, all 4 lines (AL, Cargo, PD, GL)'}</div>
              {dragOver === 'lossRuns'
                ? null
                : hasFiles
                  ? <span className="doc-count-badge">{lrFiles.length} file{lrFiles.length > 1 ? 's' : ''}</span>
                  : <div className="doc-upload-hint">Click or drag to upload</div>
              }
            </div>
            {uploadProgress['lossRuns'] === 'uploading' && (
              <div className="doc-upload-status uploading" onClick={e => e.stopPropagation()}>
                <span className="spinner doc-upload-spinner" /> Uploading to cloud...
              </div>
            )}
            {uploadProgress['lossRuns'] === 'error' && (
              <div className="doc-upload-status error" onClick={e => e.stopPropagation()}>
                &#9888; Upload failed — stored locally only
              </div>
            )}
            {hasFiles && (
              <>
                <ul className="doc-file-list">
                  {lrFiles.map((f, i) => (
                    <li key={i} className="doc-file-item">
                      <span className="doc-file-name" title={f.name}>
                        {f.name}
                        {f?.url && <span className="doc-cloud-icon" title="Saved to cloud">&#9729;</span>}
                      </span>
                      <button
                        className="doc-file-remove"
                        onClick={e => { e.stopPropagation(); handleRemoveFile('lossRuns', i); }}
                        title="Remove file"
                      >✕</button>
                    </li>
                  ))}
                  <li className="doc-file-add" onClick={() => handleDocClick('lossRuns')}>＋ Add more</li>
                </ul>
                <div className="parse-action">
                  {['autoLiability','cargo','physicalDamage','generalLiability'].some(k => (appData?.lossSummary?.[k] || []).some(r => r.insurer)) && (
                    <div className="app-lr-banner">
                      📋 Loss summary data from the uploaded application is being used as the primary source. Re-verify only if you want to cross-check against individual loss run documents.
                    </div>
                  )}
                  <div className="parse-btn-row">
                    <div className="parse-step-wrap">
                      <button className="parse-btn" onClick={e => { e.stopPropagation(); parseLossRunsIntoTab(); }} disabled={lossRunsLoading}>
                        {lossRunsLoading ? <span className="spinner parse-spinner" /> : '🔍'} Parse Loss Runs
                      </button>
                      <div className="parse-step-hint">Step 1 — fills financial tables</div>
                    </div>
                    <div className="parse-step-wrap">
                      <button className="parse-btn parse-btn-verify" onClick={e => { e.stopPropagation(); verifyLossRuns(); }} disabled={lossRunVerifyLoading}>
                        {lossRunVerifyLoading ? <span className="spinner parse-spinner" /> : '✓'} Verify Loss Runs
                      </button>
                      <div className="parse-step-hint">Step 2 — confirms coverage grid</div>
                    </div>
                  </div>
                  {lossRunsMsg && <div className={`parse-msg${lossRunsMsg.startsWith('⚠️') ? ' error' : ''}`}>{lossRunsMsg}</div>}
                  {lossRunVerifyMsg && <div className={`parse-msg${lossRunVerifyMsg.startsWith('⚠️') ? ' error' : ''}`}>{lossRunVerifyMsg}</div>}
                  {!lossSummary._parsedAt && lossSummary.summary?.some(r => r.policyYear) && (
                    <div className="parse-msg" style={{ borderColor: '#d97706', background: '#fffbeb', color: '#92400e' }}>
                      ⚠️ Loss data was parsed before Phase 4a — some zero-loss coverages may be unverified. Re-parse to update.
                    </div>
                  )}
                  {trainingCount !== null && (
                    <div className="lr-training-count">📊 {trainingCount} verified example{trainingCount !== 1 ? 's' : ''} in training set</div>
                  )}
                </div>
              </>
            )}
            <input ref={fileRefs.current['lossRuns']} type="file" multiple className="file-input-hidden"
              onChange={e => handleFileChange('lossRuns', e)} />
          </div>
        </div>

        {lossWarnings.length > 0 && (
          <div className="loss-warnings-bar">
            <div className="loss-warnings-title">⚠ Data warnings — review before exporting:</div>
            <ul className="loss-warnings-list">
              {lossWarnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}
        {(lossSummary.summary.some(r => parseNum(r.totalIncurred) > 0 || parseNum(r.totalClaims) > 0) ||
          ['autoLiability','cargo','physicalDamage','generalLiability'].some(k =>
            lossSummary[k].some(r => r.insurer && !r.insurer.startsWith('N/A'))
          )
        ) && (
          <div className="loss-export-bar">
            <button className="ai-btn export" onClick={exportLossExcel}>
              📊 Export Loss Summary to Excel
            </button>
          </div>
        )}

        {/* Verification grid — shown once Verify Loss Runs has been run */}
        {lossRunVerification && <LossRunVerificationCard data={lossRunVerification} yib={parseFloat(leadSheet?.yearsInBusiness) || 0} />}

        {/* Feedback bar — only show for AI-verified results (not APP-sourced grids) */}
        {lossRunVerification && !lossRunVerification.fromApp && (
          <div className="lr-feedback-bar">
            {lrFeedbackMsg && (
              <div className={`lr-fb-msg${lrFeedbackMsg.startsWith('⚠️') ? ' lr-fb-warn' : ''}`}>
                {lrFeedbackMsg}
              </div>
            )}
            {lrFeedbackMode === '' && (
              <div className="lr-fb-actions">
                <span className="lr-fb-label">Was this extraction correct?</span>
                <button className="lr-fb-btn lr-fb-correct" onClick={async () => {
                  setLrFeedbackMsg('Saving…');
                  const ok = await saveLrTrainingExample(lossRunVerification, 'confirmed');
                  setLrFeedbackMsg(ok ? '✓ Saved as confirmed training example.' : '⚠️ Save failed — check API connection.');
                }}>
                  ✓ Mark as Correct
                </button>
                <button className="lr-fb-btn lr-fb-fix" onClick={() => {
                  setLrCorrectionGrid(lossRunVerification);
                  setLrFeedbackMode('correcting');
                }}>
                  ✗ Fix Errors
                </button>
              </div>
            )}
            {lrFeedbackMode === 'correcting' && lrCorrectionGrid && (
              <LrCorrectionEditor
                grid={lrCorrectionGrid}
                onGridChange={setLrCorrectionGrid}
                onSave={async () => {
                  setLrFeedbackMsg('Saving correction…');
                  const ok = await saveLrTrainingExample(lrCorrectionGrid, 'corrected');
                  if (ok) {
                    setLossRunVerification(lrCorrectionGrid);
                    setLrFeedbackMode('');
                    setLrCorrectionGrid(null);
                    setLrFeedbackMsg('✓ Correction saved as training example.');
                  } else {
                    setLrFeedbackMsg('⚠️ Save failed — check API connection.');
                  }
                }}
                onCancel={() => { setLrFeedbackMode(''); setLrCorrectionGrid(null); }}
              />
            )}
          </div>
        )}

        {/* Loss data entry — summary totals + per-line tables */}
        {(['autoLiability','cargo','physicalDamage','generalLiability'].some(k =>
          lossSummary[k].some(r => r.needsReview)
        )) && (
          <div className="parse-msg" style={{ borderColor: '#d97706', background: '#fffbeb', color: '#92400e', marginBottom: 8 }}>
            ⚠️ Some coverage sections have APP-reported figures that could not be confirmed from loss run documents.
            Rows marked <strong>NEEDS REVIEW</strong> show APP data as a reference — verify against the actual loss run and enter correct values.
          </div>
        )}
        <div className="card">
          <div className="card-title"><span className="section-icon">📉</span>Loss Data</div>

          {/* Summary Totals section */}
          <div className="loss-section">
            <div className="loss-section-title">SUMMARY TOTALS</div>
            {(cabData?.fleet?.annualMileage || cabData?.fleet?.totalPowerUnits) && (
              <div className="loss-cab-note">
                📋 Mileage and units auto-populated from CAB report
                {cabData.fleet.totalPowerUnits ? ` — ${cabData.fleet.totalPowerUnits} power units` : ''}
                {cabData.fleet.annualMileage   ? `, ${Number(String(cabData.fleet.annualMileage).replace(/,/g,'')).toLocaleString()} annual miles` : ''}
                {' '}applied to {POLICY_YEARS[0]}
              </div>
            )}
            <div className="table-wrap">
              <table className="data-table loss-table">
                <thead>
                  <tr>
                    <th>Policy Year</th>
                    <th>Loss Reserve</th>
                    <th>Total Incurred</th>
                    <th>Total Claims</th>
                    <th>Units</th>
                    <th>Total Mileage</th>
                  </tr>
                </thead>
                <tbody>
                  {lossSummary.summary.map((row, i) => (
                    <tr key={i}>
                      <td><input className="cell-input" value={row.policyYear} onChange={e => updateLossRow('summary', i, 'policyYear', e.target.value)} /></td>
                      <td><input className="cell-input" value={row.lossReserve} onChange={e => updateLossRow('summary', i, 'lossReserve', e.target.value)} placeholder="$0" /></td>
                      <td><input className="cell-input" value={row.totalIncurred} onChange={e => updateLossRow('summary', i, 'totalIncurred', e.target.value)} placeholder="$0" /></td>
                      <td><input className="cell-input" value={row.totalClaims} onChange={e => updateLossRow('summary', i, 'totalClaims', e.target.value)} placeholder="0" /></td>
                      <td><input className="cell-input" value={row.units} onChange={e => updateLossRow('summary', i, 'units', e.target.value)} placeholder="0" /></td>
                      <td><input className="cell-input" value={row.totalMileage} onChange={e => updateLossRow('summary', i, 'totalMileage', e.target.value)} placeholder="0" /></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="loss-total-row">
                    <td><strong>TOTAL</strong></td>
                    <td><strong>${fmtNum(sumTotals.lossReserve)}</strong></td>
                    <td><strong>${fmtNum(sumTotals.totalIncurred)}</strong></td>
                    <td><strong>{fmtNum(sumTotals.totalClaims)}</strong></td>
                    <td><strong>{fmtNum(sumTotals.units)}</strong></td>
                    <td><strong>{fmtNum(sumTotals.totalMileage)}</strong></td>
                  </tr>
                  <tr className="loss-avg-row">
                    <td><strong>AVG / YEAR</strong></td>
                    <td>${fmtNum(Math.round(sumAvgs.lossReserve))}</td>
                    <td>${fmtNum(Math.round(sumAvgs.totalIncurred))}</td>
                    <td>{(sumAvgs.totalClaims).toFixed(1)}</td>
                    <td>{(sumAvgs.units).toFixed(1)}</td>
                    <td>{fmtNum(Math.round(sumAvgs.totalMileage))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <LossSectionTable sectionKey="autoLiability"    title="AUTO LIABILITY LOSSES"    rows={lossSummary.autoLiability}    onUpdate={updateLossRow} />
          <LossSectionTable sectionKey="cargo"            title="MOTOR TRUCK CARGO LOSSES" rows={lossSummary.cargo}            onUpdate={updateLossRow} />
          <LossSectionTable sectionKey="physicalDamage"   title="PHYSICAL DAMAGE LOSSES"   rows={lossSummary.physicalDamage}   onUpdate={updateLossRow} />
          <LossSectionTable sectionKey="generalLiability" title="GENERAL LIABILITY LOSSES"  rows={lossSummary.generalLiability} onUpdate={updateLossRow} />
        </div>

      </>
    );
  };

  const renderEquipment = () => {
    const unitsOv = overrides.units;
    return (
      <div className="card">
        <div className="card-title">
          <span className="section-icon">🚛</span>Equipment
          {unitsOv && (
            <span className={`override-badge${unitsOv.confirmed ? ' override-confirmed' : ' override-unresolved'}`}
              title={`Using ${unitsOv.value} units (source: ${unitsOv.source === 'cab' ? 'CAB Report' : unitsOv.source.replace(/_/g,' ')})`}>
              {tractors.filter(r => r.year || r.make || r.vin).length} units
              {cabData?.fleet?.totalPowerUnits && tractors.filter(r => r.year || r.make || r.vin).length !== parseInt(cabData.fleet.totalPowerUnits)
                ? ` · CAB: ${cabData.fleet.totalPowerUnits}` : ''} — override active
            </span>
          )}
          {!unitsOv && cabData?.fleet?.totalPowerUnits && (() => {
            const count = tractors.filter(r => r.year || r.make || r.vin).length;
            const cab = parseInt(cabData.fleet.totalPowerUnits) || 0;
            return count > 0 && cab > 0 && count !== cab
              ? <span className="override-badge override-unresolved" title="Count differs from CAB report">{count} units · CAB: {cab} ⚠️</span>
              : null;
          })()}
        </div>
        <div className="subtab-nav" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {['Tractors', 'Trailers'].map(s => (
            <button key={s} className={`subtab-btn${equipSub === s ? ' active' : ''}`} onClick={() => setEquipSub(s)}>{s} ({s === 'Tractors' ? tractors.length : trailers.length})</button>
          ))}
          <button className="parse-btn" style={{ marginLeft: 'auto' }} onClick={exportEquipmentExcel}>📊 Export Equipment</button>
        </div>
        {equipSub === 'Tractors'
          ? <EditableTable cols={TRACTOR_COLS} rows={tractors} onRowChange={tH.onChange} onAddRow={tH.onAdd} onDelRow={tH.onDel} />
          : <EditableTable cols={TRAILER_COLS} rows={trailers} onRowChange={trH.onChange} onAddRow={trH.onAdd} onDelRow={trH.onDel} />
        }
        {unitsOv && (
          <button className="override-reset-btn" onClick={() => { setOverrides(p => ({ ...p, units: null })); checkConflicts(); }}>
            Reset power units override
          </button>
        )}
      </div>
    );
  };

  const renderDrivers = () => {
    const driversOv = overrides.drivers;
    return (
      <div className="card">
        <div className="card-title">
          <span className="section-icon">👤</span>Drivers ({drivers.length})
          {driversOv && (
            <span className={`override-badge${driversOv.confirmed ? ' override-confirmed' : ' override-unresolved'}`}
              title={`Using ${driversOv.value} drivers (source: ${driversOv.source === 'cab' ? 'CAB Report' : driversOv.source.replace(/_/g,' ')})`}>
              {drivers.filter(r => r.name || r.dob).length} drivers
              {cabData?.fleet?.drivers && drivers.filter(r => r.name || r.dob).length !== parseInt(cabData.fleet.drivers)
                ? ` · CAB: ${cabData.fleet.drivers}` : ''} — override active
            </span>
          )}
          {!driversOv && cabData?.fleet?.drivers && (() => {
            const count = drivers.filter(r => r.name || r.dob).length;
            const cab = parseInt(cabData.fleet.drivers) || 0;
            return count > 0 && cab > 0 && count !== cab
              ? <span className="override-badge override-unresolved" title="Count differs from CAB report">{count} drivers · CAB: {cab} ⚠️</span>
              : null;
          })()}
        </div>
        <button className="parse-btn" style={{ marginLeft: 'auto', marginBottom: 8 }} onClick={exportDriversExcel}>📊 Export Drivers</button>
        <EditableTable cols={DRIVER_COLS} rows={drivers} onRowChange={dH.onChange} onAddRow={dH.onAdd} onDelRow={dH.onDel} />
        {driversOv && (
          <button className="override-reset-btn" onClick={() => { setOverrides(p => ({ ...p, drivers: null })); checkConflicts(); }}>
            Reset drivers override
          </button>
        )}
      </div>
    );
  };

  const AIResultBox = ({ content, label }) => (
    content
      ? <div className="ai-result-box md-content" dangerouslySetInnerHTML={renderMarkdown(content)} />
      : <div className="ai-result-box empty">{label} output will appear here after generation.</div>
  );

  // Split narrative at "REVISIONS MADE" and render the two parts with different styles
  const renderNarrativeContent = (text, maxHeight) => {
    if (!text) return null;
    const splitIdx = text.search(/\n\s*REVISIONS MADE\s*\n/i);
    if (splitIdx === -1) {
      return <div className="ai-result-box md-content" style={maxHeight ? { maxHeight } : undefined} dangerouslySetInnerHTML={renderMarkdown(text)} />;
    }
    const narrative = text.slice(0, splitIdx).trim();
    const revisions = text.slice(splitIdx).trim();
    return (
      <>
        <div className="ai-result-box md-content" style={maxHeight ? { maxHeight } : undefined} dangerouslySetInnerHTML={renderMarkdown(narrative)} />
        <div className="revisions-box">
          <div className="revisions-box-label">Revisions Made</div>
          <div className="md-content revisions-content" dangerouslySetInnerHTML={renderMarkdown(revisions.replace(/^REVISIONS MADE\s*/i, ''))} />
        </div>
      </>
    );
  };

  const renderAIAnalysis = () => (
    <>
    <CABErrorBoundary key={carrierId || 'new'}>
      {cabData && <CABBasicsPanel cabData={cabData} />}
    </CABErrorBoundary>
    <div className="ai-grid">
      <div className="ai-panel">
        <div className="ai-panel-title">🔵 Risk Analysis {loading.risk && <span className="spinner" />}</div>
        <AIResultBox content={results.risk ? results.risk.replace(/^(Date:\s+).+$/m, `$1${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`) : ''} label="Risk Analysis" />
      </div>
      <div className="ai-panel">
        <div className="ai-panel-title">
          🟣 Submission Narrative
          {loading.narrative && (
            <span className="narrative-loading">
              <span className="spinner" />
              <span className="narrative-phase-label">
                {narrativePhase === 'generating' ? 'Generating narrative…'
                  : narrativePhase === 'refining' ? 'Refining for underwriters…'
                  : narrativePhase.startsWith('retry-p1-') ? `Retrying… (attempt ${narrativePhase.slice(-1)} of 3)`
                  : narrativePhase.startsWith('retry-p2-') ? `Retrying refinement… (attempt ${narrativePhase.slice(-1)} of 3)`
                  : ''}
              </span>
            </span>
          )}
        </div>
        {results.narrative
          ? renderNarrativeContent(results.narrative, null)
          : <div className="ai-result-box empty">Submission Narrative output will appear here after generation.</div>
        }
      </div>
      <div className="ai-panel">
        <div className="ai-panel-title">🟡 Missing Items Report {loading.missing && <span className="spinner" />}</div>
        <AIResultBox content={results.missing} label="Missing Items" />
      </div>
      <div className="ai-panel">
        <div className="ai-panel-title">🟢 Safety Playbook {loading.playbook && <span className="spinner" />}</div>
        <AIResultBox content={results.playbook} label="Safety Playbook" />
      </div>
    </div>
    </>
  );

  const renderSubmission = () => (
    <div className="card">
      <div className="card-title"><span className="section-icon">📤</span>Underwriter Submission Narrative</div>
      {results.narrative
        ? <>
            {renderNarrativeContent(results.narrative, 600)}
            <button className="copy-btn" onClick={() => copyText(results.narrative)}>
              {copied ? '✓ Copied!' : '📋 Copy to Clipboard'}
            </button>
          </>
        : <div className="empty-state">
            <div className="empty-state-icon">📝</div>
            <p>Generate the Submission Narrative using the button below to see it here.</p>
          </div>
      }
    </div>
  );

  const renderMissing = () => (
    <>
      <div className="card">
        <div className="card-title"><span className="section-icon">⚠️</span>Missing Items Report</div>
        {results.missing
          ? <div className="ai-result-box md-content" style={{ maxHeight: 400 }} dangerouslySetInnerHTML={renderMarkdown(results.missing)} />
          : <div className="empty-state"><div className="empty-state-icon">🔍</div><p>Click "Missing Items" to generate a report.</p></div>
        }
      </div>
      {results.missingEmail && (
        <div className="card">
          <div className="card-title"><span className="section-icon">✉️</span>Client Email Draft</div>
          <div className="email-draft-box">{results.missingEmail}</div>
          <button className="copy-btn" onClick={() => copyText(results.missingEmail)}>
            {copied ? '✓ Copied!' : '📋 Copy Email'}
          </button>
        </div>
      )}
    </>
  );

  const renderPlaybook = () => (
    <div className="card">
      <div className="card-title"><span className="section-icon">📚</span>Safety Coaching Playbook</div>
      {results.playbook
        ? <>
            <div className="ai-result-box md-content" style={{ maxHeight: 700 }} dangerouslySetInnerHTML={renderMarkdown(results.playbook)} />
            <button className="copy-btn" onClick={() => copyText(results.playbook)}>
              {copied ? '✓ Copied!' : '📋 Copy Playbook'}
            </button>
          </>
        : <div className="empty-state"><div className="empty-state-icon">📚</div><p>Click "Safety Playbook" to generate a customized coaching playbook.</p></div>
      }
    </div>
  );

  // ── Markets tab renderer ──
  const followUpCount = markets.filter(m => {
    const statusAlert = (m.status === 'submitted' || m.status === 'underwriting') && (() => {
      const d = daysSince(m.lastContactedAt || m.dateSubmitted);
      return d !== null && d >= 5;
    })();
    const borAlert = m.bor?.sent && m.bor?.status === 'pending' && (() => {
      const d = daysSince(m.bor.dateSent);
      return d !== null && d >= 5;
    })();
    return statusAlert || borAlert;
  }).length;
  // ── Market Intelligence runner ────────────────────────────────────────────────
  // Builds normalized account from loaded lead sheet and calls /api/markets/score-batch,
  // which uses the deterministic eligibility engine (api/_lib/eligibility.js).
  // Pass forceRefresh=true to bypass the Redis cache (used by Refresh button).
  const runTliAnalysis = async (forceRefresh = false) => {
    const ls = leadSheet || {};
    const state = (ls.state || ls.domicileState || ls.garagingState || '').trim().toUpperCase();
    if (!state) return;

    const units = (tractors || []).filter(t => t && (t.vin || t.year || t.make)).length
      || parseInt(ls.powerUnits || ls.units || '0') || 1;

    const opStatesRaw = ls.operatingStates || ls.states || '';
    const opStates = Array.isArray(opStatesRaw)
      ? opStatesRaw.map(s => String(s).trim().toUpperCase()).filter(Boolean)
      : String(opStatesRaw).split(/[\s,]+/).map(s => s.trim().toUpperCase()).filter(Boolean);

    const commsRaw = ls.commodities || ls.commodity || commodities || '';
    const commsArr = Array.isArray(commsRaw)
      ? commsRaw
      : String(commsRaw).split(',').map(s => s.trim()).filter(Boolean);

    const teleSource = ls.telematics ?? ls.eldsInstalled ?? ls.hasTelematics;
    const telematicsPresent = (teleSource == null || teleSource === '')
      ? null
      : /yes|true|1/i.test(String(teleSource)) ? true : false;

    // Derive max vehicle age from tractors list (oldest unit in years)
    const currentYear = new Date().getFullYear();
    const tractorYears = (tractors || [])
      .map(t => t && t.year ? parseInt(t.year) : null)
      .filter(y => y && y > 1980 && y <= currentYear);
    const maxVehicleAge = tractorYears.length > 0
      ? currentYear - Math.min(...tractorYears)
      : (ls.maxVehicleAge != null ? Number(ls.maxVehicleAge) : null);

    // Build drivers array from lead sheet if available
    const driversArr = Array.isArray(ls.drivers)
      ? ls.drivers.map(d => ({ age: Number(d.age || d.dob || 0), experience: Number(d.experience || d.exp || 0) })).filter(d => d.age > 0)
      : [];

    const profile = {
      units,
      yib:                Number(ls.yearsInBusiness || ls.yib || 0),
      state,
      operatingStates:    opStates,
      cabAlerts:          Number(ls.fmcsaAlerts || ls.fmcsaAlertCount || ls.cabAlerts || ls.basicAlerts || 0),
      telematicsPresent,
      telematicsProvider: ls.eldBrand || ls.telematicsProvider || '',
      commodities:        commsArr,
      hasB1Drivers:       !!(ls.b1Drivers || ls.internationalDrivers || ls.hasB1OrInternationalDrivers),
      naftaOperations:    !!(ls.naftaOperations || ls.isNaftaFocused),
      lossHistory:        ls.lossHistoryNotes || null,
      newVenture:         !!(ls.isNewVenture || ls.newVenture),
      requiresAutoLiability: true,
      radius:             ls.radius != null ? Number(ls.radius) : (ls.operatingRadius != null ? Number(ls.operatingRadius) : null),
      dotRating:          ls.dotRating || ls.dotSafetyRating || null,
      maxVehicleAge,
      drivers:            driversArr,
    };

    setTliRunning(true);
    setTliAiError('');
    setTliAccountProfile({ state, units, yib: profile.yib, opStates, commsArr, cabAlerts: profile.cabAlerts, telematicsPresent, hasB1Drivers: profile.hasB1Drivers, naftaOperations: profile.naftaOperations, radius: profile.radius, dotRating: profile.dotRating, maxVehicleAge });
    const accountKey = `${state}|${units}|${profile.yib}|${profile.cabAlerts}|${commsArr.join(',')}`;

    try {
      const res = await apiFetch(`${API_BASE}/api/markets/score-batch`, {
        method: 'POST',
        body: JSON.stringify({ accountDOT: dotNumber || '', forceRefresh, profile }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Eligibility evaluation failed');

      const transformed = (data.markets || []).map(m => transformAiResult(m));
      setTliResults(transformed);
      setTliGroupedMarkets(data.groupedMarkets || []);
      setTliOutdated(false);
      setTliLastKey(accountKey);
      setTliAnalyzedAt(data.analyzedAt || new Date().toISOString());
    } catch (err) {
      console.error('[runTliAnalysis]', err.message);
      setTliAiError(err.message);
    } finally {
      setTliRunning(false);
    }
  };

  // Transform a score-batch market result into the shape renderIntelligence expects.
  // score-batch now returns eligibility engine output; field names are mapped here.
  function transformAiResult(m) {
    // status comes through as uppercase from eligibility engine; tier is lowercase
    const result = m.status || (m.tier === 'green' ? 'GREEN' : m.tier === 'yellow' ? 'YELLOW' : 'RED');
    const contacts = m.contactInfo || m.contacts || [];
    const primaryContact = contacts.find(c => c.name || c.email) || {};
    const ch = {
      name:         m.channel || '',
      contactName:  primaryContact.name || '',
      contactEmail: primaryContact.email || '',
      contactPhone: primaryContact.phone || '',
    };
    const allChannelContacts = contacts.length > 0
      ? contacts.map(c => ({ name: c.channel || m.channel || '', contactName: c.name || '', contactEmail: c.email || '', contactPhone: c.phone || '' }))
      : (ch.contactName ? [ch] : []);
    return {
      op: {
        id:                     m.id,
        carrierId:              m.id,
        specialAppetiteNotes:   m.appetiteNotes || '',
        submissionInstructions: m.submissionInstructions || '',
        criticalAlerts:         m.criticalAlerts || [],
      },
      carrier:           { name: m.name },
      channel:           ch,
      result,
      // Eligibility engine fields
      hardReasons:       m.hardDeclines   || (m.hardDisqualifier ? [m.hardDisqualifier] : []),
      softFlags:         m.reviewTriggers || m.watchOutFor || [],
      fitReasons:        m.passes         || m.fitReasons || [],
      missingData:       m.missingData    || [],
      decision:          m.decision       || '',
      badge:             '',
      channelNames:      m.channel ? [m.channel] : [],
      allChannelContacts,
      score:             0, // eligibility engine is not score-based
    };
  }

  const handleMktRecommend = async () => {
    const p = mktFinderProfile;
    if (!p.domicileState.trim()) { setMktRecommendError('Domicile state is required.'); return; }
    setMktRecommendLoading(true);
    setMktRecommendError('');
    setMktRecommendResults(null);
    try {
      const body = {
        domicileState:               p.domicileState.trim().toUpperCase(),
        operatingStates:             p.operatingStates.split(/[\s,]+/).map(s => s.trim().toUpperCase()).filter(Boolean),
        powerUnits:                  Number(p.powerUnits) || 1,
        yearsInBusiness:             Number(p.yearsInBusiness) || 0,
        isNewVenture:                p.isNewVenture,
        commodities:                 p.commodities.split(/,/).map(s => s.trim()).filter(Boolean),
        driverAges:                  p.driverAges.split(/[\s,]+/).map(Number).filter(n => n > 0),
        driverExperienceYears:       p.driverExperienceYears.split(/[\s,]+/).map(Number).filter(n => n >= 0),
        fmcsaAlertCount:             Number(p.fmcsaAlertCount) || 0,
        hasTelematics:               p.hasTelematics,  // 'yes' | 'no' | 'unsure'
        telematicsProvider:          p.hasTelematics === 'yes' ? p.telematicsProvider.trim() : '',
        hasB1OrInternationalDrivers: p.hasB1OrInternationalDrivers,
        isNaftaFocused:              p.isNaftaFocused,
        lossHistoryNotes:            null,
      };
      const res = await apiFetch(`${API_BASE}/api/markets/recommend`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unknown error');
      setMktRecommendResults(data);
    } catch (err) {
      setMktRecommendError(err.message);
    } finally {
      setMktRecommendLoading(false);
    }
  };

  const handleAiScore = async (marketId) => {
    const market = allMktMarkets.find(m => m.id === marketId);
    if (!market) return;
    const p = mktFinderProfile;
    const profile = {
      domicileState:               p.domicileState.trim().toUpperCase(),
      operatingStates:             p.operatingStates.split(/[\s,]+/).map(s => s.trim().toUpperCase()).filter(Boolean),
      powerUnits:                  Number(p.powerUnits) || 1,
      yearsInBusiness:             Number(p.yearsInBusiness) || 0,
      isNewVenture:                p.isNewVenture,
      commodities:                 p.commodities.split(/,/).map(s => s.trim()).filter(Boolean),
      fmcsaAlertCount:             Number(p.fmcsaAlertCount) || 0,
      hasTelematics:               p.hasTelematics,
      telematicsProvider:          p.hasTelematics === 'yes' ? p.telematicsProvider.trim() : '',
      hasB1OrInternationalDrivers: p.hasB1OrInternationalDrivers,
      isNaftaFocused:              p.isNaftaFocused,
      lossHistoryNotes:            p.lossHistoryNotes || null,
    };
    setAiScores(prev => ({ ...prev, [marketId]: { loading: true, result: null, error: null } }));
    try {
      const res = await apiFetch(`${API_BASE}/api/markets/score`, {
        method: 'POST',
        body: JSON.stringify({ profile, market }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'AI scoring failed');
      setAiScores(prev => ({ ...prev, [marketId]: { loading: false, result: data, error: null } }));
    } catch (err) {
      setAiScores(prev => ({ ...prev, [marketId]: { loading: false, result: null, error: err.message } }));
    }
  };

  const renderMarkets = () => {
    // ── Shared helpers for Submission Tracker view ─────────────────────────────
    const enriched = markets.map(mkt => {
      const refDate = mkt.lastContactedAt || mkt.dateSubmitted;
      const daysSinceContact = daysSince(refDate);
      const statusFollowUp = (mkt.status === 'submitted' || mkt.status === 'underwriting')
        && daysSinceContact !== null && daysSinceContact >= 5;
      const borDays = mkt.bor?.sent ? daysSince(mkt.bor.dateSent) : null;
      const borFollowUp = mkt.bor?.sent && mkt.bor?.status === 'pending' && borDays !== null && borDays >= 5;
      const isFollowUp = statusFollowUp || borFollowUp;
      return { mkt, isFollowUp, daysSinceContact, borDays, borFollowUp };
    });

    const quotedMarkets = markets.filter(m => m.status === 'quoted' || m.status === 'bound');
    const waitingCount  = markets.filter(m => m.status === 'submitted' || m.status === 'underwriting').length;
    const declinedCount = markets.filter(m => m.status === 'declined').length;
    const premiums      = quotedMarkets.map(m => parseMarketPremium(m.quotedPremium)).filter(p => p !== null);
    const bestPremium   = premiums.length ? Math.min(...premiums) : null;

    const filtered = enriched.filter(({ mkt, isFollowUp }) => {
      if (marketFilter === 'followup') return isFollowUp;
      if (marketFilter === 'quoted')   return mkt.status === 'quoted' || mkt.status === 'bound';
      if (marketFilter === 'active')   return mkt.status !== 'declined';
      return true;
    });

    const updateMkt  = (id, field, val) => setMarkets(p => p.map(m => m.id === id ? { ...m, [field]: val } : m));
    const deleteMkt  = (id)             => setMarkets(p => p.filter(m => m.id !== id));
    const statusMkt  = (id, status)     => setMarkets(p => p.map(m => {
      if (m.id !== id) return m;
      const updates = { status };
      if (status === 'quoted' && m.status !== 'quoted' && !m.quotedAt)
        updates.quotedAt = new Date().toISOString().slice(0, 10);
      return { ...m, ...updates };
    }));
    const contactMkt = (id)             => setMarkets(p => p.map(m =>
      m.id === id ? { ...m, lastContactedAt: new Date().toISOString().slice(0, 10) } : m
    ));

    const FILTER_BTNS = [
      { key: 'all',      label: 'All Markets' },
      { key: 'followup', label: `Needs Follow-Up${followUpCount ? ` (${followUpCount})` : ''}` },
      { key: 'quoted',   label: 'Quotes Received' },
      { key: 'active',   label: 'Active' },
    ];


    // ── TLI filtered results ──────────────────────────────────────────────────
    const tliFiltered = tliFilter === 'all'
      ? tliResults
      : tliResults.filter(r => r.result === tliFilter);

    const tliCounts = { GREEN: 0, YELLOW: 0, RED: 0, UNKNOWN: 0 };
    tliResults.forEach(r => { if (tliCounts[r.result] !== undefined) tliCounts[r.result]++; });

    const TLI_DOT_COLORS = { GREEN: '#22c55e', YELLOW: '#f59e0b', RED: '#ef4444', UNKNOWN: '#94a3b8' };
    const TLI_BG_COLORS  = { GREEN: '#f0fdf4', YELLOW: '#fffbeb', RED: '#fef2f2', UNKNOWN: '#f8fafc' };
    const TLI_BORDER_COLORS = { GREEN: '#bbf7d0', YELLOW: '#fde68a', RED: '#fecaca', UNKNOWN: '#e2e8f0' };

    // ── View tab switcher ──────────────────────────────────────────────────────
    const VIEW_TABS = [
      { key: 'finder',       label: 'Market Finder' },
      { key: 'directory',    label: 'Market Directory' },
      { key: 'intelligence', label: 'Market Intelligence' },
      { key: 'tracker',      label: 'Submission Tracker' },
    ];

    // ── Market Finder view ────────────────────────────────────────────────────
    const finderFld = (k, v) => setMktFinderProfile(p => ({ ...p, [k]: v }));

    const renderFinder = () => (
      <div style={{ paddingBottom: 24 }}>
        {/* ── Profile form ── */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: mktFinderAutoFilled ? 4 : 12 }}>
            <div className="card-title" style={{ margin: 0 }}>Account Profile</div>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>* required</span>
          </div>
          {mktFinderAutoFilled && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: 11, color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '2px 8px' }}>
                Auto-filled from lead sheet
              </span>
              <button
                onClick={() => {
                  setMktFinderProfile({ powerUnits: '', yearsInBusiness: '', isNewVenture: false, domicileState: '', operatingStates: '', commodities: '', driverAges: '', driverExperienceYears: '', fmcsaAlertCount: '0', hasTelematics: 'unsure', telematicsProvider: '', hasB1OrInternationalDrivers: false, isNaftaFocused: false });
                  mktFinderLastAutoFillRef.current = {};
                  setMktFinderAutoFilled(false);
                }}
                style={{ fontSize: 11, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
              >
                Clear
              </button>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px 16px' }}>
            <div>
              <label className="form-label">Power Units *</label>
              <input className="form-input" type="number" min="1" value={mktFinderProfile.powerUnits}
                onChange={e => finderFld('powerUnits', e.target.value)} placeholder="e.g. 3" />
            </div>
            <div>
              <label className="form-label">Years in Business</label>
              <input className="form-input" type="number" min="0" value={mktFinderProfile.yearsInBusiness}
                onChange={e => finderFld('yearsInBusiness', e.target.value)} placeholder="e.g. 2" />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 22 }}>
              <input type="checkbox" id="mkt-nv" checked={mktFinderProfile.isNewVenture}
                onChange={e => finderFld('isNewVenture', e.target.checked)} />
              <label htmlFor="mkt-nv" className="form-label" style={{ margin: 0 }}>New Venture</label>
            </div>
            <div>
              <label className="form-label">Domicile State *</label>
              <input className="form-input" value={mktFinderProfile.domicileState}
                onChange={e => finderFld('domicileState', e.target.value)} placeholder="e.g. IA" />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label className="form-label">Operating States (space or comma separated)</label>
              <input className="form-input" value={mktFinderProfile.operatingStates}
                onChange={e => finderFld('operatingStates', e.target.value)} placeholder="e.g. IA MN WI IL" />
            </div>
            <div style={{ gridColumn: 'span 3' }}>
              <label className="form-label">Commodities (comma separated)</label>
              <input className="form-input" value={mktFinderProfile.commodities}
                onChange={e => finderFld('commodities', e.target.value)} placeholder="e.g. dry van, flatbed" />
            </div>
            <div>
              <label className="form-label">Driver Ages (comma separated)</label>
              <input className="form-input" value={mktFinderProfile.driverAges}
                onChange={e => finderFld('driverAges', e.target.value)} placeholder="e.g. 28, 35, 45" />
            </div>
            <div>
              <label className="form-label">Min Driver Experience (years)</label>
              <input className="form-input" value={mktFinderProfile.driverExperienceYears}
                onChange={e => finderFld('driverExperienceYears', e.target.value)} placeholder="e.g. 2" />
            </div>
            <div>
              <label className="form-label">FMCSA Alert Count</label>
              <input className="form-input" type="number" min="0" value={mktFinderProfile.fmcsaAlertCount}
                onChange={e => finderFld('fmcsaAlertCount', e.target.value)} placeholder="0" />
            </div>
            <div>
              <label className="form-label">Telematics / ELD</label>
              <select className="form-input" value={mktFinderProfile.hasTelematics}
                onChange={e => finderFld('hasTelematics', e.target.value)}>
                <option value="unsure">Unsure</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
            {mktFinderProfile.hasTelematics === 'yes' && (
              <div>
                <label className="form-label">ELD Provider</label>
                <input className="form-input" value={mktFinderProfile.telematicsProvider}
                  onChange={e => finderFld('telematicsProvider', e.target.value)} placeholder="e.g. Motive" />
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 22 }}>
              <input type="checkbox" id="mkt-b1" checked={mktFinderProfile.hasB1OrInternationalDrivers}
                onChange={e => finderFld('hasB1OrInternationalDrivers', e.target.checked)} />
              <label htmlFor="mkt-b1" className="form-label" style={{ margin: 0 }}>B1 / International Drivers</label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 22 }}>
              <input type="checkbox" id="mkt-nafta" checked={mktFinderProfile.isNaftaFocused}
                onChange={e => finderFld('isNaftaFocused', e.target.checked)} />
              <label htmlFor="mkt-nafta" className="form-label" style={{ margin: 0 }}>NAFTA Operations</label>
            </div>
            <div style={{ gridColumn: 'span 3' }}>
              <label className="form-label">Loss History Notes <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span></label>
              <input className="form-input" value={mktFinderProfile.lossHistoryNotes || ''}
                onChange={e => finderFld('lossHistoryNotes', e.target.value || null)}
                placeholder="e.g. 1 at-fault loss $45K in 2022, otherwise clean" />
            </div>
          </div>
          <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn-primary" onClick={handleMktRecommend} disabled={mktRecommendLoading} style={{ minWidth: 160 }}>
              {mktRecommendLoading ? 'Searching…' : 'Find Best Markets'}
            </button>
            {mktRecommendResults && (
              <span style={{ fontSize: 13, color: '#64748b' }}>
                {mktRecommendResults.eligible} eligible of {mktRecommendResults.total} markets screened
              </span>
            )}
            {mktRecommendError && <span style={{ fontSize: 13, color: '#dc2626' }}>{mktRecommendError}</span>}
          </div>
        </div>

        {/* ── Results ── */}
        {mktRecommendResults && mktRecommendResults.recommendations.length > 0 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 12 }}>
              Top {mktRecommendResults.recommendations.length} Recommendations
            </div>
            {mktRecommendResults.recommendations.map((rec, idx) => {
              const scoreColor  = rec.score >= 85 ? '#15803d' : rec.score >= 65 ? '#b45309' : '#dc2626';
              const scoreBg     = rec.score >= 85 ? '#f0fdf4' : rec.score >= 65 ? '#fffbeb' : '#fef2f2';
              const scoreBorder = rec.score >= 85 ? '#86efac' : rec.score >= 65 ? '#fde68a' : '#fca5a5';
              return (
                <div key={rec.id} className="card" style={{ marginBottom: 12, padding: '16px 18px' }}>
                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 8 }}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: scoreBg, border: `2px solid ${scoreBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14, color: scoreColor, flexShrink: 0 }}>
                      {idx + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginBottom: 3 }}>
                        <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>{rec.name}</span>
                        {rec.mgaOrWholesaleBroker && <span style={{ fontSize: 12, color: '#94a3b8' }}>via {rec.mgaOrWholesaleBroker}</span>}
                        {rec.programType && <span style={{ fontSize: 11, background: '#f1f5f9', color: '#475569', padding: '2px 7px', borderRadius: 10, fontWeight: 500 }}>{rec.programType}</span>}
                      </div>
                    </div>
                    {/* Score */}
                    <div style={{ textAlign: 'center', flexShrink: 0, background: scoreBg, border: `1px solid ${scoreBorder}`, borderRadius: 8, padding: '4px 12px' }}>
                      <div style={{ fontWeight: 800, fontSize: 22, color: scoreColor, lineHeight: 1 }}>{rec.score}</div>
                      <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 500 }}>/ 100</div>
                    </div>
                  </div>
                  {/* Score bar */}
                  <div style={{ height: 4, borderRadius: 2, background: '#f1f5f9', marginBottom: 10 }}>
                    <div style={{ height: '100%', borderRadius: 2, background: scoreColor, width: `${rec.score}%` }} />
                  </div>
                  {/* Explanation */}
                  <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, marginBottom: 10 }}>
                    {rec.explanation}
                  </div>
                  {/* Conditions */}
                  {rec.conditions && rec.conditions.length > 0 && (
                    <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 12px', marginBottom: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>Conditions &amp; Subjectivities</div>
                      <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#78350f', lineHeight: 1.7 }}>
                        {rec.conditions.map((c, i) => <li key={i}>{c}</li>)}
                      </ul>
                    </div>
                  )}
                  {/* AI Score Panel */}
                  {(() => {
                    const ai = aiScores[rec.id];
                    if (!ai) return null;
                    if (ai.loading) return (
                      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '10px 14px', marginBottom: 10, fontSize: 13, color: '#64748b' }}>
                        AI is analyzing fit… this takes a few seconds
                      </div>
                    );
                    if (ai.error) return (
                      <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 12, color: '#dc2626' }}>
                        AI scoring failed: {ai.error}
                      </div>
                    );
                    if (!ai.result) return null;
                    const r = ai.result;
                    const aiColor  = r.status === 'green' ? '#15803d' : r.status === 'yellow' ? '#b45309' : '#dc2626';
                    const aiBg     = r.status === 'green' ? '#f0fdf4' : r.status === 'yellow' ? '#fffbeb' : '#fef2f2';
                    const aiBorder = r.status === 'green' ? '#86efac' : r.status === 'yellow' ? '#fde68a' : '#fca5a5';
                    return (
                      <div style={{ border: `1px solid ${aiBorder}`, background: aiBg, borderRadius: 6, padding: '12px 14px', marginBottom: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: aiColor, textTransform: 'uppercase', letterSpacing: '0.07em' }}>AI Analysis</span>
                          <span style={{ fontWeight: 800, fontSize: 15, color: aiColor }}>{r.score}/100</span>
                          <span style={{ fontSize: 11, background: aiColor, color: '#fff', padding: '1px 8px', borderRadius: 10, fontWeight: 600, textTransform: 'uppercase' }}>{r.status}</span>
                        </div>
                        <p style={{ margin: '0 0 8px', fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{r.recommendation}</p>
                        {r.fitReasons && r.fitReasons.length > 0 && (
                          <div style={{ marginBottom: 6 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Why it fits</div>
                            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#166534', lineHeight: 1.7 }}>
                              {r.fitReasons.map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                          </div>
                        )}
                        {r.watchOutFor && r.watchOutFor.length > 0 && (
                          <div style={{ marginBottom: 6 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Watch out for</div>
                            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#92400e', lineHeight: 1.7 }}>
                              {r.watchOutFor.map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                          </div>
                        )}
                        {r.hardDisqualifiers && r.hardDisqualifiers.length > 0 && (
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Hard disqualifiers</div>
                            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#991b1b', lineHeight: 1.7 }}>
                              {r.hardDisqualifiers.map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {/* Footer */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
                    {rec.contact?.email
                      ? <a href={`mailto:${rec.contact.email}`} style={{ fontSize: 12, color: '#2563eb', textDecoration: 'none' }}>
                          {rec.contact.name ? <><strong style={{ color: '#374151' }}>{rec.contact.name}</strong> &nbsp;·&nbsp; </> : null}{rec.contact.email}
                        </a>
                      : rec.contact?.name
                        ? <span style={{ fontSize: 12, color: '#64748b' }}>{rec.contact.name}</span>
                        : null
                    }
                    {rec.contact?.phone && <span style={{ fontSize: 12, color: '#94a3b8' }}>{rec.contact.phone}</span>}
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                      <button
                        className="btn-secondary"
                        style={{ padding: '4px 14px', fontSize: 12, background: aiScores[rec.id]?.result ? '#f0fdf4' : undefined, borderColor: aiScores[rec.id]?.result ? '#86efac' : undefined }}
                        disabled={aiScores[rec.id]?.loading}
                        onClick={() => handleAiScore(rec.id)}
                      >
                        {aiScores[rec.id]?.loading ? 'Analyzing…' : aiScores[rec.id]?.result ? 'Re-analyze' : '✦ AI Score'}
                      </button>
                      <button className="btn-secondary" style={{ padding: '4px 14px', fontSize: 12 }}
                        onClick={() => setMktDetailMarket(allMktMarkets.find(m => m.id === rec.id) || null)}>
                        View Full Profile
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {mktRecommendResults && mktRecommendResults.recommendations.length === 0 && (
          <div className="card" style={{ textAlign: 'center', color: '#64748b', padding: 40, fontSize: 14 }}>
            No eligible markets found for this account profile. Adjust the criteria and try again.
          </div>
        )}
      </div>
    );

    // ── Market Directory view ─────────────────────────────────────────────────
    const renderDirectory = () => {
      const search = mktDirSearch.toLowerCase();
      const f = mktDirFilters;
      const filteredDir = allMktMarkets.filter(m => {
        if (search) {
          const hay = `${m.name} ${m.mgaOrWholesaleBroker || ''} ${(m.tags || []).join(' ')}`.toLowerCase();
          if (!hay.includes(search)) return false;
        }
        if (f.programType && m.programType !== f.programType) return false;
        if (f.state) {
          const s = f.state.trim().toUpperCase();
          if (m.activeStates && m.activeStates.length > 0 && !m.activeStates.includes(s)) return false;
        }
        if (f.newVenture === 'yes' && !m.newVentureEligible) return false;
        if (f.newVenture === 'no'  &&  m.newVentureEligible) return false;
        if (f.telematics === 'required' && !['required_pre_quote','required_pre_bind'].includes(m.telematicsRequirement)) return false;
        if (f.telematics === 'not_required' && ['required_pre_quote','required_pre_bind'].includes(m.telematicsRequirement)) return false;
        if (f.tag) {
          const tl = f.tag.toLowerCase();
          if (!(m.tags || []).some(t => t.toLowerCase().includes(tl))) return false;
        }
        return true;
      });
      const programTypes = [...new Set(allMktMarkets.map(m => m.programType).filter(Boolean))].sort();
      const hasFilters = search || f.programType || f.state || f.newVenture || f.telematics || f.tag;
      return (
        <div>
          {/* Filter bar */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr auto', gap: '8px', marginBottom: 12, alignItems: 'end' }}>
            <div>
              <label className="form-label">Search</label>
              <input className="form-input" placeholder="Carrier, MGA, or tag…" value={mktDirSearch}
                onChange={e => setMktDirSearch(e.target.value)} />
            </div>
            <div>
              <label className="form-label">Program Type</label>
              <select className="form-input" value={f.programType}
                onChange={e => setMktDirFilters(p => ({ ...p, programType: e.target.value }))}>
                <option value="">All Types</option>
                {programTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">State</label>
              <input className="form-input" placeholder="e.g. IA" value={f.state}
                onChange={e => setMktDirFilters(p => ({ ...p, state: e.target.value }))} />
            </div>
            <div>
              <label className="form-label">New Venture</label>
              <select className="form-input" value={f.newVenture || ''}
                onChange={e => setMktDirFilters(p => ({ ...p, newVenture: e.target.value }))}>
                <option value="">Any</option>
                <option value="yes">Eligible</option>
                <option value="no">Not Eligible</option>
              </select>
            </div>
            <div>
              <label className="form-label">Telematics</label>
              <select className="form-input" value={f.telematics || ''}
                onChange={e => setMktDirFilters(p => ({ ...p, telematics: e.target.value }))}>
                <option value="">Any</option>
                <option value="required">Required</option>
                <option value="not_required">Not Required</option>
              </select>
            </div>
            <div>
              <label className="form-label">Tag</label>
              <input className="form-input" placeholder="e.g. flatbed" value={f.tag || ''}
                onChange={e => setMktDirFilters(p => ({ ...p, tag: e.target.value }))} />
            </div>
            {hasFilters && (
              <button className="btn-secondary" style={{ alignSelf: 'flex-end' }}
                onClick={() => { setMktDirSearch(''); setMktDirFilters({ programType: '', state: '', newVenture: '', telematics: '', tag: '' }); }}>
                Clear
              </button>
            )}
          </div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 10 }}>
            {filteredDir.length} of {allMktMarkets.length} markets
          </div>
          {/* Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {filteredDir.map(m => (
              <div key={m.id} className="card" style={{ padding: '12px 14px', cursor: 'pointer', position: 'relative' }}
                onClick={() => setMktDetailMarket(m)}>
                {(currentRole === 'admin' || currentRole === 'platform_owner' || currentRole === 'market_editor' || currentRole === 'marketer') && (
                  <button
                    title="Edit market"
                    onClick={e => { e.stopPropagation(); setMktDirEditMarket({ ...m }); }}
                    style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#94a3b8', padding: '2px 4px', borderRadius: 4, lineHeight: 1 }}
                  >✏️</button>
                )}
                <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 2, paddingRight: (currentRole === 'admin' || currentRole === 'platform_owner' || currentRole === 'market_editor' || currentRole === 'marketer') ? 22 : 0 }}>{m.name}</div>
                {m.mgaOrWholesaleBroker && <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>via {m.mgaOrWholesaleBroker}</div>}
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 4 }}>
                  {m.marketType === 'mga' && <span style={{ fontSize: 11, background: '#fff7ed', color: '#c2410c', padding: '2px 7px', borderRadius: 10, fontWeight: 600 }}>MGA</span>}
                  {m.marketType === 'program' && <span style={{ fontSize: 11, background: '#eff6ff', color: '#1d4ed8', padding: '2px 7px', borderRadius: 10, fontWeight: 600 }}>Program</span>}
                  {m.programType && <span style={{ fontSize: 11, background: '#f1f5f9', color: '#475569', padding: '2px 7px', borderRadius: 10, fontWeight: 500 }}>{m.programType}</span>}
                  {m.amBestRating && <span style={{ fontSize: 11, background: '#eff6ff', color: '#1d4ed8', padding: '2px 7px', borderRadius: 10 }}>{m.amBestRating}</span>}
                  {(!m.activeStates || m.activeStates.length === 0) && <span style={{ fontSize: 11, background: '#f0fdf4', color: '#15803d', padding: '2px 7px', borderRadius: 10 }}>Nationwide</span>}
                  {m.newVentureEligible && <span style={{ fontSize: 11, background: '#fefce8', color: '#854d0e', padding: '2px 7px', borderRadius: 10 }}>New Venture OK</span>}
                  {['required_pre_quote','required_pre_bind'].includes(m.telematicsRequirement) && <span style={{ fontSize: 11, background: '#f5f3ff', color: '#6d28d9', padding: '2px 7px', borderRadius: 10 }}>Tele Required</span>}
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>
                  {m.marketType === 'mga' ? 'Market access / brokerage' : `${m.minUnits || 1}–${m.maxUnits || '∞'} units${m.minYearsInBusiness ? ` · ${m.minYearsInBusiness}+ YIB` : ''}`}
                  {m.marketType === 'program' && m.parentMarketId && (() => { const parent = allMktMarkets.find(x => x.id === m.parentMarketId); return parent ? ` · via ${parent.name}` : ''; })()}
                </div>
              </div>
            ))}
          </div>
          {filteredDir.length === 0 && (
            <div style={{ textAlign: 'center', color: '#94a3b8', padding: 40 }}>No markets match your filters.</div>
          )}
        </div>
      );
    };

    // ── Market Directory edit modal — rendered as <MarketDirectoryEditModal> below ─

    // ── Market Detail modal ───────────────────────────────────────────────────
    const renderDetailModal = () => {
      const m = mktDetailMarket;
      if (!m) return null;

      const Section = ({ title, children }) => (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#64748b', borderBottom: '1px solid #f1f5f9', paddingBottom: 5, marginBottom: 10 }}>
            {title}
          </div>
          {children}
        </div>
      );
      const Row = ({ label, value, children }) => (value || children) ? (
        <div style={{ display: 'flex', gap: 8, marginBottom: 5, fontSize: 13 }}>
          <span style={{ color: '#94a3b8', minWidth: 170, flexShrink: 0 }}>{label}</span>
          <span style={{ color: '#1e293b', flex: 1 }}>{children || value}</span>
        </div>
      ) : null;

      // Standard checklist items always required
      const STANDARD_CHECKLIST = [
        'Completed application — all pages signed',
        'Driver list in Excel format (name, DOB, CDL #, issuing state, hire date)',
        'Vehicle list with VINs in Excel format',
        'Loss runs for all lines of business — currently valued within the last 90 days',
        'IFTAs — 4 most recent quarters',
        'MVRs for all drivers — pulled within the last 30 days',
        'Narrative / cover letter describing the operation',
      ];

      const telematicsLabel = {
        not_required:       'Not Required',
        optional_discount:  'Optional — discount available',
        required_pre_quote: 'Required before quote',
        required_pre_bind:  'Required before bind',
      };

      return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '32px 16px', overflowY: 'auto' }}
          onClick={e => { if (e.target === e.currentTarget) setMktDetailMarket(null); }}>
          <div style={{ background: '#fff', borderRadius: 12, maxWidth: 760, width: '100%', padding: '28px 32px', position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
            <button onClick={() => setMktDetailMarket(null)} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>×</button>

            {/* ── Title ── */}
            <div style={{ marginBottom: 20, paddingRight: 32 }}>
              <div style={{ fontWeight: 800, fontSize: 22, color: '#0f172a', marginBottom: 3 }}>{m.name}</div>
              {m.mgaOrWholesaleBroker && <div style={{ fontSize: 13, color: '#64748b' }}>via {m.mgaOrWholesaleBroker}</div>}
            </div>

            {/* ── 1. Overview ── */}
            <Section title="Overview">
              <Row label="Market Type" value={m.marketType === 'mga' ? 'MGA / Wholesale Brokerage' : m.marketType === 'program' ? 'Program (carrier-level)' : 'Carrier'} />
              {m.parentMarketId && (
                <Row label="Parent MGA">
                  {(() => { const parent = allMktMarkets.find(x => x.id === m.parentMarketId); return parent ? parent.name : m.parentMarketId; })()}
                </Row>
              )}
              <Row label="Program Type" value={m.programType} />
              <Row label="AM Best Rating" value={m.amBestRating || '—'} />
              {m.marketType !== 'mga' && <Row label="Unit Range" value={`${m.minUnits || 1} – ${m.maxUnits ? m.maxUnits : '∞'} power units`} />}
              {m.marketType !== 'mga' && <Row label="Min Years in Business" value={m.minYearsInBusiness ? `${m.minYearsInBusiness} years` : 'None'} />}
              {m.marketType !== 'mga' && <Row label="New Venture" value={m.newVentureEligible ? 'Eligible' : 'Not eligible'} />}
              {m.tags && m.tags.length > 0 && (
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 6 }}>
                  {m.tags.map(t => <span key={t} style={{ fontSize: 11, background: '#f1f5f9', color: '#475569', padding: '2px 8px', borderRadius: 10 }}>{t}</span>)}
                </div>
              )}
            </Section>

            {/* ── Market Access (MGA only) ── */}
            {m.marketType === 'mga' && (() => {
              const linked = allMktMarkets.filter(x =>
                Array.isArray(x.accessMGAs) && (x.accessMGAs.includes(m.id) || x.accessMGAs.includes(m.name))
              );
              return (
                <Section title="Market Access">
                  {linked.length > 0
                    ? <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#374151', lineHeight: 1.9 }}>
                        {linked.map(x => <li key={x.id}>{x.name}</li>)}
                      </ul>
                    : <div style={{ fontSize: 13, color: '#94a3b8' }}>No linked market access found yet.</div>
                  }
                </Section>
              );
            })()}

            {/* ── 2. Appetite ── */}
            <Section title="Appetite">
              <Row label="Active States" value={(!m.activeStates || m.activeStates.length === 0) ? 'Nationwide' : m.activeStates.join(', ')} />
              {m.ineligibleStates && m.ineligibleStates.length > 0 && <Row label="Ineligible States" value={m.ineligibleStates.join(', ')} />}
              {m.preferredCommodities && m.preferredCommodities.length > 0
                ? <Row label="Preferred Commodities" value={m.preferredCommodities.join(', ')} />
                : <Row label="Preferred Commodities" value="No restriction — all standard commodities" />}
              {m.ineligibleCommodities && m.ineligibleCommodities.length > 0 && <Row label="Ineligible Commodities" value={m.ineligibleCommodities.join(', ')} />}
              {m.targetStates && m.targetStates.length > 0 && <Row label="Target States" value={m.targetStates.join(', ')} />}
              {m.nonTargetStates && m.nonTargetStates.length > 0 && <Row label="Non-Target States" value={m.nonTargetStates.join(', ')} />}
              {m.regionalFocus && <Row label="Regional Focus" value={{ midwest: 'Midwest', northeast: 'Northeast', california: 'California', texas: 'Texas' }[m.regionalFocus] || m.regionalFocus} />}
            </Section>

            {/* ── 3. Driver Requirements ── */}
            <Section title="Driver Requirements">
              <Row label="Min Age (hard filter)" value={m.driverMinAge ? `${m.driverMinAge}` : 'None'} />
              <Row label="Max Age (scoring)" value={m.driverMaxAge ? `${m.driverMaxAge}` : 'None'} />
              <Row label="Min CDL Experience (hard filter)" value={m.driverMinExperienceYears ? `${m.driverMinExperienceYears} years` : 'None'} />
              {m.vehicleAgeMax != null && <Row label="Max Vehicle Age" value={`${m.vehicleAgeMax} years old`} />}
            </Section>

            {/* ── 4. Telematics ── */}
            <Section title="Telematics">
              <Row label="Requirement" value={telematicsLabel[m.telematicsRequirement] || 'Not Required'} />
              {m.telematicsCompatibleProviders && m.telematicsCompatibleProviders.length > 0 && (
                <Row label="Compatible Providers" value={m.telematicsCompatibleProviders.join(', ')} />
              )}
            </Section>

            {/* ── 5. Submission Checklist ── */}
            <Section title="Submission Checklist">
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#374151', lineHeight: 1.9 }}>
                {STANDARD_CHECKLIST.map((item, i) => <li key={i}>{item}</li>)}
                {m.submissionInstructions && m.submissionInstructions.trim() && (
                  <li style={{ color: '#1d4ed8', fontWeight: 500 }}>{m.submissionInstructions}</li>
                )}
              </ul>
            </Section>

            {/* ── 6. Key Contacts ── */}
            <Section title="Key Contacts">
              {m.keyContactName
                ? <Row label="Primary Contact" value={m.keyContactName} />
                : <div style={{ fontSize: 13, color: '#94a3b8' }}>No contact on file.</div>}
              {m.keyContactPhone && (
                <Row label="Phone">
                  <a href={`tel:${m.keyContactPhone.replace(/\D/g,'')}`} style={{ color: '#2563eb', textDecoration: 'none' }}>{m.keyContactPhone}</a>
                </Row>
              )}
              {m.keyContactEmail && (
                <Row label="Email">
                  <a href={`mailto:${m.keyContactEmail}`} style={{ color: '#2563eb', textDecoration: 'none' }}>{m.keyContactEmail}</a>
                </Row>
              )}
              {m.submissionEmail && m.submissionEmail !== m.keyContactEmail && (
                <Row label="Submission Email">
                  <a href={`mailto:${m.submissionEmail}`} style={{ color: '#2563eb', textDecoration: 'none' }}>{m.submissionEmail}</a>
                </Row>
              )}
            </Section>

            {/* ── 7. Eligibility Rules ── */}
            <Section title="Eligibility Rules">
              <Row label="B1 / Intl Drivers" value={
                m.b1DriversAccepted === true ? 'Accepted' :
                m.b1DriversAccepted === false ? 'Hard decline' : 'Not evaluated'} />
              <Row label="NAFTA / Cross-Border" value={
                m.naftaAccepted === true ? 'Accepted' :
                m.naftaAccepted === false ? 'Hard decline' : 'Not evaluated'} />
              {m.fmcsaAlertMax != null && <Row label="FMCSA Alert Max" value={`≤ ${m.fmcsaAlertMax} alerts`} />}
              {m.lossHistoryTolerance && <Row label="Loss History" value={{
                clean: 'Clean only', moderate: 'Moderate', adverse: 'Adverse OK', any: 'Any'
              }[m.lossHistoryTolerance] || m.lossHistoryTolerance} />}
              {m.radiusMax != null && <Row label="Max Radius" value={`${m.radiusMax} miles`} />}
              {m.dotRatingRequired && <Row label="DOT Rating" value={
                m.dotRatingRequired === 'satisfactory_or_none' ? 'Satisfactory or None only' : 'Any rating accepted'} />}
              {m.apdMtcOnly && <Row label="Coverage" value="APD / MTC only — does not write Auto Liability" />}
              {m.domesticOnly && <Row label="Operations" value="Domestic only — no cross-border" />}
              {m.crossBorderOnly && <Row label="Operations" value="Cross-border / Mexico only" />}
            </Section>
            {/* ── 8. Billing ── */}
            <Section title="Billing">
              <Row label="Billing Type" value={m.billingType
                ? m.billingType.charAt(0).toUpperCase() + m.billingType.slice(1) + ' bill'
                : 'Not specified'} />
            </Section>

            {/* ── 8. Critical Alerts ── */}
            {m.criticalAlerts && m.criticalAlerts.length > 0 && (
              <Section title="Critical Alerts">
                {m.criticalAlerts.map((a, i) => (
                  <div key={i} style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 8 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#991b1b', marginBottom: 3 }}>⚠ {a.title}</div>
                    <div style={{ fontSize: 12, color: '#7f1d1d', lineHeight: 1.5 }}>{a.description}</div>
                  </div>
                ))}
              </Section>
            )}

            {/* Key Notes (full) */}
            {m.keyNotes && (
              <Section title="Key Notes">
                <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{m.keyNotes}</div>
              </Section>
            )}

            {/* ── Documents ── */}
            <Section title="Documents">
              {(m.documents || []).length === 0 && !mktDetailDocUploading && (
                <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 8 }}>No documents uploaded yet.</div>
              )}
              {(m.documents || []).map((doc, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 13 }}>
                  <a href={doc.url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'none', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    📄 {doc.filename}
                  </a>
                  {doc.uploadedAt && <span style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>{new Date(doc.uploadedAt).toLocaleDateString()}</span>}
                  {(currentRole === 'admin' || currentRole === 'platform_owner' || currentRole === 'market_editor' || currentRole === 'marketer') && (
                    <button
                      onClick={async () => {
                        if (!window.confirm('Remove this document from the market record?')) return;
                        const docs = (m.documents || []).filter((_, j) => j !== i);
                        try {
                          const res = await apiFetch(`${API_BASE}/api/admin/markets`, {
                            method: 'POST',
                            body: JSON.stringify({ ...m, documents: docs }),
                          });
                          if (!res.ok) throw new Error(await res.text());
                          const saved = await res.json();
                          setAllMktMarkets(prev => prev.map(mk => mk.id === saved.market.id ? saved.market : mk));
                          setMktDetailMarket(saved.market);
                        } catch (err) {
                          setMktDetailDocError('Delete failed: ' + err.message);
                        }
                      }}
                      style={{ fontSize: 11, background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px 4px', borderRadius: 4 }}
                      title="Remove document"
                    >✕</button>
                  )}
                </div>
              ))}
              {mktDetailDocError && <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 6 }}>{mktDetailDocError}</div>}
              {(currentRole === 'admin' || currentRole === 'platform_owner' || currentRole === 'market_editor' || currentRole === 'marketer') && (
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#2563eb', cursor: mktDetailDocUploading ? 'not-allowed' : 'pointer', marginTop: 4 }}>
                  <input type="file" style={{ display: 'none' }} disabled={mktDetailDocUploading}
                    onChange={async e => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      e.target.value = '';
                      setMktDetailDocUploading(true);
                      setMktDetailDocError('');
                      try {
                        const token = localStorage.getItem('tsp_auth') || '';
                        const upRes = await fetch(
                          `${API_BASE}/api/files/upload?carrierId=${encodeURIComponent('market-docs-' + m.id)}&category=docs&filename=${encodeURIComponent(file.name)}`,
                          { method: 'POST', body: file, headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'Content-Type': file.type || 'application/octet-stream' } }
                        );
                        const upData = await upRes.json();
                        if (!upRes.ok) throw new Error(upData.error || 'Upload failed');
                        const docs = [...(m.documents || []), { filename: file.name, url: upData.url, uploadedAt: new Date().toISOString() }];
                        const saveRes = await apiFetch(`${API_BASE}/api/admin/markets`, {
                          method: 'POST',
                          body: JSON.stringify({ ...m, documents: docs }),
                        });
                        if (!saveRes.ok) throw new Error('Upload succeeded but failed to save metadata');
                        const saved = await saveRes.json();
                        setAllMktMarkets(prev => prev.map(mk => mk.id === saved.market.id ? saved.market : mk));
                        setMktDetailMarket(saved.market);
                      } catch (err) {
                        setMktDetailDocError(err.message);
                      } finally {
                        setMktDetailDocUploading(false);
                      }
                    }}
                  />
                  {mktDetailDocUploading ? 'Uploading…' : '+ Upload Document'}
                </label>
              )}
            </Section>
          </div>
        </div>
      );
    };

    // ── Market Intelligence view ──────────────────────────────────────────────
    const renderIntelligence = () => {
      // Deduplicate by carrier name (score-batch already deduplicates, but guard here too)
      const seen = new Set();
      const dedupedAll = tliResults.filter(entry => {
        const key = (entry.carrier?.name || entry.op?.carrierId || '').toLowerCase().trim();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      const tliCounts = { GREEN: 0, YELLOW: 0, RED: 0, UNKNOWN: 0 };
      dedupedAll.forEach(r => { if (tliCounts[r.result] !== undefined) tliCounts[r.result]++; });

      const tliFiltered = tliFilter === 'all' ? dedupedAll : dedupedAll.filter(r => r.result === tliFilter);

      const allExpanded = tliFiltered.length > 0 && tliFiltered.every(r => !!tliExpanded[r.op.id]);
      const toggleAll = () => {
        if (allExpanded) {
          setTliExpanded(prev => {
            const next = { ...prev };
            tliFiltered.forEach(r => { delete next[r.op.id]; });
            return next;
          });
        } else {
          setTliExpanded(prev => {
            const next = { ...prev };
            tliFiltered.forEach(r => { next[r.op.id] = true; });
            return next;
          });
        }
      };

      // Full-tab loading state
      if (tliRunning && dedupedAll.length === 0) {
        const name = companyName || 'this account';
        return (
          <div style={{ textAlign: 'center', padding: '48px 20px' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#1a2332', marginBottom: 10 }}>
              Evaluating {name} against all markets…
            </div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 24 }}>
              Running deterministic eligibility rules…
            </div>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
              {['#22c55e','#f59e0b','#ef4444'].map((c, i) => (
                <div key={i} style={{ width: 10, height: 10, borderRadius: '50%', background: c, animation: `pulse 1.2s ease-in-out ${i * 0.3}s infinite` }} />
              ))}
            </div>
          </div>
        );
      }

      // Error state
      if (tliAiError && dedupedAll.length === 0) {
        return (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '16px 20px', margin: '12px 0' }}>
            <div style={{ fontWeight: 600, color: '#991b1b', marginBottom: 4 }}>Evaluation failed</div>
            <div style={{ fontSize: 13, color: '#7f1d1d' }}>{tliAiError}</div>
            <button
              onClick={() => runTliAnalysis(false)}
              style={{ marginTop: 12, fontSize: 12, padding: '5px 14px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fff', color: '#991b1b', cursor: 'pointer' }}
            >
              Retry
            </button>
          </div>
        );
      }

      // ── Account Profile Used box ──────────────────────────────────────────
      const acct = tliAccountProfile;
      const renderAccountBox = () => {
        if (!acct) return null;
        const rows = [
          ['Domicile State',    acct.state || '—'],
          ['Operating States',  acct.opStates?.length ? acct.opStates.join(', ') : 'Same as domicile'],
          ['Power Units',       acct.units ?? '—'],
          ['Years in Business', acct.yib    ?? '—'],
          ['Commodities',       acct.commsArr?.length ? acct.commsArr.join(', ') : '—'],
          ['FMCSA Alerts',      acct.cabAlerts ?? '0'],
          ['Telematics',        acct.telematicsPresent === true ? 'Yes' : acct.telematicsPresent === false ? 'No' : 'Unknown'],
          ['B1 / Intl Drivers', acct.hasB1Drivers ? 'Yes' : 'No'],
          ['NAFTA Operations',  acct.naftaOperations ? 'Yes' : 'No'],
          ...(acct.radius      != null ? [['Operating Radius', `${acct.radius} miles`]] : []),
          ...(acct.dotRating              ? [['DOT Safety Rating', acct.dotRating]] : []),
          ...(acct.maxVehicleAge != null  ? [['Max Vehicle Age',   `${acct.maxVehicleAge} years`]] : []),
        ];
        return (
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', marginBottom: 8 }}>
              Account Profile Used
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '4px 20px' }}>
              {rows.map(([label, value]) => (
                <div key={label} style={{ display: 'flex', gap: 6, fontSize: 12 }}>
                  <span style={{ color: '#94a3b8', flexShrink: 0 }}>{label}:</span>
                  <span style={{ color: '#1e293b', fontWeight: 500 }}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        );
      };

      return (
        <div style={{ paddingBottom: 24 }}>
          {/* Account profile box */}
          {renderAccountBox()}

          {/* Summary line */}
          {dedupedAll.length > 0 && (
            <div style={{ fontSize: 13, color: '#475569', marginBottom: 10 }}>
              <span style={{ color: '#15803d', fontWeight: 600 }}>{tliCounts.GREEN} submit</span>
              {' · '}
              <span style={{ color: '#92400e', fontWeight: 600 }}>{tliCounts.YELLOW} review</span>
              {' · '}
              <span style={{ color: '#991b1b', fontWeight: 600 }}>{tliCounts.RED} decline</span>
              {companyName ? ` — ${companyName}` : ''}
            </div>
          )}

          {/* Header: count pills + outdated warning + refresh button */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            {[
              { key: 'GREEN',  bg: '#f0fdf4', border: '#bbf7d0', text: '#15803d', dot: '#22c55e' },
              { key: 'YELLOW', bg: '#fffbeb', border: '#fde68a', text: '#92400e', dot: '#f59e0b' },
              { key: 'RED',    bg: '#fef2f2', border: '#fecaca', text: '#991b1b', dot: '#ef4444' },
            ].map(c => (
              <span key={c.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: c.bg, border: `1px solid ${c.border}`, color: c.text, borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.dot, flexShrink: 0 }} />
                {tliCounts[c.key]}
              </span>
            ))}
            {tliOutdated && (
              <span style={{ fontSize: 11, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '2px 8px' }}>
                Analysis may be outdated — click Refresh
              </span>
            )}
            <button
              onClick={() => { setTliResults([]); setTliGroupedMarkets([]); setTliAnalyzedAt(null); runTliAnalysis(true); }}
              disabled={tliRunning}
              style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#475569', cursor: tliRunning ? 'not-allowed' : 'pointer', fontWeight: 500, flexShrink: 0 }}
            >
              {tliRunning ? 'Running…' : '↻ Refresh Analysis'}
            </button>
            <button
              onClick={() => exportMarketIntelligencePDF({ tliResults, tliGroupedMarkets, tliAccountProfile, tliAnalyzedAt, companyName })}
              disabled={tliRunning || tliResults.length === 0}
              style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#475569', cursor: (tliRunning || tliResults.length === 0) ? 'not-allowed' : 'pointer', fontWeight: 500, flexShrink: 0, opacity: (tliRunning || tliResults.length === 0) ? 0.5 : 1 }}
            >
              ↓ Export PDF
            </button>
          </div>

          {/* Filter pill tabs + expand/collapse all toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
            {[
              { key: 'all',    label: 'All',    dot: null,      activeBg: '#1a2332', activeBorder: '#1a2332' },
              { key: 'GREEN',  label: 'Submit', dot: '#22c55e', activeBg: '#22c55e', activeBorder: '#22c55e' },
              { key: 'YELLOW', label: 'Review', dot: '#f59e0b', activeBg: '#f59e0b', activeBorder: '#f59e0b' },
              { key: 'RED',    label: 'Decline',dot: '#ef4444', activeBg: '#ef4444', activeBorder: '#ef4444' },
            ].map(tab => {
              const isActive = tliFilter === tab.key;
              return (
                <button key={tab.key} onClick={() => setTliFilter(tab.key)} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  borderRadius: 999, padding: '4px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer',
                  background: isActive ? tab.activeBg : 'transparent',
                  border: `1px solid ${isActive ? tab.activeBorder : '#e2e8f0'}`,
                  color: isActive ? '#fff' : '#64748b',
                }}>
                  {tab.dot && <span style={{ width: 7, height: 7, borderRadius: '50%', background: isActive ? '#fff' : tab.dot, flexShrink: 0 }} />}
                  {tab.label}
                  {tab.key !== 'all' && <span style={{ opacity: 0.85 }}>({tliCounts[tab.key]})</span>}
                </button>
              );
            })}
            {tliFiltered.length > 0 && (
              <button
                onClick={toggleAll}
                style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px' }}
              >
                {allExpanded ? 'Collapse all' : 'Expand all'}
              </button>
            )}
          </div>

          {/* Cards */}
          {tliResults.length === 0 && !tliRunning ? (
            <div style={{ textAlign: 'center', color: '#94a3b8', padding: '28px 0', fontSize: 13 }}>
              {leadSheet?.state ? 'Starting evaluation…' : 'Load an account to run Market Intelligence.'}
            </div>
          ) : tliFiltered.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#94a3b8', padding: '28px 0', fontSize: 13 }}>
              {tliFilter === 'GREEN'  ? 'No markets are a clear fit for this account. Check review markets for near-fits.'
               : tliFilter === 'YELLOW' ? 'No markets require review for this account.'
               : 'No markets declined for this account.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {tliFiltered.map(({ op, carrier: car, channel: ch, result, hardReasons, softFlags, fitReasons, missingData, decision, channelNames, allChannelContacts }) => {
                const isExpanded = !!tliExpanded[op.id];
                const S = {
                  GREEN:   { dot: '#22c55e', leftBorder: '#22c55e', badgeBg: '#f0fdf4', badgeBorder: '#bbf7d0', badgeText: '#15803d', fitLabel: 'Submit — Strong Fit'              },
                  YELLOW:  { dot: '#f59e0b', leftBorder: '#f59e0b', badgeBg: '#fffbeb', badgeBorder: '#fde68a', badgeText: '#92400e', fitLabel: 'Submit — Review / Possible Quote' },
                  RED:     { dot: '#ef4444', leftBorder: '#ef4444', badgeBg: '#fef2f2', badgeBorder: '#fecaca', badgeText: '#991b1b', fitLabel: 'Do Not Submit — Hard Decline'    },
                  UNKNOWN: { dot: '#94a3b8', leftBorder: '#e2e8f0', badgeBg: '#f8fafc', badgeBorder: '#e2e8f0', badgeText: '#64748b', fitLabel: 'Unknown'                         },
                }[result] || { dot: '#94a3b8', leftBorder: '#e2e8f0', badgeBg: '#f8fafc', badgeBorder: '#e2e8f0', badgeText: '#64748b', fitLabel: 'Unknown' };

                const viaLine = (channelNames && channelNames.length > 0)
                  ? channelNames.join(', ')
                  : ch?.name;

                // Grouped results: child programs and access routes for this carrier
                const grouped = tliGroupedMarkets.find(g => g.id === op.id);
                const cardPrograms = grouped?.programs || [];
                const cardAccessRoutes = grouped?.accessRoutes || [];

                return (
                  <div key={op.id} style={{ background: '#fff', border: '1px solid #e8edf2', borderLeft: `4px solid ${S.leftBorder}`, borderRadius: 8, overflow: 'hidden' }}>
                    {/* Closed row */}
                    <div
                      onClick={() => setTliExpanded(p => ({ ...p, [op.id]: !p[op.id] }))}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', cursor: 'pointer', userSelect: 'none' }}
                    >
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: S.dot, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1a2332', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.3 }}>
                          {car?.name || op.carrierId}
                        </div>
                        {viaLine && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1, lineHeight: 1.3 }}>via {viaLine}</div>}
                      {(cardPrograms.length > 0 || cardAccessRoutes.length > 0) && (
                        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1, lineHeight: 1.3 }}>
                          {cardPrograms.length > 0 && `${cardPrograms.length} program${cardPrograms.length > 1 ? 's' : ''}`}
                          {cardPrograms.length > 0 && cardAccessRoutes.length > 0 && ' · '}
                          {cardAccessRoutes.length > 0 && `via ${cardAccessRoutes.map(a => a.channel || a.name).join(', ')}`}
                        </div>
                      )}
                      </div>
                      <span style={{ fontSize: 11, background: S.badgeBg, border: `1px solid ${S.badgeBorder}`, color: S.badgeText, borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {S.fitLabel}
                      </span>
                      {(result === 'GREEN' || result === 'YELLOW') && (() => {
                        const alreadyIn = markets.some(m =>
                          m.tliMarketId === op.id || m.name.trim().toLowerCase() === (car?.name || '').trim().toLowerCase()
                        );
                        return alreadyIn ? (
                          <span
                            onClick={e => e.stopPropagation()}
                            style={{ fontSize: 11, color: '#16a34a', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap', flexShrink: 0, fontWeight: 600 }}
                          >✓ In Tracker</span>
                        ) : (
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              const reasons = result === 'GREEN'
                                ? (fitReasons || []).slice(0, 3).join('; ')
                                : (softFlags || []).slice(0, 3).join('; ');
                              setMarkets(p => [...p, {
                                id:                `mkt_${Date.now()}_${Math.random().toString(36).slice(2, 5)}`,
                                name:              car?.name || op.carrierId || '',
                                tliMarketId:       op.id,
                                uwName:            ch?.contactName || '',
                                uwEmail:           ch?.contactEmail || '',
                                uwPhone:           ch?.contactPhone || '',
                                dateSubmitted:     new Date().toISOString().slice(0, 10),
                                status:            'submitted',
                                quotedPremium:     '',
                                quotedAt:          null,
                                lastContactedAt:   null,
                                source:            'Market Intelligence',
                                intelligenceResult: result,
                                notes:             reasons ? `${result === 'GREEN' ? 'GREEN' : 'YELLOW'} — ${reasons}` : result,
                              }]);
                            }}
                            style={{ fontSize: 11, color: '#1e40af', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap', flexShrink: 0, cursor: 'pointer', fontWeight: 600 }}
                          >+ Tracker</button>
                        );
                      })()}
                      <span style={{ color: '#94a3b8', fontSize: 11, flexShrink: 0, display: 'inline-block', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}>▼</span>
                    </div>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <div style={{ borderTop: '1px solid #f1f5f9', padding: '12px 14px 14px', background: '#fafbfc' }}>

                        {/* Add to Tracker — visible in expanded view for GREEN/YELLOW */}
                        {(result === 'GREEN' || result === 'YELLOW') && (() => {
                          const alreadyIn = markets.some(m =>
                            m.tliMarketId === op.id || m.name.trim().toLowerCase() === (car?.name || '').trim().toLowerCase()
                          );
                          if (alreadyIn) return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, padding: '7px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6 }}>
                              <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 700 }}>✓ In Submission Tracker</span>
                              <span style={{ fontSize: 12, color: '#16a34a', opacity: 0.7 }}>Already added</span>
                            </div>
                          );
                          const reasons = result === 'GREEN'
                            ? (fitReasons || []).slice(0, 3).join('; ')
                            : (softFlags || []).slice(0, 3).join('; ');
                          return (
                            <div style={{ marginBottom: 12 }}>
                              <button
                                onClick={() => {
                                  setMarkets(p => [...p, {
                                    id:                `mkt_${Date.now()}_${Math.random().toString(36).slice(2, 5)}`,
                                    name:              car?.name || op.carrierId || '',
                                    tliMarketId:       op.id,
                                    uwName:            ch?.contactName || '',
                                    uwEmail:           ch?.contactEmail || '',
                                    uwPhone:           ch?.contactPhone || '',
                                    dateSubmitted:     new Date().toISOString().slice(0, 10),
                                    status:            'submitted',
                                    quotedPremium:     '',
                                    quotedAt:          null,
                                    lastContactedAt:   null,
                                    source:            'Market Intelligence',
                                    intelligenceResult: result,
                                    notes:             reasons ? `${result} — ${reasons}` : result,
                                  }]);
                                }}
                                style={{ fontSize: 12, fontWeight: 700, color: '#1e40af', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                              >
                                + Add to Submission Tracker
                              </button>
                            </div>
                          );
                        })()}

                        {/* Decision summary */}
                        {decision && (
                          <div style={{ fontSize: 12, color: '#374151', background: '#f1f5f9', borderRadius: 6, padding: '7px 10px', marginBottom: 12, lineHeight: 1.5 }}>
                            {decision}
                          </div>
                        )}

                        {/* Hard declines — RED */}
                        {hardReasons && hardReasons.length > 0 && (
                          <div style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#991b1b', marginBottom: 4 }}>Hard Declines</div>
                            <ul style={{ margin: 0, padding: '0 0 0 16px', fontSize: 12, color: '#374151', lineHeight: 1.7 }}>
                              {hardReasons.map((r, i) => <li key={i}>{r}</li>)}
                            </ul>
                          </div>
                        )}

                        {/* Review triggers — YELLOW */}
                        {softFlags && softFlags.length > 0 && (
                          <div style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#92400e', marginBottom: 4 }}>Needs Review</div>
                            <ul style={{ margin: 0, padding: '0 0 0 16px', fontSize: 12, color: '#374151', lineHeight: 1.7 }}>
                              {softFlags.map((f, i) => <li key={i}>{f}</li>)}
                            </ul>
                          </div>
                        )}

                        {/* Missing data */}
                        {missingData && missingData.length > 0 && (
                          <div style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Missing Account Data</div>
                            <ul style={{ margin: 0, padding: '0 0 0 16px', fontSize: 12, color: '#64748b', lineHeight: 1.7 }}>
                              {missingData.map((d, i) => <li key={i}>{d}</li>)}
                            </ul>
                          </div>
                        )}

                        {/* Passed rules — GREEN */}
                        {fitReasons && fitReasons.length > 0 && result === 'GREEN' && (
                          <div style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#15803d', marginBottom: 4 }}>Rules Passed</div>
                            <ul style={{ margin: 0, padding: '0 0 0 16px', fontSize: 12, color: '#374151', lineHeight: 1.7 }}>
                              {fitReasons.slice(0, 8).map((n, i) => <li key={i}>{n}</li>)}
                            </ul>
                          </div>
                        )}

                        {/* Submission instructions */}
                        {op.submissionInstructions && (
                          <div style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Submission Instructions</div>
                            <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6 }}>{op.submissionInstructions}</div>
                          </div>
                        )}

                        {/* Contact — all channels */}
                        {(allChannelContacts && allChannelContacts.length > 0 ? allChannelContacts : (ch ? [ch] : [])).map((c, i) => c?.contactName ? (
                          <div key={i} style={{ fontSize: 12, color: '#64748b', marginTop: i === 0 ? 8 : 4, paddingTop: i === 0 ? 8 : 0, borderTop: i === 0 ? '1px solid #f1f5f9' : 'none' }}>
                            {allChannelContacts && allChannelContacts.length > 1 && (
                              <span style={{ fontWeight: 600, color: '#475569' }}>{c.name}: </span>
                            )}
                            <strong style={{ color: '#1a2332' }}>{c.contactName}</strong>
                            {c.contactEmail && <> · <a href={`mailto:${c.contactEmail}`} style={{ color: '#3b82f6' }}>{c.contactEmail}</a></>}
                            {c.contactPhone && <> · {c.contactPhone}</>}
                          </div>
                        ) : null)}

                        {/* Child programs — evaluated eligibility, shown under parent */}
                        {cardPrograms.length > 0 && (
                          <div style={{ marginTop: 12, borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>Available Programs</div>
                            {cardPrograms.map(p => {
                              const pDot = p.tier === 'green' ? '#22c55e' : p.tier === 'yellow' ? '#f59e0b' : '#ef4444';
                              const pLabel = p.tier === 'green' ? 'Eligible' : p.tier === 'yellow' ? 'Review' : 'Ineligible';
                              return (
                                <div key={p.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6, fontSize: 12 }}>
                                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: pDot, flexShrink: 0, marginTop: 3 }} />
                                  <div style={{ minWidth: 0 }}>
                                    <div style={{ fontWeight: 500, color: '#1a2332' }}>{p.name} <span style={{ fontWeight: 400, color: pDot, fontSize: 11 }}>({pLabel})</span></div>
                                    {p.decision && <div style={{ color: '#64748b', marginTop: 1, lineHeight: 1.4 }}>{p.decision}</div>}
                                    {p.tier !== 'green' && p.reviewTriggers && p.reviewTriggers.length > 0 && (
                                      <div style={{ color: '#92400e', marginTop: 2, fontSize: 11 }}>{p.reviewTriggers[0]}</div>
                                    )}
                                    {p.tier === 'red' && p.hardDeclines && p.hardDeclines.length > 0 && (
                                      <div style={{ color: '#991b1b', marginTop: 2, fontSize: 11 }}>{p.hardDeclines[0]}</div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Access routes — routing options under this carrier */}
                        {cardAccessRoutes.length > 0 && (
                          <div style={{ marginTop: 12, borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>Access Routes</div>
                            {cardAccessRoutes.map(a => (
                              <div key={a.id} style={{ marginBottom: 6, fontSize: 12 }}>
                                <div style={{ fontWeight: 500, color: '#1a2332' }}>{a.channel || a.name}</div>
                                {a.submissionInstructions && (
                                  <div style={{ color: '#64748b', marginTop: 2, lineHeight: 1.4 }}>{a.submissionInstructions}</div>
                                )}
                                {/* TODO (V2): Dedicated "MGA Market Bundles" top-level section */}
                                {a.eligibleMarkets && a.eligibleMarkets.length > 0 && (
                                  <div style={{ marginTop: 8, paddingTop: 6, borderTop: '1px solid #f1f5f9' }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                                      Also eligible through {a.accessProviderName || a.channel || a.name} ({a.eligibleMarkets.length})
                                    </div>
                                    {a.eligibleMarkets.map(em => {
                                      const emDot   = em.tier === 'green' ? '#22c55e' : '#f59e0b';
                                      const emLabel = em.tier === 'green' ? 'Eligible' : 'Review';
                                      const emNote  = em.tier === 'green' ? em.fitReasons?.[0] : em.softFlags?.[0];
                                      return (
                                        <div key={em.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 4, fontSize: 11 }}>
                                          <span style={{ width: 7, height: 7, borderRadius: '50%', background: emDot, flexShrink: 0, marginTop: 3 }} />
                                          <div style={{ minWidth: 0 }}>
                                            <span style={{ fontWeight: 500, color: '#1a2332' }}>{em.name}</span>
                                            {em.parentMarketId && <span style={{ color: '#94a3b8', marginLeft: 4, fontSize: 10 }}>program</span>}
                                            <span style={{ color: emDot, marginLeft: 4, fontWeight: 400 }}>({emLabel})</span>
                                            {emNote && <div style={{ color: '#64748b', marginTop: 1, lineHeight: 1.4 }}>{emNote}</div>}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Timestamp footer */}
          {tliAnalyzedAt && (
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>
                Last evaluated: {new Date(tliAnalyzedAt).toLocaleString()}
              </span>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>
                {tliCounts.GREEN} submit · {tliCounts.YELLOW} review · {tliCounts.RED} decline · {dedupedAll.length} total
              </span>
            </div>
          )}
        </div>
      );
    };

    // ── Submission Tracker view ───────────────────────────────────────────────
    const renderTracker = () => {
      const quotedMarkets = markets.filter(m => m.status === 'quoted' || m.status === 'bound');
      const waitingCount  = markets.filter(m => m.status === 'submitted' || m.status === 'underwriting').length;
      const declinedCount = markets.filter(m => m.status === 'declined').length;
      const premiums      = quotedMarkets.map(m => parseMarketPremium(m.quotedPremium)).filter(p => p !== null);
      const bestPremium   = premiums.length ? Math.min(...premiums) : null;
      const filtered = enriched.filter(({ mkt, isFollowUp }) => {
        if (marketFilter === 'followup') return isFollowUp;
        if (marketFilter === 'quoted')   return mkt.status === 'quoted' || mkt.status === 'bound';
        if (marketFilter === 'active')   return mkt.status !== 'declined';
        return true;
      });
      const updateMkt  = (id, field, val) => setMarkets(p => p.map(m => m.id === id ? { ...m, [field]: val } : m));
      const deleteMkt  = (id)             => setMarkets(p => p.filter(m => m.id !== id));
      const statusMkt  = (id, status)     => setMarkets(p => p.map(m => {
        if (m.id !== id) return m;
        const updates = { status };
        if (status === 'quoted' && m.status !== 'quoted' && !m.quotedAt)
          updates.quotedAt = new Date().toISOString().slice(0, 10);
        return { ...m, ...updates };
      }));
      const contactMkt = (id)             => setMarkets(p => p.map(m =>
        m.id === id ? { ...m, lastContactedAt: new Date().toISOString().slice(0, 10) } : m
      ));
      const FILTER_BTNS = [
        { key: 'all',      label: 'All Markets' },
        { key: 'followup', label: `Needs Follow-Up${followUpCount ? ` (${followUpCount})` : ''}` },
        { key: 'quoted',   label: 'Quotes Received' },
        { key: 'active',   label: 'Active' },
      ];
      return (
        <>
          {showAddMarket && (
            <AddMarketForm
              onAdd={mkt => setMarkets(p => [...p, mkt])}
              onClose={() => setShowAddMarket(false)}
            />
          )}
          {editingMkt && (
            <EditMarketModal
              mkt={editingMkt}
              onSave={updates => setMarkets(p => p.map(m => m.id === editingMkt.id ? { ...m, ...updates } : m))}
              onClose={() => setEditingMkt(null)}
            />
          )}

        {/* Overall summary bar */}
        <div className="mkt-summary-bar">
          <div className="mkt-summary-stat">
            <div className="mkt-summary-num">{markets.length}</div>
            <div className="mkt-summary-lbl">Total Markets</div>
          </div>
          <div className="mkt-summary-stat">
            <div className="mkt-summary-num">{waitingCount}</div>
            <div className="mkt-summary-lbl">Waiting on Response</div>
          </div>
          <div className="mkt-summary-stat">
            <div className="mkt-summary-num">{quotedMarkets.length}</div>
            <div className="mkt-summary-lbl">Quotes Received</div>
          </div>
          <div className="mkt-summary-stat">
            <div className="mkt-summary-num">{declinedCount}</div>
            <div className="mkt-summary-lbl">Declined</div>
          </div>
          <div className="mkt-summary-stat">
            <div className="mkt-summary-num">{bestPremium !== null ? fmtPremium(String(bestPremium)) : '—'}</div>
            <div className="mkt-summary-lbl">Best Quote</div>
          </div>
          {followUpCount > 0 && (
            <div className="mkt-summary-stat mkt-summary-alert">
              <div className="mkt-summary-num">{followUpCount}</div>
              <div className="mkt-summary-lbl">Need Follow-Up Today</div>
            </div>
          )}
        </div>

        {/* Quote comparison table */}
        {quotedMarkets.length > 0 && (
          <div className="card mkt-quotes-card">
            <div className="card-title"><span className="section-icon">📊</span>Quote Comparison</div>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Market</th>
                    <th>Quoted Premium</th>
                    <th>Status</th>
                    <th>Days to Quote</th>
                  </tr>
                </thead>
                <tbody>
                  {quotedMarkets
                    .slice()
                    .sort((a, b) => (parseMarketPremium(a.quotedPremium) ?? Infinity) - (parseMarketPremium(b.quotedPremium) ?? Infinity))
                    .map(m => {
                      const info = MARKET_STATUS_MAP[m.status];
                      const prem = parseMarketPremium(m.quotedPremium);
                      const isBest = bestPremium !== null && prem === bestPremium;
                      const daysToQuote = m.quotedAt && m.dateSubmitted
                        ? Math.max(0, Math.floor((new Date(`${m.quotedAt}T12:00:00`) - new Date(`${m.dateSubmitted}T12:00:00`)) / 86400000))
                        : '—';
                      return (
                        <tr key={m.id} className={isBest ? 'mkt-best-row' : ''}>
                          <td>{isBest && <span className="mkt-best-inline">🏆 </span>}<strong>{m.name}</strong></td>
                          <td className="mkt-premium-cell">{fmtPremium(m.quotedPremium)}</td>
                          <td>
                            <span className="mkt-status-pill-sm" style={{ background: info?.bg, color: info?.color, border: `1px solid ${info?.border}` }}>
                              {info?.label}
                            </span>
                          </td>
                          <td>{daysToQuote}</td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div className="mkt-toolbar">
          <div className="mkt-filter-btns">
            {FILTER_BTNS.map(f => (
              <button
                key={f.key}
                className={`mkt-filter-btn${marketFilter === f.key ? ' active' : ''}`}
                onClick={() => setMarketFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <button className="mkt-add-btn" onClick={exportTrackerExcel} disabled={markets.length === 0} title="Export all markets to Excel">⬇ Export Excel</button>
          <button className="mkt-add-btn" onClick={() => setShowAddMarket(true)}>＋ Add Market</button>
        </div>

        {/* Cards */}
        {filtered.length === 0
          ? <div className="mkt-empty">
              {markets.length === 0
                ? 'No markets added yet. Click "+ Add Market" to get started.'
                : 'No markets match this filter.'}
            </div>
          : <div className="mkt-cards">
              {filtered.map(({ mkt, isFollowUp, daysSinceContact, borDays, borFollowUp }) => {
                const prem = parseMarketPremium(mkt.quotedPremium);
                const isBestQuote = bestPremium !== null && prem === bestPremium
                  && (mkt.status === 'quoted' || mkt.status === 'bound');
                return (
                  <MarketCard
                    key={mkt.id}
                    mkt={mkt}
                    isFollowUp={isFollowUp}
                    daysSinceContact={daysSinceContact}
                    borFollowUp={borFollowUp}
                    borDays={borDays}
                    isBestQuote={isBestQuote}
                    onUpdate={(field, val) => updateMkt(mkt.id, field, val)}
                    onDelete={() => deleteMkt(mkt.id)}
                    onEdit={() => setEditingMkt(mkt)}
                    onStatusChange={status => statusMkt(mkt.id, status)}
                    onMarkContacted={() => contactMkt(mkt.id)}
                  />
                );
              })}
            </div>
        }
        </>
      );
    };

    // ── Main render ───────────────────────────────────────────────────────────
    return (
      <div>
        {/* Detail modal — rendered over any view */}
        {mktDetailMarket && renderDetailModal()}
        {mktDirEditMarket && (
          <MarketDirectoryEditModal
            mkt={mktDirEditMarket}
            onSave={savedMarket => setAllMktMarkets(prev => prev.map(m => m.id === savedMarket.id ? savedMarket : m))}
            onDelete={deletedId => setAllMktMarkets(prev => prev.filter(m => m.id !== deletedId))}
            onClose={() => setMktDirEditMarket(null)}
            apiFetch={apiFetch}
          />
        )}

        {/* View tab switcher */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 18, borderBottom: '2px solid #e2e8f0', paddingBottom: 0 }}>
          {VIEW_TABS.map(v => (
            <button
              key={v.key}
              onClick={() => setMktMainView(v.key)}
              style={{
                padding: '8px 18px', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer',
                borderBottom: mktMainView === v.key ? '2px solid #2563eb' : '2px solid transparent',
                marginBottom: -2, background: 'none',
                color: mktMainView === v.key ? '#2563eb' : '#64748b',
                transition: 'color 0.15s',
              }}
            >
              {v.label}
            </button>
          ))}
        </div>

        {mktMainView === 'finder'       && renderFinder()}
        {mktMainView === 'directory'    && renderDirectory()}
        {mktMainView === 'intelligence' && renderIntelligence()}
        {mktMainView === 'tracker'      && renderTracker()}

        {/* Admin: User Management */}
        {(currentRole === 'admin' || currentRole === 'platform_owner') && (() => {
          const loadUsers = async () => {
            setAdminUsersLoading(true);
            try {
              const r = await apiFetch(`${API_BASE}/api/admin/users`);
              if (r.ok) setAdminUsers(await r.json());
            } catch {}
            setAdminUsersLoading(false);
          };
          const createUser = async () => {
            const { username, name, email, password, role } = adminUserForm;
            if (!username || !password) { setAdminUserMsg('Username and password required'); return; }
            if (password.length < 8) { setAdminUserMsg('Password must be at least 8 characters'); return; }
            try {
              const r = await apiFetch(`${API_BASE}/api/admin/users`, {
                method: 'POST',
                body: JSON.stringify({ username, name, email, password, role }),
              });
              const d = await r.json();
              if (!r.ok) { setAdminUserMsg('Error: ' + d.error); return; }
              setAdminUserMsg(`Created ${username} ✓`);
              setAdminUserForm({ username: '', name: '', email: '', password: '', role: 'producer' });
              loadUsers();
            } catch (e) { setAdminUserMsg('Error: ' + e.message); }
            setTimeout(() => setAdminUserMsg(''), 4000);
          };
          const toggleDisable = async (u) => {
            try {
              const r = await apiFetch(`${API_BASE}/api/admin/users`, {
                method: 'PATCH',
                body: JSON.stringify({ id: u.id, disabled: !u.disabled }),
              });
              const d = await r.json();
              if (!r.ok) { setAdminUserMsg('Error: ' + d.error); setTimeout(() => setAdminUserMsg(''), 4000); return; }
              loadUsers();
            } catch {}
          };
          const changeRole = async (u) => {
            // Cycle through roles active during the bridge period.
            // agency_admin, marketer, platform_owner are recognized but not cycled until Phase 3+.
            const roles = ['producer', 'market_editor', 'admin'];
            const idx   = roles.indexOf(u.role);
            const next  = idx >= 0 ? roles[(idx + 1) % roles.length] : u.role;
            if (!window.confirm(`Change ${u.username}'s role from "${u.role}" to "${next}"?`)) return;
            try {
              const r = await apiFetch(`${API_BASE}/api/admin/users`, {
                method: 'PATCH',
                body: JSON.stringify({ id: u.id, role: next }),
              });
              const d = await r.json();
              if (!r.ok) { setAdminUserMsg('Error: ' + d.error); setTimeout(() => setAdminUserMsg(''), 4000); return; }
              setAdminUserMsg(`Role updated to ${next} ✓`);
              setTimeout(() => setAdminUserMsg(''), 3000);
              loadUsers();
            } catch (e) { setAdminUserMsg('Error: ' + e.message); }
          };
          const resetPassword = async (u) => {
            const pw = window.prompt(`New password for ${u.username} (min 8 characters):`);
            if (!pw) return;
            if (pw.length < 8) { setAdminUserMsg('Password must be at least 8 characters'); setTimeout(() => setAdminUserMsg(''), 3000); return; }
            try {
              const r = await apiFetch(`${API_BASE}/api/admin/users`, {
                method: 'PATCH',
                body: JSON.stringify({ id: u.id, password: pw }),
              });
              const d = await r.json();
              if (!r.ok) { setAdminUserMsg('Error: ' + d.error); return; }
              setAdminUserMsg(`Password reset for ${u.username} ✓ (existing sessions revoked)`);
              setTimeout(() => setAdminUserMsg(''), 4000);
            } catch (e) { setAdminUserMsg('Error: ' + e.message); }
          };
          const ROLE_LABELS = { admin: 'Admin', platform_owner: 'Platform Owner', agency_admin: 'Agency Admin', marketer: 'Marketer', producer: 'Producer', market_editor: 'Market Editor' };
          const s = { fontSize: 13, padding: '6px 10px', borderRadius: 6, border: '1.5px solid #e2e8f0', fontFamily: 'inherit' };
          return (
            <div style={{ marginTop: 28, borderTop: '1.5px solid #e2e8f0', paddingTop: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#0f2148', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
                User Management
                <button onClick={loadUsers} style={{ fontSize: 12, background: 'none', border: '1px solid #e2e8f0', borderRadius: 5, padding: '2px 8px', cursor: 'pointer', color: '#64748b' }}>
                  {adminUsersLoading ? 'Loading…' : 'Refresh'}
                </button>
              </div>
              {adminUsers && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 18 }}>
                  <thead>
                    <tr style={{ borderBottom: '1.5px solid #e2e8f0', color: '#64748b', fontWeight: 600 }}>
                      {['Username','Name','Role','Status','Legal','Actions'].map(h => (
                        <th key={h} style={{ textAlign: 'left', paddingBottom: 6, paddingRight: 10 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {adminUsers.map(u => (
                      <tr key={u.id} style={{ borderBottom: '1px solid #f1f5f9', opacity: u.disabled ? 0.55 : 1 }}>
                        <td style={{ padding: '5px 10px 5px 0', fontWeight: 600 }}>{u.username}</td>
                        <td style={{ paddingRight: 10 }}>{u.name || '—'}</td>
                        <td style={{ paddingRight: 10 }}>
                          <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 8,
                            background: u.role === 'admin' || u.role === 'platform_owner' ? '#ede9fe' : u.role === 'agency_admin' ? '#fce7f3' : u.role === 'marketer' || u.role === 'market_editor' ? '#fef9c3' : '#f0f9ff',
                            color:      u.role === 'admin' || u.role === 'platform_owner' ? '#7c3aed' : u.role === 'agency_admin' ? '#9d174d' : u.role === 'marketer' || u.role === 'market_editor' ? '#a16207' : '#0369a1' }}>
                            {ROLE_LABELS[u.role] || u.role}
                          </span>
                        </td>
                        <td style={{ paddingRight: 10 }}>
                          <span style={{ background: u.disabled ? '#fee2e2' : '#dcfce7', color: u.disabled ? '#dc2626' : '#16a34a', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                            {u.disabled ? 'Disabled' : 'Active'}
                          </span>
                        </td>
                        <td style={{ paddingRight: 10 }}>
                          {u.termsVersion && u.privacyVersion ? (
                            <span style={{ background: '#dcfce7', color: '#16a34a', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }} title={`Terms v${u.termsVersion} · Privacy v${u.privacyVersion}\nAccepted: ${u.termsAcceptedAt ? new Date(u.termsAcceptedAt).toLocaleDateString() : '—'}`}>
                              v{u.termsVersion} ✓
                            </span>
                          ) : (
                            <span style={{ background: '#fef9c3', color: '#a16207', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                              Pending
                            </span>
                          )}
                        </td>
                        <td style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button onClick={() => toggleDisable(u)} style={{ fontSize: 11, background: 'none', border: '1px solid #e2e8f0', borderRadius: 5, padding: '2px 8px', cursor: 'pointer', color: '#64748b' }}>
                            {u.disabled ? 'Enable' : 'Disable'}
                          </button>
                          <button onClick={() => changeRole(u)} style={{ fontSize: 11, background: 'none', border: '1px solid #e2e8f0', borderRadius: 5, padding: '2px 8px', cursor: 'pointer', color: '#64748b' }}>
                            Change Role
                          </button>
                          <button onClick={() => resetPassword(u)} style={{ fontSize: 11, background: 'none', border: '1px solid #e2e8f0', borderRadius: 5, padding: '2px 8px', cursor: 'pointer', color: '#64748b' }}>
                            Reset PW
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {!adminUsers && !adminUsersLoading && (
                <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 14 }}>Click Refresh to load users.</div>
              )}
              <div style={{ fontSize: 14, fontWeight: 600, color: '#334155', marginBottom: 10 }}>Add Producer</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 8 }}>
                <input placeholder="Username *" value={adminUserForm.username} onChange={e => setAdminUserForm(p => ({...p, username: e.target.value}))} style={s} />
                <input placeholder="Full Name" value={adminUserForm.name} onChange={e => setAdminUserForm(p => ({...p, name: e.target.value}))} style={s} />
                <input placeholder="Email" value={adminUserForm.email} onChange={e => setAdminUserForm(p => ({...p, email: e.target.value}))} style={s} />
                <input type="password" placeholder="Password * (min 8 chars)" value={adminUserForm.password} onChange={e => setAdminUserForm(p => ({...p, password: e.target.value}))} style={s} />
                <select value={adminUserForm.role} onChange={e => setAdminUserForm(p => ({...p, role: e.target.value}))} style={s}>
                  <option value="producer">Producer</option>
                  <option value="market_editor">Market Editor (legacy)</option>
                  <option value="admin">Admin (legacy)</option>
                  {/* agency_admin, marketer, platform_owner require Phase 3+ org flow — not active yet */}
                </select>
              </div>
              <button onClick={createUser} style={{ background: '#0f2148', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 18px', fontSize: 13, cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>
                Add User
              </button>
              {adminUserMsg && <span style={{ marginLeft: 12, fontSize: 13, color: adminUserMsg.startsWith('Error') ? '#ef4444' : '#16a34a' }}>{adminUserMsg}</span>}

              {/* ── Invite Link (legacy admin only — platform_owner uses Platform Console) ── */}
              {currentRole === 'admin' && (
              <div style={{ marginTop: 24, paddingTop: 18, borderTop: '1px solid #f1f5f9' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#334155', marginBottom: 10 }}>Invite Link</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <select value={inviteRole} onChange={e => { setInviteRole(e.target.value); setInviteLink(''); }} style={s}>
                    <option value="producer">Producer</option>
                    <option value="market_editor">Market Editor (legacy)</option>
                  </select>
                  <button
                    onClick={async () => {
                      setInviteLoading(true);
                      setInviteLink('');
                      setInviteCopied(false);
                      try {
                        const r = await apiFetch(`${API_BASE}/api/invite/create`, {
                          method: 'POST',
                          body: JSON.stringify({ role: inviteRole }),
                        });
                        const d = await r.json();
                        if (r.ok && d.url) setInviteLink(d.url);
                        else setInviteLink('Error: ' + (d.error || 'Failed'));
                      } catch (e) { setInviteLink('Error: ' + e.message); }
                      setInviteLoading(false);
                    }}
                    disabled={inviteLoading}
                    style={{ background: '#0f2148', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 16px', fontSize: 13, cursor: inviteLoading ? 'wait' : 'pointer', fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap' }}
                  >
                    {inviteLoading ? 'Generating…' : 'Generate Link'}
                  </button>
                </div>
                {inviteLink && !inviteLink.startsWith('Error') && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      readOnly
                      value={inviteLink}
                      style={{ flex: 1, fontSize: 12, padding: '7px 10px', borderRadius: 6, border: '1px solid #e2e8f0', fontFamily: 'monospace', color: '#334155', background: '#f8fafc' }}
                      onFocus={e => e.target.select()}
                    />
                    <button
                      onClick={() => { navigator.clipboard.writeText(inviteLink); setInviteCopied(true); setTimeout(() => setInviteCopied(false), 2000); }}
                      style={{ background: inviteCopied ? '#16a34a' : '#e2e8f0', color: inviteCopied ? '#fff' : '#334155', border: 'none', borderRadius: 6, padding: '7px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit', transition: 'background 0.2s', whiteSpace: 'nowrap' }}
                    >
                      {inviteCopied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                )}
                {inviteLink && inviteLink.startsWith('Error') && (
                  <div style={{ fontSize: 13, color: '#ef4444' }}>{inviteLink}</div>
                )}
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>Link expires in 7 days and can only be used once.</div>
              </div>
              )}
            </div>
          );
        })()}

        {/* Agency Admin: Team Management */}
        {currentRole === 'agency_admin' && (() => {
          const ROLE_LABEL = { agency_admin: 'Agency Admin', marketer: 'Marketer', producer: 'Producer' };
          const ROLE_COLOR = { agency_admin: { bg: '#fce7f3', color: '#9d174d' }, marketer: { bg: '#fef9c3', color: '#a16207' }, producer: { bg: '#f0f9ff', color: '#0369a1' } };
          const INV_STATUS_COLOR = { pending: '#2563eb', claimed: '#16a34a', revoked: '#94a3b8', expired: '#f59e0b' };

          const loadTeamUsers = async () => {
            setTeamUsersLoading(true);
            try {
              const r = await apiFetch(`${API_BASE}/api/agency/users`);
              if (r.ok) {
                const d = await r.json();
                setTeamUsers(d.users || []);
                setTeamSeatUsage(d.seatUsage || null);
              }
            } catch {}
            setTeamUsersLoading(false);
          };

          const performTeamAction = async (userId, action, newRole) => {
            setTeamActionLoading(userId);
            setTeamMsg('');
            try {
              const r = await apiFetch(`${API_BASE}/api/agency/users`, {
                method: 'PATCH',
                body: JSON.stringify({ userId, action, ...(newRole ? { newRole } : {}) }),
              });
              const d = await r.json();
              if (r.ok) {
                setTeamMsg(action === 'disable' ? 'User disabled.' : action === 'reactivate' ? 'User reactivated.' : 'Role updated.');
                setTeamRoleEditingId(null);
                setOffboardUser(null);
                setOffboardToUserId('');
                loadTeamUsers();
              } else {
                setTeamMsg('Error: ' + (d.error || 'Action failed'));
              }
            } catch (e) { setTeamMsg('Error: ' + e.message); }
            setTeamActionLoading(null);
            setTimeout(() => setTeamMsg(''), 5000);
          };

          const handleDisable = async (user) => {
            if (user.ownedCarrierCount > 0) {
              setOffboardUser(user);
              setOffboardToUserId('');
            } else {
              if (!window.confirm(`Disable ${user.username}? Their session will be revoked immediately.`)) return;
              await performTeamAction(user.id, 'disable');
            }
          };

          const handleReassignAndDisable = async () => {
            if (!offboardUser || !offboardToUserId) return;
            setOffboardLoading(true);
            setTeamMsg('');
            try {
              const r = await apiFetch(`${API_BASE}/api/agency/carriers/bulk-reassign`, {
                method: 'POST',
                body: JSON.stringify({ fromUserId: offboardUser.id, toUserId: offboardToUserId }),
              });
              const d = await r.json();
              if (!r.ok || !d.ok) { setTeamMsg('Reassignment failed: ' + (d.error || (d.failed ? `${d.failed} carrier(s) could not be transferred` : 'Error'))); setOffboardLoading(false); return; }
              await performTeamAction(offboardUser.id, 'disable');
            } catch (e) { setTeamMsg('Error: ' + e.message); }
            setOffboardLoading(false);
          };

          const loadTeamAudit = async () => {
            setTeamAuditLoading(true);
            try {
              const r = await apiFetch(`${API_BASE}/api/agency/audit`);
              if (r.ok) setTeamAuditLog(await r.json());
              else setTeamAuditLog([]);
            } catch { setTeamAuditLog([]); }
            setTeamAuditLoading(false);
          };

          const loadAgencyInvites = async () => {
            setAgencyInvitesLoading(true);
            try {
              const r = await apiFetch(`${API_BASE}/api/invite/list`);
              if (r.ok) { const d = await r.json(); setAgencyInvites(d.invites || []); }
              else setAgencyInvites([]);
            } catch { setAgencyInvites([]); }
            setAgencyInvitesLoading(false);
          };
          const createAgencyInvite = async () => {
            setAgencyInviteCreating(true); setAgencyInviteLink(''); setAgencyInviteCopied(false);
            try {
              const r = await apiFetch(`${API_BASE}/api/invite/create`, { method: 'POST', body: JSON.stringify({ role: agencyInviteRole }) });
              const d = await r.json();
              if (r.ok && d.url) { setAgencyInviteLink(d.url); loadAgencyInvites(); }
              else setAgencyInviteLink('Error: ' + (d.error || 'Failed'));
            } catch (e) { setAgencyInviteLink('Error: ' + e.message); }
            setAgencyInviteCreating(false);
          };
          const revokeAgencyInvite = async (inviteId) => {
            setAgencyInviteRevoking(inviteId); setAgencyInviteMsg('');
            try {
              const r = await apiFetch(`${API_BASE}/api/invite/revoke`, { method: 'POST', body: JSON.stringify({ inviteId }) });
              const d = await r.json();
              if (r.ok) { setAgencyInviteMsg('Invite revoked.'); loadAgencyInvites(); }
              else setAgencyInviteMsg('Error: ' + (d.error || 'Failed'));
            } catch (e) { setAgencyInviteMsg('Error: ' + e.message); }
            setAgencyInviteRevoking(null);
            setTimeout(() => setAgencyInviteMsg(''), 4000);
          };

          // Auto-load based on active tab
          if (teamTab !== 'invites' && teamTab !== 'activity' && !teamUsers && !teamUsersLoading) loadTeamUsers();
          if (teamTab === 'invites' && !agencyInvites && !agencyInvitesLoading) loadAgencyInvites();
          if (teamTab === 'activity' && !teamAuditLog && !teamAuditLoading) loadTeamAudit();

          const activeUsers   = (teamUsers || []).filter(u => !u.disabled);
          const disabledUsers = (teamUsers || []).filter(u =>  u.disabled);
          const seatUsed  = teamSeatUsage?.used  ?? activeUsers.length;
          const seatLimit = teamSeatUsage?.limit ?? '…';

          const TAB_STYLE = (t) => ({
            padding: '7px 16px', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', background: 'none',
            borderBottom: teamTab === t ? '2px solid #2563eb' : '2px solid transparent',
            marginBottom: -2, color: teamTab === t ? '#2563eb' : '#64748b', fontFamily: 'inherit',
          });
          const BTN = (label, onClick, opts = {}) => (
            <button onClick={onClick} disabled={opts.disabled}
              style={{ fontSize: 12, padding: '4px 10px', border: opts.danger ? '1px solid #fca5a5' : '1px solid #e2e8f0', borderRadius: 5,
                background: opts.danger ? '#fef2f2' : '#f8fafc', color: opts.danger ? '#dc2626' : '#334155',
                cursor: opts.disabled ? 'not-allowed' : 'pointer', fontWeight: 600, fontFamily: 'inherit',
                opacity: opts.disabled ? 0.6 : 1, ...opts.style }}>
              {label}
            </button>
          );

          return (
            <div style={{ marginTop: 36, background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', maxWidth: 900, overflow: 'hidden' }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 20px', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#0f2148', flex: 1 }}>Team</div>
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  <strong>{seatUsed}</strong> / {seatLimit} seats active
                </span>
                <button onClick={() => { setTeamUsers(null); setTeamAuditLog(null); setAgencyInvites(null); }}
                  style={{ fontSize: 12, padding: '4px 10px', border: '1px solid #e2e8f0', borderRadius: 5, background: '#f8fafc', color: '#64748b', cursor: 'pointer', fontFamily: 'inherit' }}>
                  Refresh
                </button>
              </div>

              {/* Sub-tabs */}
              <div style={{ display: 'flex', gap: 2, padding: '0 20px', borderBottom: '2px solid #f1f5f9' }}>
                {[['active','Active'],['disabled','Disabled'],['invites','Invites'],['activity','Activity']].map(([t,label]) => (
                  <button key={t} onClick={() => setTeamTab(t)} style={TAB_STYLE(t)}>{label}</button>
                ))}
              </div>

              <div style={{ padding: '16px 20px' }}>
                {/* Message */}
                {teamMsg && (
                  <div style={{ marginBottom: 12, fontSize: 13, fontWeight: 600, color: teamMsg.startsWith('Error') ? '#dc2626' : '#16a34a', padding: '8px 12px', background: teamMsg.startsWith('Error') ? '#fef2f2' : '#f0fdf4', borderRadius: 6 }}>
                    {teamMsg}
                  </div>
                )}

                {/* Offboarding panel */}
                {offboardUser && (
                  <div style={{ marginBottom: 16, padding: '14px 16px', background: '#fffbeb', border: '1.5px solid #fde68a', borderRadius: 8 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#92400e', marginBottom: 8 }}>
                      Disable {offboardUser.username} — {offboardUser.ownedCarrierCount} owned account{offboardUser.ownedCarrierCount > 1 ? 's' : ''} must be reassigned
                    </div>
                    <div style={{ fontSize: 12, color: '#78350f', marginBottom: 10 }}>
                      Select an active team member to receive all owned accounts before disabling this user.
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <select value={offboardToUserId} onChange={e => setOffboardToUserId(e.target.value)}
                        style={{ padding: '7px 10px', border: '1.5px solid #fde68a', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', flex: 1 }}>
                        <option value="">— Select reassign target —</option>
                        {activeUsers.filter(u => u.id !== offboardUser.id).map(u => (
                          <option key={u.id} value={u.id}>{u.username} ({ROLE_LABEL[u.role] || u.role})</option>
                        ))}
                      </select>
                      <button onClick={handleReassignAndDisable} disabled={!offboardToUserId || offboardLoading}
                        style={{ padding: '7px 14px', fontSize: 12, fontWeight: 600, background: offboardToUserId ? '#dc2626' : '#e2e8f0', color: offboardToUserId ? '#fff' : '#94a3b8', border: 'none', borderRadius: 6, cursor: offboardToUserId ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}>
                        {offboardLoading ? 'Working…' : 'Reassign & Disable'}
                      </button>
                      <button onClick={() => { setOffboardUser(null); setOffboardToUserId(''); }}
                        style={{ padding: '7px 12px', fontSize: 12, background: '#f1f5f9', border: 'none', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Active users tab */}
                {teamTab === 'active' && (
                  teamUsersLoading ? <div style={{ fontSize: 13, color: '#94a3b8', padding: '12px 0' }}>Loading…</div>
                  : activeUsers.length === 0 ? <div style={{ fontSize: 13, color: '#94a3b8', padding: '12px 0' }}>No active users.</div>
                  : (
                    <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1.5px solid #f1f5f9' }}>
                          {['User','Role','Accounts','Actions'].map(h => (
                            <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 12 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {activeUsers.map(u => (
                          <tr key={u.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                            <td style={{ padding: '9px 10px', fontWeight: 600, color: '#0f2148' }}>
                              {u.username}
                              {u.name && u.name !== u.username && <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>{u.name}</div>}
                            </td>
                            <td style={{ padding: '9px 10px' }}>
                              {teamRoleEditingId === u.id ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                  <select value={teamRoleNew[u.id] || u.role} onChange={e => setTeamRoleNew(prev => ({ ...prev, [u.id]: e.target.value }))}
                                    style={{ padding: '4px 8px', border: '1.5px solid #2563eb', borderRadius: 5, fontSize: 12, fontFamily: 'inherit' }}>
                                    <option value="producer">Producer</option>
                                    <option value="marketer">Marketer</option>
                                    <option value="agency_admin">Agency Admin</option>
                                  </select>
                                  {BTN('Save', () => performTeamAction(u.id, 'change_role', teamRoleNew[u.id] || u.role), { disabled: teamActionLoading === u.id, style: { background: '#0f2148', color: '#fff', border: 'none' } })}
                                  {BTN('Cancel', () => setTeamRoleEditingId(null))}
                                </span>
                              ) : (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, ...ROLE_COLOR[u.role] }}>{ROLE_LABEL[u.role] || u.role}</span>
                                  {BTN('Change', () => { setTeamRoleEditingId(u.id); setTeamRoleNew(prev => ({ ...prev, [u.id]: u.role })); })}
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '9px 10px', color: u.ownedCarrierCount > 0 ? '#0f2148' : '#94a3b8' }}>
                              {u.ownedCarrierCount > 0 ? u.ownedCarrierCount : '—'}
                            </td>
                            <td style={{ padding: '9px 10px' }}>
                              {BTN(teamActionLoading === u.id ? '…' : 'Disable', () => handleDisable(u), { danger: true, disabled: teamActionLoading === u.id || offboardUser?.id === u.id })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                )}

                {/* Disabled users tab */}
                {teamTab === 'disabled' && (
                  teamUsersLoading ? <div style={{ fontSize: 13, color: '#94a3b8', padding: '12px 0' }}>Loading…</div>
                  : disabledUsers.length === 0 ? <div style={{ fontSize: 13, color: '#94a3b8', padding: '12px 0' }}>No disabled users.</div>
                  : (
                    <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1.5px solid #f1f5f9' }}>
                          {['User','Role','Disabled On',''].map(h => (
                            <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 12 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {disabledUsers.map(u => (
                          <tr key={u.id} style={{ borderBottom: '1px solid #f8fafc', opacity: 0.7 }}>
                            <td style={{ padding: '9px 10px', fontWeight: 600, color: '#0f2148' }}>{u.username}</td>
                            <td style={{ padding: '9px 10px' }}>
                              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, ...ROLE_COLOR[u.role] }}>{ROLE_LABEL[u.role] || u.role}</span>
                            </td>
                            <td style={{ padding: '9px 10px', fontSize: 12, color: '#94a3b8' }}>{u.disabledAt ? new Date(u.disabledAt).toLocaleDateString() : '—'}</td>
                            <td style={{ padding: '9px 10px' }}>
                              {BTN(teamActionLoading === u.id ? '…' : 'Reactivate', () => performTeamAction(u.id, 'reactivate'), { disabled: teamActionLoading === u.id, style: { background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' } })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                )}

                {/* Invites tab — reuse existing invite management */}
                {teamTab === 'invites' && (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                      <select value={agencyInviteRole} onChange={e => { setAgencyInviteRole(e.target.value); setAgencyInviteLink(''); }}
                        style={{ padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' }}>
                        <option value="producer">Producer</option>
                        <option value="marketer">Marketer</option>
                      </select>
                      <button onClick={createAgencyInvite} disabled={agencyInviteCreating}
                        style={{ background: '#0f2148', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 14px', fontSize: 13, cursor: agencyInviteCreating ? 'wait' : 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>
                        {agencyInviteCreating ? 'Generating…' : 'Generate Invite Link'}
                      </button>
                    </div>
                    {agencyInviteLink && !agencyInviteLink.startsWith('Error') && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <input readOnly value={agencyInviteLink} onFocus={e => e.target.select()}
                          style={{ flex: 1, fontSize: 12, padding: '7px 10px', borderRadius: 6, border: '1px solid #e2e8f0', fontFamily: 'monospace', color: '#334155', background: '#f8fafc' }} />
                        <button onClick={() => { navigator.clipboard.writeText(agencyInviteLink); setAgencyInviteCopied(true); setTimeout(() => setAgencyInviteCopied(false), 2000); }}
                          style={{ background: agencyInviteCopied ? '#16a34a' : '#e2e8f0', color: agencyInviteCopied ? '#fff' : '#334155', border: 'none', borderRadius: 6, padding: '7px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                          {agencyInviteCopied ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                    )}
                    {agencyInviteLink && agencyInviteLink.startsWith('Error') && <div style={{ fontSize: 13, color: '#ef4444', marginBottom: 8 }}>{agencyInviteLink}</div>}
                    {agencyInviteMsg && <div style={{ fontSize: 13, marginBottom: 8, color: agencyInviteMsg.startsWith('Error') ? '#ef4444' : '#16a34a', fontWeight: 600 }}>{agencyInviteMsg}</div>}
                    <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 14 }}>Links expire in 7 days and can only be used once.</div>

                    {agencyInvitesLoading && <div style={{ fontSize: 13, color: '#94a3b8' }}>Loading invites…</div>}
                    {agencyInvites && agencyInvites.length === 0 && !agencyInvitesLoading && <div style={{ fontSize: 13, color: '#94a3b8' }}>No invites yet.</div>}
                    {agencyInvites && agencyInvites.length > 0 && (
                      <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '1.5px solid #f1f5f9' }}>
                            {['Role','Status','Created','Claimed By',''].map(h => (
                              <th key={h} style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 12 }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {agencyInvites.map(inv => (
                            <tr key={inv.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                              <td style={{ padding: '8px 8px', color: '#1e293b' }}>{ROLE_LABEL[inv.role] || inv.role}</td>
                              <td style={{ padding: '8px 8px' }}>
                                <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: INV_STATUS_COLOR[inv.status] + '1a', color: INV_STATUS_COLOR[inv.status] }}>
                                  {inv.status}
                                </span>
                              </td>
                              <td style={{ padding: '8px 8px', color: '#64748b' }}>{inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : '—'}</td>
                              <td style={{ padding: '8px 8px', color: '#64748b' }}>{inv.inviteeUsername || '—'}</td>
                              <td style={{ padding: '8px 8px' }}>
                                {inv.status === 'pending' && (
                                  <button onClick={() => revokeAgencyInvite(inv.id)} disabled={agencyInviteRevoking === inv.id}
                                    style={{ fontSize: 11, padding: '3px 10px', border: '1px solid #fca5a5', borderRadius: 5, background: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>
                                    {agencyInviteRevoking === inv.id ? '…' : 'Revoke'}
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                {/* Activity tab — org-filtered audit log */}
                {teamTab === 'activity' && (
                  teamAuditLoading ? <div style={{ fontSize: 13, color: '#94a3b8', padding: '12px 0' }}>Loading activity…</div>
                  : !teamAuditLog || teamAuditLog.length === 0
                    ? <div style={{ fontSize: 13, color: '#94a3b8', padding: '12px 0' }}>No activity yet.</div>
                    : (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                          <button onClick={loadTeamAudit} style={{ fontSize: 12, padding: '4px 10px', border: '1px solid #e2e8f0', borderRadius: 5, background: '#f8fafc', color: '#64748b', cursor: 'pointer', fontFamily: 'inherit' }}>Refresh</button>
                        </div>
                        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: '1.5px solid #f1f5f9' }}>
                              {['Action','Actor','Details','When'].map(h => (
                                <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 11 }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {teamAuditLog.map((e, i) => (
                              <tr key={i} style={{ borderBottom: '1px solid #f8fafc' }}>
                                <td style={{ padding: '7px 10px', fontWeight: 600, color: '#0f2148', whiteSpace: 'nowrap' }}>{e.action}</td>
                                <td style={{ padding: '7px 10px', color: '#64748b' }}>{e.metadata?.actorUsername || '—'}</td>
                                <td style={{ padding: '7px 10px', color: '#64748b' }}>
                                  {e.metadata?.targetUsername && <span>{e.metadata.targetUsername}</span>}
                                  {e.metadata?.oldRole && e.metadata?.newRole && <span style={{ marginLeft: 4 }}>{e.metadata.oldRole} → {e.metadata.newRole}</span>}
                                  {e.metadata?.newOwnerUsername && <span>→ {e.metadata.newOwnerUsername}</span>}
                                </td>
                                <td style={{ padding: '7px 10px', color: '#94a3b8', whiteSpace: 'nowrap' }}>{e.timestamp ? new Date(e.timestamp).toLocaleDateString() : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )
                )}
              </div>
            </div>
          );
        })()}
      </div>
    );
  };



  const renderNotes = () => (
    <AccountNotesBox
      carrierId={carrierId}
      notes={accountNotes}
      onNotesChange={setAccountNotes}
      apiFetch={apiFetch}
    />
  );

  const tabContent = {
    Documents:      renderDocs(),
    Notes:          renderNotes(),
    'Lead Sheet':   renderLeadSheet(),
    'Loss Runs':    renderLossRuns(),
    Equipment:      renderEquipment(),
    Drivers:        renderDrivers(),
    'AI Analysis':  renderAIAnalysis(),
    Submission:     renderSubmission(),
    'Missing Items':renderMissing(),
    Playbook:       renderPlaybook(),
    Markets:        renderMarkets(),
  };

  // ── Platform Console (platform_owner only) ──────────────────────────────────
  function renderPlatformConsole() {
    const loadOrgs = async () => {
      setPlatformOrgsLoading(true);
      setPlatformOrgsError('');
      try {
        const r = await apiFetch(`${API_BASE}/api/platform/organizations`);
        if (!r.ok) { const d = await r.json(); setPlatformOrgsError(d.error || 'Failed to load organizations'); return; }
        setPlatformOrgs(await r.json());
      } catch (e) { setPlatformOrgsError(e.message); }
      finally { setPlatformOrgsLoading(false); }
    };

    const loadOrgDetail = async (org) => {
      setPlatformSelectedOrg(org);
      setPlatformView('org-detail');
      setPlatformOrgDetail(null);
      setPlatformOrgDetailLoading(true);
      setPlatformOrgDetailTab('overview');
      setPlatformEditingSettings(false);
      setPlatformSettingsMsg('');
      setPlatformOrgInvites(null);
      setPlatformInviteMsg('');
      try {
        const r = await apiFetch(`${API_BASE}/api/platform/org-detail?orgId=${org.id}`);
        if (!r.ok) { const d = await r.json(); setPlatformOrgsError(d.error || 'Failed to load org'); return; }
        setPlatformOrgDetail(await r.json());
      } catch (e) { setPlatformOrgsError(e.message); }
      finally { setPlatformOrgDetailLoading(false); }
    };

    const loadPlatformCarrier = async (carrierId) => {
      setPlatformCarrierLoading(true);
      setPlatformCarrier(null);
      try {
        const r = await apiFetch(`${API_BASE}/api/carriers/${carrierId}`);
        if (!r.ok) { const d = await r.json(); alert('Could not load carrier: ' + (d.error || 'unknown error')); return; }
        setPlatformCarrier(await r.json());
        setPlatformView('carrier');
      } catch (e) { alert('Error: ' + e.message); }
      finally { setPlatformCarrierLoading(false); }
    };

    const updateOrgStatus = async (orgId, status) => {
      setPlatformStatusMsg('');
      try {
        const r = await apiFetch(`${API_BASE}/api/platform/org-detail?orgId=${orgId}`, {
          method: 'PATCH',
          body: JSON.stringify({ status }),
        });
        const d = await r.json();
        if (!r.ok) { setPlatformStatusMsg('Error: ' + (d.error || 'Failed')); return; }
        setPlatformStatusMsg(status === 'suspended' ? 'Organization suspended.' : 'Organization activated.');
        if (platformOrgDetail) {
          setPlatformOrgDetail(prev => prev ? { ...prev, org: d.org } : prev);
        }
        setPlatformSelectedOrg(d.org);
        loadOrgs();
        setTimeout(() => setPlatformStatusMsg(''), 4000);
      } catch (e) { setPlatformStatusMsg('Error: ' + e.message); }
    };

    const createOrg = async () => {
      setPlatformCreateOrgMsg('');
      const { name, slug, seatLimit, status, subscriptionStatus } = platformCreateOrgForm;
      if (!name.trim() || !slug.trim()) { setPlatformCreateOrgMsg('Name and slug are required.'); return; }
      try {
        const r = await apiFetch(`${API_BASE}/api/platform/organizations`, {
          method: 'POST',
          body: JSON.stringify({ name: name.trim(), slug: slug.trim(), seatLimit, status, subscriptionStatus }),
        });
        const d = await r.json();
        if (!r.ok) { setPlatformCreateOrgMsg('Error: ' + (d.error || 'Failed')); return; }
        setPlatformCreateOrgMsg('Organization created.');
        setPlatformCreateOrgForm({ name: '', slug: '', seatLimit: 25, status: 'active', subscriptionStatus: 'manual' });
        loadOrgs();
        setTimeout(() => { setPlatformCreateOrgMsg(''); setPlatformView('orgs'); }, 1500);
      } catch (e) { setPlatformCreateOrgMsg('Error: ' + e.message); }
    };

    const loadAuditLog = async () => {
      setPlatformAuditLoading(true);
      try {
        const r = await apiFetch(`${API_BASE}/api/platform/audit?limit=100`);
        if (r.ok) setPlatformAuditLog(await r.json());
      } catch {}
      finally { setPlatformAuditLoading(false); }
    };

    const updateOrgSettings = async () => {
      setPlatformSettingsSaving(true);
      setPlatformSettingsMsg('');
      try {
        const orgId = platformOrgDetail?.org?.id;
        if (!orgId) return;
        const r = await apiFetch(`${API_BASE}/api/platform/org-detail?orgId=${orgId}`, {
          method: 'PATCH',
          body: JSON.stringify(platformSettingsForm),
        });
        const d = await r.json();
        if (!r.ok) { setPlatformSettingsMsg('Error: ' + (d.error || 'Failed')); return; }
        setPlatformOrgDetail(prev => prev ? { ...prev, org: d.org } : prev);
        setPlatformSelectedOrg(d.org);
        setPlatformEditingSettings(false);
        setPlatformSettingsMsg('Settings saved.');
        loadOrgs();
        setTimeout(() => setPlatformSettingsMsg(''), 4000);
      } catch (e) { setPlatformSettingsMsg('Error: ' + e.message); }
      finally { setPlatformSettingsSaving(false); }
    };

    const navStyle = (active) => ({
      display: 'block', width: '100%', textAlign: 'left', padding: '9px 16px',
      background: active ? '#ede9fe' : 'none', color: active ? '#7c3aed' : '#374151',
      border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: active ? 700 : 500,
      fontSize: 14, marginBottom: 2,
    });

    const badgeStyle = (status) => ({
      display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
      background: status === 'active' ? '#dcfce7' : status === 'suspended' ? '#fee2e2' : '#f1f5f9',
      color: status === 'active' ? '#16a34a' : status === 'suspended' ? '#dc2626' : '#64748b',
    });

    const subsStyle = (s) => ({
      display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
      background: s === 'active' ? '#dcfce7' : s === 'trial' ? '#fef9c3' : '#f1f5f9',
      color: s === 'active' ? '#16a34a' : s === 'trial' ? '#a16207' : '#64748b',
    });

    const roleStyle = (role) => ({
      display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
      background: role === 'agency_admin' ? '#fce7f3' : role === 'marketer' ? '#fef9c3' : '#f0f9ff',
      color: role === 'agency_admin' ? '#9d174d' : role === 'marketer' ? '#a16207' : '#0369a1',
    });

    // Load orgs on first view
    if (platformView === 'orgs' && platformOrgs === null && !platformOrgsLoading) {
      loadOrgs();
    }

    return (
      <div style={{ display: 'flex', minHeight: '100vh', fontFamily: 'Inter, system-ui, sans-serif', background: '#f8fafc' }}>
        {/* Sidebar */}
        <div style={{ width: 220, background: '#fff', borderRight: '1.5px solid #e2e8f0', padding: '24px 12px', flexShrink: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: '#7c3aed', marginBottom: 4, paddingLeft: 4 }}>Processly</div>
          <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 20, paddingLeft: 4 }}>PLATFORM CONSOLE</div>

          <button style={navStyle(platformView === 'orgs' || platformView === 'org-detail' || platformView === 'carrier')}
            onClick={() => { setPlatformView('orgs'); if (platformOrgs === null) loadOrgs(); }}>
            Organizations
          </button>
          <button style={navStyle(platformView === 'create-org')}
            onClick={() => setPlatformView('create-org')}>
            + New Organization
          </button>
          <button style={navStyle(platformView === 'audit')}
            onClick={() => { setPlatformView('audit'); if (!platformAuditLog) loadAuditLog(); }}>
            Audit Log
          </button>

          <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 24, paddingTop: 16 }}>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 8, paddingLeft: 4 }}>SIGNED IN AS</div>
            <div style={{ fontSize: 13, color: '#374151', fontWeight: 600, paddingLeft: 4 }}>{currentUser}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', paddingLeft: 4, marginBottom: 12 }}>Platform Owner</div>
            <button onClick={logout} style={{ fontSize: 12, background: 'none', border: '1px solid #e2e8f0', borderRadius: 5, padding: '5px 12px', cursor: 'pointer', color: '#64748b', marginLeft: 4 }}>Sign Out</button>
          </div>
        </div>

        {/* Main content */}
        <div style={{ flex: 1, padding: '32px 40px', overflow: 'auto' }}>

          {/* ── Organizations list ─────────────────────────────────────────── */}
          {platformView === 'orgs' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
                <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f2148' }}>Organizations</h1>
                <button onClick={loadOrgs} disabled={platformOrgsLoading}
                  style={{ fontSize: 12, background: 'none', border: '1px solid #e2e8f0', borderRadius: 5, padding: '4px 10px', cursor: 'pointer', color: '#64748b' }}>
                  {platformOrgsLoading ? 'Loading…' : '↻ Refresh'}
                </button>
              </div>
              {platformOrgsError && <div style={{ color: '#dc2626', marginBottom: 12 }}>{platformOrgsError}</div>}
              {platformOrgsLoading && !platformOrgs && <div style={{ color: '#64748b' }}>Loading organizations…</div>}
              {platformOrgs && (
                <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}>
                      {['Organization', 'Slug', 'Status', 'Subscription', 'Seats', 'Created', ''].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: 12, fontWeight: 700, color: '#64748b', letterSpacing: '0.02em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {platformOrgs.length === 0 && (
                      <tr><td colSpan={7} style={{ padding: '20px 14px', color: '#94a3b8', fontStyle: 'italic' }}>No organizations yet.</td></tr>
                    )}
                    {platformOrgs.map(org => (
                      <tr key={org.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 14px', fontWeight: 700, color: '#0f2148' }}>
                          <button onClick={() => loadOrgDetail(org)}
                            style={{ background: 'none', border: 'none', color: '#7c3aed', fontWeight: 700, cursor: 'pointer', padding: 0, fontSize: 14 }}>
                            {org.name}
                          </button>
                          <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400, marginTop: 1 }}>{org.id}</div>
                        </td>
                        <td style={{ padding: '10px 14px', fontSize: 13, color: '#64748b' }}>{org.slug}</td>
                        <td style={{ padding: '10px 14px' }}><span style={badgeStyle(org.status)}>{org.status}</span></td>
                        <td style={{ padding: '10px 14px' }}><span style={subsStyle(org.subscriptionStatus)}>{org.subscriptionStatus}</span></td>
                        <td style={{ padding: '10px 14px', fontSize: 13, color: '#374151' }}>{org.seatLimit}</td>
                        <td style={{ padding: '10px 14px', fontSize: 12, color: '#94a3b8' }}>{org.createdAt ? new Date(org.createdAt).toLocaleDateString() : '—'}</td>
                        <td style={{ padding: '10px 14px' }}>
                          <button onClick={() => loadOrgDetail(org)}
                            style={{ fontSize: 12, background: '#ede9fe', color: '#7c3aed', border: 'none', borderRadius: 5, padding: '4px 10px', cursor: 'pointer', fontWeight: 600 }}>
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ── Organization Detail ────────────────────────────────────────── */}
          {platformView === 'org-detail' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <button onClick={() => { setPlatformView('orgs'); setPlatformSelectedOrg(null); setPlatformEditingSettings(false); setPlatformSettingsMsg(''); }}
                  style={{ background: 'none', border: 'none', color: '#7c3aed', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  ← Organizations
                </button>
              </div>

              {platformOrgDetailLoading && <div style={{ color: '#64748b' }}>Loading organization…</div>}

              {platformOrgDetail && (() => {
                const { org, users, carriers } = platformOrgDetail;

                const PLATFORM_AGENCY_ROLES = ['producer', 'marketer', 'agency_admin'];
                const PLATFORM_ROLE_LABEL   = { agency_admin: 'Agency Admin', marketer: 'Marketer', producer: 'Producer' };

                const platformUserAction = async (userId, action, newRole) => {
                  setPlatformUserActionLoading(userId);
                  setPlatformUserMsg('');
                  try {
                    const r = await apiFetch(`${API_BASE}/api/agency/users`, {
                      method: 'PATCH',
                      body: JSON.stringify({ userId, action, orgId: org.id, ...(newRole ? { newRole } : {}) }),
                    });
                    const d = await r.json();
                    if (r.ok) {
                      setPlatformUserMsg(action === 'disable' ? 'User disabled.' : action === 'reactivate' ? 'User reactivated.' : 'Role updated.');
                      setPlatformUserEditingId(null);
                      loadOrgDetail(platformSelectedOrg);
                    } else {
                      setPlatformUserMsg('Error: ' + (d.error || 'Action failed'));
                    }
                  } catch (e) { setPlatformUserMsg('Error: ' + e.message); }
                  setPlatformUserActionLoading(null);
                  setTimeout(() => setPlatformUserMsg(''), 5000);
                };

                return (
                  <div>
                    {/* Org header */}
                    <div style={{ background: '#fff', borderRadius: 10, padding: '20px 24px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f2148' }}>{org.name}</h1>
                        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{org.id} · {org.slug}</div>
                        <div style={{ marginTop: 10, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                          <span style={badgeStyle(org.status)}>{org.status}</span>
                          <span style={subsStyle(org.subscriptionStatus)}>{org.subscriptionStatus}</span>
                          <span style={{ fontSize: 11, color: '#64748b' }}>Seats: {org.seatLimit}</span>
                          <span style={{ fontSize: 11, color: '#64748b' }}>Created: {org.createdAt ? new Date(org.createdAt).toLocaleDateString() : '—'}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexDirection: 'column', alignItems: 'flex-end' }}>
                        {org.status === 'active'
                          ? <button onClick={() => { if (window.confirm(`Suspend ${org.name}? Agency users will be blocked from logging in.`)) updateOrgStatus(org.id, 'suspended'); }}
                              style={{ fontSize: 12, background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 5, padding: '6px 12px', cursor: 'pointer', fontWeight: 600 }}>
                              Suspend
                            </button>
                          : <button onClick={() => { if (window.confirm(`Activate ${org.name}?`)) updateOrgStatus(org.id, 'active'); }}
                              style={{ fontSize: 12, background: '#dcfce7', color: '#16a34a', border: 'none', borderRadius: 5, padding: '6px 12px', cursor: 'pointer', fontWeight: 600 }}>
                              Activate
                            </button>
                        }
                        {platformStatusMsg && <div style={{ fontSize: 12, color: '#7c3aed', fontWeight: 600 }}>{platformStatusMsg}</div>}
                      </div>
                    </div>

                    {/* Tabs */}
                    <div style={{ display: 'flex', gap: 0, marginBottom: 20, background: '#fff', borderRadius: 8, padding: 4, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', width: 'fit-content' }}>
                      {[['overview','Overview'],['users',`Users (${users.length})`],['carriers',`Carriers (${carriers.length})`],['invites','Invites']].map(([tab, label]) => (
                        <button key={tab} onClick={() => setPlatformOrgDetailTab(tab)}
                          style={{ padding: '7px 16px', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: 'pointer',
                            background: platformOrgDetailTab === tab ? '#ede9fe' : 'none',
                            color: platformOrgDetailTab === tab ? '#7c3aed' : '#64748b' }}>
                          {label}
                        </button>
                      ))}
                    </div>

                    {/* Overview tab */}
                    {platformOrgDetailTab === 'overview' && (
                      <div style={{ background: '#fff', borderRadius: 10, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                        {!platformEditingSettings ? (
                          <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                              <div style={{ fontWeight: 700, fontSize: 14, color: '#0f2148' }}>Organization Settings</div>
                              <button onClick={() => { setPlatformEditingSettings(true); setPlatformSettingsForm({ name: org.name, subscriptionStatus: org.subscriptionStatus, seatLimit: org.seatLimit }); setPlatformSettingsMsg(''); }}
                                style={{ fontSize: 12, background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 5, padding: '5px 12px', cursor: 'pointer', fontWeight: 600 }}>
                                Edit Settings
                              </button>
                            </div>
                            <table style={{ fontSize: 14 }}>
                              <tbody>
                                {[
                                  ['Name', org.name],
                                  ['ID', org.id],
                                  ['Slug', org.slug],
                                  ['Status', <span style={badgeStyle(org.status)}>{org.status}</span>],
                                  ['Subscription', <span style={subsStyle(org.subscriptionStatus)}>{org.subscriptionStatus}</span>],
                                  ['Seat Limit', org.seatLimit],
                                  ['Users', users.length],
                                  ['Carriers', carriers.length],
                                  ['Created', org.createdAt ? new Date(org.createdAt).toLocaleString() : '—'],
                                  ['Updated', org.updatedAt ? new Date(org.updatedAt).toLocaleString() : '—'],
                                ].map(([label, value]) => (
                                  <tr key={label}>
                                    <td style={{ padding: '7px 0', paddingRight: 24, fontWeight: 600, color: '#64748b', verticalAlign: 'top', width: 140 }}>{label}</td>
                                    <td style={{ padding: '7px 0', color: '#0f2148' }}>{value}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {platformSettingsMsg && <div style={{ marginTop: 12, fontSize: 13, color: platformSettingsMsg.startsWith('Error') ? '#dc2626' : '#16a34a', fontWeight: 600 }}>{platformSettingsMsg}</div>}
                          </>
                        ) : (
                          <>
                            <div style={{ fontWeight: 700, fontSize: 14, color: '#0f2148', marginBottom: 16 }}>Edit Organization Settings</div>
                            {[
                              { label: 'Organization Name', field: 'name', type: 'text' },
                            ].map(({ label, field, type }) => (
                              <div key={field} style={{ marginBottom: 14 }}>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</label>
                                <input type={type} value={platformSettingsForm[field] || ''}
                                  onChange={e => setPlatformSettingsForm(p => ({ ...p, [field]: e.target.value }))}
                                  style={{ width: 340, padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                              </div>
                            ))}
                            <div style={{ marginBottom: 14 }}>
                              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Subscription Status</label>
                              <select value={platformSettingsForm.subscriptionStatus || 'manual'}
                                onChange={e => setPlatformSettingsForm(p => ({ ...p, subscriptionStatus: e.target.value }))}
                                style={{ padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
                                {['manual','trial','active','past_due','canceled'].map(s => <option key={s} value={s}>{s}</option>)}
                              </select>
                            </div>
                            <div style={{ marginBottom: 20 }}>
                              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Seat Limit</label>
                              <input type="number" min={1} max={10000} value={platformSettingsForm.seatLimit ?? org.seatLimit}
                                onChange={e => setPlatformSettingsForm(p => ({ ...p, seatLimit: parseInt(e.target.value, 10) || 1 }))}
                                style={{ width: 120, padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: 13 }} />
                            </div>
                            {platformSettingsMsg && <div style={{ marginBottom: 12, fontSize: 13, color: platformSettingsMsg.startsWith('Error') ? '#dc2626' : '#16a34a', fontWeight: 600 }}>{platformSettingsMsg}</div>}
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button onClick={updateOrgSettings} disabled={platformSettingsSaving}
                                style={{ fontSize: 13, background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontWeight: 700, cursor: 'pointer', opacity: platformSettingsSaving ? 0.7 : 1 }}>
                                {platformSettingsSaving ? 'Saving…' : 'Save'}
                              </button>
                              <button onClick={() => { setPlatformEditingSettings(false); setPlatformSettingsMsg(''); }}
                                style={{ fontSize: 13, background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 6, padding: '7px 14px', fontWeight: 600, cursor: 'pointer' }}>
                                Cancel
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {/* Users tab */}
                    {platformOrgDetailTab === 'users' && (
                      <div style={{ background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                        {platformUserMsg && (
                          <div style={{ padding: '8px 14px', fontSize: 13, fontWeight: 600, borderBottom: '1px solid #f1f5f9', color: platformUserMsg.startsWith('Error') ? '#dc2626' : '#16a34a', background: platformUserMsg.startsWith('Error') ? '#fef2f2' : '#f0fdf4' }}>
                            {platformUserMsg}
                          </div>
                        )}
                        {users.filter(u => PLATFORM_AGENCY_ROLES.includes(u.role)).length === 0
                          ? <div style={{ padding: '20px 24px', color: '#94a3b8', fontStyle: 'italic' }}>No agency users in this organization.</div>
                          : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}>
                                  {['Username','Role','Status','Created','Actions'].map(h => (
                                    <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: 12, fontWeight: 700, color: '#64748b' }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {users.filter(u => PLATFORM_AGENCY_ROLES.includes(u.role)).map(u => (
                                  <tr key={u.id} style={{ borderBottom: '1px solid #f1f5f9', opacity: u.disabled ? 0.65 : 1 }}>
                                    <td style={{ padding: '9px 14px', fontWeight: 600, color: '#0f2148' }}>
                                      {u.username}
                                      {u.name && u.name !== u.username && <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>{u.name}</div>}
                                    </td>
                                    <td style={{ padding: '9px 14px' }}>
                                      {platformUserEditingId === u.id ? (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                          <select value={platformUserRoleNew[u.id] || u.role}
                                            onChange={e => setPlatformUserRoleNew(p => ({ ...p, [u.id]: e.target.value }))}
                                            style={{ padding: '4px 8px', border: '1.5px solid #7c3aed', borderRadius: 5, fontSize: 12, fontFamily: 'inherit' }}>
                                            <option value="producer">Producer</option>
                                            <option value="marketer">Marketer</option>
                                            <option value="agency_admin">Agency Admin</option>
                                          </select>
                                          <button onClick={() => platformUserAction(u.id, 'change_role', platformUserRoleNew[u.id] || u.role)}
                                            disabled={platformUserActionLoading === u.id}
                                            style={{ fontSize: 11, padding: '4px 10px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>
                                            {platformUserActionLoading === u.id ? '…' : 'Save'}
                                          </button>
                                          <button onClick={() => setPlatformUserEditingId(null)}
                                            style={{ fontSize: 11, padding: '4px 8px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 5, cursor: 'pointer', fontFamily: 'inherit' }}>
                                            Cancel
                                          </button>
                                        </span>
                                      ) : (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                          <span style={roleStyle(u.role)}>{PLATFORM_ROLE_LABEL[u.role] || u.role}</span>
                                          <button onClick={() => { setPlatformUserEditingId(u.id); setPlatformUserRoleNew(p => ({ ...p, [u.id]: u.role })); }}
                                            style={{ fontSize: 11, padding: '3px 8px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 5, cursor: 'pointer', fontFamily: 'inherit', color: '#374151' }}>
                                            Change
                                          </button>
                                        </span>
                                      )}
                                    </td>
                                    <td style={{ padding: '9px 14px' }}>
                                      <span style={{ background: u.disabled ? '#fee2e2' : '#dcfce7', color: u.disabled ? '#dc2626' : '#16a34a', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                                        {u.disabled ? 'Disabled' : 'Active'}
                                      </span>
                                    </td>
                                    <td style={{ padding: '9px 14px', fontSize: 12, color: '#94a3b8' }}>
                                      {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}
                                    </td>
                                    <td style={{ padding: '9px 14px' }}>
                                      {u.disabled ? (
                                        <button onClick={() => platformUserAction(u.id, 'reactivate')}
                                          disabled={platformUserActionLoading === u.id}
                                          style={{ fontSize: 11, padding: '4px 10px', background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: 5, cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>
                                          {platformUserActionLoading === u.id ? '…' : 'Reactivate'}
                                        </button>
                                      ) : (
                                        <button onClick={() => { if (window.confirm(`Disable ${u.username}?`)) platformUserAction(u.id, 'disable'); }}
                                          disabled={platformUserActionLoading === u.id}
                                          style={{ fontSize: 11, padding: '4px 10px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 5, cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>
                                          {platformUserActionLoading === u.id ? '…' : 'Disable'}
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )
                        }
                      </div>
                    )}

                    {/* Carriers tab */}
                    {platformOrgDetailTab === 'carriers' && (
                      <div style={{ background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                        {carriers.length === 0
                          ? <div style={{ padding: '20px 24px', color: '#94a3b8', fontStyle: 'italic' }}>No carriers for this organization.</div>
                          : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}>
                                  {['Company','DOT','Producer','Status','X-Date',''].map(h => (
                                    <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: 12, fontWeight: 700, color: '#64748b' }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {carriers.map(c => (
                                  <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '9px 14px', fontWeight: 600, color: '#0f2148' }}>{c.companyName || '—'}</td>
                                    <td style={{ padding: '9px 14px', fontSize: 13, color: '#64748b' }}>{c.dotNumber || '—'}</td>
                                    <td style={{ padding: '9px 14px', fontSize: 13, color: '#64748b' }}>{c.username || c.owner || '—'}</td>
                                    <td style={{ padding: '9px 14px' }}>
                                      {c.status && <span style={badgeStyle(c.status === 'prospect' ? 'active' : c.status)}>{c.status}</span>}
                                    </td>
                                    <td style={{ padding: '9px 14px', fontSize: 12, color: '#94a3b8' }}>{c.currentXDate || '—'}</td>
                                    <td style={{ padding: '9px 14px' }}>
                                      <button
                                        onClick={() => loadPlatformCarrier(c.id)}
                                        disabled={platformCarrierLoading}
                                        style={{ fontSize: 12, background: '#f0fdf4', color: '#16a34a', border: 'none', borderRadius: 5, padding: '4px 10px', cursor: 'pointer', fontWeight: 600 }}>
                                        {platformCarrierLoading ? '…' : 'View'}
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )
                        }
                      </div>
                    )}

                    {/* Invites tab */}
                    {platformOrgDetailTab === 'invites' && (() => {
                      const org = platformOrgDetail.org;
                      const loadPlatformInvites = async () => {
                        setPlatformOrgInvitesLoading(true);
                        try {
                          const r = await apiFetch(`${API_BASE}/api/invite/list?orgId=${org.id}`);
                          if (r.ok) { const d = await r.json(); setPlatformOrgInvites(d.invites || []); }
                          else setPlatformOrgInvites([]);
                        } catch { setPlatformOrgInvites([]); }
                        setPlatformOrgInvitesLoading(false);
                      };
                      const revokePlatformInvite = async (inviteId) => {
                        setPlatformInviteRevoking(inviteId);
                        setPlatformInviteMsg('');
                        try {
                          const r = await apiFetch(`${API_BASE}/api/invite/revoke`, {
                            method: 'POST',
                            body: JSON.stringify({ inviteId }),
                          });
                          const d = await r.json();
                          if (r.ok) { setPlatformInviteMsg('Revoked.'); loadPlatformInvites(); }
                          else setPlatformInviteMsg('Error: ' + (d.error || 'Failed'));
                        } catch (e) { setPlatformInviteMsg('Error: ' + e.message); }
                        setPlatformInviteRevoking(null);
                        setTimeout(() => setPlatformInviteMsg(''), 4000);
                      };

                      if (!platformOrgInvites && !platformOrgInvitesLoading) loadPlatformInvites();

                      const STATUS_COLOR_P = { pending: '#2563eb', claimed: '#16a34a', revoked: '#94a3b8', expired: '#f59e0b' };

                      return (
                        <div style={{ background: '#fff', borderRadius: 10, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                          {/* Header */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                            <div style={{ fontWeight: 700, fontSize: 14, color: '#0f2148', flex: 1 }}>Invites</div>
                            {!platformCreateInviteOpen && !platformCreatedInviteUrl && (
                              <button
                                data-testid="create-invite-btn"
                                onClick={() => { setPlatformCreateInviteOpen(true); setPlatformCreateInviteRole('producer'); setPlatformInviteMsg(''); }}
                                style={{ fontSize: 12, background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 5, padding: '5px 13px', cursor: 'pointer', fontWeight: 700, fontFamily: 'inherit' }}>
                                + Create Invite
                              </button>
                            )}
                            <button onClick={loadPlatformInvites} disabled={platformOrgInvitesLoading}
                              style={{ fontSize: 12, background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 5, padding: '4px 10px', cursor: 'pointer', fontWeight: 600 }}>
                              Refresh
                            </button>
                            {platformInviteMsg && <span style={{ fontSize: 13, color: platformInviteMsg.startsWith('Error') ? '#ef4444' : '#16a34a', fontWeight: 600 }}>{platformInviteMsg}</span>}
                          </div>

                          {/* Created invite URL — shown immediately after creation, hidden on Refresh/Done */}
                          {platformCreatedInviteUrl && (
                            <div style={{ background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: 8, padding: '16px 18px', marginBottom: 16 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: '#15803d', marginBottom: 10 }}>Invite created successfully</div>
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <input
                                  readOnly
                                  data-testid="created-invite-url"
                                  value={platformCreatedInviteUrl}
                                  style={{ flex: 1, fontSize: 12, padding: '7px 10px', border: '1px solid #d1fae5', borderRadius: 6, background: '#fff', fontFamily: 'monospace', color: '#0f2148', outline: 'none' }}
                                  onFocus={e => e.target.select()}
                                />
                                <button
                                  onClick={() => { navigator.clipboard.writeText(platformCreatedInviteUrl); setPlatformCreateInviteUrlCopied(true); setTimeout(() => setPlatformCreateInviteUrlCopied(false), 2000); }}
                                  style={{ fontSize: 12, background: platformCreateInviteUrlCopied ? '#15803d' : '#e2e8f0', color: platformCreateInviteUrlCopied ? '#fff' : '#374151', border: 'none', borderRadius: 6, padding: '7px 12px', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap', transition: 'background 0.2s', fontFamily: 'inherit' }}>
                                  {platformCreateInviteUrlCopied ? 'Copied!' : 'Copy Link'}
                                </button>
                                <button
                                  onClick={() => { setPlatformCreatedInviteUrl(''); setPlatformCreateInviteUrlCopied(false); }}
                                  style={{ fontSize: 12, background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 6, padding: '7px 12px', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>
                                  Done
                                </button>
                              </div>
                              <div style={{ fontSize: 11, color: '#64748b', marginTop: 8 }}>This link is shown once — copy it now.</div>
                            </div>
                          )}

                          {/* Create invite form */}
                          {platformCreateInviteOpen && (
                            <div style={{ background: '#faf5ff', border: '1.5px solid #ddd6fe', borderRadius: 8, padding: '16px 18px', marginBottom: 16 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: '#4c1d95', marginBottom: 12 }}>New Invite — {org.name}</div>
                              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                <div>
                                  <label style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', display: 'block', marginBottom: 4 }}>Role</label>
                                  <select
                                    data-testid="invite-role-select"
                                    value={platformCreateInviteRole}
                                    onChange={e => setPlatformCreateInviteRole(e.target.value)}
                                    style={{ fontSize: 13, padding: '6px 10px', border: '1px solid #ddd6fe', borderRadius: 6, background: '#fff', color: '#1e293b', fontFamily: 'inherit' }}>
                                    <option value="producer">Producer</option>
                                    <option value="marketer">Marketer</option>
                                    <option value="agency_admin">Agency Admin</option>
                                  </select>
                                </div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                  <button
                                    data-testid="submit-create-invite"
                                    disabled={platformCreateInviteLoading}
                                    onClick={async () => {
                                      setPlatformCreateInviteLoading(true);
                                      setPlatformInviteMsg('');
                                      try {
                                        const r = await apiFetch(`${API_BASE}/api/invite/create`, {
                                          method: 'POST',
                                          body: JSON.stringify({ organizationId: org.id, role: platformCreateInviteRole }),
                                        });
                                        const d = await r.json();
                                        if (r.ok && d.url) {
                                          setPlatformCreatedInviteUrl(d.url);
                                          setPlatformCreateInviteOpen(false);
                                          loadPlatformInvites();
                                        } else {
                                          setPlatformInviteMsg('Error: ' + (d.error || 'Failed to create invite'));
                                        }
                                      } catch (e) {
                                        setPlatformInviteMsg('Error: ' + e.message);
                                      }
                                      setPlatformCreateInviteLoading(false);
                                    }}
                                    style={{ fontSize: 13, background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', cursor: platformCreateInviteLoading ? 'wait' : 'pointer', fontWeight: 700, fontFamily: 'inherit' }}>
                                    {platformCreateInviteLoading ? 'Creating…' : 'Create Invite'}
                                  </button>
                                  <button
                                    onClick={() => { setPlatformCreateInviteOpen(false); setPlatformInviteMsg(''); }}
                                    style={{ fontSize: 13, background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 6, padding: '7px 14px', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Invite list */}
                          {platformOrgInvitesLoading && <div style={{ color: '#94a3b8', fontSize: 13 }}>Loading…</div>}
                          {platformOrgInvites && platformOrgInvites.length === 0 && !platformOrgInvitesLoading && (
                            <div style={{ color: '#94a3b8', fontSize: 13, fontStyle: 'italic' }}>No invites for this organization.</div>
                          )}
                          {platformOrgInvites && platformOrgInvites.length > 0 && (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                              <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}>
                                  {['Role', 'Status', 'Created By', 'Created', 'Claimed By', ''].map(h => (
                                    <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, fontWeight: 700, color: '#64748b' }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {platformOrgInvites.map(inv => (
                                  <tr key={inv.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '8px 12px', color: '#1e293b', fontWeight: 600 }}>{inv.role}</td>
                                    <td style={{ padding: '8px 12px' }}>
                                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: (STATUS_COLOR_P[inv.status] || '#94a3b8') + '1a', color: STATUS_COLOR_P[inv.status] || '#94a3b8' }}>
                                        {inv.status}
                                      </span>
                                    </td>
                                    <td style={{ padding: '8px 12px', color: '#64748b' }}>{inv.createdBy || '—'}</td>
                                    <td style={{ padding: '8px 12px', color: '#64748b' }}>{inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : '—'}</td>
                                    <td style={{ padding: '8px 12px', color: '#64748b' }}>{inv.inviteeUsername || '—'}</td>
                                    <td style={{ padding: '8px 12px' }}>
                                      {inv.status === 'pending' && (
                                        <button onClick={() => revokePlatformInvite(inv.id)} disabled={platformInviteRevoking === inv.id}
                                          style={{ fontSize: 11, padding: '3px 10px', border: '1px solid #fca5a5', borderRadius: 5, background: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>
                                          {platformInviteRevoking === inv.id ? '…' : 'Revoke'}
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}
            </div>
          )}

          {/* ── Platform Carrier View ─────────────────────────────────────── */}
          {platformView === 'carrier' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <button onClick={() => { setPlatformView('org-detail'); setPlatformCarrier(null); }}
                  style={{ background: 'none', border: 'none', color: '#7c3aed', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  ← Back to Organization
                </button>
              </div>

              {/* PLATFORM VIEW banner */}
              <div style={{ background: '#fef9c3', border: '1.5px solid #fde68a', borderRadius: 8, padding: '10px 18px', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontWeight: 800, color: '#92400e', fontSize: 13 }}>PLATFORM VIEW</span>
                <span style={{ color: '#a16207', fontSize: 12 }}>Read-only. Changes cannot be made to agency data from the platform console.</span>
              </div>

              {platformCarrierLoading && <div style={{ color: '#64748b' }}>Loading carrier…</div>}
              {platformCarrier && (() => {
                const c = platformCarrier;
                const ls = c.leadSheet || {};
                const rows = [
                  ['Company', c.companyName || '—'],
                  ['DOT #', c.dotNumber || '—'],
                  ['Carrier ID', c.id || '—'],
                  ['Status', c.status || '—'],
                  ['Organization', c.organizationId || '—'],
                  ['Owner', c.username || '—'],
                  ['X-Date', c.currentXDate || '—'],
                  ['Updated', c.updatedAt ? new Date(c.updatedAt).toLocaleString() : '—'],
                ];
                const lsRows = Object.entries(ls).filter(([k]) => !['__v'].includes(k));
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                    <div style={{ background: '#fff', borderRadius: 10, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#0f2148', marginBottom: 14 }}>Account Overview</div>
                      <table style={{ fontSize: 13, width: '100%' }}>
                        <tbody>
                          {rows.map(([label, value]) => (
                            <tr key={label}>
                              <td style={{ padding: '5px 0', paddingRight: 16, fontWeight: 600, color: '#64748b', width: 120 }}>{label}</td>
                              <td style={{ padding: '5px 0', color: '#0f2148' }}>{value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {lsRows.length > 0 && (
                      <div style={{ background: '#fff', borderRadius: 10, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'auto', maxHeight: 400 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#0f2148', marginBottom: 14 }}>Lead Sheet</div>
                        <table style={{ fontSize: 12, width: '100%' }}>
                          <tbody>
                            {lsRows.map(([k, v]) => (
                              <tr key={k}>
                                <td style={{ padding: '3px 0', paddingRight: 12, fontWeight: 600, color: '#64748b', verticalAlign: 'top', width: 160 }}>{k}</td>
                                <td style={{ padding: '3px 0', color: '#374151', wordBreak: 'break-word' }}>
                                  {typeof v === 'object' ? JSON.stringify(v) : String(v || '—')}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {c.accountNotes && c.accountNotes.length > 0 && (
                      <div style={{ background: '#fff', borderRadius: 10, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', gridColumn: '1 / -1' }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#0f2148', marginBottom: 14 }}>Notes ({c.accountNotes.length})</div>
                        {c.accountNotes.map((note, i) => (
                          <div key={i} style={{ padding: '8px 0', borderBottom: i < c.accountNotes.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                            <div style={{ fontSize: 13, color: '#374151' }}>{note.text}</div>
                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                              {note.author || '—'} · {note.createdAt ? new Date(note.createdAt).toLocaleString() : ''}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div style={{ background: '#fff', borderRadius: 10, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', gridColumn: '1 / -1' }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#0f2148', marginBottom: 14 }}>Documents</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                        {DOC_TYPES.map(({ id, label }) => {
                          const raw   = c.documents?.[id];
                          const files = Array.isArray(raw) ? raw : (raw ? [raw] : []);
                          return (
                            <div key={id} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 12px', fontSize: 12, minWidth: 170 }}>
                              <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</div>
                              {files.length === 0 && <div style={{ color: '#94a3b8', fontStyle: 'italic' }}>None</div>}
                              {files.map((doc, i) => (
                                <div key={i} style={{ marginTop: i > 0 ? 6 : 0 }}>
                                  {!doc.id && !doc.url
                                    ? <div style={{ color: '#dc2626', fontStyle: 'italic', fontSize: 11 }}>Document unavailable</div>
                                    : (
                                      <>
                                        <div style={{ color: '#64748b', marginBottom: 2, wordBreak: 'break-all' }}>{doc.name || doc.id || 'file'}</div>
                                        {doc.id && (
                                          <a href={`${API_BASE}/api/files/read?carrierId=${c.id}&fileId=${doc.id}&token=${localStorage.getItem('tsp_auth') || ''}`}
                                            target="_blank" rel="noopener noreferrer"
                                            style={{ color: '#7c3aed', fontSize: 11, display: 'block' }}>
                                            Open ↗
                                          </a>
                                        )}
                                        {!doc.id && doc.url && (
                                          <a href={doc.url} target="_blank" rel="noopener noreferrer"
                                            style={{ color: '#94a3b8', fontSize: 11, display: 'block' }}>
                                            Open (legacy) ↗
                                          </a>
                                        )}
                                      </>
                                    )
                                  }
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* ── Create Organization ────────────────────────────────────────── */}
          {platformView === 'create-org' && (
            <div>
              <h1 style={{ margin: '0 0 24px', fontSize: 22, fontWeight: 800, color: '#0f2148' }}>New Organization</h1>
              <div style={{ background: '#fff', borderRadius: 10, padding: '24px 28px', maxWidth: 520, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                {[
                  ['Organization Name', 'name', 'text', 'Whitinger Insurance'],
                  ['Slug', 'slug', 'text', 'whitinger'],
                ].map(([label, field, type, placeholder]) => (
                  <div key={field} style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</label>
                    <input type={type} placeholder={placeholder} value={platformCreateOrgForm[field]}
                      onChange={e => setPlatformCreateOrgForm(p => ({ ...p, [field]: e.target.value }))}
                      style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                ))}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Seat Limit</label>
                  <input type="number" min={1} max={10000} value={platformCreateOrgForm.seatLimit}
                    onChange={e => setPlatformCreateOrgForm(p => ({ ...p, seatLimit: parseInt(e.target.value, 10) || 25 }))}
                    style={{ width: 120, padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: 13 }} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Status</label>
                  <select value={platformCreateOrgForm.status} onChange={e => setPlatformCreateOrgForm(p => ({ ...p, status: e.target.value }))}
                    style={{ padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Subscription Status</label>
                  <select value={platformCreateOrgForm.subscriptionStatus} onChange={e => setPlatformCreateOrgForm(p => ({ ...p, subscriptionStatus: e.target.value }))}
                    style={{ padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
                    <option value="manual">Manual</option>
                    <option value="trial">Trial</option>
                    <option value="active">Active</option>
                    <option value="past_due">Past Due</option>
                    <option value="canceled">Canceled</option>
                  </select>
                </div>
                {platformCreateOrgMsg && (
                  <div style={{ marginBottom: 12, color: platformCreateOrgMsg.startsWith('Error') ? '#dc2626' : '#16a34a', fontWeight: 600, fontSize: 13 }}>
                    {platformCreateOrgMsg}
                  </div>
                )}
                <button onClick={createOrg}
                  style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, padding: '9px 20px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                  Create Organization
                </button>
              </div>
            </div>
          )}

          {/* ── Audit Log ─────────────────────────────────────────────────── */}
          {platformView === 'audit' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
                <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f2148' }}>Audit Log</h1>
                <button onClick={loadAuditLog} disabled={platformAuditLoading}
                  style={{ fontSize: 12, background: 'none', border: '1px solid #e2e8f0', borderRadius: 5, padding: '4px 10px', cursor: 'pointer', color: '#64748b' }}>
                  {platformAuditLoading ? 'Loading…' : '↻ Refresh'}
                </button>
              </div>
              {platformAuditLoading && !platformAuditLog && <div style={{ color: '#64748b' }}>Loading audit log…</div>}
              {platformAuditLog && (
                <div style={{ background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  {platformAuditLog.length === 0
                    ? <div style={{ padding: '20px 24px', color: '#94a3b8', fontStyle: 'italic' }}>No audit events yet.</div>
                    : (
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}>
                            {['Timestamp','Action','Actor','Organization','Details'].map(h => (
                              <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: 12, fontWeight: 700, color: '#64748b' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {platformAuditLog.map(e => (
                            <tr key={e.eventId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '9px 14px', fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                                {e.timestamp ? new Date(e.timestamp).toLocaleString() : '—'}
                              </td>
                              <td style={{ padding: '9px 14px', fontWeight: 600, fontSize: 12, color: '#374151' }}>{e.action}</td>
                              <td style={{ padding: '9px 14px', fontSize: 12, color: '#64748b' }}>{e.actorUserId || '—'}</td>
                              <td style={{ padding: '9px 14px', fontSize: 12, color: '#7c3aed' }}>{e.targetOrganizationId || '—'}</td>
                              <td style={{ padding: '9px 14px', fontSize: 11, color: '#94a3b8' }}>
                                {e.metadata && Object.keys(e.metadata).length > 0
                                  ? JSON.stringify(e.metadata, null, 1).slice(0, 120)
                                  : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )
                  }
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    );
  }

  if (!authChecked) return null;
  if (!authed) return <LoginScreen onLogin={(token, uname, role, trial, alreadyAccepted) => {
    setAuthed(true);
    setLegalAccepted(!!alreadyAccepted);
    if (uname)  { setCurrentUser(uname); setCurrentRole(role || ''); }
    if (trial)  setTrialEndsAt(trial);
    // fetchCarriersForUser runs on mount before login sets localStorage,
    // so call it again now that the auth token is available.
    if (alreadyAccepted) fetchCarriersForUser(uname);
  }} />;

  if (authed && !legalAccepted) return <LegalAcceptanceScreen
    username={currentUser}
    onAccepted={() => {
      setLegalAccepted(true);
      fetchCarriersForUser(currentUser);
    }}
    onLogout={logout}
  />;

  // ── Platform Console (platform_owner only) ──────────────────────────────────
  if (currentRole === 'platform_owner') {
    return renderPlatformConsole();
  }

  return (
    <div className="app-wrapper">
      {/* Conflict Modal */}
      <ConflictModal conflict={conflictQueue[0] || null} onResolve={resolveConflict} />

      {/* Header */}
      <header className="app-header">
        <div className="header-left">
          <div className="header-logo">
            Trucking Submission Pro
            <span>Commercial Trucking Insurance</span>
          </div>
          <img src="/processly-logo.svg" alt="Processly" style={{ width: 120, height: 'auto', display: 'block', flexShrink: 0 }} />
        </div>
        <div className="progress-ring-wrap">
          <div className="progress-ring-label">
            <strong>{headerReadiness.completeCount}/{headerReadiness.totalCount}</strong>
            Readiness
          </div>
          <ProgressRing pct={headerReadiness.score} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {currentUser && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>{currentUser}</div>
              {trialEndsAt && (() => {
                const daysLeft = Math.ceil((new Date(trialEndsAt) - Date.now()) / 86400000);
                const color    = daysLeft <= 5 ? '#ef4444' : daysLeft <= 10 ? '#f59e0b' : '#64748b';
                return daysLeft > 0
                  ? <div style={{ fontSize: 10, color }}>{daysLeft}d trial left</div>
                  : <div style={{ fontSize: 10, color: '#ef4444' }}>Trial expired</div>;
              })()}
            </div>
          )}
          <button className="logout-btn" onClick={logout} title="Sign out">Logout</button>
        </div>
      </header>

      {/* Carrier Info Bar */}
      <div className="carrier-bar">
        <label className="carrier-bar-label">Carrier</label>
        <input
          className="carrier-input"
          placeholder="Company Name"
          value={companyName}
          onChange={e => setCompanyName(e.target.value)}
        />
        <span className="carrier-bar-sep">DOT #</span>
        <input
          className="carrier-input carrier-input-dot"
          placeholder="0000000"
          value={dotNumber}
          onChange={e => setDotNumber(e.target.value)}
        />
        {carrierId && (
          <select
            className="carrier-status-select"
            value={carrierStatus}
            style={{
              background: PIPELINE_STATUSES[carrierStatus]?.bg || '#EEF2FF',
              color: PIPELINE_STATUSES[carrierStatus]?.color || '#6366F1',
              borderColor: PIPELINE_STATUSES[carrierStatus]?.border || '#A5B4FC',
            }}
            onChange={e => {
              const newStatus = e.target.value;
              if (newStatus === 'lost') { setShowLostModal(true); }
              else if (newStatus === 'bound') { setShowBoundModal(true); }
              else { patchCarrierStatus(carrierId, newStatus); }
            }}
          >
            {Object.entries(PIPELINE_STATUSES).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        )}
        {(carrierStatus === 'bound' || carrierStatus === 'lost') && (pipelineData?.market || pipelineData?.premium || pipelineData?.reason) && (
          <div style={{ fontSize: 11, background: carrierStatus === 'lost' ? '#fef2f2' : '#f0fdf4', border: '1px solid', borderColor: carrierStatus === 'lost' ? '#fca5a5' : '#bbf7d0', borderRadius: 6, padding: '6px 10px', marginTop: 6, lineHeight: 1.5 }}>
            <span style={{ fontWeight: 600, color: carrierStatus === 'lost' ? '#991b1b' : '#166534' }}>
              {carrierStatus === 'lost' ? 'Lost' : 'Bound'}
            </span>
            {pipelineData.market && <> · {pipelineData.market}</>}
            {pipelineData.premium && <> · {pipelineData.premium}</>}
            {pipelineData.reason && <> · {pipelineData.reason}</>}
            {carrierStatus === 'bound' && pipelineData.effectiveDate && <> · Eff {new Date(pipelineData.effectiveDate).toLocaleDateString()}</>}
            {carrierStatus === 'bound' && pipelineData.expDate && <> – Exp {new Date(pipelineData.expDate).toLocaleDateString()}</>}
            {pipelineData.notes && (
              <div style={{ color: '#64748b', fontStyle: 'italic', marginTop: 2 }}>{pipelineData.notes}</div>
            )}
          </div>
        )}
        <div className="carrier-bar-actions">
          <button className="carrier-btn" onClick={newCarrier}>＋ New Carrier</button>
          <div className="carriers-wrap">
            <button className="carrier-btn" onClick={() => { setShowCarrierList(p => !p); setSavedSearch(''); }}>
              📂 Saved {savedCarriers.length > 0 && <span className="badge" style={{ marginLeft: 4 }}>{savedCarriers.length}</span>}
            </button>
            {showCarrierList && (() => {
              const effectiveStatus = (c) => {
                const s = c.status || 'prospect';
                if (s === 'bound' && c.pipelineData?.expDate) {
                  const days = Math.ceil((new Date(c.pipelineData.expDate) - new Date()) / (1000 * 60 * 60 * 24));
                  if (days <= 90) return 'renewal';
                }
                return s;
              };
              const daysUntilExp = (c) => {
                if (!c.pipelineData?.expDate) return null;
                return Math.ceil((new Date(c.pipelineData.expDate) - new Date()) / (1000 * 60 * 60 * 24));
              };
              const counts = savedCarriers.reduce((acc, c) => {
                const s = effectiveStatus(c);
                acc[s] = (acc[s] || 0) + 1;
                return acc;
              }, {});
              const activeCount = (counts.prospect || 0) + (counts.in_progress || 0);
              const totalBound = (counts.bound || 0) + (counts.renewal || 0);
              const totalLost  = counts.lost || 0;
              const winRate    = totalBound + totalLost > 0 ? Math.round(totalBound / (totalBound + totalLost) * 100) : null;
              const totalBoundPremium = savedCarriers
                .filter(c => { const s = effectiveStatus(c); return s === 'bound' || s === 'renewal'; })
                .reduce((sum, c) => {
                  const raw = c.pipelineData?.premium || '';
                  const num = parseFloat(raw.replace(/[^0-9.]/g, ''));
                  return sum + (isNaN(num) ? 0 : num);
                }, 0);
              const searchQuery = savedSearch.trim().toLowerCase();
              const filtered = savedCarriers.filter(c => {
                const s = effectiveStatus(c);
                const matchesTab = (
                  pipelineTab === 'active' ? (s === 'prospect' || s === 'in_progress') :
                  pipelineTab === 'bound'  ? (s === 'bound' || s === 'renewal') :
                  pipelineTab === 'lost'   ? s === 'lost' :
                  pipelineTab === 'analytics' ? false :
                  true
                );
                if (!matchesTab) return false;
                if (searchQuery) {
                  const nameMatch  = (c.companyName || '').toLowerCase().includes(searchQuery);
                  const dotMatch   = (c.dotNumber   || '').toLowerCase().includes(searchQuery);
                  const ownerMatch = (c.username || c.owner || '').toLowerCase().includes(searchQuery);
                  return nameMatch || dotMatch || ownerMatch;
                }
                return true;
              });
              return (
                <div className="carriers-panel">
                  {/* Admin: My Accounts / All Accounts view switcher */}
                  {(currentRole === 'admin' || currentRole === 'platform_owner') && (
                    <div className="saved-view-tabs">
                      <button
                        className={`saved-view-tab${savedViewMode === 'mine' ? ' active' : ''}`}
                        onClick={() => setSavedViewMode('mine')}
                      >My Accounts</button>
                      <button
                        className={`saved-view-tab${savedViewMode === 'all' ? ' active' : ''}`}
                        onClick={() => {
                          setSavedViewMode('all');
                          if (!allCarriersLoading && allCarriers.length === 0) fetchAllCarriers();
                        }}
                      >All Accounts</button>
                    </div>
                  )}
                  {savedViewMode === 'all' && (currentRole === 'admin' || currentRole === 'platform_owner') ? (() => {
                    const displayName = (uname) => userDisplayNames[(uname || '').toLowerCase()] || uname || '—';
                    const allProducers = [...new Set(allCarriers.map(c => c.username || c.owner).filter(Boolean))].sort();
                    const allFiltered  = allCarriers.filter(c =>
                      !allProducerFilter || (c.username || c.owner) === allProducerFilter
                    );
                    return (
                      <div className="all-accounts-view">
                        <div className="all-accounts-toolbar">
                          {allProducers.length > 1 && (
                            <select
                              className="all-accounts-filter"
                              value={allProducerFilter}
                              onChange={e => setAllProducerFilter(e.target.value)}
                            >
                              <option value="">All Producers ({allCarriers.length})</option>
                              {allProducers.map(u => {
                                const cnt = allCarriers.filter(c => (c.username || c.owner) === u).length;
                                return <option key={u} value={u}>{displayName(u)} ({cnt})</option>;
                              })}
                            </select>
                          )}
                          <button
                            className="all-accounts-refresh"
                            onClick={fetchAllCarriers}
                            disabled={allCarriersLoading}
                            title="Refresh all accounts"
                          >↻</button>
                        </div>
                        {allCarriersLoading && <div className="carriers-empty">Loading…</div>}
                        {allCarriersError  && <div className="carriers-empty" style={{ color: '#ef4444' }}>{allCarriersError}</div>}
                        {!allCarriersLoading && !allCarriersError && allFiltered.length === 0 && (
                          <div className="carriers-empty">No accounts found.</div>
                        )}
                        {!allCarriersLoading && allFiltered.map(c => {
                          const es = effectiveStatus(c);
                          const statusInfo = PIPELINE_STATUSES[es] || PIPELINE_STATUSES.prospect;
                          const days = daysUntilExp(c);
                          const isShared = !!c.isShared;
                          const producer = c.username || c.owner;
                          return (
                            <div key={c.id} className="carrier-row" onClick={() => selectCarrier(c.id)} style={{ cursor: 'pointer' }}>
                              <div className="carrier-row-info">
                                <div className="carrier-row-name">
                                  {c.companyName || 'Unnamed Carrier'}
                                  <span className="carrier-status-badge" style={{ background: statusInfo.bg, color: statusInfo.color, borderColor: statusInfo.border }}>
                                    {statusInfo.label}
                                  </span>
                                  {c.pipelineData?.readiness && (() => {
                                    const rs     = c.pipelineData.readiness;
                                    const rbg    = rs.status === 'READY' ? '#f0fdf4' : rs.status === 'CONDITIONALLY_READY' ? '#fffbeb' : '#fef2f2';
                                    const rcol   = rs.status === 'READY' ? '#16a34a' : rs.status === 'CONDITIONALLY_READY' ? '#d97706' : '#dc2626';
                                    const rbrd   = rs.status === 'READY' ? '#bbf7d0' : rs.status === 'CONDITIONALLY_READY' ? '#fde68a' : '#fecaca';
                                    const rlabel = rs.status === 'READY' ? 'READY' : rs.status === 'CONDITIONALLY_READY' ? 'COND' : 'NOT READY';
                                    return (
                                      <span style={{ display: 'inline-block', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 8, background: rbg, color: rcol, border: `1px solid ${rbrd}`, marginLeft: 4, verticalAlign: 'middle' }}>
                                        {rlabel} {rs.score}%
                                      </span>
                                    );
                                  })()}
                                  {es === 'renewal' && days !== null && (
                                    <span className="carrier-renewal-badge">{days}d</span>
                                  )}
                                  {isShared && (
                                    <span className="carrier-shared-badge" title={`Owner: ${displayName(c.owner)}`}>
                                      👥 Shared
                                    </span>
                                  )}
                                </div>
                                <div className="carrier-row-meta">
                                  {c.dotNumber && c.currentXDate
                                    ? `DOT: ${c.dotNumber} · ${c.currentXDate}`
                                    : c.dotNumber && !c.currentXDate
                                    ? `DOT: ${c.dotNumber} · No X-Date`
                                    : !c.dotNumber && c.currentXDate
                                    ? `X-Date: ${c.currentXDate}`
                                    : '—'}
                                  {c.pipelineData?.market   && <> · {c.pipelineData.market}</>}
                                  {c.pipelineData?.premium  && <> · {c.pipelineData.premium}</>}
                                </div>
                                {producer && (
                                  <div className="carrier-row-producer">Producer: {displayName(producer)}</div>
                                )}
                                {es === 'lost' && c.pipelineData?.reason && (
                                  <div style={{ fontSize: 11, color: '#ef4444', marginTop: 2 }}>
                                    {c.pipelineData.reason}
                                    {c.pipelineData.notes && <span style={{ color: '#94a3b8', marginLeft: 6, fontStyle: 'italic' }}>
                                      {c.pipelineData.notes.slice(0, 60)}{c.pipelineData.notes.length > 60 ? '…' : ''}
                                    </span>}
                                  </div>
                                )}
                                {(es === 'bound' || es === 'renewal') && (c.pipelineData?.effectiveDate || c.pipelineData?.expDate) && (
                                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                                    {c.pipelineData.effectiveDate && `Eff ${new Date(c.pipelineData.effectiveDate).toLocaleDateString()}`}
                                    {c.pipelineData.effectiveDate && c.pipelineData.expDate && ' – '}
                                    {c.pipelineData.expDate && `Exp ${new Date(c.pipelineData.expDate).toLocaleDateString()}`}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })() : <>
                  {/* Account Search */}
                  <div style={{ padding: '8px 10px 0' }}>
                    <input
                      type="text"
                      value={savedSearch}
                      onChange={e => setSavedSearch(e.target.value)}
                      placeholder="Search accounts…"
                      style={{
                        width: '100%', boxSizing: 'border-box', padding: '6px 10px',
                        fontSize: 13, border: '1px solid #e2e8f0', borderRadius: 6,
                        outline: 'none', background: '#f8fafc', color: '#1e293b',
                        fontFamily: 'inherit',
                      }}
                    />
                  </div>
                  {/* Pipeline Stats */}
                  <div className="pipeline-stats">
                    <span className="pipeline-stat pipeline-stat-active">{activeCount} Active</span>
                    <span className="pipeline-stat pipeline-stat-bound">{(counts.bound || 0) + (counts.renewal || 0)} Bound</span>
                    <span className="pipeline-stat pipeline-stat-renewal">{counts.renewal || 0} Renewal</span>
                    <span className="pipeline-stat pipeline-stat-lost">{counts.lost || 0} Lost</span>
                    {winRate !== null && <span className="pipeline-stat" style={{ color: '#166534' }}>{winRate}% Win</span>}
                    {totalBoundPremium > 0 && <span className="pipeline-stat" style={{ color: '#166534' }}>${(totalBoundPremium / 1000).toFixed(0)}k Bound</span>}
                  </div>
                  {/* Pipeline Tabs */}
                  <div className="pipeline-tabs">
                    {[['active', 'Active'], ['bound', 'Bound'], ['lost', 'Lost'], ['analytics', 'Analytics'], ['all', 'All']].map(([key, label]) => (
                      <button key={key} className={`pipeline-tab${pipelineTab === key ? ' active' : ''}`} onClick={() => setPipelineTab(key)}>
                        {label}
                      </button>
                    ))}
                  </div>
                  {/* Carrier List */}
                  {pipelineTab === 'analytics' ? (() => {
                    const now = new Date();
                    const lostArr = savedCarriers.filter(c => effectiveStatus(c) === 'lost');
                    const reasonCounts = lostArr.reduce((acc, c) => {
                      if (c.pipelineData?.reason) acc[c.pipelineData.reason] = (acc[c.pipelineData.reason] || 0) + 1;
                      return acc;
                    }, {});
                    const reasonEntries = Object.entries(reasonCounts).sort((a, b) => b[1] - a[1]);
                    const boundArr = savedCarriers.filter(c => { const s = effectiveStatus(c); return s === 'bound' || s === 'renewal'; });
                    const marketGroups = boundArr.reduce((acc, c) => {
                      if (c.pipelineData?.market) {
                        const m = c.pipelineData.market;
                        if (!acc[m]) acc[m] = { count: 0, premium: 0 };
                        acc[m].count++;
                        const num = parseFloat((c.pipelineData.premium || '').replace(/[^0-9.]/g, ''));
                        if (!isNaN(num)) acc[m].premium += num;
                      }
                      return acc;
                    }, {});
                    const marketEntries = Object.entries(marketGroups).sort((a, b) => b[1].count - a[1].count).slice(0, 5);
                    const upcomingRenewals = savedCarriers
                      .filter(c => { const s = effectiveStatus(c); return (s === 'bound' || s === 'renewal') && c.pipelineData?.expDate; })
                      .map(c => ({ name: c.companyName || 'Unnamed', expDate: c.pipelineData.expDate, days: Math.ceil((new Date(c.pipelineData.expDate) - now) / (1000 * 60 * 60 * 24)), id: c.id }))
                      .filter(r => r.days > 0 && r.days <= 120)
                      .sort((a, b) => a.days - b.days);
                    const maxReason = reasonEntries[0]?.[1] || 1;
                    const hasData = reasonEntries.length > 0 || marketEntries.length > 0 || upcomingRenewals.length > 0;
                    return (
                      <div style={{ padding: '12px 10px', fontSize: 12 }}>
                        {!hasData && <div className="carriers-empty">No pipeline data yet. Mark carriers as Bound or Lost to see analytics.</div>}
                        {reasonEntries.length > 0 && (
                          <div style={{ marginBottom: 16 }}>
                            <div style={{ fontWeight: 700, color: '#374151', marginBottom: 8, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Loss Reasons</div>
                            {reasonEntries.map(([reason, count]) => (
                              <div key={reason} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                <span style={{ minWidth: 130, color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 11 }}>{reason}</span>
                                <span style={{ height: 6, background: '#fecaca', borderRadius: 3, width: Math.round((count / maxReason) * 70) }}></span>
                                <span style={{ color: '#ef4444', fontWeight: 600, minWidth: 16, textAlign: 'right' }}>{count}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {marketEntries.length > 0 && (
                          <div style={{ marginBottom: 16 }}>
                            <div style={{ fontWeight: 700, color: '#374151', marginBottom: 8, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Top Bound Markets</div>
                            {marketEntries.map(([market, data]) => (
                              <div key={market} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                <span style={{ color: '#374151', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{market}</span>
                                <span style={{ color: '#6b7280', fontSize: 11 }}>
                                  {data.count} {data.count === 1 ? 'acct' : 'accts'}{data.premium > 0 ? ` · $${(data.premium / 1000).toFixed(0)}k` : ''}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        {upcomingRenewals.length > 0 && (
                          <div>
                            <div style={{ fontWeight: 700, color: '#374151', marginBottom: 8, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Renewals — Next 120 Days</div>
                            {upcomingRenewals.map(r => (
                              <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, cursor: 'pointer' }} onClick={() => selectCarrier(r.id)}>
                                <span style={{ color: r.days <= 30 ? '#dc2626' : '#374151', fontWeight: r.days <= 30 ? 600 : 400, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                                <span style={{ fontSize: 11, color: r.days <= 30 ? '#dc2626' : '#6b7280' }}>
                                  {new Date(r.expDate).toLocaleDateString()} · {r.days}d
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })() : filtered.length === 0
                    ? <div className="carriers-empty">{searchQuery ? 'No matching accounts.' : 'No carriers in this stage.'}</div>
                    : filtered.map(c => {
                        const hasNewPortal = portals.some(p => p.carrierId === c.id && p.status === 'submitted' && !p.notificationSeen);
                        const isShared = !!c.isShared;
                        // For org-wide roles, ownership is determined by actual owner field,
                        // not by isShared — they see accounts they don't own without isShared being set.
                        const isOwner = (currentRole === 'marketer' || currentRole === 'agency_admin')
                          ? (c.username === currentUser || c.owner === currentUser)
                          : (!isShared || c.owner === currentUser);
                        const es = effectiveStatus(c);
                        const statusInfo = PIPELINE_STATUSES[es] || PIPELINE_STATUSES.prospect;
                        const days = daysUntilExp(c);
                        return (
                          <div key={c.id} className="carrier-row">
                            <div className="carrier-row-info" onClick={() => selectCarrier(c.id)}>
                              <div className="carrier-row-name">
                                {c.companyName || 'Unnamed Carrier'}
                                <span className="carrier-status-badge" style={{ background: statusInfo.bg, color: statusInfo.color, borderColor: statusInfo.border }}>
                                  {statusInfo.label}
                                </span>
                                {c.pipelineData?.readiness && (() => {
                                  const rs = c.pipelineData.readiness;
                                  const rbg    = rs.status === 'READY' ? '#f0fdf4' : rs.status === 'CONDITIONALLY_READY' ? '#fffbeb' : '#fef2f2';
                                  const rcol   = rs.status === 'READY' ? '#16a34a' : rs.status === 'CONDITIONALLY_READY' ? '#d97706' : '#dc2626';
                                  const rbrd   = rs.status === 'READY' ? '#bbf7d0' : rs.status === 'CONDITIONALLY_READY' ? '#fde68a' : '#fecaca';
                                  const rlabel = rs.status === 'READY' ? 'READY' : rs.status === 'CONDITIONALLY_READY' ? 'COND' : 'NOT READY';
                                  return (
                                    <span style={{ display: 'inline-block', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 8, background: rbg, color: rcol, border: `1px solid ${rbrd}`, marginLeft: 4, verticalAlign: 'middle' }}>
                                      {rlabel} {rs.score}%
                                    </span>
                                  );
                                })()}
                                {es === 'renewal' && days !== null && (
                                  <span className="carrier-renewal-badge">{days}d</span>
                                )}
                                {hasNewPortal && <span className="portal-carrier-badge">● New Submission</span>}
                                {isShared && (
                                  <span className="carrier-shared-badge" title={isOwner ? `Shared with ${(c.sharedWith || []).join(', ')}` : `Shared by ${c.owner}`}>
                                    👥 {isOwner ? `Shared` : `From ${c.owner}`}
                                  </span>
                                )}
                              </div>
                              <div className="carrier-row-meta">
                                {c.dotNumber && c.currentXDate
                                  ? `DOT: ${c.dotNumber} · ${c.currentXDate}`
                                  : c.dotNumber && !c.currentXDate
                                  ? `DOT: ${c.dotNumber} · No X-Date`
                                  : !c.dotNumber && c.currentXDate
                                  ? `X-Date: ${c.currentXDate}`
                                  : '—'}
                                {c.pipelineData?.market && <> · {c.pipelineData.market}</>}
                                {c.pipelineData?.premium && <> · {c.pipelineData.premium}</>}
                              </div>
                              {(currentRole === 'marketer' || currentRole === 'agency_admin') && (c.username || c.owner) && (
                                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                                  Producer: {c.username || c.owner}
                                </div>
                              )}
                              {es === 'lost' && c.pipelineData?.reason && (
                                <div style={{ fontSize: 11, color: '#ef4444', marginTop: 2 }}>
                                  {c.pipelineData.reason}
                                  {c.pipelineData.notes && <span style={{ color: '#94a3b8', marginLeft: 6, fontStyle: 'italic' }}>
                                    {c.pipelineData.notes.slice(0, 60)}{c.pipelineData.notes.length > 60 ? '…' : ''}
                                  </span>}
                                </div>
                              )}
                              {(es === 'bound' || es === 'renewal') && (c.pipelineData?.effectiveDate || c.pipelineData?.expDate) && (
                                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                                  {c.pipelineData.effectiveDate && `Eff ${new Date(c.pipelineData.effectiveDate).toLocaleDateString()}`}
                                  {c.pipelineData.effectiveDate && c.pipelineData.expDate && ' – '}
                                  {c.pipelineData.expDate && `Exp ${new Date(c.pipelineData.expDate).toLocaleDateString()}`}
                                </div>
                              )}
                            </div>
                            {isOwner && (
                              <button className="carrier-row-del" onClick={e => { e.stopPropagation(); deleteCarrier(c.id); }}>✕</button>
                            )}
                          </div>
                        );
                      })
                  }
                  </>}
                </div>
              );
            })()}
          </div>
          <button
            className={`carrier-btn carrier-btn-save${saveState === 'conflict' ? ' carrier-btn-conflict' : ''}`}
            onClick={() => saveCarrier(false)}
            disabled={saveState === 'saving'}
          >
            {saveState === 'saving' ? 'Saving…' : '💾 Save'}
          </button>
          {carrierId && (
            <div className="share-wrap">
              {carrierShared && carrierOwner !== currentUser ? (
                <div className="shared-collaborators">
                  <span className="shared-badge-label">Owner: {carrierOwner}</span>
                  {carrierSharedWith.length > 0 && (
                    <span className="shared-badge-label" style={{ marginLeft: 8 }}>
                      Collaborators: {carrierSharedWith.join(', ')}
                    </span>
                  )}
                </div>
              ) : carrierShared ? (
                <>
                  <div className="shared-collaborators">
                    <span className="shared-badge-label">Owner: {currentUser}</span>
                    {carrierSharedWith.length > 0 && (
                      <span className="shared-badge-label" style={{ marginLeft: 8 }}>Collaborators:</span>
                    )}
                    {carrierSharedWith.map(u => (
                      <span key={u} className="collaborator-chip">
                        {u}
                        <button
                          className="collaborator-remove"
                          title={`Remove ${u}`}
                          onClick={() => handleRemoveCollaborator(u)}
                        >×</button>
                      </span>
                    ))}
                    <div className="share-dropdown-wrap share-add-wrap">
                      <button
                        className="carrier-btn carrier-btn-share carrier-btn-share-add"
                        onClick={() => { fetchAvailableUsers(); setShareSearch(''); setShowShareDropdown(p => !p); }}
                        title="Share with another user"
                      >+ Add</button>
                      {showShareDropdown && (() => {
                        const roleLabel = r => r === 'agency_admin' ? 'Agency Admin' : r === 'marketer' ? 'Marketer' : 'Producer';
                        const q = shareSearch.trim().toLowerCase();
                        const eligible = availableUsers.filter(u =>
                          !carrierSharedWith.includes(u.username) &&
                          (!q || u.username.toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q))
                        );
                        return (
                          <div className="share-dropdown">
                            <input
                              type="text"
                              className="share-dropdown-search"
                              placeholder="Search people…"
                              value={shareSearch}
                              onChange={e => setShareSearch(e.target.value)}
                              autoFocus
                            />
                            {eligible.length === 0
                              ? <div className="share-dropdown-empty">{q ? 'No matches' : 'No other users'}</div>
                              : eligible.map(u => (
                                <button key={u.username} className="share-dropdown-item" onClick={() => { setShareSearch(''); handleShare(u.username); }}>
                                  <span className="share-dropdown-name">{u.name && u.name !== u.username ? u.name : u.username}</span>
                                  <span className="share-dropdown-role">{roleLabel(u.role)}</span>
                                </button>
                              ))
                            }
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  <button className="carrier-btn carrier-btn-unshare" onClick={handleUnshare}>Unshare All</button>
                </>
              ) : (
                <div className="share-dropdown-wrap">
                  <button
                    className="carrier-btn carrier-btn-share"
                    onClick={() => { fetchAvailableUsers(); setShareSearch(''); setShowShareDropdown(p => !p); }}
                  >
                    👥 Share
                  </button>
                  {showShareDropdown && (() => {
                    const roleLabel = r => r === 'agency_admin' ? 'Agency Admin' : r === 'marketer' ? 'Marketer' : 'Producer';
                    const q = shareSearch.trim().toLowerCase();
                    const eligible = availableUsers.filter(u =>
                      !q || u.username.toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q)
                    );
                    return (
                      <div className="share-dropdown">
                        <input
                          type="text"
                          className="share-dropdown-search"
                          placeholder="Search people…"
                          value={shareSearch}
                          onChange={e => setShareSearch(e.target.value)}
                          autoFocus
                        />
                        {eligible.length === 0
                          ? <div className="share-dropdown-empty">{q ? 'No matches' : 'No other users'}</div>
                          : eligible.map(u => (
                            <button key={u.username} className="share-dropdown-item" onClick={() => { setShareSearch(''); handleShare(u.username); }}>
                              <span className="share-dropdown-name">{u.name && u.name !== u.username ? u.name : u.username}</span>
                              <span className="share-dropdown-role">{roleLabel(u.role)}</span>
                            </button>
                          ))
                        }
                      </div>
                    );
                  })()}
                </div>
              )}
              {shareMsg && <span className="saved-msg">{shareMsg}</span>}
            </div>
          )}
          {!carrierId && !!(companyName.trim() || dotNumber.trim()) && saveState === 'idle' && (
            <span className="saved-msg" style={{ color: '#94a3b8' }}>Not yet saved</span>
          )}
          {savedMsg && <span className="saved-msg">{savedMsg}</span>}
        </div>
      </div>

      {/* Tab Nav */}
      <nav className="tab-nav">
        {TABS.map(t => (
          <button key={t} className={`tab-btn${activeTab === t ? ' active' : ''}`} onClick={() => setActiveTab(t)}>
            {t}
            {t === 'Documents'  && uploadedCount > 0 && <span className="badge">{uploadedCount}</span>}
            {t === 'Notes'      && (() => {
              const total    = accountNotes.length;
              const openTodo = accountNotes.filter(n => n.isTodo && !n.completed).length;
              if (!total) return null;
              return <span className={`badge${openTodo > 0 ? ' badge-warn' : ''}`}>{openTodo > 0 ? `${openTodo} ✓` : total}</span>;
            })()}
            {t === 'Lead Sheet' && (() => {
              const filled = LEAD_SHEET_REQUIRED_FIELDS.filter(f => leadSheet[f]).length;
              if (!filled) return null;
              const pct = Math.round(filled / LEAD_SHEET_REQUIRED_FIELDS.length * 100);
              return <span className={`badge${pct < 100 ? ' badge-warn' : ''}`}>{pct}%</span>;
            })()}
            {t === 'Loss Runs' && (() => {
              const lrFiles = documents['lossRuns'] || [];
              if (!lossRunVerification && !lrFiles.length) return null;
              if (!lossRunVerification) return <span className="badge">{lrFiles.length}</span>;
              const lines = ['autoLiability','cargo','physicalDamage','generalLiability'];
              const totalCells = lines.length * (lossRunVerification.yearSlots || POLICY_YEARS).length;
              const presentCells = lines.reduce((s, k) =>
                s + (lossRunVerification[k] || []).filter(e => e.present).length, 0);
              const pct = Math.round((presentCells / totalCells) * 100);
              const hasCriticalMissing = lines.some(k =>
                (lossRunVerification[k] || []).every(e => !e.present));
              return <span className={`badge${hasCriticalMissing ? ' badge-red' : pct < 100 ? ' badge-warn' : ''}`}>{pct}%</span>;
            })()}
            {t === 'Equipment' && <span className="badge">{tractors.length + trailers.length}</span>}
            {t === 'Drivers'   && <span className="badge">{drivers.length}</span>}
            {t === 'Markets'   && followUpCount > 0 && <span className="badge badge-red">{followUpCount}</span>}
          </button>
        ))}
      </nav>

      {/* Content */}
      <main className="tab-content">
        {tabContent[activeTab]}
      </main>


      {/* Bottom action bar */}
      <div className="action-bar">
        <span className="action-bar-label">AI Generate:</span>

        <button className="ai-btn risk" disabled={anyLoading} onClick={() => { generateRisk(); setActiveTab('AI Analysis'); }}>
          {loading.risk ? <span className="spinner" /> : '🔵'} Risk Analysis
        </button>
        <button className="ai-btn narrative" disabled={anyLoading} onClick={() => { generateNarrative(); setActiveTab('Submission'); }}>
          {loading.narrative
            ? <><span className="spinner" /> {
                narrativePhase === 'refining' ? 'Refining for underwriters…'
                : narrativePhase.startsWith('retry-') ? `Retrying… (${narrativePhase.slice(-1)}/3)`
                : 'Generating narrative…'
              }</>
            : '🟣 Submission Narrative'
          }
        </button>
        <button className="ai-btn missing" disabled={anyLoading} onClick={() => { generateMissing(); setActiveTab('Missing Items'); }}>
          {loading.missing ? <span className="spinner" /> : '🟡'} Missing Items
        </button>
        <button className="ai-btn playbook" disabled={anyLoading} onClick={() => { generatePlaybook(); setActiveTab('Playbook'); }}>
          {loading.playbook ? <span className="spinner" /> : '🟢'} Safety Playbook
        </button>
        <button className="ai-btn run-all" disabled={anyLoading} onClick={runAll}>
          {anyLoading ? <><span className="spinner dark" /> Running…</> : '⚡ Run All'}
        </button>

        <div style={{ flex: 1 }} />

        <button className="ai-btn export" onClick={exportExcel}>
          📊 Export to Excel
        </button>
      </div>

      {/* Pipeline: Lost Modal */}
      {showLostModal && (
        <div className="modal-overlay" onClick={() => setShowLostModal(false)}>
          <div className="modal-box pipeline-modal" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Mark Account as Lost</h3>
            <label className="modal-label">Reason Lost</label>
            <select className="modal-input" value={lostForm.reason} onChange={e => setLostForm(f => ({ ...f, reason: e.target.value }))}>
              <option value="">Select reason…</option>
              {LOST_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <label className="modal-label">Market / Carrier</label>
            <input className="modal-input" placeholder="e.g. Progressive" value={lostForm.market} onChange={e => setLostForm(f => ({ ...f, market: e.target.value }))} />
            <label className="modal-label">Premium (if known)</label>
            <input className="modal-input" placeholder="e.g. $12,500" value={lostForm.premium} onChange={e => setLostForm(f => ({ ...f, premium: e.target.value }))} />
            <label className="modal-label">Notes</label>
            <textarea className="modal-textarea" rows={3} placeholder="Additional context…" value={lostForm.notes} onChange={e => setLostForm(f => ({ ...f, notes: e.target.value }))} />
            <div className="modal-actions">
              <button className="modal-btn-cancel" onClick={() => setShowLostModal(false)}>Cancel</button>
              <button className="modal-btn-confirm modal-btn-lost" onClick={() => {
                setShowLostModal(false);
                if (carrierId) patchCarrierStatus(carrierId, 'lost', lostForm);
              }}>Mark Lost</button>
            </div>
          </div>
        </div>
      )}

      {/* Pipeline: Bound Modal */}
      {showBoundModal && (
        <div className="modal-overlay" onClick={() => setShowBoundModal(false)}>
          <div className="modal-box pipeline-modal" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Mark Account as Bound</h3>
            <label className="modal-label">Market / Carrier Bound With</label>
            <input className="modal-input" placeholder="e.g. Great West Casualty" value={boundForm.market} onChange={e => setBoundForm(f => ({ ...f, market: e.target.value }))} />
            <label className="modal-label">Effective Date</label>
            <input className="modal-input" type="date" value={boundForm.effectiveDate} onChange={e => setBoundForm(f => ({ ...f, effectiveDate: e.target.value }))} />
            <label className="modal-label">Expiration Date</label>
            <input className="modal-input" type="date" value={boundForm.expDate} onChange={e => setBoundForm(f => ({ ...f, expDate: e.target.value }))} />
            <label className="modal-label">Annual Premium</label>
            <input className="modal-input" placeholder="e.g. $18,000" value={boundForm.premium} onChange={e => setBoundForm(f => ({ ...f, premium: e.target.value }))} />
            <div className="modal-actions">
              <button className="modal-btn-cancel" onClick={() => setShowBoundModal(false)}>Cancel</button>
              <button className="modal-btn-confirm modal-btn-bound" onClick={() => {
                setShowBoundModal(false);
                if (carrierId) patchCarrierStatus(carrierId, 'bound', boundForm);
              }}>Confirm Bound</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

